import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Plus, Check, X, Trophy, Store, Bell as BellIcon, Crown, RefreshCw, Compass, Flame, Thermometer, QrCode, Tag, Save, Package as PackageIcon, Zap, CreditCard, Lock, Trash2, Gift, Target, Loader2, LocateFixed, BarChart3, Download, FileText, Info, KeyRound, UserPlus, Send, Coins, Sparkles } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import QrPosterModal from "@/components/QrPosterModal";
import ZonePicker from "@/components/ZonePicker";
import PhotoUploader from "@/components/PhotoUploader";
import { BadgeTile } from "@/components/BadgeStrip";
import { ftToM, mToFt } from "@/lib/units";
import { fmtEstDateTime, etLocalToIso, isoToEtDatetimeLocal } from "@/lib/time";

function StatCard({ k, v, color = "#76B82A" }) {
  return (
    <div className="loot-card-flat p-4">
      <div className="overline">{k}</div>
      <div className="font-display text-4xl mt-1" style={{ color }}>{v}</div>
    </div>
  );
}

/**
 * Reusable "Use my current location" button.
 * Calls navigator.geolocation and invokes onLocate({ lat, lng }) with 6-decimal
 * precision. Handles permission-denied, timeout, insecure-origin, and browsers
 * that don't support geolocation — each with a friendly toast.
 *
 * Usage:
 *   <UseMyLocationButton onLocate={({lat, lng}) => setForm({...form, lat, lng})} />
 */
function UseMyLocationButton({ onLocate, testid = "use-my-location", compact = false, className = "" }) {
  const [loading, setLoading] = useState(false);
  const click = () => {
    if (!navigator.geolocation) {
      toast.error("This browser doesn't support geolocation.");
      return;
    }
    if (typeof window !== "undefined" && window.location?.protocol !== "https:" && window.location?.hostname !== "localhost") {
      toast.error("Geolocation requires HTTPS. Try the deployed site.");
      return;
    }
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLoading(false);
        const lat = Number(pos.coords.latitude.toFixed(6));
        const lng = Number(pos.coords.longitude.toFixed(6));
        onLocate({ lat, lng, accuracy_m: pos.coords.accuracy });
        const acc = Math.round(pos.coords.accuracy || 0);
        toast.success(`Pinned your spot · accurate to ±${acc} m`);
      },
      (err) => {
        setLoading(false);
        const msg =
          err.code === err.PERMISSION_DENIED
            ? "Location permission denied. Enable it in your browser settings."
            : err.code === err.POSITION_UNAVAILABLE
            ? "Location unavailable. Try again outdoors."
            : err.code === err.TIMEOUT
            ? "Location lookup timed out. Try again."
            : err.message || "Couldn't read your location.";
        toast.error(msg);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  };
  return (
    <button
      type="button"
      onClick={click}
      disabled={loading}
      className={`btn-ghost inline-flex items-center gap-1 ${compact ? "text-[11px] px-2 py-1" : "text-xs"} ${className}`}
      data-testid={testid}
      title="Use the current device location"
    >
      {loading ? <Loader2 size={12} className="animate-spin" /> : <LocateFixed size={12} />}
      {loading ? "Locating…" : "Use my location"}
    </button>
  );
}



// ======================================================================
// HintBandsCard — admin-configurable warmer/colder distance thresholds
// (in feet). Lives at the top of the Drops tab. Affects every drop AND
// loot quest hint band response.
// ======================================================================
function HintBandsCard() {
  const [bands, setBands] = useState(null);
  const [busy, setBusy] = useState(false);
  const [hot, setHot] = useState(165);
  const [warmer, setWarmer] = useState(656);
  const [warm, setWarm] = useState(1640);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/hint-bands");
      setBands(data);
      setHot(data.hot_max_ft);
      setWarmer(data.warmer_max_ft);
      setWarm(data.warm_max_ft);
    } catch (e) {
      setBands({});
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const h = parseInt(hot, 10), wr = parseInt(warmer, 10), wm = parseInt(warm, 10);
    if (!h || !wr || !wm || h <= 0 || wr <= h || wm <= wr) {
      toast.error("Thresholds must be positive and ordered: ON FIRE < WARMER < WARM");
      return;
    }
    setBusy(true);
    try {
      await api.put("/admin/hint-bands", { hot_max_ft: h, warmer_max_ft: wr, warm_max_ft: wm });
      toast.success("Hint bands saved");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save");
    } finally { setBusy(false); }
  };

  const reset = async () => {
    if (!confirm("Reset hint band thresholds to defaults (165 / 656 / 1640 ft)?")) return;
    setBusy(true);
    try {
      await api.post("/admin/hint-bands/reset");
      toast.success("Reset");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't reset");
    } finally { setBusy(false); }
  };

  if (!bands) return null;

  return (
    <div className="loot-card-flat p-5 mb-6" data-testid="hint-bands-card">
      <div className="overline">Warmer / colder thresholds</div>
      <div className="font-display uppercase text-xl mt-1">Hint distance bands</div>
      <p className="text-[#F5F2E8]/55 text-xs mt-1 max-w-xl">
        How close hunters need to get for each warmer/colder band, in feet. Affects every live drop and every Loot Quest chest. The "Colder" band stretches from <span className="text-[#76B82A] font-mono">WARM</span> to each drop's own hint radius; "Way off" is anything beyond.
      </p>
      <div className="grid md:grid-cols-3 gap-3 mt-4">
        <div className="border-l-4 pl-3" style={{ borderColor: "#E53935" }} data-testid="hint-band-hot">
          <div className="splash-mono uppercase tracking-widest text-[10px]" style={{ color: "#E53935" }}>ON FIRE — under</div>
          <div className="flex items-baseline gap-2 mt-1">
            <input
              type="number" min="1" max="50000" className="loot-input flex-1"
              value={hot} onChange={(e) => setHot(e.target.value)}
              data-testid="hint-band-hot-input"
            />
            <span className="font-mono text-xs text-[#F5F2E8]/55">ft</span>
          </div>
        </div>
        <div className="border-l-4 pl-3" style={{ borderColor: "#D7A62A" }} data-testid="hint-band-warmer">
          <div className="splash-mono uppercase tracking-widest text-[10px]" style={{ color: "#D7A62A" }}>WARMER — under</div>
          <div className="flex items-baseline gap-2 mt-1">
            <input
              type="number" min="2" max="50000" className="loot-input flex-1"
              value={warmer} onChange={(e) => setWarmer(e.target.value)}
              data-testid="hint-band-warmer-input"
            />
            <span className="font-mono text-xs text-[#F5F2E8]/55">ft</span>
          </div>
        </div>
        <div className="border-l-4 pl-3" style={{ borderColor: "#76B82A" }} data-testid="hint-band-warm">
          <div className="splash-mono uppercase tracking-widest text-[10px]" style={{ color: "#76B82A" }}>WARM — under</div>
          <div className="flex items-baseline gap-2 mt-1">
            <input
              type="number" min="3" max="50000" className="loot-input flex-1"
              value={warm} onChange={(e) => setWarm(e.target.value)}
              data-testid="hint-band-warm-input"
            />
            <span className="font-mono text-xs text-[#F5F2E8]/55">ft</span>
          </div>
        </div>
      </div>
      <div className="flex gap-2 mt-4">
        <button onClick={save} disabled={busy} className="btn-primary text-xs" data-testid="hint-bands-save">
          {busy ? "Saving…" : "Save thresholds"}
        </button>
        <button onClick={reset} disabled={busy} className="btn-ghost text-xs" data-testid="hint-bands-reset">
          Reset to defaults
        </button>
        <span className="font-mono text-[10px] text-[#F5F2E8]/40 ml-auto self-center">
          Defaults: {bands?.defaults?.hot_max_ft || 165} / {bands?.defaults?.warmer_max_ft || 656} / {bands?.defaults?.warm_max_ft || 1640} ft
        </span>
      </div>
    </div>
  );
}


function DropsTab() {
  const [drops, setDrops] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const blank = {
    title: "", city: "", amount: 50, sponsor_type: "self", sponsor_id: "",
    zone_description: "", claim_code: "", push_message: "", mystery_amount: false, is_golden: false,
    enable_gps_hints: false, hint_center_lat: "", hint_center_lng: "", hint_radius_ft: 3280,
    signal_enabled: false,
    prize_type: "cash", prize_custom_label: "",
    is_bounty: false, bounty_instructions: "",
    launch_time_et: "",
    clues: [{ text: "", order: 1, release_offset_minutes: 0, type: "clue" }],
  };
  const [form, setForm] = useState(blank);
  const [sponsors, setSponsors] = useState([]);
  const [sponsorLinkDropId, setSponsorLinkDropId] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [tplName, setTplName] = useState("");
  const [tplNotes, setTplNotes] = useState("");
  const [tplCounts, setTplCounts] = useState({}); // { [tplId]: count }

  const refresh = async () => {
    const [d, s] = await Promise.all([api.get("/drops"), api.get("/sponsors")]);
    setDrops(d.data.drops); setSponsors(s.data.sponsors);
  };
  const refreshTemplates = async () => {
    try {
      const { data } = await api.get("/admin/drop-templates");
      setTemplates(data.items || []);
    } catch {
      // silent — panel is optional
    }
  };
  useEffect(() => { refresh(); refreshTemplates(); }, []);

  const saveTemplateFromForm = async () => {
    const name = (tplName || "").trim();
    if (!name) { toast.error("Give the template a name."); return; }
    const payload = {
      ...form,
      amount: Number(form.amount),
      hint_radius_m: ftToM(Number(form.hint_radius_ft) || 3280),
      hint_center_lat: form.hint_center_lat === "" ? null : Number(form.hint_center_lat),
      hint_center_lng: form.hint_center_lng === "" ? null : Number(form.hint_center_lng),
    };
    delete payload.hint_radius_ft;
    delete payload.launch_time_et;
    delete payload.claim_code;
    try {
      await api.post("/admin/drop-templates", { name, notes: tplNotes, payload });
      toast.success(`Saved template "${name}"`);
      setTplName(""); setTplNotes("");
      refreshTemplates();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    }
  };

  const saveTemplateFromDrop = async (d) => {
    const name = window.prompt(`Template name for "${d.title}"?`, d.title);
    if (!name || !name.trim()) return;
    try {
      await api.post("/admin/drop-templates", {
        name: name.trim(),
        notes: `Cloned from drop "${d.title}"`,
        from_drop_id: d.id,
      });
      toast.success(`Saved template "${name.trim()}"`);
      refreshTemplates();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    }
  };

  const instantiateTemplate = async (tpl) => {
    const count = Math.max(1, Math.min(25, Number(tplCounts[tpl.id]) || 1));
    try {
      const { data } = await api.post(`/admin/drop-templates/${tpl.id}/instantiate`, { count });
      toast.success(`Created ${data.created} draft${data.created === 1 ? "" : "s"} — edit each below.`);
      refresh();
      // Scroll to the drops list so the admin can see new drafts.
      setTimeout(() => window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" }), 200);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to create drafts");
    }
  };

  const deleteTemplate = async (tpl) => {
    if (!window.confirm(`Delete template "${tpl.name}"?`)) return;
    try {
      await api.delete(`/admin/drop-templates/${tpl.id}`);
      toast.success("Template deleted");
      refreshTemplates();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
    }
  };

  const updateStatus = async (id, status) => {
    await api.patch(`/drops/${id}`, { status });
    toast.success(`Drop ${status}`);
    refresh();
  };
  const del = async (id) => {
    if (!confirm("Delete this drop?")) return;
    await api.delete(`/drops/${id}`);
    refresh();
  };

  const startEdit = async (d) => {
    setEditingId(d.id);
    setOpen(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
    // The public /api/drops response redacts claim_code + hint coordinates to
    // prevent cheating. For the admin edit form we need the full document.
    let full = d;
    try {
      const { data } = await api.get(`/admin/drops/${d.id}`);
      full = data.drop || d;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't load full drop — check your admin session.");
    }
    setForm({
      title: full.title || "",
      city: full.city || "",
      amount: full.amount ?? 0,
      sponsor_type: full.sponsor_type || "self",
      sponsor_id: full.sponsor_id || "",
      zone_description: full.zone_description || "",
      claim_code: full.claim_code || "",
      push_message: full.push_message || "",
      mystery_amount: !!full.mystery_amount,
      is_golden: !!full.is_golden,
      enable_gps_hints: !!full.enable_gps_hints,
      hint_center_lat: full.hint_center_lat ?? "",
      hint_center_lng: full.hint_center_lng ?? "",
      hint_radius_ft: full.hint_radius_m ? mToFt(full.hint_radius_m) : 3280,
      signal_enabled: !!full.signal_enabled,
      prize_type: full.prize_type || "cash",
      prize_custom_label: full.prize_custom_label || "",
      is_bounty: !!full.is_bounty,
      bounty_instructions: full.bounty_instructions || "",
      launch_time_et: isoToEtDatetimeLocal(full.launch_time),
      clues: (full.clues && full.clues.length > 0) ? full.clues : blank.clues,
    });
  };
  const cancelEdit = () => { setEditingId(null); setOpen(false); setForm(blank); };

  const addClue = () => setForm({
    ...form,
    clues: [...form.clues, { text: "", order: form.clues.length + 1, release_offset_minutes: form.clues.length * 15, type: "clue" }],
  });
  const setClue = (i, k, v) => {
    const c = [...form.clues]; c[i] = { ...c[i], [k]: v }; setForm({ ...form, clues: c });
  };

  const selectedSponsor = sponsors.find((s) => s.id === form.sponsor_id);
  const canGolden =
    form.sponsor_type === "business" &&
    selectedSponsor?.package === "city_takeover";

  const create = async (e, asDraft = false) => {
    if (e?.preventDefault) e.preventDefault();
    try {
      const payload = {
        ...form,
        amount: Number(form.amount),
        hint_radius_m: ftToM(Number(form.hint_radius_ft) || 3280),
        hint_center_lat: form.hint_center_lat === "" ? null : Number(form.hint_center_lat),
        hint_center_lng: form.hint_center_lng === "" ? null : Number(form.hint_center_lng),
        launch_time: form.launch_time_et ? etLocalToIso(form.launch_time_et) : null,
      };
      delete payload.hint_radius_ft;
      delete payload.launch_time_et;
      if (!payload.sponsor_id) delete payload.sponsor_id;
      if (payload.sponsor_type === "self") { payload.sponsor_id = null; payload.is_golden = false; payload.is_bounty = false; }
      if (payload.is_golden && !canGolden) {
        toast.error("Golden drops require a City Takeover business sponsor.");
        return;
      }
      if (payload.is_bounty) {
        if (payload.sponsor_type !== "business" || !payload.sponsor_id) {
          toast.error("Bounty drops require a business sponsor (drives foot traffic into their shop).");
          return;
        }
        // Bounty drops need GPS hints enabled so the hunter gets the
        // "get inside & scan" prompt when they approach the shop.
        payload.enable_gps_hints = true;
      } else {
        payload.bounty_instructions = null;
      }
      if ((payload.enable_gps_hints || payload.is_golden) &&
          (payload.hint_center_lat === null || payload.hint_center_lng === null)) {
        toast.error("GPS hints require center lat/lng coordinates.");
        return;
      }
      // Draft mode: stays private, no notification, hidden from public lists.
      if (asDraft) {
        payload.status = "draft";
      }
      if (editingId) {
        await api.patch(`/drops/${editingId}`, payload);
        toast.success("Drop updated");
      } else {
        await api.post("/drops", payload);
        toast.success(
          asDraft
            ? "Saved as DRAFT — hunters won't see it yet"
            : (payload.is_golden ? "Golden drop launched ★" : (payload.is_bounty ? "Bounty drop launched ◎" : "Drop launched")),
        );
      }
      cancelEdit(); refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div>
      <HintBandsCard />

      {/* Drop templates — save any drop's setup once, stamp out N drafts anytime */}
      <div
        className="loot-card-flat p-5 mb-4"
        style={{ borderColor: "rgba(215,166,42,0.35)" }}
        data-testid="drop-templates-panel"
      >
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <div className="overline" style={{ color: "#D7A62A" }}>Drop templates</div>
            <div className="font-mono text-[11px] text-[#F5F2E8]/60 mt-1">
              Save a drop's setup (city, amount, sponsor, clues) as a template. Stamp out N drafts — then just tweak locations + launch times.
            </div>
          </div>
          <button
            type="button"
            onClick={refreshTemplates}
            className="btn-ghost text-xs"
            data-testid="tpl-refresh"
          >
            Refresh
          </button>
        </div>

        {open && (
          <div
            className="border border-dashed p-3 mb-3 flex flex-col md:flex-row gap-2 md:items-end"
            style={{ borderColor: "rgba(215,166,42,0.45)" }}
          >
            <div className="flex-1">
              <span className="font-mono text-[10px] text-[#F5F2E8]/60 uppercase tracking-widest block mb-1">
                Save current form as template
              </span>
              <input
                className="loot-input"
                placeholder="Template name (e.g. Kingsport $25 dropstyle)"
                value={tplName}
                onChange={(e) => setTplName(e.target.value)}
                data-testid="tpl-name"
              />
            </div>
            <div className="flex-1">
              <span className="font-mono text-[10px] text-[#F5F2E8]/60 uppercase tracking-widest block mb-1">
                Notes (optional)
              </span>
              <input
                className="loot-input"
                placeholder="What to change per instance"
                value={tplNotes}
                onChange={(e) => setTplNotes(e.target.value)}
                data-testid="tpl-notes"
              />
            </div>
            <button
              type="button"
              onClick={saveTemplateFromForm}
              className="btn-loot text-xs whitespace-nowrap"
              data-testid="tpl-save-from-form"
            >
              Save template
            </button>
          </div>
        )}

        {templates.length === 0 ? (
          <div className="font-mono text-xs text-[#F5F2E8]/50 italic">
            No templates yet. Fill the "New drop" form and click "Save template", or use "Save as template" on any existing drop below.
          </div>
        ) : (
          <div className="space-y-2">
            {templates.map((t) => (
              <div
                key={t.id}
                className="flex items-center justify-between gap-2 flex-wrap p-3 border border-[#F5F2E8]/10"
                data-testid={`tpl-row-${t.id}`}
              >
                <div className="min-w-0">
                  <div className="font-display uppercase text-base">{t.name}</div>
                  <div className="font-mono text-[10px] text-[#F5F2E8]/60 truncate">
                    {(t.payload?.city || "—")} · ${t.payload?.amount ?? 0} · {(t.payload?.clues || []).length} clue{(t.payload?.clues || []).length === 1 ? "" : "s"}
                    {t.notes ? ` · ${t.notes}` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={1}
                    max={25}
                    className="loot-input"
                    style={{ width: "70px" }}
                    value={tplCounts[t.id] ?? 1}
                    onChange={(e) => setTplCounts({ ...tplCounts, [t.id]: e.target.value })}
                    data-testid={`tpl-count-${t.id}`}
                  />
                  <button
                    type="button"
                    onClick={() => instantiateTemplate(t)}
                    className="btn-loot text-xs"
                    data-testid={`tpl-instantiate-${t.id}`}
                  >
                    Create drafts
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteTemplate(t)}
                    className="btn-ghost text-xs"
                    style={{ color: "#E53935" }}
                    data-testid={`tpl-delete-${t.id}`}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-between items-center mb-4">
        <div className="overline">Drop management</div>
        <button
          onClick={() => (open ? cancelEdit() : setOpen(true))}
          className="btn-loot"
          data-testid="admin-create-drop-btn"
        >
          <Plus size={14} className="inline mr-1" /> {open ? "Cancel" : "New drop"}
        </button>
      </div>

      {open && (
        <form onSubmit={create} className="loot-card-flat p-6 mb-6 space-y-3" data-testid="admin-drop-form">
          <input className="loot-input" placeholder="Title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <div className="grid md:grid-cols-3 gap-3">
            <input className="loot-input" placeholder="City" required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
            <input className="loot-input" type="number" placeholder="Amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
            <select className="loot-input" value={form.sponsor_type} onChange={(e) => setForm({ ...form, sponsor_type: e.target.value, is_golden: false, is_bounty: e.target.value === "business" ? form.is_bounty : false, bounty_instructions: e.target.value === "business" ? form.bounty_instructions : "" })}>
              <option value="self">Self-funded (The Local Loot)</option>
              <option value="business">Business sponsored</option>
            </select>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <select
              className="loot-input"
              value={form.prize_type}
              onChange={(e) => setForm({ ...form, prize_type: e.target.value })}
              data-testid="admin-prize-type"
            >
              <option value="cash">Cash</option>
              <option value="gift_card">Gift Card</option>
              <option value="gift">Gift / Experience</option>
              <option value="merch">Merch</option>
              <option value="other">Other (custom)</option>
            </select>
            {form.prize_type !== "cash" && (
              <input
                className="loot-input"
                placeholder={
                  form.prize_type === "gift_card"
                    ? "e.g. Starbucks, Amazon, local diner"
                    : form.prize_type === "merch"
                    ? "e.g. Nike Hoodie XL"
                    : form.prize_type === "gift"
                    ? "e.g. Dinner for 2 at Joe's"
                    : "Describe the prize"
                }
                value={form.prize_custom_label}
                onChange={(e) => setForm({ ...form, prize_custom_label: e.target.value })}
                data-testid="admin-prize-custom"
                maxLength={120}
              />
            )}
          </div>
          {form.sponsor_type === "business" && (
            <select
              className="loot-input"
              value={form.sponsor_id}
              onChange={(e) => setForm({ ...form, sponsor_id: e.target.value, is_golden: false, is_bounty: e.target.value ? form.is_bounty : false, bounty_instructions: e.target.value ? form.bounty_instructions : "" })}
            >
              <option value="">— Choose sponsor —</option>
              {sponsors.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.business_name} · {s.package.replace("_", " ")}
                </option>
              ))}
            </select>
          )}
          <input className="loot-input" placeholder="Zone description (e.g. downtown Kingsport)" value={form.zone_description} onChange={(e) => setForm({ ...form, zone_description: e.target.value })} />
          <input className="loot-input font-mono uppercase" placeholder="Claim code (e.g. LOOT-XXX-001)" required value={form.claim_code} onChange={(e) => setForm({ ...form, claim_code: e.target.value.toUpperCase() })} data-testid="drop-claim-code" />
          <input className="loot-input" placeholder="Push message" value={form.push_message} onChange={(e) => setForm({ ...form, push_message: e.target.value })} />
          <div>
            <label className="overline block mb-1">Launch time (ET)</label>
            <div className="flex items-center gap-2 flex-wrap">
              <input
                type="datetime-local"
                className="loot-input"
                value={form.launch_time_et}
                onChange={(e) => setForm({ ...form, launch_time_et: e.target.value })}
                data-testid="admin-drop-launch-time"
              />
              {form.launch_time_et && (
                <button
                  type="button"
                  onClick={() => setForm({ ...form, launch_time_et: "" })}
                  className="btn-ghost text-xs"
                  data-testid="admin-drop-launch-clear"
                >
                  Clear
                </button>
              )}
              <span className="font-mono text-[10px] text-[#F5F2E8]/50">
                {form.launch_time_et
                  ? "Drop will be scheduled (or go live immediately if the time is already past)."
                  : "Leave blank to launch the drop live right now."}
              </span>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.mystery_amount} onChange={(e) => setForm({ ...form, mystery_amount: e.target.checked })} />
              Mystery amount (hide prize)
            </label>
            <label
              className={`flex items-center gap-2 text-sm ${canGolden ? "" : "opacity-50"}`}
              title={canGolden ? "" : "Requires a City Takeover business sponsor"}
            >
              <input
                type="checkbox"
                disabled={!canGolden}
                checked={form.is_golden}
                onChange={(e) => setForm({ ...form, is_golden: e.target.checked, enable_gps_hints: e.target.checked ? true : form.enable_gps_hints })}
                data-testid="admin-golden-check"
              />
              <span className="flex items-center gap-1">
                <Crown size={14} style={{ color: "#D7A62A" }} /> Mark as Golden Drop
              </span>
              {!canGolden && (
                <span className="font-mono text-[10px] text-[#F5F2E8]/50">
                  (Requires City Takeover sponsor)
                </span>
              )}
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.enable_gps_hints}
                onChange={(e) => setForm({ ...form, enable_gps_hints: e.target.checked })}
                data-testid="admin-gps-check"
              />
              <span>Enable warmer/colder GPS hints</span>
              {form.is_golden && (
                <span className="font-mono text-[10px] text-[#D7A62A]">(auto for Golden)</span>
              )}
              {form.is_bounty && (
                <span className="font-mono text-[10px] text-[#D7A62A]">(auto for Bounty)</span>
              )}
            </label>
            <label
              className={`flex items-center gap-2 text-sm ${form.enable_gps_hints || form.is_golden ? "" : "opacity-50"}`}
              title={form.enable_gps_hints || form.is_golden ? "" : "Requires GPS hints to be enabled"}
            >
              <input
                type="checkbox"
                disabled={!(form.enable_gps_hints || form.is_golden)}
                checked={form.signal_enabled}
                onChange={(e) => setForm({ ...form, signal_enabled: e.target.checked })}
                data-testid="admin-signal-check"
              />
              <span className="flex items-center gap-1">
                <Compass size={14} style={{ color: "#E53935" }} /> Enable The Signal (Geiger-counter pulse)
              </span>
              <span className="font-mono text-[10px] text-[#F5F2E8]/50">
                (Audio + vibration that speeds up as the hunter gets close)
              </span>
            </label>
            <label
              className={`flex items-center gap-2 text-sm ${form.sponsor_type === "business" && form.sponsor_id ? "" : "opacity-50"}`}
              title={form.sponsor_type === "business" && form.sponsor_id ? "" : "Requires a business sponsor"}
            >
              <input
                type="checkbox"
                disabled={!(form.sponsor_type === "business" && form.sponsor_id)}
                checked={form.is_bounty}
                onChange={(e) => setForm({
                  ...form,
                  is_bounty: e.target.checked,
                  enable_gps_hints: e.target.checked ? true : form.enable_gps_hints,
                })}
                data-testid="admin-bounty-check"
              />
              <span className="flex items-center gap-1">
                <Target size={14} style={{ color: "#D7A62A" }} /> Mark as Bounty Drop
              </span>
              <span className="font-mono text-[10px] text-[#F5F2E8]/50">
                (Hunters must go inside the shop for the final hint)
              </span>
            </label>
            {form.is_bounty && (
              <textarea
                className="loot-input"
                rows={2}
                maxLength={280}
                placeholder="In-shop instructions for the hunter (e.g. Ask the barista for today's Loot code at the counter). Leave blank for default message."
                value={form.bounty_instructions}
                onChange={(e) => setForm({ ...form, bounty_instructions: e.target.value })}
                data-testid="admin-bounty-instructions"
              />
            )}
            {(form.enable_gps_hints || form.is_golden) && (
              <>
                <ZonePicker
                  lat={form.hint_center_lat}
                  lng={form.hint_center_lng}
                  radius={ftToM(form.hint_radius_ft)}
                  onChange={({ lat, lng }) =>
                    setForm((f) => ({ ...f, hint_center_lat: lat, hint_center_lng: lng }))
                  }
                />
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="font-mono text-[11px] text-[#F5F2E8]/50">
                    Hiding spot coordinates (used for warmer/colder GPS hints).
                  </span>
                  <UseMyLocationButton
                    testid="admin-drop-use-location"
                    onLocate={({ lat, lng }) =>
                      setForm((f) => ({
                        ...f,
                        hint_center_lat: String(lat),
                        hint_center_lng: String(lng),
                      }))
                    }
                  />
                </div>
                <div className="grid md:grid-cols-3 gap-2">
                  <input
                    className="loot-input"
                    type="number"
                    step="any"
                    placeholder="Hint center lat (e.g. 36.5484)"
                    value={form.hint_center_lat}
                    onChange={(e) => setForm({ ...form, hint_center_lat: e.target.value })}
                    data-testid="admin-gps-lat"
                  />
                  <input
                    className="loot-input"
                    type="number"
                    step="any"
                    placeholder="Hint center lng (e.g. -82.5618)"
                    value={form.hint_center_lng}
                    onChange={(e) => setForm({ ...form, hint_center_lng: e.target.value })}
                    data-testid="admin-gps-lng"
                  />
                  <input
                    className="loot-input"
                    type="number"
                    placeholder="Max radius (ft)"
                    value={form.hint_radius_ft}
                    onChange={(e) => setForm({ ...form, hint_radius_ft: e.target.value })}
                    data-testid="admin-gps-radius"
                  />
                </div>
              </>
            )}
          </div>
          <div>
            <div className="overline mb-2">Clues</div>
            <p className="font-mono text-[11px] text-[#F5F2E8]/50 mb-3">
              Order = display order (1, 2, 3...). Release after = minutes from launch before hunters can see this clue (0 = shown immediately).
            </p>
            {form.clues.map((c, i) => (
              <div key={i} className="loot-card-flat p-3 mb-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="overline">Clue {i + 1}</div>
                  {form.clues.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, clues: form.clues.filter((_, idx) => idx !== i) })}
                      className="font-mono text-[10px] text-[#E53935]"
                      data-testid={`clue-remove-${i}`}
                    >
                      Remove
                    </button>
                  )}
                </div>
                <input
                  className="loot-input"
                  placeholder="Clue text (e.g. 'Start at the old stone wall downtown')"
                  value={c.text}
                  onChange={(e) => setClue(i, "text", e.target.value)}
                  data-testid={`clue-text-${i}`}
                />
                <div className="grid grid-cols-3 gap-2">
                  <label className="block">
                    <span className="font-mono text-[10px] text-[#F5F2E8]/60 uppercase tracking-widest block mb-1">Order</span>
                    <input
                      className="loot-input"
                      type="number"
                      min={1}
                      value={c.order}
                      onChange={(e) => setClue(i, "order", Number(e.target.value))}
                      data-testid={`clue-order-${i}`}
                    />
                  </label>
                  <label className="block">
                    <span className="font-mono text-[10px] text-[#F5F2E8]/60 uppercase tracking-widest block mb-1">Release after (min)</span>
                    <input
                      className="loot-input"
                      type="number"
                      min={0}
                      value={c.release_offset_minutes}
                      onChange={(e) => setClue(i, "release_offset_minutes", Number(e.target.value))}
                      data-testid={`clue-offset-${i}`}
                    />
                  </label>
                  <label className="block">
                    <span className="font-mono text-[10px] text-[#F5F2E8]/60 uppercase tracking-widest block mb-1">Type</span>
                    <select
                      className="loot-input"
                      value={c.type}
                      onChange={(e) => setClue(i, "type", e.target.value)}
                      data-testid={`clue-type-${i}`}
                    >
                      <option value="clue">clue</option>
                      <option value="riddle">riddle</option>
                      <option value="bonus">bonus</option>
                      <option value="final">final</option>
                    </select>
                  </label>
                </div>
              </div>
            ))}
            <button type="button" onClick={addClue} className="btn-ghost text-xs">+ Add clue</button>
          </div>
          <button type="submit" className="btn-loot w-full" data-testid="admin-drop-submit">
            {editingId ? "Save changes" : (form.is_golden ? "Launch GOLDEN drop ★" : (form.is_bounty ? "Launch BOUNTY drop ◎" : "Launch drop"))}
          </button>
          {!editingId && (
            <button
              type="button"
              onClick={(e) => create(e, true)}
              className="btn-ghost w-full mt-2"
              style={{ borderColor: "#D7A62A", color: "#D7A62A" }}
              data-testid="admin-drop-save-draft"
            >
              Save as DRAFT (private — test before launching)
            </button>
          )}
        </form>
      )}

      <div className="space-y-2">
        {drops.map((d) => (
          <div
            key={d.id}
            className="loot-card-flat p-4 flex items-center justify-between"
            style={
              d.status === "draft"
                ? { borderColor: "#D7A62A", borderStyle: "dashed" }
                : (d.is_golden ? { borderColor: "#D7A62A" } : (d.is_bounty ? { borderColor: "#D7A62A" } : undefined))
            }
            data-testid={`drop-row-${d.id}`}
          >
            <div>
              <div className="font-display uppercase text-lg flex items-center gap-2">
                {d.is_golden && <Crown size={14} style={{ color: "#D7A62A" }} />}
                {d.is_bounty && <Target size={14} style={{ color: "#D7A62A" }} />}
                {d.title}
                {d.status === "draft" && (
                  <span
                    className="font-mono text-[10px] px-1.5 py-0.5"
                    style={{ color: "#D7A62A", border: "1px dashed #D7A62A" }}
                    data-testid={`drop-draft-badge-${d.id}`}
                  >
                    DRAFT · PRIVATE
                  </span>
                )}
                {d.is_bounty && (
                  <span className="font-mono text-[10px] text-[#D7A62A] border border-[#D7A62A] px-1.5 py-0.5">BOUNTY</span>
                )}
              </div>
              <div className="font-mono text-xs text-[#F5F2E8]/60">
                {d.city} · ${d.amount} · {d.status}
                {d.status === "scheduled" && d.launch_time && (
                  <> · <span className="text-[#D7A62A]">launches {fmtEstDateTime(d.launch_time)}</span></>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => startEdit(d)} className="btn-ghost text-xs" data-testid={`drop-edit-${d.id}`}>Edit</button>
              <button
                onClick={() => saveTemplateFromDrop(d)}
                className="btn-ghost text-xs"
                style={{ color: "#D7A62A", borderColor: "#D7A62A" }}
                data-testid={`drop-save-tpl-${d.id}`}
                title="Save this drop's setup as a reusable template"
              >
                Save as template
              </button>
              {d.status === "draft" && (
                <a
                  href={`/drops/${d.id}?preview=1`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-ghost text-xs"
                  style={{ color: "#D7A62A", borderColor: "#D7A62A" }}
                  data-testid={`drop-preview-${d.id}`}
                >
                  Preview as hunter
                </a>
              )}
              {d.status === "draft" && (
                <button
                  onClick={() => setSponsorLinkDropId(d.id)}
                  className="btn-ghost text-xs"
                  style={{ color: "#D7A62A", borderColor: "#D7A62A" }}
                  data-testid={`drop-sponsor-link-${d.id}`}
                >
                  Sponsor link
                </button>
              )}
              {d.status === "draft" && (
                <button
                  onClick={() => updateStatus(d.id, "live")}
                  className="btn-ghost text-xs"
                  style={{ color: "#76B82A", borderColor: "#76B82A" }}
                  data-testid={`drop-publish-${d.id}`}
                >
                  Publish
                </button>
              )}
              {d.status !== "live" && d.status !== "draft" && <button onClick={() => updateStatus(d.id, "live")} className="btn-ghost text-xs">Go live</button>}
              {d.status !== "closed" && d.status !== "draft" && <button onClick={() => updateStatus(d.id, "closed")} className="btn-ghost text-xs">Close</button>}
              <button onClick={() => del(d.id)} className="btn-ghost text-xs" style={{ color: "#E53935" }} data-testid={`drop-del-${d.id}`}>Delete</button>
            </div>
          </div>
        ))}
      </div>
      {sponsorLinkDropId && (
        <SponsorPreviewLinkModal
          dropId={sponsorLinkDropId}
          drop={drops.find((x) => x.id === sponsorLinkDropId)}
          onClose={() => setSponsorLinkDropId(null)}
        />
      )}
    </div>
  );
}

function SponsorPreviewLinkModal({ dropId, drop, onClose, resourceType = "drop", resourceTitle, resourcePath = "drops" }) {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [issuing, setIssuing] = useState(false);
  const [label, setLabel] = useState("");
  const [days, setDays] = useState(7);
  const [latestUrl, setLatestUrl] = useState("");

  // Endpoints differ by resource. Drops use /admin/drops/{id}/preview-token,
  // Loot Quests use /admin/loot-quests/{id}/preview-token; both share the
  // generic /admin/preview-tokens/{token_id} delete.
  const listUrl = resourceType === "loot_quest"
    ? `/admin/loot-quests/${dropId}/preview-tokens`
    : `/admin/drops/${dropId}/preview-tokens`;
  const issueUrl = resourceType === "loot_quest"
    ? `/admin/loot-quests/${dropId}/preview-token`
    : `/admin/drops/${dropId}/preview-token`;

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(listUrl);
      setTokens(data.tokens || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [dropId]);

  const issue = async (e) => {
    e.preventDefault();
    setIssuing(true);
    try {
      const { data } = await api.post(issueUrl, {
        label: label.trim() || null,
        expires_in_days: Number(days) || 7,
      });
      // Build URL on frontend so it always uses the admin's current hostname.
      const url = `${window.location.origin}/${resourcePath}/${dropId}?preview_token=${data.token}`;
      setLatestUrl(url);
      setLabel("");
      try {
        await navigator.clipboard?.writeText(url);
        toast.success("Sponsor link copied to clipboard");
      } catch {
        toast.success("Sponsor link issued");
      }
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to issue link");
    } finally {
      setIssuing(false);
    }
  };

  const revoke = async (tokenId) => {
    if (!confirm("Revoke this sponsor link? It will stop working immediately.")) return;
    try {
      await api.delete(`/admin/preview-tokens/${tokenId}`);
      toast.success("Link revoked");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Revoke failed");
    }
  };

  const copy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Copied");
    } catch {
      toast.error("Copy failed — long-press to copy manually");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
      data-testid="sponsor-link-modal"
      onClick={onClose}
    >
      <div
        className="loot-card-flat p-6 max-w-lg w-full max-h-[90vh] overflow-auto"
        style={{ background: "#0F0F0F", borderColor: "#D7A62A" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="overline" style={{ color: "#D7A62A", borderColor: "#D7A62A" }}>
              Sponsor preview link
            </div>
            <div className="font-display uppercase text-xl mt-1">
              {resourceTitle || drop?.title || (resourceType === "loot_quest" ? "Draft Loot Quest" : "Draft drop")}
            </div>
          </div>
          <button onClick={onClose} className="text-[#F5F2E8]/60 text-xl" aria-label="Close">✕</button>
        </div>
        <p className="text-xs text-[#F5F2E8]/65 mb-4 leading-relaxed">
          Issue a one-tap URL you can DM/email a sponsor. They'll see this draft drop exactly as hunters will — clue unlock, GPS warmer/colder hints, and the claim modal. The actual claim is disabled. Token is scoped to this drop only and expires automatically.
        </p>

        <form onSubmit={issue} className="space-y-2 mb-5" data-testid="sponsor-link-form">
          <input
            className="loot-input"
            placeholder="Label (e.g. Acme Coffee — internal note)"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            maxLength={80}
            data-testid="sponsor-link-label"
          />
          <div className="flex items-center gap-2">
            <input
              type="number"
              className="loot-input flex-1"
              min={1}
              max={60}
              value={days}
              onChange={(e) => setDays(e.target.value)}
              data-testid="sponsor-link-days"
            />
            <span className="font-mono text-xs text-[#F5F2E8]/60">days valid</span>
          </div>
          <button
            type="submit"
            disabled={issuing}
            className="btn-loot w-full"
            data-testid="sponsor-link-issue"
          >
            {issuing ? "Issuing…" : "Generate sponsor link"}
          </button>
        </form>

        {latestUrl && (
          <div
            className="border border-dashed p-3 mb-4 text-xs break-all font-mono"
            style={{ borderColor: "#76B82A" }}
            data-testid="sponsor-link-latest"
          >
            <div className="flex items-center justify-between gap-2 mb-2">
              <span className="overline" style={{ color: "#76B82A", borderColor: "#76B82A" }}>Just issued</span>
              <button type="button" onClick={() => copy(latestUrl)} className="btn-ghost text-[10px]">Copy again</button>
            </div>
            {latestUrl}
          </div>
        )}

        <div className="overline mb-2">All links for this drop</div>
        {loading ? (
          <div className="text-[#F5F2E8]/50 font-mono text-xs">Loading…</div>
        ) : tokens.length === 0 ? (
          <div className="text-[#F5F2E8]/50 font-mono text-xs">No links issued yet.</div>
        ) : (
          <div className="space-y-2">
            {tokens.map((t) => (
              <div
                key={t.id}
                className="loot-card-flat p-3 flex items-center justify-between gap-3"
                data-testid={`sponsor-link-row-${t.id}`}
                style={t.revoked ? { opacity: 0.5 } : undefined}
              >
                <div className="min-w-0 flex-1">
                  <div className="font-display uppercase text-sm leading-tight truncate">
                    {t.label || "Unlabelled"}
                  </div>
                  <div className="font-mono text-[10px] text-[#F5F2E8]/55 truncate">
                    …{t.token_suffix} · views: {t.used_count || 0}
                    {t.expires_at ? ` · exp ${new Date(t.expires_at).toLocaleDateString()}` : ""}
                    {t.revoked ? " · REVOKED" : ""}
                  </div>
                </div>
                {!t.revoked && (
                  <button
                    onClick={() => revoke(t.id)}
                    className="btn-ghost text-[10px]"
                    style={{ color: "#E53935", borderColor: "#E53935" }}
                    data-testid={`sponsor-link-revoke-${t.id}`}
                  >
                    Revoke
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function WinnersTab() {
  const [filter, setFilter] = useState("pending"); // pending | approved | rejected | all | trash
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({
    amount: "", hide_amount: false, photo_url: "", selfie_url: "",
    story: "", social_handle: "",
    prize_type: "cash", prize_custom_label: "", is_mystery: false,
  });
  const [form, setForm] = useState({
    username: "", name: "", city: "", amount: "", drop_id: "",
    prize_type: "cash", prize_custom_label: "", story: "",
    photo_url: "", social_handle: "", mark_drop_found: false,
    hide_amount: false, is_mystery: false,
  });
  const [allDrops, setAllDrops] = useState([]);

  const loadDrops = async () => {
    try {
      const { data } = await api.get("/drops");
      setAllDrops(data.drops || []);
    } catch {
      /* noop */
    }
  };
  useEffect(() => { loadDrops(); }, []);

  const refresh = async () => {
    setLoading(true);
    try {
      if (filter === "trash") {
        const { data } = await api.get("/admin/trash/winners");
        setRows(data.winners || []);
      } else if (filter === "pending") {
        const { data } = await api.get("/winners/pending");
        setRows(data.winners || []);
      } else {
        const params = filter === "all" ? {} : { status: filter };
        const { data } = await api.get("/admin/winners/all", { params });
        setRows(data.winners || []);
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, [filter]);

  const approve = async (id) => {
    await api.post("/winners/approve", { winner_id: id });
    toast.success("Winner published");
    refresh();
  };
  const reject = async (id) => {
    const reason = window.prompt("Reason? (shown to no one — stays on admin record)") || "";
    try {
      await api.post(`/admin/winners/${id}/reject`, { reason });
      toast.success("Marked rejected");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };
  const softDelete = async (id) => {
    if (!window.confirm("Move this winner to Trash? (can be restored)")) return;
    try {
      await api.delete(`/admin/winners/${id}`);
      toast.success("Winner moved to Trash");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };
  const restore = async (id) => {
    await api.post(`/admin/winners/${id}/restore`);
    toast.success("Restored");
    refresh();
  };
  const hardDelete = async (id) => {
    if (!window.confirm("Permanently delete? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/winners/${id}/hard`);
      toast.success("Permanently deleted");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const startEdit = (w) => {
    setEditingId(w.id);
    setEditForm({
      amount: w.amount ?? "",
      hide_amount: !!w.hide_amount,
      photo_url: w.photo_url || "",
      selfie_url: w.selfie_url || "",
      story: w.story || "",
      social_handle: w.social_handle || "",
      prize_type: w.prize_type || "cash",
      prize_custom_label: w.prize_custom_label || "",
      is_mystery: !!w.is_mystery,
    });
  };
  const saveEdit = async () => {
    try {
      const payload = {
        amount: editForm.amount === "" ? undefined : Number(editForm.amount),
        hide_amount: !!editForm.hide_amount,
        is_mystery: !!editForm.is_mystery,
        prize_type: editForm.prize_type || "cash",
        prize_custom_label: editForm.prize_custom_label || null,
        photo_url: editForm.photo_url || null,
        selfie_url: editForm.selfie_url || null,
        story: editForm.story || null,
        social_handle: editForm.social_handle || null,
      };
      await api.patch(`/admin/winners/${editingId}`, payload);
      toast.success("Winner updated");
      setEditingId(null);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    }
  };

  const submitManual = async () => {
    if (!form.username.trim()) {
      toast.error("Username is required");
      return;
    }
    try {
      const payload = {
        username: form.username.trim(),
        name: form.name || undefined,
        city: form.city || undefined,
        amount: form.amount ? Number(form.amount) : undefined,
        drop_id: form.drop_id || undefined,
        prize_type: form.prize_type,
        prize_custom_label: form.prize_custom_label || undefined,
        story: form.story || undefined,
        photo_url: form.photo_url || undefined,
        social_handle: form.social_handle || undefined,
        mark_drop_found: form.mark_drop_found,
        hide_amount: form.hide_amount,
        is_mystery: form.is_mystery,
      };
      await api.post("/admin/winners", payload);
      toast.success(`Winner @${form.username} added`);
      setAdding(false);
      setForm({
        username: "", name: "", city: "", amount: "", drop_id: "",
        prize_type: "cash", prize_custom_label: "", story: "",
        photo_url: "", social_handle: "", mark_drop_found: false,
        hide_amount: false, is_mystery: false,
      });
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to add winner");
    }
  };

  const filters = [
    { k: "pending", l: "Pending" },
    { k: "approved", l: "Approved" },
    { k: "rejected", l: "Rejected" },
    { k: "all", l: "All live" },
    { k: "trash", l: "Trash" },
  ];

  return (
    <div className="space-y-3" data-testid="admin-winners">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-2 flex-wrap">
          {filters.map((f) => (
            <button
              key={f.k}
              onClick={() => setFilter(f.k)}
              className={`tag-pill text-xs ${filter === f.k ? "border-[#76B82A] text-[#76B82A]" : ""}`}
              data-testid={`winfilter-${f.k}`}
            >
              {f.l}
            </button>
          ))}
        </div>
        <button
          onClick={() => setAdding((a) => !a)}
          className="btn-loot text-xs inline-flex items-center gap-1"
          data-testid="add-winner-btn"
        >
          <Plus size={12} /> {adding ? "Cancel" : "Add winner manually"}
        </button>
      </div>

      {adding && (
        <div className="loot-card-flat p-5 space-y-3" data-testid="add-winner-form">
          <div className="overline">Manually crown a winner</div>
          <p className="font-mono text-xs text-[#F5F2E8]/60">
            Use this for off-platform wins, contest winners, or retroactively seeding past finds. Auto-approved. If the username matches an existing hunter, it links automatically.
          </p>
          <div className="grid md:grid-cols-3 gap-2">
            <input
              className="loot-input"
              placeholder="@username (required)"
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              data-testid="mw-username"
            />
            <input
              className="loot-input"
              placeholder="Display name (optional)"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="mw-name"
            />
            <input
              className="loot-input"
              placeholder="City"
              value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
              data-testid="mw-city"
            />
          </div>
          <div className="grid md:grid-cols-3 gap-2">
            <select
              className="loot-input"
              value={form.drop_id}
              onChange={(e) => {
                const d = allDrops.find((x) => x.id === e.target.value);
                setForm({
                  ...form,
                  drop_id: e.target.value,
                  city: form.city || d?.city || "",
                  amount: form.amount || (d?.amount ?? ""),
                  prize_type: d?.prize_type || form.prize_type,
                  prize_custom_label: d?.prize_custom_label || form.prize_custom_label,
                });
              }}
              data-testid="mw-drop"
            >
              <option value="">No drop (off-platform find)</option>
              {allDrops.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.title} · {d.city} · ${d.amount}
                </option>
              ))}
            </select>
            <input
              type="number"
              className="loot-input"
              placeholder="Prize amount (USD)"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              data-testid="mw-amount"
            />
            <select
              className="loot-input"
              value={form.prize_type}
              onChange={(e) => setForm({ ...form, prize_type: e.target.value })}
              data-testid="mw-prize-type"
            >
              <option value="cash">Cash</option>
              <option value="gift_card">Gift Card</option>
              <option value="gift">Gift / Experience</option>
              <option value="merch">Merch</option>
              <option value="other">Other</option>
            </select>
          </div>
          {form.prize_type !== "cash" && (
            <input
              className="loot-input"
              placeholder="Prize label (e.g. Starbucks $25)"
              value={form.prize_custom_label}
              onChange={(e) => setForm({ ...form, prize_custom_label: e.target.value })}
              data-testid="mw-prize-label"
              maxLength={120}
            />
          )}
          <div className="grid md:grid-cols-2 gap-2">
            <div>
              <div className="overline mb-1">Winner photo</div>
              <PhotoUploader
                value={form.photo_url}
                onChange={(url) => setForm({ ...form, photo_url: url })}
                testid="mw-photo"
              />
            </div>
            <input
              className="loot-input"
              placeholder="Social handle (optional)"
              value={form.social_handle}
              onChange={(e) => setForm({ ...form, social_handle: e.target.value })}
              data-testid="mw-social"
            />
          </div>
          {form.prize_type === "cash" && (
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.hide_amount}
                onChange={(e) => setForm({ ...form, hide_amount: e.target.checked })}
                data-testid="mw-hide-amount"
              />
              Hide the dollar amount — just say "Found CASH"
            </label>
          )}
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.is_mystery}
              onChange={(e) => setForm({ ...form, is_mystery: e.target.checked })}
              data-testid="mw-mystery"
            />
            <span className="flex items-center gap-1">
              <span style={{ color: "#D7A62A" }}>🎁</span> Mystery prize — hide everything, show "Mystery Loot"
            </span>
          </label>
          <textarea
            className="loot-input"
            rows={2}
            placeholder="Story / caption (optional)"
            value={form.story}
            onChange={(e) => setForm({ ...form, story: e.target.value })}
            data-testid="mw-story"
            maxLength={600}
          />
          {form.drop_id && (
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.mark_drop_found}
                onChange={(e) => setForm({ ...form, mark_drop_found: e.target.checked })}
                data-testid="mw-mark-found"
              />
              Also mark this drop as FOUND
            </label>
          )}
          <div className="flex items-center gap-2">
            <button onClick={submitManual} className="btn-loot text-xs" data-testid="mw-submit">
              Crown winner
            </button>
            <button onClick={() => setAdding(false)} className="btn-ghost text-xs">
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading && <div className="text-[#F5F2E8]/60">Loading…</div>}
      {!loading && rows.length === 0 && (
        <div className="text-[#F5F2E8]/60">No winners in this view.</div>
      )}
      {rows.map((w) => {
        const isEditing = editingId === w.id;
        return (
        <div key={w.id} className="loot-card-flat p-4 space-y-3">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="min-w-0">
            <div className="font-display uppercase text-lg">
              @{w.username} · {w.city} · {w.hide_amount ? <span title="Amount hidden on Winners Wall">CASH</span> : `$${w.amount}`}
              {w.approval_status === "approved" && (
                <span className="tag-pill ml-2 text-[10px]" style={{ color: "#76B82A", borderColor: "#76B82A" }}>approved</span>
              )}
              {w.approval_status === "rejected" && (
                <span className="tag-pill ml-2 text-[10px]" style={{ color: "#E53935", borderColor: "#E53935" }}>rejected</span>
              )}
              {w.approval_status === "pending" && (
                <span className="tag-pill ml-2 text-[10px]" style={{ color: "#D7A62A", borderColor: "#D7A62A" }}>pending</span>
              )}
              {w.hide_amount && (
                <span className="tag-pill ml-2 text-[10px]" style={{ color: "#D7A62A", borderColor: "#D7A62A" }}>amount hidden</span>
              )}
            </div>
            {w.story && <div className="text-sm text-[#F5F2E8]/60 italic truncate">"{w.story}"</div>}
            <div className="font-mono text-xs text-[#F5F2E8]/50">
              {w.time_to_find_minutes}m to find · {w.created_at && fmtEstDateTime(w.created_at)}
            </div>
            {w.admin_note && (
              <div className="font-mono text-xs text-[#E53935] mt-1">admin note: {w.admin_note}</div>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap shrink-0">
            {filter === "trash" ? (
              <>
                <button onClick={() => restore(w.id)} className="btn-ghost text-xs" data-testid={`winrestore-${w.id}`}>
                  Restore
                </button>
                <button
                  onClick={() => hardDelete(w.id)}
                  className="btn-ghost text-xs"
                  style={{ color: "#E53935", borderColor: "#E53935" }}
                  data-testid={`winhard-${w.id}`}
                >
                  <Trash2 size={12} className="inline" /> Hard delete
                </button>
              </>
            ) : (
              <>
                {w.approval_status === "pending" && (
                  <>
                    <button onClick={() => approve(w.id)} className="btn-loot text-xs" data-testid={`approve-${w.id}`}>
                      <Check size={14} className="inline" /> Approve
                    </button>
                    <button
                      onClick={() => reject(w.id)}
                      className="btn-ghost text-xs"
                      style={{ color: "#E53935", borderColor: "#E53935" }}
                      data-testid={`reject-${w.id}`}
                    >
                      Reject
                    </button>
                  </>
                )}
                <button
                  onClick={() => (isEditing ? setEditingId(null) : startEdit(w))}
                  className="btn-ghost text-xs"
                  data-testid={`winedit-${w.id}`}
                >
                  {isEditing ? "Cancel" : "Edit"}
                </button>
                <button
                  onClick={() => softDelete(w.id)}
                  className="btn-ghost text-xs"
                  data-testid={`windelete-${w.id}`}
                >
                  <Trash2 size={12} className="inline" /> Delete
                </button>
              </>
            )}
          </div>
          </div>
          {isEditing && (
            <div className="grid md:grid-cols-2 gap-3 pt-3 border-t border-[#F5F2E8]/10" data-testid={`winedit-form-${w.id}`}>
              <input
                className="loot-input"
                type="number"
                placeholder="Amount ($)"
                value={editForm.amount}
                onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
              />
              <select
                className="loot-input"
                value={editForm.prize_type}
                onChange={(e) => setEditForm({ ...editForm, prize_type: e.target.value })}
                data-testid={`winprize-${w.id}`}
              >
                <option value="cash">Cash</option>
                <option value="gift_card">Gift Card</option>
                <option value="gift">Gift / Experience</option>
                <option value="merch">Merch</option>
                <option value="other">Other</option>
              </select>
              {editForm.prize_type !== "cash" && (
                <input
                  className="loot-input md:col-span-2"
                  placeholder="Prize label (e.g. Starbucks $25, T-shirt + sticker pack)"
                  value={editForm.prize_custom_label}
                  onChange={(e) => setEditForm({ ...editForm, prize_custom_label: e.target.value })}
                  data-testid={`winlabel-${w.id}`}
                  maxLength={120}
                />
              )}
              {editForm.prize_type === "cash" && (
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={editForm.hide_amount}
                    onChange={(e) => setEditForm({ ...editForm, hide_amount: e.target.checked })}
                    data-testid={`winhide-${w.id}`}
                  />
                  Hide amount — show "CASH"
                </label>
              )}
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={editForm.is_mystery}
                  onChange={(e) => setEditForm({ ...editForm, is_mystery: e.target.checked })}
                  data-testid={`winmystery-${w.id}`}
                />
                <span className="flex items-center gap-1">
                  <span style={{ color: "#D7A62A" }}>🎁</span> Mystery prize — show "Mystery Loot"
                </span>
              </label>
              <div className="md:col-span-2">
                <div className="flex items-center justify-between mb-1">
                  <div className="overline">Selfie (shown on Winners Wall)</div>
                  {editForm.selfie_url && (
                    <a
                      href={editForm.selfie_url}
                      download={`local-loot-${w.username}-selfie`}
                      target="_blank"
                      rel="noreferrer"
                      className="font-mono text-[10px] uppercase tracking-widest text-[#D7A62A] hover:text-[#76B82A] inline-flex items-center gap-1"
                      data-testid={`windl-selfie-${w.id}`}
                      title="Download for social media"
                    >
                      <Download size={12} /> Download
                    </a>
                  )}
                </div>
                <PhotoUploader
                  value={editForm.selfie_url}
                  onChange={(url) => setEditForm({ ...editForm, selfie_url: url })}
                  testid={`winselfie-${w.id}`}
                />
              </div>
              <div className="md:col-span-2">
                <div className="flex items-center justify-between mb-1">
                  <div className="overline">Proof photo</div>
                  {editForm.photo_url && (
                    <a
                      href={editForm.photo_url}
                      download={`local-loot-${w.username}-proof`}
                      target="_blank"
                      rel="noreferrer"
                      className="font-mono text-[10px] uppercase tracking-widest text-[#D7A62A] hover:text-[#76B82A] inline-flex items-center gap-1"
                      data-testid={`windl-photo-${w.id}`}
                      title="Download for social media"
                    >
                      <Download size={12} /> Download
                    </a>
                  )}
                </div>
                <PhotoUploader
                  value={editForm.photo_url}
                  onChange={(url) => setEditForm({ ...editForm, photo_url: url })}
                  testid={`winphoto-${w.id}`}
                />
              </div>
              <input
                className="loot-input md:col-span-2"
                placeholder="Social handle"
                value={editForm.social_handle}
                onChange={(e) => setEditForm({ ...editForm, social_handle: e.target.value })}
              />
              <textarea
                className="loot-input md:col-span-2"
                rows={2}
                placeholder="Story / caption"
                value={editForm.story}
                onChange={(e) => setEditForm({ ...editForm, story: e.target.value })}
                maxLength={600}
              />
              <button onClick={saveEdit} className="btn-loot text-xs md:col-span-2" data-testid={`winsave-${w.id}`}>
                Save changes
              </button>
            </div>
          )}
        </div>
        );
      })}
    </div>
  );
}

function SponsorsTab() {
  const blank = { business_name: "", city: "", description: "", website: "", package: "mini", contact_name: "", email: "", phone: "", logo_url: "" };
  const [form, setForm] = useState(blank);
  const [sponsors, setSponsors] = useState([]);
  const [scans, setScans] = useState({}); // sponsor_id -> count
  const [posterFor, setPosterFor] = useState(null); // {city, name, id}
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(blank);
  const refresh = async () => {
    const [s, sc] = await Promise.all([
      api.get("/sponsors"),
      api.get("/admin/sponsor-scans", { params: { days: 30 } }).catch(() => ({ data: { by_sponsor: {} } })),
    ]);
    setSponsors(s.data.sponsors);
    setScans(sc.data.by_sponsor || {});
  };
  useEffect(() => { refresh(); }, []);
  const create = async (e) => {
    e.preventDefault();
    try {
      const payload = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, v === "" ? null : v])
      );
      await api.post("/sponsors", payload);
      toast.success("Sponsor added");
      setForm(blank);
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };
  const startEdit = (s) => {
    setEditingId(s.id);
    setEditForm({
      business_name: s.business_name || "", city: s.city || "",
      description: s.description || "", website: s.website || "",
      package: s.package || "mini",
      contact_name: s.contact_name || "", email: s.email || "",
      phone: s.phone || "", logo_url: s.logo_url || "",
    });
  };
  const saveEdit = async () => {
    try {
      const payload = Object.fromEntries(
        Object.entries(editForm).map(([k, v]) => [k, v === "" ? null : v])
      );
      await api.patch(`/sponsors/${editingId}`, payload);
      toast.success("Sponsor updated");
      setEditingId(null);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    }
  };
  const remove = async (id, name) => {
    if (!window.confirm(`Delete sponsor "${name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/sponsors/${id}`);
      toast.success("Sponsor deleted");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };
  return (
    <div className="space-y-4">
      <form onSubmit={create} className="loot-card-flat p-4 grid md:grid-cols-2 gap-2" data-testid="admin-sponsor-form">
        <input className="loot-input" placeholder="Business name" required value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} data-testid="sponsor-new-name" />
        <input className="loot-input" placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
        <input className="loot-input" placeholder="Website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
        <select className="loot-input" value={form.package} onChange={(e) => setForm({ ...form, package: e.target.value })}>
          <option value="mini">Mini</option><option value="featured">Featured</option><option value="city_takeover">City Takeover</option>
        </select>
        <input className="loot-input" placeholder="Contact name" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
        <input className="loot-input" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <input className="loot-input" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        <div className="md:col-span-2">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1">Sponsor logo (image upload)</div>
          <PhotoUploader value={form.logo_url} onChange={(url) => setForm({ ...form, logo_url: url })} testid="sponsor-new-logo" />
        </div>
        <textarea className="loot-input md:col-span-2" rows={2} placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <button className="btn-loot md:col-span-2" type="submit" data-testid="sponsor-add-btn">Add sponsor</button>
      </form>
      <div className="space-y-2">
        {sponsors.map((s) => {
          const scanCount = scans[s.id] || 0;
          const isEditing = editingId === s.id;
          return (
            <div key={s.id} className="loot-card-flat p-3 space-y-2" data-testid={`sponsor-row-${s.id}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                  <Store size={14} className="text-[#76B82A]" />
                  <span className="font-display uppercase">{s.business_name}</span>
                  <span className="font-mono text-xs text-[#F5F2E8]/50">· {s.city || "—"}</span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs text-[#D7A62A] uppercase">{s.package.replace("_", " ")}</span>
                  {scanCount > 0 && (
                    <span
                      className="font-mono text-[10px] tag-pill"
                      style={{ color: "#76B82A", borderColor: "#76B82A" }}
                      data-testid={`scan-count-${s.id}`}
                      title={`${scanCount} poster scans in last 30 days`}
                    >
                      {scanCount} scans/30d
                    </span>
                  )}
                  {s.city && (
                    <button
                      onClick={() => setPosterFor({ city: s.city, name: s.business_name, id: s.id, logo_url: s.logo_url })}
                      className="btn-ghost text-xs inline-flex items-center gap-1"
                      data-testid={`admin-qr-${s.id}`}
                    >
                      <QrCode size={12} /> QR poster
                    </button>
                  )}
                  <button
                    onClick={() => (isEditing ? setEditingId(null) : startEdit(s))}
                    className="btn-ghost text-xs"
                    data-testid={`sponsor-edit-${s.id}`}
                  >
                    {isEditing ? "Cancel" : "Edit"}
                  </button>
                  <button
                    onClick={() => remove(s.id, s.business_name)}
                    className="btn-ghost text-xs"
                    style={{ color: "#E53935", borderColor: "#E53935" }}
                    data-testid={`sponsor-del-${s.id}`}
                  >
                    <Trash2 size={12} className="inline" />
                  </button>
                </div>
              </div>
              {isEditing && (
                <div className="grid md:grid-cols-2 gap-2 pt-2 border-t border-[#F5F2E8]/10" data-testid={`sponsor-edit-form-${s.id}`}>
                  <input className="loot-input" placeholder="Business name" value={editForm.business_name} onChange={(e) => setEditForm({ ...editForm, business_name: e.target.value })} />
                  <input className="loot-input" placeholder="City" value={editForm.city} onChange={(e) => setEditForm({ ...editForm, city: e.target.value })} />
                  <input className="loot-input" placeholder="Website" value={editForm.website} onChange={(e) => setEditForm({ ...editForm, website: e.target.value })} />
                  <select className="loot-input" value={editForm.package} onChange={(e) => setEditForm({ ...editForm, package: e.target.value })}>
                    <option value="mini">Mini</option><option value="featured">Featured</option><option value="city_takeover">City Takeover</option>
                  </select>
                  <input className="loot-input" placeholder="Contact" value={editForm.contact_name} onChange={(e) => setEditForm({ ...editForm, contact_name: e.target.value })} />
                  <input className="loot-input" placeholder="Email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
                  <input className="loot-input" placeholder="Phone" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} />
                  <div className="md:col-span-2">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1">Sponsor logo (image upload)</div>
                    <PhotoUploader value={editForm.logo_url} onChange={(url) => setEditForm({ ...editForm, logo_url: url })} testid={`sponsor-edit-logo-${s.id}`} />
                  </div>
                  <textarea rows={2} className="loot-input md:col-span-2" placeholder="Description" value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} />
                  <button onClick={saveEdit} className="btn-loot text-xs md:col-span-2" data-testid={`sponsor-save-${s.id}`}>Save changes</button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <QrPosterModal
        open={Boolean(posterFor)}
        city={posterFor?.city || ""}
        sponsorName={posterFor?.name || null}
        sponsorId={posterFor?.id || null}
        sponsorLogoUrl={posterFor?.logo_url || null}
        onClose={() => setPosterFor(null)}
      />
    </div>
  );
}

function LeadsTab() {
  const [apps, setApps] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({});
  const refresh = async () => {
    const { data } = await api.get("/sponsor-applications");
    setApps(data.applications || []);
  };
  useEffect(() => { refresh(); }, []);
  const startEdit = (a) => {
    setEditingId(a.id);
    setForm({
      business_name: a.business_name || "",
      contact_name: a.contact_name || "",
      email: a.email || "",
      phone: a.phone || "",
      website: a.website || "",
      city: a.city || "",
      package_interest: a.package_interest || "mini",
      budget: a.budget || "",
      notes: a.notes || "",
      status: a.status || "new",
    });
  };
  const save = async () => {
    try {
      const payload = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, v === "" ? null : v])
      );
      await api.patch(`/admin/inquiries/${editingId}`, payload);
      toast.success("Lead updated");
      setEditingId(null);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    }
  };
  const remove = async (id, name) => {
    if (!window.confirm(`Delete lead "${name}"?`)) return;
    try {
      await api.delete(`/admin/inquiries/${id}`);
      toast.success("Lead deleted");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };
  const quickStatus = async (id, status) => {
    try {
      await api.patch(`/admin/inquiries/${id}`, { status });
      toast.success(`Marked ${status}`);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };
  const STATUS_COLOR = {
    new: "#D7A62A", contacted: "#1e88e5", won: "#76B82A", lost: "#E53935", closed: "#F5F2E8",
  };
  return (
    <div className="space-y-3" data-testid="admin-leads">
      <div className="overline">Sponsor leads ({apps.length})</div>
      {apps.length === 0 && <div className="text-[#F5F2E8]/60">No leads yet.</div>}
      {apps.map((a) => {
        const isEditing = editingId === a.id;
        return (
          <div key={a.id} className="loot-card-flat p-4 space-y-2" data-testid={`lead-row-${a.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="font-display uppercase text-lg flex items-center gap-2">
                  {a.business_name}
                  <span
                    className="tag-pill text-[10px]"
                    style={{ color: STATUS_COLOR[a.status] || "#F5F2E8", borderColor: STATUS_COLOR[a.status] || "#F5F2E8" }}
                  >
                    {a.status || "new"}
                  </span>
                </div>
                <div className="text-sm text-[#F5F2E8]/70">{a.contact_name} · {a.email}{a.phone ? ` · ${a.phone}` : ""}</div>
                <div className="font-mono text-xs text-[#F5F2E8]/50">{a.city} · tier: {a.package_interest} · budget: {a.budget || "—"}</div>
                {a.notes && <p className="text-sm text-[#F5F2E8]/60 mt-2 italic">"{a.notes}"</p>}
              </div>
              <div className="flex items-center gap-1 flex-wrap shrink-0">
                {a.status !== "contacted" && <button onClick={() => quickStatus(a.id, "contacted")} className="btn-ghost text-xs">Contacted</button>}
                {a.status !== "won" && <button onClick={() => quickStatus(a.id, "won")} className="btn-ghost text-xs" style={{ color: "#76B82A", borderColor: "#76B82A" }}>Won</button>}
                {a.status !== "lost" && <button onClick={() => quickStatus(a.id, "lost")} className="btn-ghost text-xs" style={{ color: "#E53935", borderColor: "#E53935" }}>Lost</button>}
                <button
                  onClick={() => (isEditing ? setEditingId(null) : startEdit(a))}
                  className="btn-ghost text-xs"
                  data-testid={`lead-edit-${a.id}`}
                >
                  {isEditing ? "Cancel" : "Edit"}
                </button>
                <button
                  onClick={() => remove(a.id, a.business_name)}
                  className="btn-ghost text-xs"
                  style={{ color: "#E53935", borderColor: "#E53935" }}
                  data-testid={`lead-del-${a.id}`}
                >
                  <Trash2 size={12} className="inline" />
                </button>
              </div>
            </div>
            {isEditing && (
              <div className="grid md:grid-cols-2 gap-2 pt-2 border-t border-[#F5F2E8]/10" data-testid={`lead-edit-form-${a.id}`}>
                <input className="loot-input" placeholder="Business" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} />
                <input className="loot-input" placeholder="Contact name" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
                <input className="loot-input" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                <input className="loot-input" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                <input className="loot-input" placeholder="Website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
                <input className="loot-input" placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
                <select className="loot-input" value={form.package_interest} onChange={(e) => setForm({ ...form, package_interest: e.target.value })}>
                  <option value="mini">Mini</option><option value="featured">Featured</option><option value="city_takeover">City Takeover</option>
                </select>
                <input className="loot-input" placeholder="Budget" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} />
                <select className="loot-input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  <option value="new">new</option>
                  <option value="contacted">contacted</option>
                  <option value="won">won</option>
                  <option value="lost">lost</option>
                  <option value="closed">closed</option>
                </select>
                <textarea rows={2} className="loot-input md:col-span-2" placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                <button onClick={save} className="btn-loot text-xs md:col-span-2" data-testid={`lead-save-${a.id}`}>Save</button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function HuntersTab() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({});
  const refresh = async () => {
    const params = q.trim() ? { q: q.trim() } : {};
    const { data } = await api.get("/admin/users", { params });
    setUsers(data.users || []);
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);
  const onSearch = (e) => { e.preventDefault(); refresh(); };
  const startEdit = (u) => {
    setEditingId(u.id);
    setForm({
      name: u.name || "", username: u.username || "",
      email: u.email || "", city: u.city || "",
      role: u.role || "hunter",
      lootmaster_cities: u.lootmaster_cities || [],
    });
  };
  const save = async () => {
    try {
      await api.patch(`/admin/users/${editingId}`, form);
      toast.success("Hunter updated");
      setEditingId(null);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    }
  };
  const remove = async (id, name) => {
    if (!window.confirm(`Delete @${name}? This permanently removes the account.`)) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("Hunter deleted");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };
  const resetPw = async (u) => {
    const pw = window.prompt(`Set a new password for @${u.username || u.email}. Minimum 6 characters.`, "");
    if (!pw) return;
    if (pw.length < 6) { toast.error("Password must be at least 6 characters."); return; }
    try {
      await api.patch(`/admin/users/${u.id}/reset-password`, { new_password: pw });
      toast.success(`Password reset for @${u.username || u.email}. Share it with them out-of-band.`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Reset failed");
    }
  };
  return (
    <div className="space-y-3" data-testid="admin-hunters">
      <form onSubmit={onSearch} className="flex gap-2">
        <input
          className="loot-input flex-1"
          placeholder="Search by email / username / name"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="hunter-search-input"
        />
        <button type="submit" className="btn-ghost text-xs" data-testid="hunter-search-btn">Search</button>
      </form>
      <div className="overline">Hunters ({users.length})</div>
      {users.length === 0 && <div className="text-[#F5F2E8]/60">No hunters match.</div>}
      {users.map((u) => {
        const isEditing = editingId === u.id;
        return (
          <div key={u.id} className="loot-card-flat p-4 space-y-2" data-testid={`hunter-row-${u.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="font-display uppercase text-lg flex items-center gap-2">
                  @{u.username || "—"}
                  <span
                    className="tag-pill text-[10px]"
                    style={{
                      color: u.role === "admin" ? "#E53935" : (u.role === "lootmaster" ? "#D7A62A" : "#76B82A"),
                      borderColor: u.role === "admin" ? "#E53935" : (u.role === "lootmaster" ? "#D7A62A" : "#76B82A"),
                    }}
                  >
                    {u.role}
                  </span>
                </div>
                <div className="text-sm text-[#F5F2E8]/70">{u.name} · {u.email}</div>
                <div className="font-mono text-xs text-[#F5F2E8]/50">
                  {u.city || "—"} · referrals: {u.referral_count ?? 0} · badges: {(u.badges || []).length}
                </div>
              </div>
              <div className="flex items-center gap-1 flex-wrap shrink-0">
                <button
                  onClick={() => resetPw(u)}
                  className="btn-ghost text-xs"
                  data-testid={`hunter-resetpw-${u.id}`}
                  title="Set a new password for this user"
                >
                  Reset pw
                </button>
                <button
                  onClick={() => (isEditing ? setEditingId(null) : startEdit(u))}
                  className="btn-ghost text-xs"
                  data-testid={`hunter-edit-${u.id}`}
                >
                  {isEditing ? "Cancel" : "Edit"}
                </button>
                <button
                  onClick={() => remove(u.id, u.username)}
                  className="btn-ghost text-xs"
                  style={{ color: "#E53935", borderColor: "#E53935" }}
                  data-testid={`hunter-del-${u.id}`}
                >
                  <Trash2 size={12} className="inline" />
                </button>
              </div>
            </div>
            {isEditing && (
              <div className="grid md:grid-cols-2 gap-2 pt-2 border-t border-[#F5F2E8]/10" data-testid={`hunter-edit-form-${u.id}`}>
                <input className="loot-input" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                <input className="loot-input" placeholder="Username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
                <input className="loot-input" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                <input className="loot-input" placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
                <select className="loot-input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                  <option value="hunter">hunter</option>
                  <option value="lootmaster">lootmaster</option>
                  <option value="admin">admin</option>
                </select>
                {form.role === "lootmaster" && (
                  <input
                    className="loot-input md:col-span-2"
                    placeholder="Cities they can run (comma-separated, e.g. Kingsport, Bristol)"
                    value={(form.lootmaster_cities || []).join(", ")}
                    onChange={(e) => setForm({
                      ...form,
                      lootmaster_cities: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                    })}
                    data-testid={`hunter-lootmaster-cities-${u.id}`}
                  />
                )}
                <button onClick={save} className="btn-loot text-xs" data-testid={`hunter-save-${u.id}`}>Save</button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}


function LootmastersTab() {
  const [program, setProgram] = useState({ enabled: true });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const [p, l] = await Promise.all([
        api.get("/admin/lootmaster/program"),
        api.get("/admin/lootmasters"),
      ]);
      setProgram(p.data);
      setRows(l.data.lootmasters || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Load failed");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const toggleProgram = async () => {
    const next = !program.enabled;
    if (!confirm(next
      ? "Re-enable the Lootmaster program? All assigned lootmasters can resume creating drops."
      : "Pause the entire Lootmaster program? Every lootmaster account is frozen until you re-enable it.")) return;
    try {
      const { data } = await api.put("/admin/lootmaster/program", { enabled: next });
      setProgram({ enabled: data.enabled });
      toast.success(data.enabled ? "Program ON — lootmasters active" : "Program OFF — all lootmasters paused");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Toggle failed");
    }
  };

  const toggleAccount = async (lm) => {
    const next = !lm.lootmaster_active;
    try {
      await api.patch(`/admin/users/${lm.id}`, { lootmaster_active: next });
      toast.success(`@${lm.username || lm.email} ${next ? "activated" : "paused"}`);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Toggle failed");
    }
  };

  const demote = async (lm) => {
    if (!confirm(`Remove Lootmaster role from @${lm.username || lm.email}? Their existing hunts stay published; they just lose access to the console.`)) return;
    try {
      await api.patch(`/admin/users/${lm.id}`, { role: "hunter", lootmaster_active: false });
      toast.success(`Demoted @${lm.username || lm.email} to hunter`);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Demote failed");
    }
  };

  if (loading) return <div className="text-[#F5F2E8]/55 font-mono text-xs">Loading lootmasters…</div>;

  return (
    <div className="space-y-5" data-testid="lootmasters-tab">
      <div>
        <div className="overline">Multi-city operators</div>
        <h2 className="font-display uppercase text-3xl tracking-tight mt-1">
          Lootmaster <span style={{ color: "#D7A62A" }}>program</span>
        </h2>
        <p className="text-sm text-[#F5F2E8]/65 mt-2 max-w-2xl">
          People you've hired in different cities to run their own drops, bounty hunts, loot quests, and waymarks. They can publish directly in their assigned cities and see global hunter data read-only.
        </p>
      </div>

      <div
        className="loot-card-flat p-5 flex items-center justify-between gap-4"
        style={{ borderColor: program.enabled ? "#76B82A" : "#E53935" }}
        data-testid="lootmaster-program-toggle"
      >
        <div className="min-w-0">
          <div className="font-mono text-[10px] tracking-widest uppercase" style={{ color: program.enabled ? "#76B82A" : "#E53935" }}>
            {program.enabled ? "PROGRAM ACTIVE" : "PROGRAM PAUSED"}
          </div>
          <div className="font-display uppercase text-xl mt-1">Site-wide kill switch</div>
          <div className="text-xs text-[#F5F2E8]/65 mt-1">
            {program.enabled
              ? "Every lootmaster with an active account can use the console right now."
              : "Every lootmaster account is frozen — none can create or edit hunts."}
          </div>
        </div>
        <button
          onClick={toggleProgram}
          className="btn-loot"
          style={program.enabled ? { background: "#E53935" } : undefined}
          data-testid="lootmaster-program-toggle-btn"
        >
          {program.enabled ? "Pause program" : "Resume program"}
        </button>
      </div>

      {rows.length === 0 ? (
        <div className="loot-card-flat p-8 text-center">
          <div className="font-display uppercase text-xl">No lootmasters yet</div>
          <p className="text-sm text-[#F5F2E8]/60 mt-2">
            Find a hunter in the <span className="font-mono">Hunters</span> tab, click <span className="font-mono">Edit</span>, set role to <span className="font-mono">lootmaster</span>, and assign their cities.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((lm) => (
            <div
              key={lm.id}
              className="loot-card-flat p-4 grid md:grid-cols-[2fr_1fr_auto] gap-4 items-center"
              style={{ borderColor: lm.lootmaster_active ? "#D7A62A" : "rgba(245,242,232,0.15)" }}
              data-testid={`lootmaster-row-${lm.id}`}
            >
              <div className="min-w-0">
                <div className="font-display uppercase text-lg flex items-center gap-2">
                  @{lm.username || "—"}
                  {!lm.lootmaster_active && (
                    <span className="font-mono text-[10px] px-1.5 py-0.5"
                      style={{ color: "#E53935", border: "1px solid #E53935" }}>
                      PAUSED
                    </span>
                  )}
                </div>
                <div className="font-mono text-xs text-[#F5F2E8]/55">{lm.email}</div>
                <div className="font-mono text-[11px] text-[#F5F2E8]/65 mt-1">
                  Cities: {(lm.lootmaster_cities || []).join(" · ") || "(none)"}
                </div>
              </div>
              <div className="font-mono text-[11px] text-[#F5F2E8]/65 leading-relaxed">
                Drops {lm.drops_created || 0} · Quests {lm.loot_quests_created || 0} · Waymarks {lm.waymarks_created || 0}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => toggleAccount(lm)}
                  className="btn-ghost text-xs"
                  style={lm.lootmaster_active
                    ? { color: "#E53935", borderColor: "#E53935" }
                    : { color: "#76B82A", borderColor: "#76B82A" }}
                  data-testid={`lootmaster-toggle-${lm.id}`}
                >
                  {lm.lootmaster_active ? "Pause" : "Activate"}
                </button>
                <button
                  onClick={() => demote(lm)}
                  className="btn-ghost text-xs"
                  data-testid={`lootmaster-demote-${lm.id}`}
                >
                  Demote
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function NotifTab() {
  const [form, setForm] = useState({ title: "", message: "", tag: "drop", city: "" });
  const send = async (e) => {
    e.preventDefault();
    await api.post("/notifications", form);
    toast.success("Posted");
    setForm({ title: "", message: "", tag: "drop", city: "" });
  };
  return (
    <form onSubmit={send} className="loot-card-flat p-4 space-y-3 max-w-lg" data-testid="admin-notif-form">
      <input className="loot-input" placeholder="Title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
      <textarea rows={2} className="loot-input" placeholder="Message" required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
      <div className="grid grid-cols-2 gap-3">
        <select className="loot-input" value={form.tag} onChange={(e) => setForm({ ...form, tag: e.target.value })}>
          {["drop", "clue", "winner", "sponsor", "vote", "general"].map((t) => <option key={t}>{t}</option>)}
        </select>
        <input className="loot-input" placeholder="City (optional)" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
      </div>
      <button className="btn-loot w-full" type="submit">Broadcast</button>
    </form>
  );
}

function DailyQuoteTab() {
  const [settings, setSettings] = useState(null);
  const [today, setToday] = useState(null);
  const [bank, setBank] = useState([]);
  const [newQ, setNewQ] = useState({ text: "", author: "" });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ text: "", author: "", enabled: true });
  const [loading, setLoading] = useState(false);

  const refresh = async () => {
    try {
      const [s, t, b] = await Promise.all([
        api.get("/admin/daily-quote/settings"),
        api.get("/daily-quote"),
        api.get("/admin/daily-quote/bank"),
      ]);
      setSettings(s.data);
      setToday(t.data);
      setBank(b.data.bank || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    }
  };
  useEffect(() => { refresh(); }, []);

  const toggle = async (enabled) => {
    try {
      await api.patch("/admin/daily-quote/settings", { enabled });
      toast.success(enabled ? "Daily quote turned ON" : "Daily quote turned OFF");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };
  const reroll = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/admin/daily-quote/reroll");
      setToday(data);
      toast.success("Today's quote re-rolled");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Reroll failed");
    } finally { setLoading(false); }
  };
  const addQuote = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/daily-quote/bank", { ...newQ, enabled: true });
      toast.success("Quote added");
      setNewQ({ text: "", author: "" });
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };
  const startEdit = (q) => {
    setEditingId(q.id);
    setEditForm({ text: q.text, author: q.author || "", enabled: !!q.enabled });
  };
  const saveEdit = async () => {
    try {
      await api.patch(`/admin/daily-quote/bank/${editingId}`, editForm);
      toast.success("Saved");
      setEditingId(null);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this quote?")) return;
    try {
      await api.delete(`/admin/daily-quote/bank/${id}`);
      toast.success("Deleted");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-daily-quote">
      {/* Master switch */}
      <div className="loot-card-flat p-5 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="overline">Daily quote banner</div>
            <div className="font-mono text-xs text-[#F5F2E8]/60">
              Neon-arcade "Daily Signal" under the home-page hero. Pulls from your quote bank first,
              falls back to Gemini 3 Pro AI if the bank is empty.
            </div>
          </div>
          <button
            onClick={() => toggle(!(settings?.enabled))}
            className={`btn-${settings?.enabled ? "loot" : "ghost"} text-xs`}
            data-testid="dq-toggle"
          >
            {settings?.enabled ? "ON — click to disable" : "OFF — click to enable"}
          </button>
        </div>
      </div>

      {/* Today's quote preview */}
      <div className="loot-card-flat p-5 space-y-3">
        <div className="overline">Today's quote</div>
        {today?.enabled === false ? (
          <div className="font-mono text-xs text-[#F5F2E8]/60">Feature is OFF — the banner is hidden on the home page.</div>
        ) : today?.text ? (
          <>
            <div className="font-display uppercase text-xl" style={{ color: "#E53935" }}>
              {today.tenet} <span className="font-mono text-[10px] text-[#F5F2E8]/40">// tenet today</span>
            </div>
            <blockquote className="font-display uppercase text-lg md:text-2xl leading-tight text-[#F5F2E8]">
              "{today.text}"
            </blockquote>
            <div className="font-mono text-xs text-[#F5F2E8]/60">
              — {today.author} · source: <span style={{ color: "#76B82A" }}>{today.source}</span>
            </div>
            <button onClick={reroll} disabled={loading} className="btn-ghost text-xs" data-testid="dq-reroll">
              {loading ? "Re-rolling…" : "Re-roll today's quote"}
            </button>
          </>
        ) : (
          <div className="font-mono text-xs text-[#F5F2E8]/60">Loading…</div>
        )}
      </div>

      {/* Add to bank */}
      <form onSubmit={addQuote} className="loot-card-flat p-5 space-y-2" data-testid="dq-add-form">
        <div className="overline">Add a quote to the bank</div>
        <p className="font-mono text-xs text-[#F5F2E8]/60">
          When the bank has enabled quotes, they take priority over AI-generated ones.
        </p>
        <textarea
          className="loot-input"
          rows={2}
          maxLength={240}
          placeholder="Quote text (6–240 chars)"
          value={newQ.text}
          onChange={(e) => setNewQ({ ...newQ, text: e.target.value })}
          required
          data-testid="dq-new-text"
        />
        <input
          className="loot-input"
          placeholder="Author (optional — defaults to 'The Local Loot')"
          value={newQ.author}
          onChange={(e) => setNewQ({ ...newQ, author: e.target.value })}
          data-testid="dq-new-author"
        />
        <button type="submit" className="btn-loot text-xs" data-testid="dq-add-btn">Add quote</button>
      </form>

      {/* Bank list */}
      <div className="space-y-2">
        <div className="overline">Quote bank ({bank.length})</div>
        {bank.length === 0 && (
          <div className="font-mono text-xs text-[#F5F2E8]/60">
            Empty — AI-generated quotes will be used each day until you add your own.
          </div>
        )}
        {bank.map((q) => {
          const isEditing = editingId === q.id;
          return (
            <div key={q.id} className="loot-card-flat p-4 space-y-2" data-testid={`dq-row-${q.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="font-display uppercase text-lg leading-snug">"{q.text}"</div>
                  <div className="font-mono text-xs text-[#F5F2E8]/50 mt-1">
                    — {q.author || "The Local Loot"}
                    <span className="ml-2 tag-pill text-[10px]" style={{ color: q.enabled ? "#76B82A" : "#F5F2E8", borderColor: q.enabled ? "#76B82A" : "#F5F2E8" }}>
                      {q.enabled ? "enabled" : "disabled"}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-wrap shrink-0">
                  <button onClick={() => (isEditing ? setEditingId(null) : startEdit(q))} className="btn-ghost text-xs" data-testid={`dq-edit-${q.id}`}>
                    {isEditing ? "Cancel" : "Edit"}
                  </button>
                  <button onClick={() => remove(q.id)} className="btn-ghost text-xs" style={{ color: "#E53935", borderColor: "#E53935" }} data-testid={`dq-del-${q.id}`}>
                    <Trash2 size={12} className="inline" />
                  </button>
                </div>
              </div>
              {isEditing && (
                <div className="grid md:grid-cols-2 gap-2 pt-2 border-t border-[#F5F2E8]/10">
                  <textarea className="loot-input md:col-span-2" rows={2} maxLength={240} value={editForm.text} onChange={(e) => setEditForm({ ...editForm, text: e.target.value })} />
                  <input className="loot-input" placeholder="Author" value={editForm.author} onChange={(e) => setEditForm({ ...editForm, author: e.target.value })} />
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={editForm.enabled} onChange={(e) => setEditForm({ ...editForm, enabled: e.target.checked })} />
                    Enabled
                  </label>
                  <button onClick={saveEdit} className="btn-loot text-xs md:col-span-2" data-testid={`dq-save-${q.id}`}>Save</button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TeaserTab() {
  const [enabled, setEnabledState] = useState(true);
  const [teasers, setTeasers] = useState([]);
  const [form, setForm] = useState({ city: "", label: "", scheduled_for: "", pinned: false });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const refresh = async () => {
    try {
      const [s, list] = await Promise.all([
        api.get("/admin/teaser/settings"),
        api.get("/admin/teasers"),
      ]);
      setEnabledState(!!s.data.enabled);
      setTeasers(list.data.teasers || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };
  useEffect(() => { refresh(); }, []);

  const toggle = async (v) => {
    try {
      await api.patch("/admin/teaser/settings", { enabled: v });
      toast.success(v ? "Ticker ON — now visible on home page" : "Ticker OFF — hidden from home page");
      setEnabledState(v);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };
  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/teasers", { ...form, enabled: true });
      toast.success("Teaser added");
      setForm({ city: "", label: "", scheduled_for: "", pinned: false });
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };
  const startEdit = (t) => {
    setEditingId(t.id);
    setEditForm({
      city: t.city || "",
      label: t.label || "",
      scheduled_for: t.scheduled_for || "",
      enabled: !!t.enabled,
      pinned: !!t.pinned,
    });
  };
  const save = async () => {
    try {
      await api.patch(`/admin/teasers/${editingId}`, editForm);
      toast.success("Saved");
      setEditingId(null);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this teaser?")) return;
    try {
      await api.delete(`/admin/teasers/${id}`);
      toast.success("Deleted");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-teaser">
      {/* Master switch */}
      <div className="loot-card-flat p-5 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="overline">FOMO Ticker</div>
            <div className="font-mono text-xs text-[#F5F2E8]/60">
              Red "Next Drop" strip under the home hero. Shows the top-pinned or most-recent enabled teaser
              with its live watcher count. Multiple teasers can exist — pin one to force it to the top.
            </div>
          </div>
          <button
            onClick={() => toggle(!enabled)}
            className={`btn-${enabled ? "loot" : "ghost"} text-xs`}
            data-testid="teaser-toggle"
          >
            {enabled ? "ON — click to disable" : "OFF — click to enable"}
          </button>
        </div>
      </div>

      {/* New teaser form */}
      <form onSubmit={add} className="loot-card-flat p-5 space-y-2" data-testid="teaser-new-form">
        <div className="overline">Add a new teaser</div>
        <div className="grid md:grid-cols-2 gap-2">
          <input className="loot-input" placeholder="City (e.g. Nashville)" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} required data-testid="teaser-new-city" />
          <input className="loot-input" placeholder='Label (e.g. "Saturday @ TBD")' value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} required maxLength={120} data-testid="teaser-new-label" />
          <input className="loot-input md:col-span-2" placeholder="Scheduled date (optional, free text like '2026-05-01 14:00 ET')" value={form.scheduled_for} onChange={(e) => setForm({ ...form, scheduled_for: e.target.value })} />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.pinned} onChange={(e) => setForm({ ...form, pinned: e.target.checked })} />
          Pin to top (highest-priority teaser on the home page)
        </label>
        <button type="submit" className="btn-loot text-xs" data-testid="teaser-new-btn">Add teaser</button>
      </form>

      {/* List */}
      <div className="space-y-2">
        <div className="overline">Current teasers ({teasers.length})</div>
        {teasers.length === 0 && (
          <div className="font-mono text-xs text-[#F5F2E8]/60">No teasers yet — add one above.</div>
        )}
        {teasers.map((t) => {
          const isEditing = editingId === t.id;
          return (
            <div key={t.id} className="loot-card-flat p-4 space-y-2" data-testid={`teaser-row-${t.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="font-display uppercase text-lg flex items-center gap-2">
                    {t.city} — {t.label}
                    {t.pinned && <span className="tag-pill text-[10px]" style={{ color: "#D7A62A", borderColor: "#D7A62A" }}>pinned</span>}
                    <span className="tag-pill text-[10px]" style={{ color: t.enabled ? "#76B82A" : "#F5F2E8", borderColor: t.enabled ? "#76B82A" : "#F5F2E8" }}>
                      {t.enabled ? "enabled" : "disabled"}
                    </span>
                  </div>
                  <div className="font-mono text-xs text-[#F5F2E8]/60 mt-1">
                    {t.scheduled_for && <span>📅 {t.scheduled_for} · </span>}
                    <span style={{ color: "#76B82A" }}>{t.watchers_count || 0}</span> watching
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-wrap shrink-0">
                  <button onClick={() => (isEditing ? setEditingId(null) : startEdit(t))} className="btn-ghost text-xs" data-testid={`teaser-edit-${t.id}`}>
                    {isEditing ? "Cancel" : "Edit"}
                  </button>
                  <button onClick={() => remove(t.id)} className="btn-ghost text-xs" style={{ color: "#E53935", borderColor: "#E53935" }} data-testid={`teaser-del-${t.id}`}>
                    <Trash2 size={12} className="inline" />
                  </button>
                </div>
              </div>
              {isEditing && (
                <div className="grid md:grid-cols-2 gap-2 pt-2 border-t border-[#F5F2E8]/10">
                  <input className="loot-input" placeholder="City" value={editForm.city} onChange={(e) => setEditForm({ ...editForm, city: e.target.value })} />
                  <input className="loot-input" placeholder="Label" value={editForm.label} onChange={(e) => setEditForm({ ...editForm, label: e.target.value })} maxLength={120} />
                  <input className="loot-input md:col-span-2" placeholder="Scheduled (optional)" value={editForm.scheduled_for} onChange={(e) => setEditForm({ ...editForm, scheduled_for: e.target.value })} />
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={editForm.enabled} onChange={(e) => setEditForm({ ...editForm, enabled: e.target.checked })} />
                    Enabled
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={editForm.pinned} onChange={(e) => setEditForm({ ...editForm, pinned: e.target.checked })} />
                    Pinned
                  </label>
                  <button onClick={save} className="btn-loot text-xs md:col-span-2" data-testid={`teaser-save-${t.id}`}>Save</button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function HintTelemetryTab() {
  const [data, setData] = useState(null);
  const [resetting, setResetting] = useState(false);

  const load = async () => {
    const { data: d } = await api.get("/admin/hint-telemetry");
    setData(d);
  };
  useEffect(() => { load(); }, []);

  const resetTelemetry = async () => {
    if (!window.confirm("Wipe ALL recorded warmer/colder hint data? This can't be undone."))
      return;
    const t = window.prompt('Type RESET to confirm:');
    if (t !== "RESET") {
      toast.info("Reset cancelled.");
      return;
    }
    try {
      setResetting(true);
      const { data: r } = await api.post("/admin/hint-telemetry/reset", { confirm: "RESET" });
      toast.success(`Telemetry wiped · ${r.hint_requests_deleted} requests cleared`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Reset failed");
    } finally {
      setResetting(false);
    }
  };

  if (!data) return <div className="text-[#F5F2E8]/60">Loading…</div>;
  const { total_requests, bands, top_drops } = data;

  const bandMeta = [
    { k: "hot", label: "Hot", color: "#E53935", Icon: Flame },
    { k: "warmer", label: "Warmer", color: "#D7A62A", Icon: Thermometer },
    { k: "warm", label: "Warm", color: "#D7A62A", Icon: Thermometer },
    { k: "colder", label: "Colder", color: "#76B82A", Icon: Compass },
    { k: "cold", label: "Cold", color: "#1e88e5", Icon: Compass },
  ];

  if (total_requests === 0) {
    return (
      <div className="space-y-4">
        <div className="loot-card-flat p-10 text-center text-[#F5F2E8]/60">
          <Compass className="mx-auto text-[#F5F2E8]/30 mb-3" size={40} />
          No warmer/colder hint requests yet. Once hunters start using GPS hints on live drops, you'll see analytics here.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-telemetry">
      <div className="flex justify-end">
        <button
          onClick={resetTelemetry}
          disabled={resetting}
          className="btn-ghost text-xs inline-flex items-center gap-1"
          style={{ color: "#E53935", borderColor: "#E53935" }}
          data-testid="admin-telemetry-reset"
        >
          <Trash2 size={12} /> {resetting ? "Resetting…" : "Reset telemetry"}
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {bandMeta.map(({ k, label, color, Icon }) => {
          const v = bands[k] || 0;
          const pct = total_requests ? Math.round((v / total_requests) * 100) : 0;
          return (
            <div key={k} className="loot-card-flat p-4" style={{ borderColor: color }}>
              <div className="flex items-center gap-2">
                <Icon size={14} style={{ color }} />
                <span className="overline" style={{ color }}>{label}</span>
              </div>
              <div className="font-display text-3xl mt-1" style={{ color }}>{v}</div>
              <div className="font-mono text-[10px] text-[#F5F2E8]/50 mt-1">{pct}% of {total_requests}</div>
            </div>
          );
        })}
      </div>

      <div>
        <div className="overline mb-3">Top drops by hint requests</div>
        <div className="loot-card-flat divide-y divide-[#F5F2E8]/10">
          {top_drops.map((d) => {
            const bars = [
              { k: "hot", v: d.hot, color: "#E53935" },
              { k: "warmer", v: d.warmer, color: "#D7A62A" },
              { k: "warm", v: d.warm, color: "#D7A62A" },
              { k: "colder", v: d.colder, color: "#76B82A" },
              { k: "cold", v: d.cold, color: "#1e88e5" },
            ];
            return (
              <div key={d.drop_id} className="p-4" data-testid={`telemetry-row-${d.drop_id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="font-display uppercase text-lg leading-tight flex items-center gap-2">
                      {d.is_golden && <Crown size={14} style={{ color: "#D7A62A" }} />}
                      {d.title}
                    </div>
                    <div className="font-mono text-xs text-[#F5F2E8]/50 mt-1">
                      {d.city} · {d.status} · {d.unique_users} unique hunter{d.unique_users === 1 ? "" : "s"}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-2xl" style={{ color: "#76B82A" }}>{d.total_requests}</div>
                    <div className="font-mono text-[10px] text-[#F5F2E8]/50 uppercase">Requests</div>
                  </div>
                </div>
                <div className="mt-3 flex h-2 w-full overflow-hidden border border-[#F5F2E8]/10">
                  {bars.map(({ k, v, color }) => {
                    const pct = d.total_requests ? (v / d.total_requests) * 100 : 0;
                    if (pct === 0) return null;
                    return (
                      <div
                        key={k}
                        style={{ width: `${pct}%`, background: color }}
                        title={`${k}: ${v}`}
                      />
                    );
                  })}
                </div>
                <div className="mt-2 grid grid-cols-5 gap-2 text-[10px] font-mono">
                  {bars.map(({ k, v, color }) => (
                    <div key={k} className="flex items-center gap-1" style={{ color }}>
                      <span className="inline-block w-2 h-2" style={{ background: color }} />
                      <span>{k}: {v}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}


function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  // YYYY-MM-DDTHH:MM in local time (what <input type="datetime-local"> needs)
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function PackagesTab() {
  const [pkgs, setPkgs] = useState([]);
  const [saving, setSaving] = useState(null);

  const refresh = async () => {
    const { data } = await api.get("/admin/packages");
    // Split perks array into textarea string for local editing
    const rows = (data.packages || []).map((p) => ({
      ...p,
      _perks_text: (p.perks || []).join("\n"),
      _promo_expires_local: toLocalInput(p.promo_expires_at),
      _dirty: false,
    }));
    setPkgs(rows);
  };
  useEffect(() => { refresh(); }, []);

  const setField = (id, key, val) => {
    setPkgs((prev) =>
      prev.map((p) => (p.id === id ? { ...p, [key]: val, _dirty: true } : p))
    );
  };

  const save = async (id) => {
    const p = pkgs.find((x) => x.id === id);
    if (!p) return;
    setSaving(id);
    try {
      const perks = (p._perks_text || "")
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean);
      const payload = {
        label: p.label,
        amount: Number(p.amount),
        description: p.description || "",
        perks,
        highlight: !!p.highlight,
        active: !!p.active,
        promo_enabled: !!p.promo_enabled,
        promo_label: p.promo_label || "",
        promo_amount:
          p.promo_amount === "" || p.promo_amount === null || p.promo_amount === undefined
            ? null
            : Number(p.promo_amount),
        promo_expires_at: p._promo_expires_local || null,
      };
      // Client-side sanity check
      if (payload.promo_enabled) {
        if (payload.promo_amount === null || isNaN(payload.promo_amount)) {
          toast.error("Promo amount is required when promo is enabled.");
          setSaving(null);
          return;
        }
        if (payload.promo_amount >= payload.amount) {
          toast.error("Promo price must be less than the base price.");
          setSaving(null);
          return;
        }
      }
      await api.patch(`/admin/packages/${id}`, payload);
      toast.success(`${p.label} saved`);
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(null);
    }
  };

  if (pkgs.length === 0) {
    return <div className="text-[#F5F2E8]/60">Loading packages…</div>;
  }

  return (
    <div className="space-y-4" data-testid="admin-packages">
      <div className="loot-card-flat p-4 text-sm text-[#F5F2E8]/70 flex items-start gap-3">
        <PackageIcon size={16} className="text-[#D7A62A] mt-0.5" />
        <div>
          Edit sponsor tier <strong>labels, prices, descriptions, and perks</strong> live — no redeploy needed. Changes instantly flow to the{" "}
          <a href="/sponsor-apply" target="_blank" rel="noreferrer" className="text-[#76B82A] underline">
            Sponsor Apply page
          </a>{" "}
          and Stripe checkout. Tier IDs (mini/featured/city_takeover) are fixed — only the{" "}
          <code className="font-mono text-[#D7A62A]">city_takeover</code> tier can fund Golden Drops.
        </div>
      </div>

      {pkgs.map((p) => (
        <div
          key={p.id}
          className="loot-card-flat p-5 space-y-3"
          style={p.highlight ? { borderColor: "#D7A62A" } : undefined}
          data-testid={`pkg-${p.id}`}
        >
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Tag size={14} className="text-[#76B82A]" />
              <span className="font-mono text-xs uppercase tracking-widest text-[#F5F2E8]/50">
                {p.id}
              </span>
              {p.id === "city_takeover" && (
                <span
                  className="tag-pill inline-flex items-center gap-1"
                  style={{ color: "#D7A62A", borderColor: "#D7A62A" }}
                >
                  <Crown size={10} /> Unlocks Golden
                </span>
              )}
              {!p.active && (
                <span className="tag-pill" style={{ color: "#E53935", borderColor: "#E53935" }}>
                  Hidden
                </span>
              )}
            </div>
            <label className="flex items-center gap-2 text-xs font-mono text-[#F5F2E8]/70">
              <input
                type="checkbox"
                checked={!!p.active}
                onChange={(e) => setField(p.id, "active", e.target.checked)}
                data-testid={`pkg-active-${p.id}`}
              />
              Show on Sponsor Apply page
            </label>
          </div>

          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <div className="overline mb-1">Label</div>
              <input
                className="loot-input"
                value={p.label || ""}
                maxLength={80}
                onChange={(e) => setField(p.id, "label", e.target.value)}
                data-testid={`pkg-label-${p.id}`}
              />
            </div>
            <div>
              <div className="overline mb-1">Price (USD)</div>
              <input
                className="loot-input"
                type="number"
                step="1"
                min="0"
                max="100000"
                value={p.amount}
                onChange={(e) => setField(p.id, "amount", e.target.value)}
                data-testid={`pkg-amount-${p.id}`}
              />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={!!p.highlight}
                  onChange={(e) => setField(p.id, "highlight", e.target.checked)}
                  data-testid={`pkg-highlight-${p.id}`}
                />
                Show "Most popular" badge
              </label>
            </div>
          </div>

          <div>
            <div className="overline mb-1">Short description</div>
            <input
              className="loot-input"
              value={p.description || ""}
              maxLength={500}
              placeholder="e.g. Branded clue + winner tag + sponsor wall."
              onChange={(e) => setField(p.id, "description", e.target.value)}
              data-testid={`pkg-desc-${p.id}`}
            />
          </div>

          <div>
            <div className="overline mb-1">Perks (one per line, max 12)</div>
            <textarea
              rows={5}
              className="loot-input font-mono text-sm"
              value={p._perks_text}
              onChange={(e) => setField(p.id, "_perks_text", e.target.value)}
              data-testid={`pkg-perks-${p.id}`}
            />
            <p className="font-mono text-[10px] text-[#F5F2E8]/40 mt-1">
              Each line becomes a bullet point on the public pricing card.
            </p>
          </div>

          <div
            className="border-t border-[#F5F2E8]/10 pt-4 mt-2 space-y-3"
            style={p.promo_enabled ? { borderColor: "#E53935", borderTopWidth: 1 } : undefined}
          >
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Zap size={14} style={{ color: p.promo_enabled ? "#E53935" : "#F5F2E8" }} />
                <span
                  className="overline"
                  style={{ color: p.promo_enabled ? "#E53935" : undefined }}
                >
                  Limited-time promo
                </span>
                {p.promo_active && (
                  <span
                    className="tag-pill"
                    style={{ background: "#E53935", color: "#F5F2E8", borderColor: "#E53935" }}
                    data-testid={`pkg-promo-live-${p.id}`}
                  >
                    LIVE
                  </span>
                )}
                {p.promo_enabled && !p.promo_active && (
                  <span
                    className="tag-pill"
                    style={{ color: "#F5F2E8", borderColor: "#F5F2E8" }}
                    title="Promo is enabled but not currently active — check expiry date and that the promo price is lower than base."
                  >
                    INACTIVE
                  </span>
                )}
              </div>
              <label className="flex items-center gap-2 text-xs font-mono text-[#F5F2E8]/70">
                <input
                  type="checkbox"
                  checked={!!p.promo_enabled}
                  onChange={(e) => setField(p.id, "promo_enabled", e.target.checked)}
                  data-testid={`pkg-promo-enabled-${p.id}`}
                />
                Enable promo
              </label>
            </div>

            {p.promo_enabled && (
              <div className="grid md:grid-cols-3 gap-3">
                <div>
                  <div className="overline mb-1">Badge text</div>
                  <input
                    className="loot-input"
                    placeholder="e.g. Launch Week"
                    maxLength={40}
                    value={p.promo_label || ""}
                    onChange={(e) => setField(p.id, "promo_label", e.target.value)}
                    data-testid={`pkg-promo-label-${p.id}`}
                  />
                </div>
                <div>
                  <div className="overline mb-1">Promo price (USD)</div>
                  <input
                    className="loot-input"
                    type="number"
                    step="1"
                    min="0"
                    max="100000"
                    placeholder={`Less than ${p.amount}`}
                    value={p.promo_amount ?? ""}
                    onChange={(e) => setField(p.id, "promo_amount", e.target.value)}
                    data-testid={`pkg-promo-amount-${p.id}`}
                  />
                </div>
                <div>
                  <div className="overline mb-1">Expires at (local time)</div>
                  <input
                    className="loot-input"
                    type="datetime-local"
                    value={p._promo_expires_local || ""}
                    onChange={(e) => setField(p.id, "_promo_expires_local", e.target.value)}
                    data-testid={`pkg-promo-expires-${p.id}`}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between gap-3 pt-2">
            <div className="font-mono text-[10px] text-[#F5F2E8]/40">
              {p.updated_at ? `Last updated ${fmtEstDateTime(p.updated_at)}` : "Never edited"}
            </div>
            <button
              onClick={() => save(p.id)}
              disabled={!p._dirty || saving === p.id}
              className="btn-loot text-xs inline-flex items-center gap-1"
              data-testid={`pkg-save-${p.id}`}
            >
              <Save size={12} /> {saving === p.id ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}


function NotificationSettingsSection() {
  const [info, setInfo] = useState(null);
  const [email, setEmail] = useState("");
  const [sender, setSender] = useState("");
  const [newKey, setNewKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const refresh = async () => {
    const { data } = await api.get("/admin/settings/notifications");
    setInfo(data);
    setEmail(data.notify_email || "");
    setSender(data.sender || "");
  };
  useEffect(() => { refresh(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const body = { notify_email: email, sender };
      if (newKey.trim()) body.api_key = newKey.trim();
      await api.put("/admin/settings/notifications", body);
      toast.success("Notification settings saved");
      setNewKey("");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/settings/notifications/test");
      toast.success(`Test email sent to ${data.sent_to}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Test failed");
    } finally {
      setTesting(false);
    }
  };

  if (!info) return null;

  return (
    <div className="space-y-4" data-testid="admin-notifications">
      <div className="loot-card-flat p-4 text-sm text-[#F5F2E8]/70 flex items-start gap-3">
        <BellIcon size={16} className="text-[#76B82A] mt-0.5" />
        <div>
          Get emailed the moment a sponsor inquires or pays. Uses{" "}
          <a href="https://resend.com/api-keys" target="_blank" rel="noreferrer" className="text-[#76B82A] underline">
            Resend
          </a>{" "}
          — free tier covers 3,000 emails/month, no credit card. Grab a key from your Resend dashboard and paste it below.
        </div>
      </div>

      <div className="loot-card-flat p-5 space-y-3">
        <div>
          <div className="overline mb-1">Notification email (where to send)</div>
          <input
            className="loot-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@your-domain.com"
            data-testid="notif-email"
          />
        </div>

        <div>
          <div className="overline mb-1">From address (sender)</div>
          <input
            className="loot-input"
            value={sender}
            onChange={(e) => setSender(e.target.value)}
            placeholder="The Local Loot <onboarding@resend.dev>"
            data-testid="notif-sender"
            maxLength={160}
          />
          <p className="font-mono text-[10px] text-[#F5F2E8]/40 mt-1">
            Leave as <code>onboarding@resend.dev</code> until you verify your own domain in Resend → Domains. In test mode, Resend only delivers to the same email that signed up for the account.
          </p>
        </div>

        <div>
          <div className="overline mb-1 flex items-center gap-2">
            Resend API key
            {info.has_api_key && (
              <span className="tag-pill" style={{ color: "#76B82A", borderColor: "#76B82A" }}>
                Set · {info.api_key_source}
              </span>
            )}
          </div>
          <input
            className="loot-input font-mono text-sm"
            type="password"
            value={newKey}
            onChange={(e) => setNewKey(e.target.value)}
            placeholder={info.has_api_key ? `${info.masked_key} · paste new to rotate` : "re_…"}
            autoComplete="off"
            data-testid="notif-key"
          />
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={save} disabled={saving} className="btn-loot text-xs inline-flex items-center gap-1" data-testid="notif-save">
            <Save size={12} /> {saving ? "Saving…" : "Save"}
          </button>
          <button
            onClick={sendTest}
            disabled={testing || !info.has_api_key || !email}
            className="btn-ghost text-xs inline-flex items-center gap-1"
            data-testid="notif-test"
          >
            <BellIcon size={12} /> {testing ? "Sending…" : "Send test email"}
          </button>
        </div>
        {info.updated_at && (
          <div className="font-mono text-[10px] text-[#F5F2E8]/40">
            Last updated {fmtEstDateTime(info.updated_at)}
          </div>
        )}
      </div>
    </div>
  );
}


function LootQuestTab() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [includeTrash, setIncludeTrash] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [sponsors, setSponsors] = useState([]);
  const blank = {
    title: "", city: "", teaser: "", claim_code: "",
    prize_type: "cash", prize_amount: 0, prize_custom_label: "",
    sponsor_type: "self", sponsor_id: "",
    status: "draft", clues_text: "", teaser_image_url: "",
    enable_gps_hints: false, hint_center_lat: "", hint_center_lng: "", hint_radius_ft: 3280,
    signal_enabled: false,
  };
  const [form, setForm] = useState(blank);
  const [sponsorLinkQuestId, setSponsorLinkQuestId] = useState(null);

  const refresh = async () => {
    const params = {};
    if (filter !== "all") params.status = filter;
    if (includeTrash) params.include_trash = true;
    const { data } = await api.get("/admin/loot-quests", { params });
    setRows(data.loot_quests || []);
  };
  useEffect(() => { refresh(); }, [filter, includeTrash]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/sponsors");
        setSponsors(data.sponsors || []);
      } catch {
        setSponsors([]);
      }
    })();
  }, []);

  const startEdit = (q) => {
    setEditingId(q.id);
    setAdding(true);
    setForm({
      title: q.title || "",
      city: q.city || "",
      teaser: q.teaser || "",
      claim_code: q.claim_code || "",
      prize_type: q.prize_type || "cash",
      prize_amount: q.prize_amount ?? 0,
      prize_custom_label: q.prize_custom_label || "",
      sponsor_type: q.sponsor_type || "self",
      sponsor_id: q.sponsor_id || "",
      status: q.status || "draft",
      clues_text: (q.clues || []).map((c) => c.text).join("\n"),
      teaser_image_url: q.teaser_image_url || "",
      enable_gps_hints: !!q.enable_gps_hints,
      hint_center_lat: q.hint_center_lat ?? "",
      hint_center_lng: q.hint_center_lng ?? "",
      hint_radius_ft: q.hint_radius_ft || (q.hint_radius_m ? mToFt(q.hint_radius_m) : 3280),
      signal_enabled: !!q.signal_enabled,
    });
  };

  const submit = async () => {
    const clues = form.clues_text
      .split("\n").map((t) => t.trim()).filter(Boolean)
      .map((t, i) => ({ text: t, order: i + 1, release_offset_minutes: 0 }));
    const payload = {
      title: form.title, city: form.city, teaser: form.teaser || undefined,
      claim_code: form.claim_code, clues,
      prize_type: form.prize_type, prize_amount: Number(form.prize_amount) || 0,
      prize_custom_label: form.prize_custom_label || undefined,
      sponsor_type: form.sponsor_type,
      sponsor_id: form.sponsor_type === "business" ? form.sponsor_id : null,
      teaser_image_url: form.teaser_image_url || undefined,
      status: form.status,
      enable_gps_hints: !!form.enable_gps_hints,
      hint_center_lat: form.hint_center_lat === "" ? null : Number(form.hint_center_lat),
      hint_center_lng: form.hint_center_lng === "" ? null : Number(form.hint_center_lng),
      hint_radius_m: ftToM(Number(form.hint_radius_ft) || 3280),
      signal_enabled: !!form.signal_enabled,
    };
    if (payload.enable_gps_hints &&
        (payload.hint_center_lat === null || payload.hint_center_lng === null)) {
      toast.error("GPS hints require center lat/lng coordinates.");
      return;
    }
    try {
      if (editingId) {
        await api.patch(`/admin/loot-quests/${editingId}`, payload);
        toast.success("Loot Quest updated");
      } else {
        await api.post("/admin/loot-quests", payload);
        toast.success("Loot Quest created");
      }
      setAdding(false);
      setEditingId(null);
      setForm(blank);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/loot-quests/${id}`, { status });
      toast.success(`Marked ${status}`);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Move this Loot Quest to Trash?")) return;
    await api.delete(`/admin/loot-quests/${id}`);
    toast.success("Moved to Trash");
    refresh();
  };
  const restore = async (id) => {
    await api.post(`/admin/loot-quests/${id}/restore`);
    toast.success("Restored");
    refresh();
  };
  const hard = async (id) => {
    if (!window.confirm("Permanent delete? Cannot undo.")) return;
    await api.delete(`/admin/loot-quests/${id}/hard`);
    toast.success("Gone");
    refresh();
  };
  const resetClaim = async (id) => {
    if (!window.confirm("Un-claim this quest? The chest becomes unlocked again.")) return;
    await api.post(`/admin/loot-quests/${id}/reset-claim`);
    toast.success("Claim reset");
    refresh();
  };

  return (
    <div className="space-y-4" data-testid="admin-loot-quest">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-2 flex-wrap">
          {["all", "draft", "published", "hidden", "archived"].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`tag-pill text-xs ${filter === s ? "border-[#D7A62A] text-[#D7A62A]" : ""}`}
              data-testid={`lqfilter-${s}`}
            >
              {s}
            </button>
          ))}
          <label className="tag-pill text-xs flex items-center gap-1 cursor-pointer">
            <input type="checkbox" checked={includeTrash} onChange={(e) => setIncludeTrash(e.target.checked)} />
            Show trash
          </label>
        </div>
        <button
          onClick={() => { setAdding(!adding); setEditingId(null); setForm(blank); }}
          className="btn-loot text-xs inline-flex items-center gap-1"
          data-testid="lq-add-btn"
        >
          <Plus size={12} /> {adding ? "Cancel" : "New Loot Quest"}
        </button>
      </div>

      {adding && (
        <div className="loot-card-flat p-5 space-y-3" data-testid="lq-form">
          <div className="overline">{editingId ? "Edit" : "New"} Loot Quest</div>
          <div className="grid md:grid-cols-2 gap-2">
            <input className="loot-input" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="lq-title" />
            <input className="loot-input" placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="lq-city" />
          </div>
          <textarea className="loot-input" rows={2} placeholder="Teaser (short description shown on cards)" value={form.teaser} onChange={(e) => setForm({ ...form, teaser: e.target.value })} data-testid="lq-teaser" />
          <div className="grid md:grid-cols-2 gap-2">
            <input className="loot-input font-mono uppercase" placeholder="Unique claim code (e.g. LOOT-KP-042)" value={form.claim_code} onChange={(e) => setForm({ ...form, claim_code: e.target.value.toUpperCase() })} data-testid="lq-code" />
            <select className="loot-input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="lq-status">
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="hidden">Hidden</option>
              <option value="archived">Archived</option>
            </select>
          </div>
          <div className="grid md:grid-cols-3 gap-2">
            <select className="loot-input" value={form.prize_type} onChange={(e) => setForm({ ...form, prize_type: e.target.value })} data-testid="lq-prize-type">
              <option value="cash">Cash</option>
              <option value="gift_card">Gift Card</option>
              <option value="gift">Gift / Experience</option>
              <option value="merch">Merch</option>
              <option value="other">Other</option>
            </select>
            <input type="number" className="loot-input" placeholder="Prize amount (USD)" value={form.prize_amount} onChange={(e) => setForm({ ...form, prize_amount: e.target.value })} data-testid="lq-prize-amount" />
            {form.prize_type !== "cash" && (
              <input className="loot-input" placeholder="Prize label (e.g. Starbucks $25)" value={form.prize_custom_label} onChange={(e) => setForm({ ...form, prize_custom_label: e.target.value })} data-testid="lq-prize-label" maxLength={120} />
            )}
          </div>
          <div className="grid md:grid-cols-2 gap-2">
            <select className="loot-input" value={form.sponsor_type} onChange={(e) => setForm({ ...form, sponsor_type: e.target.value })} data-testid="lq-sponsor-type">
              <option value="self">Self-funded (The Local Loot)</option>
              <option value="business">Business sponsored</option>
            </select>
            {form.sponsor_type === "business" && (
              <select className="loot-input" value={form.sponsor_id} onChange={(e) => setForm({ ...form, sponsor_id: e.target.value })} data-testid="lq-sponsor-id">
                <option value="">Select sponsor...</option>
                {sponsors.map((s) => <option key={s.id} value={s.id}>{s.business_name}</option>)}
              </select>
            )}
          </div>
          <textarea className="loot-input font-mono text-sm" rows={4} placeholder="Clues (one per line, shown in order)" value={form.clues_text} onChange={(e) => setForm({ ...form, clues_text: e.target.value })} data-testid="lq-clues" />
          <input className="loot-input" placeholder="Teaser image URL (optional)" value={form.teaser_image_url} onChange={(e) => setForm({ ...form, teaser_image_url: e.target.value })} />

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.enable_gps_hints}
              onChange={(e) => setForm({ ...form, enable_gps_hints: e.target.checked })}
              data-testid="lq-gps-check"
            />
            <span>Enable warmer/colder GPS hints</span>
          </label>
          <label
            className={`flex items-center gap-2 text-sm ${form.enable_gps_hints ? "" : "opacity-50"}`}
            title={form.enable_gps_hints ? "" : "Requires GPS hints to be enabled"}
          >
            <input
              type="checkbox"
              disabled={!form.enable_gps_hints}
              checked={form.signal_enabled}
              onChange={(e) => setForm({ ...form, signal_enabled: e.target.checked })}
              data-testid="lq-signal-check"
            />
            <span className="flex items-center gap-1">
              <Compass size={14} style={{ color: "#E53935" }} /> Enable The Signal (Geiger-counter pulse)
            </span>
            <span className="font-mono text-[10px] text-[#F5F2E8]/50">
              (Audio + vibration that intensifies as the hunter closes in)
            </span>
          </label>
          {form.enable_gps_hints && (
            <>
              <ZonePicker
                lat={form.hint_center_lat}
                lng={form.hint_center_lng}
                radius={ftToM(form.hint_radius_ft)}
                onChange={({ lat, lng }) =>
                  setForm((f) => ({ ...f, hint_center_lat: lat, hint_center_lng: lng }))
                }
              />
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="font-mono text-[11px] text-[#F5F2E8]/50">
                  Chest coordinates (used for warmer/colder GPS hints).
                </span>
                <UseMyLocationButton
                  testid="lq-use-location"
                  onLocate={({ lat, lng }) =>
                    setForm((f) => ({
                      ...f,
                      hint_center_lat: String(lat),
                      hint_center_lng: String(lng),
                    }))
                  }
                />
              </div>
              <div className="grid md:grid-cols-3 gap-2">
                <input
                  className="loot-input"
                  type="number"
                  step="any"
                  placeholder="Hint center lat"
                  value={form.hint_center_lat}
                  onChange={(e) => setForm({ ...form, hint_center_lat: e.target.value })}
                  data-testid="lq-gps-lat"
                />
                <input
                  className="loot-input"
                  type="number"
                  step="any"
                  placeholder="Hint center lng"
                  value={form.hint_center_lng}
                  onChange={(e) => setForm({ ...form, hint_center_lng: e.target.value })}
                  data-testid="lq-gps-lng"
                />
                <input
                  className="loot-input"
                  type="number"
                  placeholder="Max radius (ft)"
                  value={form.hint_radius_ft}
                  onChange={(e) => setForm({ ...form, hint_radius_ft: e.target.value })}
                  data-testid="lq-gps-radius"
                />
              </div>
            </>
          )}
          <div className="flex items-center gap-2">
            <button onClick={submit} className="btn-loot text-xs" data-testid="lq-save">
              {editingId ? "Save changes" : "Create"}
            </button>
            <button onClick={() => { setAdding(false); setEditingId(null); setForm(blank); }} className="btn-ghost text-xs">Cancel</button>
          </div>
        </div>
      )}

      {rows.length === 0 && <div className="text-[#F5F2E8]/60">No Loot Quests in this view.</div>}
      <div className="space-y-2">
        {rows.map((q) => (
          <div
            key={q.id}
            className="loot-card-flat p-4 space-y-2"
            style={q.status === "draft" ? { borderColor: "#D7A62A", borderStyle: "dashed" } : undefined}
            data-testid={`lq-row-${q.id}`}
          >
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="min-w-0">
                <div className="font-display uppercase text-lg truncate flex items-center gap-2 flex-wrap">
                  {q.title}{" "}
                  <span className="font-mono text-xs text-[#F5F2E8]/50">· {q.city}</span>
                  {q.status === "draft" && (
                    <span
                      className="font-mono text-[10px] px-1.5 py-0.5"
                      style={{ color: "#D7A62A", border: "1px dashed #D7A62A" }}
                      data-testid={`lq-draft-badge-${q.id}`}
                    >
                      DRAFT · PRIVATE
                    </span>
                  )}
                </div>
                <div className="font-mono text-xs text-[#F5F2E8]/50 space-x-2">
                  <span className="tag-pill text-[10px]" style={{
                    color: q.status === "published" ? "#76B82A" : q.status === "archived" ? "#E53935" : "#D7A62A",
                    borderColor: q.status === "published" ? "#76B82A" : q.status === "archived" ? "#E53935" : "#D7A62A",
                  }}>
                    {q.status}
                  </span>
                  <span className="tag-pill text-[10px]" style={{ color: "#D7A62A", borderColor: "#D7A62A" }}>
                    {q.claim_code}
                  </span>
                  {q.claimed && (
                    <span className="tag-pill text-[10px]" style={{ color: "#E53935", borderColor: "#E53935" }}>
                      CLAIMED by @{q.claimed_by_username}
                    </span>
                  )}
                  {q.deleted_at && (
                    <span className="tag-pill text-[10px]" style={{ color: "#E53935", borderColor: "#E53935" }}>
                      TRASH
                    </span>
                  )}
                </div>
                <div className="font-mono text-xs text-[#F5F2E8]/50">
                  Prize: {q.prize_type === "cash" ? `$${q.prize_amount}` : `${q.prize_custom_label || q.prize_type} · ~$${q.prize_amount} value`}
                </div>
              </div>
              <div className="flex items-center gap-1 flex-wrap shrink-0">
                {!q.deleted_at && (
                  <>
                    <button onClick={() => startEdit(q)} className="btn-ghost text-xs">Edit</button>
                    {q.status === "draft" && (
                      <a
                        href={`/loot-quest/${q.id}?preview=1`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-ghost text-xs"
                        style={{ color: "#D7A62A", borderColor: "#D7A62A" }}
                        data-testid={`lq-preview-${q.id}`}
                      >
                        Preview as hunter
                      </a>
                    )}
                    {q.status === "draft" && (
                      <button
                        onClick={() => setSponsorLinkQuestId(q.id)}
                        className="btn-ghost text-xs"
                        style={{ color: "#D7A62A", borderColor: "#D7A62A" }}
                        data-testid={`lq-sponsor-link-${q.id}`}
                      >
                        Sponsor link
                      </button>
                    )}
                    {q.status !== "published" && <button onClick={() => setStatus(q.id, "published")} className="btn-ghost text-xs" style={{ color: "#76B82A", borderColor: "#76B82A" }}>Publish</button>}
                    {q.status === "published" && <button onClick={() => setStatus(q.id, "hidden")} className="btn-ghost text-xs">Hide</button>}
                    {q.status !== "archived" && <button onClick={() => setStatus(q.id, "archived")} className="btn-ghost text-xs">Archive</button>}
                    {q.claimed && <button onClick={() => resetClaim(q.id)} className="btn-ghost text-xs" style={{ color: "#D7A62A", borderColor: "#D7A62A" }}>Un-claim</button>}
                    <button onClick={() => remove(q.id)} className="btn-ghost text-xs" style={{ color: "#E53935", borderColor: "#E53935" }}>
                      <Trash2 size={12} className="inline" />
                    </button>
                  </>
                )}
                {q.deleted_at && (
                  <>
                    <button onClick={() => restore(q.id)} className="btn-ghost text-xs">Restore</button>
                    <button onClick={() => hard(q.id)} className="btn-ghost text-xs" style={{ color: "#E53935", borderColor: "#E53935" }}>Hard delete</button>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      {sponsorLinkQuestId && (
        <SponsorPreviewLinkModal
          dropId={sponsorLinkQuestId}
          drop={rows.find((x) => x.id === sponsorLinkQuestId)}
          resourceType="loot_quest"
          resourceTitle={rows.find((x) => x.id === sponsorLinkQuestId)?.title}
          resourcePath="loot-quest"
          onClose={() => setSponsorLinkQuestId(null)}
        />
      )}
    </div>
  );
}


function AutoReplySection() {
  const [tpl, setTpl] = useState(null);
  const refresh = async () => {
    const { data } = await api.get("/admin/settings/auto-reply");
    setTpl(data);
  };
  useEffect(() => { refresh(); }, []);
  if (!tpl) return null;
  const save = async () => {
    try {
      await api.put("/admin/settings/auto-reply", tpl);
      toast.success("Auto-reply template saved");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    }
  };
  return (
    <div className="space-y-3 loot-card-flat p-5" data-testid="admin-auto-reply">
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={tpl.enabled}
          onChange={(e) => setTpl({ ...tpl, enabled: e.target.checked })}
          data-testid="auto-reply-enabled"
        />
        Send an auto-reply whenever someone submits a sponsor inquiry
      </label>
      <div>
        <div className="overline mb-1">Subject line</div>
        <input
          className="loot-input"
          value={tpl.subject}
          onChange={(e) => setTpl({ ...tpl, subject: e.target.value })}
          maxLength={200}
          data-testid="auto-reply-subject"
        />
      </div>
      <div>
        <div className="overline mb-1">Body</div>
        <textarea
          className="loot-input font-mono text-sm"
          rows={8}
          value={tpl.body}
          onChange={(e) => setTpl({ ...tpl, body: e.target.value })}
          data-testid="auto-reply-body"
          maxLength={4000}
        />
        <p className="font-mono text-[10px] text-[#F5F2E8]/50 mt-1">
          Available placeholders: <code>{"{contact_name}"}</code>, <code>{"{business_name}"}</code>, <code>{"{city}"}</code>
        </p>
      </div>
      <button onClick={save} className="btn-loot text-xs inline-flex items-center gap-1" data-testid="auto-reply-save">
        <Save size={12} /> Save template
      </button>
    </div>
  );
}


function CityPopupSection() {
  const [cfg, setCfg] = useState(null);
  const refresh = async () => {
    const { data } = await api.get("/settings/city-popup");
    setCfg(data);
  };
  useEffect(() => { refresh(); }, []);
  if (!cfg) return null;
  const save = async () => {
    try {
      await api.put("/admin/settings/city-popup", cfg);
      toast.success("City popup settings saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    }
  };
  return (
    <div className="space-y-3 loot-card-flat p-5" data-testid="admin-city-popup">
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={cfg.enabled}
          onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
          data-testid="popup-enabled"
        />
        Show the email-capture popup on city pages
      </label>
      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <div className="overline mb-1">Title</div>
          <input
            className="loot-input"
            value={cfg.title}
            onChange={(e) => setCfg({ ...cfg, title: e.target.value })}
            maxLength={120}
            data-testid="popup-title"
          />
        </div>
        <div>
          <div className="overline mb-1">CTA button</div>
          <input
            className="loot-input"
            value={cfg.cta}
            onChange={(e) => setCfg({ ...cfg, cta: e.target.value })}
            maxLength={40}
            data-testid="popup-cta"
          />
        </div>
      </div>
      <div>
        <div className="overline mb-1">Subtitle</div>
        <textarea
          className="loot-input"
          rows={2}
          value={cfg.subtitle}
          onChange={(e) => setCfg({ ...cfg, subtitle: e.target.value })}
          maxLength={240}
          data-testid="popup-subtitle"
        />
      </div>
      <div>
        <div className="overline mb-1">Show after (seconds)</div>
        <input
          type="number"
          className="loot-input"
          min={0}
          max={120}
          value={cfg.delay_seconds}
          onChange={(e) => setCfg({ ...cfg, delay_seconds: Number(e.target.value) })}
          data-testid="popup-delay"
        />
      </div>
      <p className="font-mono text-[10px] text-[#F5F2E8]/50">
        Use <code>{"{city}"}</code> in title/subtitle to insert the current city.
      </p>
      <button onClick={save} className="btn-loot text-xs inline-flex items-center gap-1" data-testid="popup-save">
        <Save size={12} /> Save popup settings
      </button>
    </div>
  );
}


function BoostSettingsSection() {
  const [cfg, setCfg] = useState(null);
  const refresh = async () => {
    const { data } = await api.get("/boost/config");
    setCfg(data);
  };
  useEffect(() => { refresh(); }, []);
  if (!cfg) return null;
  const save = async () => {
    try {
      await api.put("/admin/boost/config", cfg);
      toast.success("Boost settings saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    }
  };
  return (
    <div className="space-y-3 loot-card-flat p-5" data-testid="admin-boost">
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={cfg.enabled}
          onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
          data-testid="boost-enabled"
        />
        Offer the paid Boost upgrade on drop pages (${cfg.price} / {cfg.duration_hours} h)
      </label>
      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <div className="overline mb-1">Price (USD)</div>
          <input
            type="number"
            className="loot-input"
            min={0}
            max={10000}
            value={cfg.price}
            onChange={(e) => setCfg({ ...cfg, price: Number(e.target.value) })}
            data-testid="boost-price"
          />
        </div>
        <div>
          <div className="overline mb-1">Duration (hours)</div>
          <input
            type="number"
            className="loot-input"
            min={1}
            max={720}
            value={cfg.duration_hours}
            onChange={(e) => setCfg({ ...cfg, duration_hours: Number(e.target.value) })}
            data-testid="boost-duration"
          />
        </div>
      </div>
      <button onClick={save} className="btn-loot text-xs inline-flex items-center gap-1" data-testid="boost-save">
        <Save size={12} /> Save boost settings
      </button>
    </div>
  );
}


function PurgeTestDataSection() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [purging, setPurging] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const loadPreview = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/purge-test-data/preview");
      setPreview(data.counts || {});
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setLoading(false);
    }
  };

  const purge = async () => {
    if (confirmText !== "PURGE") {
      toast.error('Type PURGE (all caps) to confirm.');
      return;
    }
    setPurging(true);
    try {
      const { data } = await api.post("/admin/purge-test-data", { confirm: "PURGE" });
      const d = data.deleted || {};
      const total = Object.values(d).reduce((a, b) => a + (b || 0), 0);
      toast.success(`Purged ${total} test rows`);
      setConfirmText("");
      loadPreview();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Purge failed");
    } finally {
      setPurging(false);
    }
  };

  const rows = preview ? Object.entries(preview) : [];
  const total = rows.reduce((a, [, v]) => a + (v || 0), 0);

  return (
    <div
      className="loot-card-flat p-5 space-y-3"
      style={{ borderColor: "#E53935" }}
      data-testid="admin-purge-test"
    >
      <div className="overline" style={{ color: "#E53935" }}>Purge test data</div>
      <p className="font-mono text-xs text-[#F5F2E8]/60">
        Hard-deletes rows whose identifiers match the QA naming conventions used during automated testing
        (prefixes <code>test_</code>, <code>TEST_</code>, <code>gw_</code>, <code>GW_</code>, <code>iter…</code>,
        <code>ci_</code>, <code>crud_</code>, <code>selfie_</code>). Admin accounts are <strong>never</strong> touched.
        Preview first, then type <strong>PURGE</strong> to confirm.
      </p>
      <div className="flex flex-wrap gap-2">
        <button
          onClick={loadPreview}
          disabled={loading}
          className="btn-ghost text-xs"
          data-testid="purge-preview-btn"
        >
          {loading ? "Loading…" : preview ? "Refresh counts" : "Preview"}
        </button>
      </div>

      {preview && (
        <div className="font-mono text-xs text-[#F5F2E8]/75 space-y-1 pt-2 border-t border-[#F5F2E8]/10" data-testid="purge-preview-list">
          {rows.map(([k, v]) => (
            <div key={k} className="flex justify-between">
              <span>{k}</span>
              <span style={{ color: v > 0 ? "#E53935" : "#F5F2E8" }}>{v}</span>
            </div>
          ))}
          <div className="flex justify-between font-display uppercase text-sm pt-2 border-t border-[#F5F2E8]/10">
            <span>TOTAL</span>
            <span style={{ color: total > 0 ? "#E53935" : "#76B82A" }}>{total}</span>
          </div>
        </div>
      )}

      {preview && total > 0 && (
        <div className="pt-2 space-y-2">
          <input
            className="loot-input"
            placeholder="Type PURGE to confirm"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            data-testid="purge-confirm-input"
          />
          <button
            onClick={purge}
            disabled={purging || confirmText !== "PURGE"}
            className="btn-ghost text-xs"
            style={{ color: "#E53935", borderColor: "#E53935" }}
            data-testid="purge-confirm-btn"
          >
            <Trash2 size={12} className="inline" /> {purging ? "Purging…" : `Purge ${total} test rows`}
          </button>
        </div>
      )}
    </div>
  );
}

function DataToolsSection() {
  const [sending, setSending] = useState(false);
  const exportFile = (path, name) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}${path}`;
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    // Needs auth cookie — fetch blob manually to include Authorization header
    (async () => {
      try {
        const res = await api.get(path, { responseType: "blob" });
        const blob = new Blob([res.data], { type: "text/csv" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = name;
        link.click();
        URL.revokeObjectURL(link.href);
      } catch {
        toast.error("Export failed");
      }
    })();
  };
  const sendWeekly = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/admin/sponsors/weekly-report/send");
      toast.success(`Sent ${data.sent} / ${data.total_sponsors} reports`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="space-y-3" data-testid="admin-data-tools">
      <div className="loot-card-flat p-5 space-y-3">
        <div className="overline">Exports</div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => exportFile("/admin/export/winners.csv", "winners.csv")}
            className="btn-ghost text-xs"
            data-testid="export-winners"
          >
            Winners CSV
          </button>
          <button
            onClick={() => exportFile("/admin/export/sponsors.csv", "sponsors.csv")}
            className="btn-ghost text-xs"
            data-testid="export-sponsors"
          >
            Sponsors CSV
          </button>
          <button
            onClick={() => exportFile("/admin/export/inquiries.csv", "inquiries.csv")}
            className="btn-ghost text-xs"
            data-testid="export-inquiries"
          >
            Inquiries CSV
          </button>
          <button
            onClick={() => exportFile("/admin/export/city-subscribers.csv", "city-subscribers.csv")}
            className="btn-ghost text-xs"
            data-testid="export-city-subs"
          >
            City Subscribers CSV
          </button>
        </div>
      </div>
      <div className="loot-card-flat p-5 space-y-3">
        <div className="overline">Sponsor weekly reports</div>
        <p className="font-mono text-xs text-[#F5F2E8]/60">
          Email every sponsor a 7-day digest (drops, winners, scans). Triggered manually — run it whenever you want (e.g. every Monday).
        </p>
        <button
          onClick={sendWeekly}
          disabled={sending}
          className="btn-loot text-xs inline-flex items-center gap-1"
          data-testid="send-weekly"
        >
          {sending ? "Sending…" : "Send this week's reports"}
        </button>
      </div>

      <div className="loot-card-flat p-5 space-y-3" data-testid="admin-sync-leaderboard">
        <div className="overline">Sync leaderboard</div>
        <p className="font-mono text-xs text-[#F5F2E8]/60">
          Recalculate leaderboard ranks, unique winners, and totals. Run this after deleting or trashing winners so the public leaderboard matches the new reality.
        </p>
        <button
          onClick={async () => {
            try {
              const { data } = await api.post("/admin/leaderboard/rebuild");
              toast.success(
                `Synced · ${data.winners_approved} approved · ${data.unique_winners} unique hunters`,
              );
            } catch (e) {
              toast.error(formatApiError(e.response?.data?.detail) || "Sync failed");
            }
          }}
          className="btn-loot text-xs inline-flex items-center gap-1"
          data-testid="sync-leaderboard-btn"
        >
          <RefreshCw size={12} /> Sync leaderboard now
        </button>
      </div>

      <PurgeTestDataSection />

      <div className="loot-card-flat p-5 space-y-3" data-testid="admin-reset-feed">
        <div className="overline" style={{ color: "#E53935" }}>Reset activity feed</div>
        <p className="font-mono text-xs text-[#F5F2E8]/60">
          Wipes the public Notifications feed so you can start fresh. Does NOT affect drops, winners, sponsors, or users.
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={async () => {
              if (!window.confirm("Clear ALL activity feed items? This cannot be undone.")) return;
              try {
                const { data } = await api.delete("/admin/notifications");
                toast.success(`Cleared ${data.deleted} items`);
              } catch (e) {
                toast.error(formatApiError(e.response?.data?.detail) || "Failed");
              }
            }}
            className="btn-ghost text-xs"
            style={{ color: "#E53935", borderColor: "#E53935" }}
            data-testid="reset-feed-all"
          >
            <Trash2 size={12} className="inline" /> Clear everything
          </button>
          <button
            onClick={async () => {
              if (!window.confirm("Clear notifications older than 7 days?")) return;
              try {
                const { data } = await api.delete("/admin/notifications?keep_days=7");
                toast.success(`Cleared ${data.deleted} old items`);
              } catch (e) {
                toast.error(formatApiError(e.response?.data?.detail) || "Failed");
              }
            }}
            className="btn-ghost text-xs"
            data-testid="reset-feed-old"
          >
            Clear older than 7 days
          </button>
        </div>
      </div>
    </div>
  );
}




function SupportTab() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [statusFilter, setStatusFilter] = useState("open");
  const [loading, setLoading] = useState(true);

  const load = async (s = statusFilter) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/support/tickets?status=${s}&limit=200`);
      setRows(data.tickets || []);
      setCounts(data.counts || {});
    } catch {
      toast.error("Couldn't load tickets");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(statusFilter); }, [statusFilter]);

  const patch = async (id, body) => {
    try {
      await api.patch(`/admin/support/tickets/${id}`, body);
      toast.success("Updated");
      load(statusFilter);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Update failed");
    }
  };

  const CATEGORY_META = {
    app_bug:       { label: "🐛 App bug", color: "#1e88e5" },
    cheating:      { label: "🚨 Cheating", color: "#E53935" },
    tos_violation: { label: "⚠️ ToS violation", color: "#D7A62A" },
    drop_issue:    { label: "📦 Drop issue", color: "#76B82A" },
    waymark_issue: { label: "🏛️ Waymark", color: "#76B82A" },
    other:         { label: "💬 Other", color: "#F5F2E8" },
  };
  const STATUS_META = {
    open:          { label: "Open", color: "#E53935" },
    investigating: { label: "Investigating", color: "#D7A62A" },
    resolved:      { label: "Resolved", color: "#76B82A" },
    dismissed:     { label: "Dismissed", color: "#F5F2E8" },
  };

  const STATUSES = ["open", "investigating", "resolved", "dismissed", "all"];

  return (
    <div className="space-y-4" data-testid="admin-support-tab">
      <div className="overline">Support tickets</div>

      <div className="flex flex-wrap gap-2">
        {STATUSES.map((s) => {
          const count = s === "all"
            ? Object.values(counts).reduce((a, b) => a + b, 0)
            : (counts[s] || 0);
          const active = statusFilter === s;
          return (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 font-display uppercase tracking-wide text-sm transition ${
                active ? "text-[#76B82A] border-b-2 border-[#76B82A]" : "text-[#F5F2E8]/60 hover:text-[#F5F2E8]"
              }`}
              data-testid={`support-filter-${s}`}
            >
              {s} <span className="font-mono text-[10px] opacity-70">({count})</span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="text-[#F5F2E8]/60">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="loot-card-flat p-10 text-center text-[#F5F2E8]/60">
          No tickets in this filter.
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((t) => {
            const cat = CATEGORY_META[t.category] || CATEGORY_META.other;
            const st = STATUS_META[t.status] || STATUS_META.open;
            return (
              <div
                key={t.id}
                className="loot-card-flat p-4 space-y-3"
                data-testid={`support-ticket-${t.id}`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className="font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border"
                        style={{ color: cat.color, borderColor: cat.color }}
                      >
                        {cat.label}
                      </span>
                      <span
                        className="font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border"
                        style={{ color: st.color, borderColor: st.color }}
                      >
                        {st.label}
                      </span>
                      <span className="font-mono text-[10px] text-[#F5F2E8]/50">
                        {fmtEstDateTime(t.created_at)}
                      </span>
                    </div>
                    <div className="font-display uppercase text-lg mt-2">{t.subject}</div>
                    <div className="font-mono text-[11px] text-[#F5F2E8]/60 mt-1">
                      {t.username ? `@${t.username}` : "anonymous"}
                      {t.contact_email && <> · {t.contact_email}</>}
                      {t.page_url && <> · <a href={t.page_url} className="underline" target="_blank" rel="noreferrer">page</a></>}
                    </div>
                  </div>
                </div>

                <div className="text-sm text-[#F5F2E8]/85 whitespace-pre-line border-l-2 border-[#F5F2E8]/15 pl-3">
                  {t.details}
                </div>

                <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-[#F5F2E8]/10">
                  {t.status !== "investigating" && (
                    <button onClick={() => patch(t.id, { status: "investigating" })} className="btn-ghost text-xs" data-testid={`ticket-investigating-${t.id}`}>
                      Mark investigating
                    </button>
                  )}
                  {t.status !== "resolved" && (
                    <button onClick={() => patch(t.id, { status: "resolved" })} className="btn-ghost text-xs" style={{ color: "#76B82A", borderColor: "#76B82A" }} data-testid={`ticket-resolve-${t.id}`}>
                      Resolve
                    </button>
                  )}
                  {t.status !== "dismissed" && (
                    <button onClick={() => patch(t.id, { status: "dismissed" })} className="btn-ghost text-xs" style={{ color: "#E53935", borderColor: "#E53935" }} data-testid={`ticket-dismiss-${t.id}`}>
                      Dismiss
                    </button>
                  )}
                  {t.contact_email && (
                    <a
                      href={`mailto:${t.contact_email}?subject=Re: ${encodeURIComponent(t.subject)}`}
                      className="btn-ghost text-xs"
                      data-testid={`ticket-email-${t.id}`}
                    >
                      Email reporter
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}



function ManualFounderGrantPanel() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [city, setCity] = useState("");
  const [note, setNote] = useState("");
  const [granting, setGranting] = useState(false);

  const search = async (raw) => {
    const v = (raw ?? q).trim();
    setQ(v);
    if (v.length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    try {
      const { data } = await api.get(`/admin/users?q=${encodeURIComponent(v)}&limit=12`);
      // hide admins (the badge is "found the founder" — admins shouldn't get it)
      setResults((data.users || []).filter((u) => u.role !== "admin"));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Search failed");
    } finally {
      setSearching(false);
    }
  };

  const grant = async () => {
    if (!selected) return;
    setGranting(true);
    try {
      const { data } = await api.post("/admin/founder/grant-badge", {
        user_id: selected.id,
        city: city.trim() || undefined,
        note: note.trim() || undefined,
      });
      if (data.already_had_badge) {
        toast.info(`@${selected.username || selected.email} already has the Founder badge.`);
      } else {
        toast.success(`🏆 Granted Founder badge to @${selected.username || selected.email}`);
      }
      if (data.streak_badges_unlocked?.length) {
        for (const b of data.streak_badges_unlocked) {
          setTimeout(() => toast.success(`🌟 Unlocked: ${b}`), 600);
        }
      }
      setSelected(null);
      setCity("");
      setNote("");
      setQ("");
      setResults([]);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Grant failed");
    } finally {
      setGranting(false);
    }
  };

  return (
    <div
      className="loot-card-flat p-6 space-y-3"
      style={{ borderColor: "rgba(215,166,42,0.4)" }}
      data-testid="founder-manual-grant"
    >
      <div className="flex items-center gap-2">
        <Crown size={14} style={{ color: "#D7A62A" }} />
        <div className="overline" style={{ color: "#D7A62A", borderColor: "#D7A62A" }}>
          Manual badge grant
        </div>
      </div>
      <p className="text-xs text-[#F5F2E8]/65 leading-relaxed">
        Use this when a hunter cracks a stealth riddle in person, meets you off-app, or
        the GPS check fails for a legit meet. Adds <span className="font-mono">"Found the Founder"</span>{" "}
        and re-evaluates the 3-city / 10-city streak badges.
      </p>

      {!selected ? (
        <div>
          <input
            type="text"
            className="loot-input"
            placeholder="Search hunter by username, email, or name…"
            value={q}
            onChange={(e) => search(e.target.value)}
            data-testid="founder-grant-search"
          />
          {searching && (
            <div className="font-mono text-[10px] text-[#F5F2E8]/50 mt-2 uppercase tracking-widest">
              Searching…
            </div>
          )}
          {!searching && q.trim().length >= 2 && results.length === 0 && (
            <div className="font-mono text-[10px] text-[#F5F2E8]/50 mt-2 uppercase tracking-widest">
              No matching hunters.
            </div>
          )}
          {results.length > 0 && (
            <div className="mt-2 border border-[#F5F2E8]/15 divide-y divide-[#F5F2E8]/10 max-h-72 overflow-auto">
              {results.map((u) => {
                const has = (u.badges || []).includes("Found the Founder");
                return (
                  <button
                    type="button"
                    key={u.id}
                    onClick={() => {
                      setSelected(u);
                      setCity(u.city || "");
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-[#F5F2E8]/5 flex items-center gap-3"
                    data-testid={`founder-grant-result-${u.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-display uppercase text-sm leading-tight truncate">
                        @{u.username || u.email}
                      </div>
                      <div className="font-mono text-[10px] text-[#F5F2E8]/50 truncate">
                        {u.name || ""}{u.name && u.email ? " · " : ""}{u.email}
                        {u.city ? ` · ${u.city}` : ""}
                      </div>
                    </div>
                    {has && (
                      <span
                        className="tag-pill"
                        style={{ color: "#D7A62A", borderColor: "#D7A62A" }}
                      >
                        Has badge
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center gap-3 p-3 border border-[#D7A62A]/40 bg-[#D7A62A]/5">
            <div className="flex-1 min-w-0">
              <div className="font-display uppercase text-base leading-tight truncate">
                @{selected.username || selected.email}
              </div>
              <div className="font-mono text-[10px] text-[#F5F2E8]/55 truncate">
                {selected.email}
                {selected.city ? ` · ${selected.city}` : ""}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setSelected(null)}
              className="btn-ghost text-xs"
              data-testid="founder-grant-clear"
            >
              Change
            </button>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <input
              className="loot-input"
              placeholder="City this counts toward (defaults to today's founder city)"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              maxLength={80}
              data-testid="founder-grant-city"
            />
            <input
              className="loot-input"
              placeholder="Note for the hunter (optional)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={200}
              data-testid="founder-grant-note"
            />
          </div>

          <button
            type="button"
            disabled={granting}
            onClick={grant}
            className="btn-gold inline-flex items-center gap-2"
            data-testid="founder-grant-submit"
          >
            <Crown size={14} />
            {granting ? "Granting…" : "Grant Founder badge"}
          </button>
        </div>
      )}
    </div>
  );
}


// =====================================================================
// Met the Founder admin — pending submissions queue + page copy editor
// =====================================================================
// =====================================================================
// The Spoils — store admin (settings + products CRUD + orders)
// =====================================================================
function SpoilsAdminTab() {
  const [tab, setTab] = useState("products");
  const [settings, setSettings] = useState(null);
  const [defaults, setDefaults] = useState({});
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState({ items: [], counts: {} });
  const [orderFilter, setOrderFilter] = useState("pending");
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadSettings = async () => {
    try { const { data } = await api.get("/admin/store/settings"); setSettings(data.settings); setDefaults(data.defaults || {}); } catch { /* */ }
  };
  const loadProducts = async () => {
    try { const { data } = await api.get("/admin/store/products"); setProducts(data.items || []); } catch { setProducts([]); }
  };
  const loadOrders = async () => {
    try { const { data } = await api.get(`/admin/store/orders?status=${orderFilter}`); setOrders(data); } catch { setOrders({ items: [], counts: {} }); }
  };
  useEffect(() => { loadSettings(); loadProducts(); }, []);
  useEffect(() => { if (tab === "orders") loadOrders(); /* eslint-disable-next-line */ }, [tab, orderFilter]);

  const updateS = (k, v) => setSettings({ ...settings, [k]: v });
  const resetField = (k) => updateS(k, defaults[k] ?? "");

  const saveSettings = async () => {
    setBusy(true);
    try {
      const payload = {};
      Object.keys(defaults).forEach((k) => { payload[k] = settings[k] ?? (typeof defaults[k] === "boolean" ? false : ""); });
      await api.put("/admin/store/settings", payload);
      toast.success("Settings saved");
      loadSettings();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };

  const newProduct = () => setEditing({
    name: "", slug: "", kind: "digital", pricing_mode: "fixed",
    price_cents: 500, min_cents: 0, suggested_cents: 0,
    description: "", cover_image_url: "", file_url: "", gallery: [],
    category: "", tags: [], stock_remaining: null, active: true, featured: false, sort_order: 0,
  });
  const editProduct = (p) => setEditing({ ...p, tags: p.tags || [], gallery: p.gallery || [] });
  const saveProduct = async () => {
    if (!editing.name?.trim()) return toast.error("Name required");
    if (editing.kind === "digital" && !editing.file_url) return toast.error("Digital products need a file_url.");
    setBusy(true);
    try {
      const payload = { ...editing };
      payload.price_cents = Number(payload.price_cents || 0);
      payload.min_cents = Number(payload.min_cents || 0);
      payload.suggested_cents = Number(payload.suggested_cents || 0);
      payload.stock_remaining = payload.stock_remaining === "" || payload.stock_remaining === null ? null : Number(payload.stock_remaining);
      if (editing.id) await api.patch(`/admin/store/products/${editing.id}`, payload);
      else await api.post("/admin/store/products", payload);
      toast.success(editing.id ? "Product updated" : "Product created");
      setEditing(null);
      loadProducts();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };
  const delProduct = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? Orders remain.`)) return;
    try { await api.delete(`/admin/store/products/${p.id}`); loadProducts(); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const updateOrder = async (o, patch) => {
    try {
      await api.patch(`/admin/store/orders/${o.id}`, patch);
      toast.success("Order updated");
      loadOrders();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const fmt = (c) => `$${(Number(c||0)/100).toFixed(2)}`;

  if (!settings) return <div className="font-mono text-xs text-[#F5F2E8]/55">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="admin-spoils">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h3 className="font-display uppercase text-xl tracking-tight">The Spoils — Storefront 💰</h3>
          <p className="font-mono text-xs text-[#F5F2E8]/55 mt-1">
            Public at <code>/spoils</code>. Toggle store on/off, manage products + orders.
          </p>
        </div>
        <label className="inline-flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={!!settings.enabled}
            onChange={async (e) => { await api.put("/admin/store/settings", { enabled: e.target.checked }); loadSettings(); toast.success(e.target.checked ? "Store OPEN" : "Store CLOSED"); }}
            data-testid="spoils-enabled-toggle"
          />
          <span className="font-display uppercase tracking-widest" style={{ color: settings.enabled ? "#76B82A" : "#E53935" }}>
            {settings.enabled ? "OPEN" : "CLOSED"}
          </span>
        </label>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 flex-wrap border-b border-[#F5F2E8]/10 pb-2">
        {[
          { k: "products", l: `Products (${products.length})` },
          { k: "orders", l: `Orders` },
          { k: "settings", l: `Settings + Copy` },
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} className={`btn-ghost text-xs ${tab === t.k ? "is-active" : ""}`}
            style={tab === t.k ? { borderColor: "#D7A62A", color: "#D7A62A" } : {}}
            data-testid={`spoils-tab-${t.k}`}>
            {t.l}
          </button>
        ))}
      </div>

      {/* PRODUCTS */}
      {tab === "products" && (
        <div className="space-y-3">
          {!editing && (
            <button onClick={newProduct} className="btn-loot text-xs inline-flex items-center gap-1" data-testid="spoils-new-product">
              <Plus size={12} /> New product
            </button>
          )}
          {editing && (
            <div className="loot-card-flat p-4 space-y-2" data-testid="spoils-product-editor">
              <div className="overline">{editing.id ? "Edit product" : "Create product"}</div>
              <div className="grid md:grid-cols-2 gap-2">
                <input className="loot-input" placeholder="Name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="spoils-p-name" />
                <input className="loot-input" placeholder="Slug (auto from name)" value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} />
                <select className="loot-input" value={editing.kind} onChange={(e) => setEditing({ ...editing, kind: e.target.value })}>
                  <option value="digital">Digital download</option>
                  <option value="merch">Merch (physical)</option>
                </select>
                <select className="loot-input" value={editing.pricing_mode} onChange={(e) => setEditing({ ...editing, pricing_mode: e.target.value })}>
                  <option value="fixed">Fixed price</option>
                  <option value="pwyw">Pay what you want</option>
                </select>
                {editing.pricing_mode === "fixed" ? (
                  <label className="block">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65">Price (cents)</span>
                    <input type="number" min="0" className="loot-input mt-1" value={editing.price_cents} onChange={(e) => setEditing({ ...editing, price_cents: e.target.value })} />
                  </label>
                ) : (
                  <>
                    <label className="block">
                      <span className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65">Minimum (cents · 0 = free)</span>
                      <input type="number" min="0" className="loot-input mt-1" value={editing.min_cents} onChange={(e) => setEditing({ ...editing, min_cents: e.target.value })} />
                    </label>
                    <label className="block">
                      <span className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65">Suggested (cents)</span>
                      <input type="number" min="0" className="loot-input mt-1" value={editing.suggested_cents} onChange={(e) => setEditing({ ...editing, suggested_cents: e.target.value })} />
                    </label>
                  </>
                )}
                <input className="loot-input" placeholder="Category (e.g. Guides, Stickers)" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} />
                {editing.kind === "merch" && (
                  <label className="block">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65">Stock (blank = unlimited)</span>
                    <input type="number" min="0" className="loot-input mt-1" value={editing.stock_remaining ?? ""} onChange={(e) => setEditing({ ...editing, stock_remaining: e.target.value })} />
                  </label>
                )}
              </div>
              <textarea rows={3} className="loot-input" placeholder="Description" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
              <div>
                <span className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65">Cover image</span>
                <PhotoUploader value={editing.cover_image_url} onChange={(v) => setEditing({ ...editing, cover_image_url: v })} testid="spoils-p-cover" />
              </div>
              {editing.kind === "digital" && (
                <div>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65">File (the actual download — PDF/zip/etc)</span>
                  <PhotoUploader value={editing.file_url} onChange={(v) => setEditing({ ...editing, file_url: v })} testid="spoils-p-file" />
                  <input className="loot-input mt-2 text-xs" placeholder="…or paste a URL" value={editing.file_url} onChange={(e) => setEditing({ ...editing, file_url: e.target.value })} />
                </div>
              )}
              <div className="flex flex-wrap gap-3">
                <label className="text-xs font-mono flex items-center gap-1"><input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} /> Active</label>
                <label className="text-xs font-mono flex items-center gap-1"><input type="checkbox" checked={editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} /> Featured</label>
                <label className="text-xs font-mono flex items-center gap-1">Sort <input type="number" className="loot-input ml-1 w-20 text-xs" value={editing.sort_order} onChange={(e) => setEditing({ ...editing, sort_order: Number(e.target.value || 0) })} /></label>
              </div>
              <div className="flex gap-2 pt-1">
                <button onClick={saveProduct} disabled={busy} className="btn-loot text-xs" data-testid="spoils-p-save">{busy ? "Saving…" : "Save"}</button>
                <button onClick={() => setEditing(null)} className="btn-ghost text-xs">Cancel</button>
              </div>
            </div>
          )}

          <div className="space-y-2">
            {products.length === 0 && <div className="font-mono text-xs text-[#F5F2E8]/45">No products yet.</div>}
            {products.map((p) => (
              <div key={p.id} className="loot-card-flat p-3 flex items-start gap-3 flex-wrap" data-testid={`spoils-admin-product-${p.id}`}>
                <div className="w-14 h-14 bg-[#0F0F0F] border border-[#F5F2E8]/15 flex items-center justify-center flex-shrink-0">
                  {p.cover_image_url ? <img src={p.cover_image_url} alt="" className="w-full h-full object-cover" /> : <span className="text-[#F5F2E8]/30">{p.kind === "digital" ? "📁" : "🎁"}</span>}
                </div>
                <div className="flex-1 min-w-[180px]">
                  <div className="font-display uppercase text-sm">{p.name} {!p.active && <span className="text-[#E53935] text-[10px]">[inactive]</span>} {p.featured && <span className="text-[#D7A62A] text-[10px]">[featured]</span>}</div>
                  <div className="font-mono text-[10px] text-[#F5F2E8]/55">
                    {p.kind} · {p.pricing_mode === "fixed" ? fmt(p.price_cents) : `PWYW · min ${fmt(p.min_cents)}`}{p.category ? ` · ${p.category}` : ""}{p.kind === "merch" && p.stock_remaining !== null ? ` · stock ${p.stock_remaining}` : ""}
                  </div>
                  <div className="font-mono text-[10px] text-[#F5F2E8]/35">/spoils/{p.slug}</div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => editProduct(p)} className="btn-ghost text-[10px]">Edit</button>
                  <button onClick={() => delProduct(p)} className="btn-ghost text-[10px]" style={{ color: "#E53935", borderColor: "#E53935" }}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ORDERS */}
      {tab === "orders" && (
        <div className="space-y-2">
          <div className="flex gap-2 flex-wrap">
            {["pending", "paid", "shipped", "delivered", "cancelled", "all"].map((s) => (
              <button key={s} onClick={() => setOrderFilter(s)}
                className="btn-ghost text-xs"
                style={orderFilter === s ? { borderColor: "#D7A62A", color: "#D7A62A" } : {}}>
                {s} ({orders.counts?.[s] || 0})
              </button>
            ))}
          </div>
          {orders.items.length === 0 && <div className="font-mono text-xs text-[#F5F2E8]/45">No {orderFilter} orders.</div>}
          {orders.items.map((o) => (
            <div key={o.id} className="loot-card-flat p-4 space-y-2" data-testid={`spoils-admin-order-${o.id}`}>
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div>
                  <div className="font-display uppercase text-sm">{o.product_name} · {fmt(o.amount_cents)}</div>
                  <div className="font-mono text-[10px] text-[#F5F2E8]/55">{o.created_at?.slice(0, 16)} · {o.payment_method} · {o.product_kind}</div>
                </div>
                <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-0.5"
                  style={{ color: { pending: "#D7A62A", paid: "#76B82A", shipped: "#76B82A", delivered: "#76B82A", cancelled: "#E53935" }[o.status] || "#F5F2E8",
                           borderLeft: `2px solid ${{ pending: "#D7A62A", paid: "#76B82A", shipped: "#76B82A", delivered: "#76B82A", cancelled: "#E53935" }[o.status] || "#F5F2E8"}` }}>
                  {o.status}
                </span>
              </div>
              <div className="text-sm">
                <span className="text-[#F5F2E8]/55">Buyer: </span>
                <strong>{o.buyer_email}</strong>{o.buyer_name ? ` (${o.buyer_name})` : ""}
              </div>
              {o.note && <p className="text-xs text-[#F5F2E8]/70 italic">"{o.note}"</p>}
              {o.shipping_address && (
                <div className="text-xs text-[#F5F2E8]/70 border-l-2 border-[#76B82A] pl-2">
                  {o.shipping_address.name}<br />
                  {o.shipping_address.address_1}{o.shipping_address.address_2 ? `, ${o.shipping_address.address_2}` : ""}<br />
                  {o.shipping_address.city}, {o.shipping_address.state} {o.shipping_address.zip}
                </div>
              )}
              <div className="flex flex-wrap gap-1">
                {o.status === "pending" && <button onClick={() => updateOrder(o, { status: "paid" })} className="btn-ghost text-[10px]" style={{ color: "#76B82A", borderColor: "#76B82A" }}>Mark paid</button>}
                {o.product_kind === "merch" && (o.status === "paid") && <button onClick={() => updateOrder(o, { status: "shipped", tracking_number: window.prompt("Tracking #?") || "" })} className="btn-ghost text-[10px]">Mark shipped</button>}
                {o.product_kind === "merch" && o.status === "shipped" && <button onClick={() => updateOrder(o, { status: "delivered" })} className="btn-ghost text-[10px]">Mark delivered</button>}
                {o.status !== "cancelled" && <button onClick={() => updateOrder(o, { status: "cancelled" })} className="btn-ghost text-[10px]" style={{ color: "#E53935", borderColor: "#E53935" }}>Cancel</button>}
                <button onClick={() => { const n = window.prompt("Note to buyer:", o.admin_note || ""); if (n !== null) updateOrder(o, { admin_note: n }); }} className="btn-ghost text-[10px]">Note</button>
              </div>
              {o.admin_note && <p className="text-xs text-[#76B82A] font-mono mt-1">Admin note: {o.admin_note}</p>}
              {o.tracking_number && <p className="text-xs text-[#F5F2E8]/65 font-mono">Tracking: {o.tracking_number}</p>}
            </div>
          ))}
        </div>
      )}

      {/* SETTINGS */}
      {tab === "settings" && (
        <div className="space-y-3">
          <PulseEditGroup title="Payment handles">
            <PulseEditRow k="cashapp_handle" label="Cash App $cashtag (blank to hide)" v={settings} u={updateS} reset={resetField} d={defaults} compact />
            <PulseEditRow k="venmo_handle" label="Venmo @handle (blank to hide)" v={settings} u={updateS} reset={resetField} d={defaults} compact />
            <label className="inline-flex items-center gap-2 text-sm pt-1">
              <input type="checkbox" checked={!!settings.stripe_enabled} onChange={(e) => updateS("stripe_enabled", e.target.checked)} data-testid="spoils-stripe-toggle" />
              <span>Enable Stripe (card payments)</span>
            </label>
            {settings.stripe_enabled && (
              <PulseEditRow k="stripe_publishable_key" label="Stripe publishable key" v={settings} u={updateS} reset={resetField} d={defaults} compact />
            )}
          </PulseEditGroup>
          <PulseEditGroup title="Store copy">
            <PulseEditRow k="store_name" label="Store name (default: The Spoils)" v={settings} u={updateS} reset={resetField} d={defaults} compact />
            <PulseEditRow k="store_tagline" label="Tagline" v={settings} u={updateS} reset={resetField} d={defaults} compact />
            <PulseEditRow k="store_hero_eyebrow" label="Hero eyebrow" v={settings} u={updateS} reset={resetField} d={defaults} compact />
            <PulseEditRow k="store_hero_subtitle" label="Hero paragraph" v={settings} u={updateS} reset={resetField} d={defaults} multiline rows={2} />
            <PulseEditRow k="tax_note" label="Footer note (tax / policy)" v={settings} u={updateS} reset={resetField} d={defaults} multiline rows={2} />
            <PulseEditRow k="thanks_message" label="Thanks message (post-purchase)" v={settings} u={updateS} reset={resetField} d={defaults} multiline rows={2} />
            <PulseEditRow k="empty_state_title" label="Closed / empty state title" v={settings} u={updateS} reset={resetField} d={defaults} compact />
            <PulseEditRow k="empty_state_body" label="Closed / empty state body" v={settings} u={updateS} reset={resetField} d={defaults} multiline rows={2} />
          </PulseEditGroup>
          <button onClick={saveSettings} disabled={busy} className="btn-loot text-xs">{busy ? "Saving…" : "Save store settings"}</button>
        </div>
      )}
    </div>
  );
}

function MetFounderTab() {
  const [statusFilter, setStatusFilter] = useState("pending");
  const [list, setList] = useState({ items: [], counts: {} });
  const [busy, setBusy] = useState({});
  const [showPage, setShowPage] = useState(false);
  const [settings, setSettings] = useState(null);
  const [defaults, setDefaults] = useState({});
  const [pageBusy, setPageBusy] = useState(false);
  const [faqs, setFaqs] = useState([]);
  const [showFaqs, setShowFaqs] = useState(false);
  const [reviewModal, setReviewModal] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/founder/met-submissions?status=${statusFilter}`);
      setList(data || { items: [], counts: {} });
    } catch { setList({ items: [], counts: {} }); }
  };
  const loadSettings = async () => {
    try {
      const { data } = await api.get("/admin/founder/met-page-settings");
      setSettings(data?.settings || {});
      setDefaults(data?.defaults || {});
      setFaqs(data?.faqs || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, [statusFilter]); // eslint-disable-line
  useEffect(() => { loadSettings(); }, []);

  const approve = async (s, city, note) => {
    setBusy({ ...busy, [s.id]: "approve" });
    try {
      await api.post(`/admin/founder/met-submissions/${s.id}/approve`, {
        city: (city || "").trim(), admin_note: (note || "").trim(),
      });
      toast.success(`Granted Met-the-Founder badge to @${s.username}`);
      setReviewModal(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Approve failed");
    } finally { setBusy({ ...busy, [s.id]: null }); }
  };

  const reject = async (s, note) => {
    setBusy({ ...busy, [s.id]: "reject" });
    try {
      await api.post(`/admin/founder/met-submissions/${s.id}/reject`, {
        admin_note: (note || "").trim(),
      });
      toast.success("Rejected");
      setReviewModal(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Reject failed");
    } finally { setBusy({ ...busy, [s.id]: null }); }
  };

  const del = async (s) => {
    if (!window.confirm("Hard-delete this submission row?")) return;
    try { await api.delete(`/admin/founder/met-submissions/${s.id}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Delete failed"); }
  };

  const updateP = (k, v) => setSettings({ ...settings, [k]: v });
  const resetField = (k) => updateP(k, defaults[k] || "");
  const savePage = async () => {
    setPageBusy(true);
    try {
      const payload = {};
      Object.keys(defaults).forEach((k) => { payload[k] = settings[k] ?? ""; });
      await api.put("/admin/founder/met-page-settings", payload);
      toast.success("Page copy saved");
      loadSettings();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setPageBusy(false); }
  };
  const resetPage = async () => {
    if (!window.confirm("Reset all Met-the-Founder copy to defaults?")) return;
    const { data } = await api.post("/admin/founder/met-page-settings/reset");
    setSettings(data?.settings || {});
    toast.success("Reset");
  };

  const saveFaqs = async () => {
    try {
      await api.put("/admin/founder/met-page-faqs", { items: faqs.filter((f) => f.q.trim() && f.a.trim()) });
      toast.success("FAQs saved");
      loadSettings();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
  };
  const resetFaqs = async () => {
    if (!window.confirm("Reset Met-the-Founder FAQs to defaults?")) return;
    const { data } = await api.post("/admin/founder/met-page-faqs/reset");
    setFaqs(data?.items || []);
    toast.success("Reset");
    loadSettings();
  };

  const counts = list.counts || {};

  return (
    <div className="space-y-4" data-testid="admin-met-founder">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h3 className="font-display uppercase text-xl tracking-tight">Met the Founder</h3>
          <p className="font-mono text-xs text-[#F5F2E8]/55 mt-1">
            Public page at <code>/met-the-founder</code> · review submissions, edit page copy + FAQs.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setShowPage(!showPage)} className="btn-ghost text-xs" data-testid="met-toggle-page">
            {showPage ? "Hide page editor" : "✏️ Edit page copy"}
          </button>
          <button onClick={() => setShowFaqs(!showFaqs)} className="btn-ghost text-xs" data-testid="met-toggle-faqs">
            {showFaqs ? "Hide FAQs editor" : "✏️ Edit FAQs"}
          </button>
        </div>
      </div>

      {/* Status filter pills */}
      <div className="flex gap-2 flex-wrap">
        {[
          { k: "pending", l: "Pending", color: "#D7A62A" },
          { k: "approved", l: "Approved", color: "#76B82A" },
          { k: "rejected", l: "Rejected", color: "#E53935" },
          { k: "all", l: "All", color: "#F5F2E8" },
        ].map((p) => (
          <button
            key={p.k}
            onClick={() => setStatusFilter(p.k)}
            className="btn-ghost text-xs inline-flex items-center gap-2"
            style={statusFilter === p.k ? { borderColor: p.color, color: p.color, background: `${p.color}10` } : {}}
            data-testid={`met-filter-${p.k}`}
          >
            {p.l} <span className="font-mono text-[10px] opacity-70">({counts[p.k] || 0})</span>
          </button>
        ))}
      </div>

      {/* Submissions list */}
      <div className="space-y-2">
        {list.items.length === 0 ? (
          <div className="loot-card-flat p-6 font-mono text-xs text-[#F5F2E8]/45">No {statusFilter !== "all" ? statusFilter : ""} submissions.</div>
        ) : (
          list.items.map((s) => (
            <div key={s.id} className="loot-card-flat p-4" data-testid={`met-sub-${s.id}`}>
              <div className="flex items-start gap-3 flex-wrap">
                {s.photo_url && (
                  <img src={s.photo_url} alt="" className="w-16 h-16 object-cover border border-[#F5F2E8]/15" />
                )}
                <div className="flex-1 min-w-[200px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display uppercase text-sm">@{s.username}</span>
                    <span
                      className="font-mono text-[10px] uppercase tracking-widest px-1.5 py-0.5"
                      style={{
                        color: s.status === "approved" ? "#76B82A" : s.status === "rejected" ? "#E53935" : "#D7A62A",
                        borderLeft: `2px solid ${s.status === "approved" ? "#76B82A" : s.status === "rejected" ? "#E53935" : "#D7A62A"}`,
                      }}
                    >
                      {s.status}
                    </span>
                  </div>
                  <div className="text-sm mt-1">
                    <strong>{s.where}</strong>
                    {s.city ? <span className="text-[#F5F2E8]/60"> · {s.city}</span> : null}
                    {s.when_text ? <span className="text-[#F5F2E8]/60"> · {s.when_text}</span> : null}
                  </div>
                  {s.note && <p className="text-xs text-[#F5F2E8]/65 italic mt-1 line-clamp-3">"{s.note}"</p>}
                  <div className="font-mono text-[10px] text-[#F5F2E8]/40 mt-1">
                    Submitted {s.created_at?.slice(0, 16)}
                    {s.reviewed_at && ` · Reviewed ${s.reviewed_at.slice(0, 16)}`}
                  </div>
                  {s.admin_note && (
                    <p className="font-mono text-[10px] text-[#76B82A] mt-1">Admin note: {s.admin_note}</p>
                  )}
                </div>
                <div className="flex flex-wrap gap-1 ml-auto">
                  {s.status === "pending" && (
                    <>
                      <button
                        onClick={() => setReviewModal({ s, mode: "approve" })}
                        className="btn-ghost text-[10px]"
                        style={{ color: "#76B82A", borderColor: "#76B82A" }}
                        disabled={busy[s.id]}
                        data-testid={`met-approve-${s.id}`}
                      >
                        ✓ Approve
                      </button>
                      <button
                        onClick={() => setReviewModal({ s, mode: "reject" })}
                        className="btn-ghost text-[10px]"
                        style={{ color: "#E53935", borderColor: "#E53935" }}
                        disabled={busy[s.id]}
                        data-testid={`met-reject-${s.id}`}
                      >
                        ✕ Reject
                      </button>
                    </>
                  )}
                  <button onClick={() => del(s)} className="btn-ghost text-[10px]" style={{ color: "#E53935", borderColor: "#E53935" }} data-testid={`met-del-${s.id}`}>
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Approve/Reject modal */}
      {reviewModal && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" onClick={() => setReviewModal(null)}>
          <div className="loot-card-flat max-w-md w-full p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="overline" style={{ color: reviewModal.mode === "approve" ? "#76B82A" : "#E53935" }}>
              {reviewModal.mode === "approve" ? "Approve meet" : "Reject meet"}
            </div>
            <div>
              <div className="font-display uppercase text-lg">@{reviewModal.s.username}</div>
              <div className="text-sm text-[#F5F2E8]/65 mt-1">{reviewModal.s.where}{reviewModal.s.city ? ` · ${reviewModal.s.city}` : ""}</div>
            </div>
            {reviewModal.mode === "approve" ? (
              <ApproveForm s={reviewModal.s} onApprove={approve} onClose={() => setReviewModal(null)} />
            ) : (
              <RejectForm s={reviewModal.s} onReject={reject} onClose={() => setReviewModal(null)} />
            )}
          </div>
        </div>
      )}

      {/* Page copy editor */}
      {showPage && settings && (
        <div className="loot-card-flat p-5 space-y-3" data-testid="met-page-editor">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="overline">Page copy — every word on /met-the-founder</div>
            <div className="flex gap-2">
              <button onClick={resetPage} className="btn-ghost text-xs">Reset all</button>
              <button onClick={savePage} disabled={pageBusy} className="btn-loot text-xs" data-testid="met-page-save">
                {pageBusy ? "Saving…" : "Save page copy"}
              </button>
            </div>
          </div>

          <PulseEditGroup title="Hero & founder identity">
            <PulseEditRow k="hero_eyebrow" label="Eyebrow (e.g. 'WANTED · ALIVE')" v={settings} u={updateP} reset={resetField} d={defaults} compact />
            <div className="grid md:grid-cols-2 gap-2">
              <PulseEditRow k="hero_title_pre" label="Title prefix" v={settings} u={updateP} reset={resetField} d={defaults} compact />
              <PulseEditRow k="hero_title_accent" label="Title accent" v={settings} u={updateP} reset={resetField} d={defaults} compact />
            </div>
            <PulseEditRow k="hero_subtitle" label="Hero paragraph" v={settings} u={updateP} reset={resetField} d={defaults} multiline rows={3} />
            <div className="grid md:grid-cols-2 gap-2">
              <PulseEditRow k="founder_name" label="Your name (e.g. Mickey)" v={settings} u={updateP} reset={resetField} d={defaults} compact />
              <PulseEditRow k="founder_handle" label="Your @handle" v={settings} u={updateP} reset={resetField} d={defaults} compact />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">Founder photo (upload or paste URL)</label>
              <PhotoUploader value={settings.founder_photo_url || ""} onChange={(v) => updateP("founder_photo_url", v)} testid="met-founder-photo" />
              <input
                className="loot-input mt-2 text-xs"
                placeholder="…or paste an image URL"
                value={settings.founder_photo_url || ""}
                onChange={(e) => updateP("founder_photo_url", e.target.value)}
              />
            </div>
            <div className="grid md:grid-cols-2 gap-2">
              <PulseEditRow k="current_status_eyebrow" label="'Last seen' label" v={settings} u={updateP} reset={resetField} d={defaults} compact />
              <PulseEditRow k="off_site_label" label="Off-site presence label" v={settings} u={updateP} reset={resetField} d={defaults} compact />
            </div>
          </PulseEditGroup>

          <PulseEditGroup title="How it works (3 steps)">
            <PulseEditRow k="how_title" label="Section title" v={settings} u={updateP} reset={resetField} d={defaults} compact />
            <PulseEditRow k="how_step_1" label="Step 1" v={settings} u={updateP} reset={resetField} d={defaults} multiline rows={2} />
            <PulseEditRow k="how_step_2" label="Step 2" v={settings} u={updateP} reset={resetField} d={defaults} multiline rows={2} />
            <PulseEditRow k="how_step_3" label="Step 3" v={settings} u={updateP} reset={resetField} d={defaults} multiline rows={2} />
          </PulseEditGroup>

          <PulseEditGroup title="Badge ladder (3 tiers)">
            <PulseEditRow k="ladder_title" label="Section title" v={settings} u={updateP} reset={resetField} d={defaults} compact />
            <PulseEditRow k="ladder_intro" label="Intro paragraph" v={settings} u={updateP} reset={resetField} d={defaults} multiline rows={2} />
            {[1, 2, 3].map((n) => (
              <div key={n} className="grid md:grid-cols-2 gap-2">
                <PulseEditRow k={`ladder_${n}_label`} label={`Tier ${n} label`} v={settings} u={updateP} reset={resetField} d={defaults} compact />
                <PulseEditRow k={`ladder_${n}_body`} label={`Tier ${n} body`} v={settings} u={updateP} reset={resetField} d={defaults} compact />
              </div>
            ))}
          </PulseEditGroup>

          <PulseEditGroup title="Form + honor roll + FAQs labels">
            <PulseEditRow k="cta_button_label" label="Submit button label" v={settings} u={updateP} reset={resetField} d={defaults} compact />
            <PulseEditRow k="honor_roll_title" label="Honor roll title" v={settings} u={updateP} reset={resetField} d={defaults} compact />
            <PulseEditRow k="honor_roll_subtitle" label="Honor roll subtitle" v={settings} u={updateP} reset={resetField} d={defaults} compact />
            <div className="grid md:grid-cols-2 gap-2">
              <PulseEditRow k="faq_eyebrow" label="FAQ eyebrow" v={settings} u={updateP} reset={resetField} d={defaults} compact />
              <PulseEditRow k="faq_title" label="FAQ section heading" v={settings} u={updateP} reset={resetField} d={defaults} compact />
            </div>
          </PulseEditGroup>

          <div className="pt-2 flex gap-2">
            <button onClick={savePage} disabled={pageBusy} className="btn-loot text-xs">
              {pageBusy ? "Saving…" : "Save page copy"}
            </button>
            <a href="/met-the-founder" target="_blank" rel="noopener noreferrer" className="btn-ghost text-xs">Preview ↗</a>
          </div>
        </div>
      )}

      {/* FAQs editor */}
      {showFaqs && (
        <div className="loot-card-flat p-5 space-y-2" data-testid="met-faqs-editor">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="overline">FAQ entries ({faqs.length})</div>
            <div className="flex gap-1">
              <button onClick={() => setFaqs([...faqs, { q: "", a: "" }])} className="btn-ghost text-xs inline-flex items-center gap-1"><Plus size={12} /> Add</button>
              <button onClick={resetFaqs} className="btn-ghost text-xs">Reset</button>
              <button onClick={saveFaqs} className="btn-loot text-xs">Save FAQs</button>
            </div>
          </div>
          {faqs.map((f, i) => (
            <div key={i} className="border border-[#F5F2E8]/10 p-3 space-y-2">
              <input
                className="loot-input text-sm"
                placeholder="Question"
                value={f.q}
                onChange={(e) => { const x = [...faqs]; x[i] = { ...x[i], q: e.target.value }; setFaqs(x); }}
              />
              <textarea
                rows={2}
                className="loot-input text-sm"
                placeholder="Answer"
                value={f.a}
                onChange={(e) => { const x = [...faqs]; x[i] = { ...x[i], a: e.target.value }; setFaqs(x); }}
              />
              <button onClick={() => setFaqs(faqs.filter((_, idx) => idx !== i))} className="btn-ghost text-[10px]" style={{ color: "#E53935", borderColor: "#E53935" }}>Remove</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ApproveForm({ s, onApprove, onClose }) {
  const [city, setCity] = useState(s.city || "");
  const [note, setNote] = useState("");
  return (
    <>
      <label className="block">
        <span className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65">City (counts toward Loot Disciple / Local Legend streak)</span>
        <input className="loot-input mt-1" value={city} onChange={(e) => setCity(e.target.value)} data-testid="met-approve-city" />
      </label>
      <label className="block">
        <span className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65">Note to hunter (optional)</span>
        <textarea className="loot-input mt-1 text-sm" rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="met-approve-note" />
      </label>
      <div className="flex gap-2 pt-1">
        <button onClick={() => onApprove(s, city, note)} className="btn-loot text-xs" data-testid="met-approve-confirm">Grant badge</button>
        <button onClick={onClose} className="btn-ghost text-xs">Cancel</button>
      </div>
    </>
  );
}

function RejectForm({ s, onReject, onClose }) {
  const [note, setNote] = useState("");
  return (
    <>
      <label className="block">
        <span className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65">Reason (visible to hunter)</span>
        <textarea className="loot-input mt-1 text-sm" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Couldn't verify — feel free to re-submit with more details." data-testid="met-reject-note" />
      </label>
      <div className="flex gap-2 pt-1">
        <button onClick={() => onReject(s, note)} className="btn-ghost text-xs" style={{ color: "#E53935", borderColor: "#E53935" }} data-testid="met-reject-confirm">Reject</button>
        <button onClick={onClose} className="btn-ghost text-xs">Cancel</button>
      </div>
    </>
  );
}


function FounderTab() {
  const [data, setData] = useState(null);
  const [report, setReport] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    mode: "off_site",
    location_note: "",
    attire_note: "",
    stealth_riddle: "",
    city: "",
    lat: "",
    lng: "",
    broadcast_to_ticker: false,
  });

  const load = async () => {
    const [f, r] = await Promise.all([
      api.get("/admin/founder"),
      api.get("/admin/founder/attempts?days=30").catch(() => ({ data: null })),
    ]);
    setData(f.data.founder);
    setReport(r.data);
    setForm({
      mode: f.data.founder.mode || "off_site",
      location_note: f.data.founder.location_note || "",
      attire_note: f.data.founder.attire_note || "",
      stealth_riddle: f.data.founder.stealth_riddle || "",
      city: f.data.founder.city || "",
      lat: f.data.founder.lat ?? "",
      lng: f.data.founder.lng ?? "",
      broadcast_to_ticker: false,
      on_site_duration_hours: f.data.founder.on_site_duration_hours ?? 3,
      send_push: true,
    });
  };

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    if ((form.mode === "on_site" || form.mode === "stealth") && (form.lat === "" || form.lng === "")) {
      toast.error("Pin your location first — hunters need a geofence for the badge.");
      return;
    }
    setSaving(true);
    try {
      const resp = await api.put("/admin/founder", {
        ...form,
        lat: form.lat === "" ? null : Number(form.lat),
        lng: form.lng === "" ? null : Number(form.lng),
        on_site_duration_hours: form.on_site_duration_hours ? Number(form.on_site_duration_hours) : null,
      });
      const pushed = resp.data?.push;
      if (pushed && !pushed.skipped && pushed.sent !== undefined) {
        toast.success(`Saved · push sent to ${pushed.sent}/${pushed.total} hunters`);
      } else {
        toast.success("Saved & broadcast to hunters");
      }
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const ModeRadio = ({ k, label, description, accent }) => (
    <label
      className={`flex-1 min-w-[200px] p-4 border cursor-pointer transition ${
        form.mode === k ? "" : "opacity-70"
      }`}
      style={{
        borderColor: form.mode === k ? accent : "rgba(245,242,232,0.2)",
        background: form.mode === k ? `${accent}15` : undefined,
      }}
      data-testid={`founder-mode-${k}`}
    >
      <input
        type="radio"
        name="founder-mode"
        checked={form.mode === k}
        onChange={() => setForm({ ...form, mode: k })}
        className="hidden"
      />
      <div className="font-display uppercase text-lg" style={{ color: form.mode === k ? accent : "#F5F2E8" }}>
        {label}
      </div>
      <div className="font-mono text-[11px] text-[#F5F2E8]/60 mt-1">{description}</div>
    </label>
  );

  return (
    <div className="space-y-6" data-testid="admin-founder-tab">
      <div className="overline">Founder presence</div>

      <form onSubmit={save} className="loot-card-flat p-6 space-y-4">
        <div className="flex gap-3 flex-wrap">
          <ModeRadio k="on_site" label="On-site · public" description="Show location + attire. Hunters can claim the badge within ~50 ft." accent="#D7A62A" />
          <ModeRadio k="stealth" label="Stealth mode" description="Show a riddle in the ticker. Badge is still claimable within ~50 ft." accent="#E53935" />
          <ModeRadio k="off_site" label="Off-site · remote" description="Badge button disabled. Card collapses to a discrete monitoring notice." accent="#76B82A" />
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          <input
            className="loot-input"
            placeholder="City (optional — shown to hunters)"
            value={form.city}
            onChange={(e) => setForm({ ...form, city: e.target.value })}
            data-testid="founder-city"
          />
          <div />
          {(form.mode === "on_site" || form.mode === "stealth") && (
            <>
              <input
                className="loot-input" type="number" step="any" placeholder="Lat"
                value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })}
                data-testid="founder-lat"
              />
              <input
                className="loot-input" type="number" step="any" placeholder="Lng"
                value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })}
                data-testid="founder-lng"
              />
            </>
          )}
        </div>

        {(form.mode === "on_site" || form.mode === "stealth") && (
          <div className="flex items-center justify-between gap-2 flex-wrap -mt-1">
            <span className="font-mono text-[11px] text-[#F5F2E8]/50">
              Coordinates are NEVER shown to hunters. Only used for the proximity gate.
            </span>
            <UseMyLocationButton
              testid="founder-use-location"
              onLocate={({ lat, lng }) =>
                setForm((f) => ({ ...f, lat: String(lat), lng: String(lng) }))
              }
            />
          </div>
        )}

        {form.mode === "on_site" && (
          <div className="space-y-3 p-4 border" style={{ borderColor: "#D7A62A" }}>
            <input
              className="loot-input"
              maxLength={200}
              placeholder="Current spot (e.g. 'Sitting on the bench by the fountain')"
              value={form.location_note}
              onChange={(e) => setForm({ ...form, location_note: e.target.value })}
              data-testid="founder-location-note"
            />
            <input
              className="loot-input"
              maxLength={200}
              placeholder="Attire (e.g. 'Black cowboy hat, Tennessee Vols flannel')"
              value={form.attire_note}
              onChange={(e) => setForm({ ...form, attire_note: e.target.value })}
              data-testid="founder-attire-note"
            />
            <div className="grid md:grid-cols-2 gap-2">
              <label className="block">
                <span className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65">
                  Time window (hours) — splash burst + countdown
                </span>
                <input
                  type="number"
                  min="0.25" max="24" step="0.25"
                  className="loot-input mt-1"
                  value={form.on_site_duration_hours ?? 3}
                  onChange={(e) => setForm({ ...form, on_site_duration_hours: e.target.value })}
                  data-testid="founder-duration"
                />
                <div className="font-mono text-[10px] text-[#F5F2E8]/45 mt-1">
                  After this, the burst disappears even if mode is still on_site.
                </div>
              </label>
              <label className="inline-flex items-start gap-2 text-sm text-[#F5F2E8]/80 pt-5">
                <input
                  type="checkbox"
                  checked={form.send_push !== false}
                  onChange={(e) => setForm({ ...form, send_push: e.target.checked })}
                  data-testid="founder-send-push"
                />
                <span>
                  Fire push notification to all hunters
                  <span className="block font-mono text-[10px] text-[#F5F2E8]/45">
                    "🎯 Mickey just went live in {form.city || "your city"}!"
                  </span>
                </span>
              </label>
            </div>
          </div>
        )}

        {form.mode === "stealth" && (
          <div className="space-y-3 p-4 border" style={{ borderColor: "#E53935" }}>
            <textarea
              className="loot-input" rows={2} maxLength={240}
              placeholder="Stealth riddle (e.g. 'I can see the fountain from where I'm sitting. First to find me gets a hint!')"
              value={form.stealth_riddle}
              onChange={(e) => setForm({ ...form, stealth_riddle: e.target.value })}
              data-testid="founder-stealth-riddle"
            />
            <label className="inline-flex items-center gap-2 text-sm text-[#F5F2E8]/80">
              <input
                type="checkbox"
                checked={form.broadcast_to_ticker}
                onChange={(e) => setForm({ ...form, broadcast_to_ticker: e.target.checked })}
                data-testid="founder-broadcast-ticker"
              />
              Also pin this riddle to the homepage FOMO ticker
            </label>
          </div>
        )}

        <button type="submit" disabled={saving} className="btn-loot w-full" data-testid="founder-save">
          {saving ? "Saving…" : "Save & broadcast"}
        </button>
        {data?.updated_at && (
          <div className="font-mono text-[10px] text-[#F5F2E8]/50 uppercase tracking-widest">
            Last updated: {fmtEstDateTime(data.updated_at)}
          </div>
        )}
      </form>

      <ManualFounderGrantPanel />

      {report && (
        <div className="loot-card-flat p-6" data-testid="founder-report">
          <div className="overline mb-3">Crowd-size report · last {report.days} days</div>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="p-3 border border-[#F5F2E8]/15">
              <div className="font-mono text-[10px] uppercase text-[#F5F2E8]/55">Total pings</div>
              <div className="font-display text-3xl text-[#F5F2E8]">{report.total_pings}</div>
            </div>
            <div className="p-3 border" style={{ borderColor: "#D7A62A" }}>
              <div className="font-mono text-[10px] uppercase text-[#D7A62A]">Successful meets</div>
              <div className="font-display text-3xl" style={{ color: "#D7A62A" }}>{report.successful_meets}</div>
            </div>
            <div className="p-3 border border-[#F5F2E8]/15">
              <div className="font-mono text-[10px] uppercase text-[#F5F2E8]/55">Near misses (≤50m)</div>
              <div className="font-display text-3xl text-[#F5F2E8]">{report.near_misses}</div>
            </div>
          </div>
          {report.by_city?.length > 0 && (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#F5F2E8]/15">
                  <th className="text-left py-2 font-mono text-[10px] uppercase text-[#F5F2E8]/55">City</th>
                  <th className="text-right py-2 font-mono text-[10px] uppercase text-[#F5F2E8]/55">Pings</th>
                  <th className="text-right py-2 font-mono text-[10px] uppercase text-[#F5F2E8]/55">Met</th>
                  <th className="text-right py-2 font-mono text-[10px] uppercase text-[#F5F2E8]/55">Near</th>
                </tr>
              </thead>
              <tbody>
                {report.by_city.map((c) => (
                  <tr key={c.city} className="border-b border-[#F5F2E8]/5">
                    <td className="py-2">{c.city}</td>
                    <td className="py-2 text-right font-mono">{c.pings}</td>
                    <td className="py-2 text-right font-mono" style={{ color: "#D7A62A" }}>{c.successful_meets}</td>
                    <td className="py-2 text-right font-mono">{c.near_misses}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="mt-3 font-mono text-[10px] text-[#F5F2E8]/50 uppercase tracking-widest">
            Use this in sponsor decks: "Our Bounty Drops drove N hunters to their door last month."
          </p>
        </div>
      )}
    </div>
  );
}


function WaymarkCategoriesPanel({ categories, onChange }) {
  const [expanded, setExpanded] = useState(false);
  const [key, setKey] = useState("");
  const [label, setLabel] = useState("");
  const [icon, setIcon] = useState("mappin");
  const [saving, setSaving] = useState(false);

  const ICONS = [
    { v: "mappin", l: "Pin" },
    { v: "landmark", l: "Landmark" },
    { v: "palette", l: "Palette" },
    { v: "sparkles", l: "Sparkles" },
    { v: "trees", l: "Trees" },
    { v: "utensils", l: "Food" },
    { v: "gift", l: "Gift" },
  ];

  const add = async (e) => {
    e.preventDefault();
    const cleanKey = key.trim().toLowerCase();
    if (!cleanKey || !label.trim()) {
      toast.error("Both key and label are required.");
      return;
    }
    if (!/^[a-z0-9_-]+$/.test(cleanKey)) {
      toast.error("Key must be lowercase letters, numbers, _ or -.");
      return;
    }
    try {
      setSaving(true);
      await api.post("/admin/waymark-categories", { key: cleanKey, label: label.trim(), icon });
      toast.success(`Added category: ${label.trim()}`);
      setKey(""); setLabel(""); setIcon("mappin");
      if (onChange) onChange();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't add category");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (k) => {
    if (!window.confirm(`Remove category "${k}"?\n\nIf any live waymarks still use this category, removal is blocked until you reassign them.`)) return;
    try {
      await api.delete(`/admin/waymark-categories/${k}`);
      toast.success(`Removed ${k}`);
      if (onChange) onChange();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't remove");
    }
  };

  return (
    <div className="loot-card-flat mb-4" data-testid="admin-waymark-categories-panel">
      <button
        type="button"
        className="w-full px-4 py-3 flex items-center justify-between text-left"
        onClick={() => setExpanded((v) => !v)}
        data-testid="admin-waymark-cat-toggle"
      >
        <div>
          <div className="overline">Manage categories</div>
          <div className="font-mono text-[11px] text-[#F5F2E8]/50 mt-1">
            {categories.length} active · click to add your own (e.g. music, sacred, sports)
          </div>
        </div>
        <span className="font-mono text-xs text-[#F5F2E8]/60">{expanded ? "hide −" : "show +"}</span>
      </button>
      {expanded && (
        <div className="border-t border-[#F5F2E8]/10 p-4 space-y-4">
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <span
                key={c.key}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs border"
                style={{ borderColor: "#F5F2E8", color: "#F5F2E8" }}
                data-testid={`admin-cat-chip-${c.key}`}
              >
                {c.label}
                <button
                  type="button"
                  onClick={() => remove(c.key)}
                  className="ml-1 text-[#E53935]"
                  title={`Remove ${c.label}`}
                  data-testid={`admin-cat-remove-${c.key}`}
                >
                  ×
                </button>
              </span>
            ))}
            {categories.length === 0 && (
              <span className="font-mono text-xs text-[#F5F2E8]/50">
                Defaults will appear on first page load.
              </span>
            )}
          </div>
          <form onSubmit={add} className="grid md:grid-cols-4 gap-2 items-end">
            <div>
              <label className="overline block mb-1">Key</label>
              <input
                className="loot-input"
                placeholder="music"
                value={key}
                maxLength={40}
                onChange={(e) => setKey(e.target.value)}
                data-testid="admin-cat-key"
              />
            </div>
            <div>
              <label className="overline block mb-1">Label</label>
              <input
                className="loot-input"
                placeholder="Music"
                value={label}
                maxLength={60}
                onChange={(e) => setLabel(e.target.value)}
                data-testid="admin-cat-label"
              />
            </div>
            <div>
              <label className="overline block mb-1">Icon</label>
              <select
                className="loot-input"
                value={icon}
                onChange={(e) => setIcon(e.target.value)}
                data-testid="admin-cat-icon"
              >
                {ICONS.map((i) => (
                  <option key={i.v} value={i.v}>{i.l}</option>
                ))}
              </select>
            </div>
            <button
              type="submit"
              className="btn-loot"
              disabled={saving}
              data-testid="admin-cat-add-btn"
            >
              {saving ? "Adding…" : "Add category"}
            </button>
          </form>
          <div className="font-mono text-[10px] text-[#F5F2E8]/40">
            The key is the internal code ("music"). Label is what hunters see ("Music").
          </div>
        </div>
      )}
    </div>
  );
}


function WaymarksTab() {
  const [rows, setRows] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const blank = {
    title: "", city: "", lat: "", lng: "",
    category: "history", difficulty: "easy",
    fun_fact: "", photo_url: "",
    is_sponsored: false, sponsor_id: "",
    coupon_code: "", coupon_terms: "", coupon_photo_url: "",
  };
  const [form, setForm] = useState(blank);
  const [generating, setGenerating] = useState(false);

  const refresh = async () => {
    const [w, s, c] = await Promise.all([
      api.get("/admin/waymarks"),
      api.get("/sponsors"),
      api.get("/waymark-categories"),
    ]);
    setRows(w.data.waymarks || []);
    setSponsors(s.data.sponsors || []);
    setCategories(c.data.categories || []);
  };
  useEffect(() => { refresh(); }, []);

  const startEdit = (w) => {
    setEditingId(w.id);
    setOpen(true);
    setForm({
      title: w.title || "",
      city: w.city || "",
      lat: w.lat ?? "",
      lng: w.lng ?? "",
      category: w.category || "history",
      difficulty: w.difficulty || "easy",
      fun_fact: w.fun_fact || "",
      photo_url: w.photo_url || "",
      is_sponsored: !!w.is_sponsored,
      sponsor_id: w.sponsor_id || "",
      coupon_code: w.coupon_code || "",
      coupon_terms: w.coupon_terms || "",
      coupon_photo_url: w.coupon_photo_url || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const cancel = () => { setOpen(false); setEditingId(null); setForm(blank); };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        lat: form.lat === "" ? null : Number(form.lat),
        lng: form.lng === "" ? null : Number(form.lng),
      };
      if (payload.lat === null || payload.lng === null) {
        toast.error("Lat/Lng are required for a waymark");
        return;
      }
      if (payload.is_sponsored && !payload.sponsor_id) {
        toast.error("Sponsored waymarks need a sponsor selected.");
        return;
      }
      if (!payload.is_sponsored) {
        payload.sponsor_id = null;
        payload.coupon_code = null;
        payload.coupon_terms = null;
        payload.coupon_photo_url = null;
      }
      if (editingId) {
        await api.patch(`/admin/waymarks/${editingId}`, payload);
        toast.success("Waymark updated");
      } else {
        await api.post("/admin/waymarks", payload);
        toast.success("Waymark created");
      }
      cancel(); refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  const del = async (id) => {
    if (!confirm("Delete this waymark?")) return;
    await api.delete(`/admin/waymarks/${id}`);
    refresh();
  };

  const generateFunFact = async () => {
    if (!form.title || !form.city) {
      toast.error("Fill in title + city first.");
      return;
    }
    try {
      setGenerating(true);
      // For new (unsaved) waymarks, create-then-generate. Simpler: require save first.
      if (!editingId) {
        toast.info("Save the waymark first, then tap Generate to auto-fill the story.");
        return;
      }
      const { data } = await api.post(`/admin/waymarks/${editingId}/generate-fun-fact`);
      setForm((f) => ({ ...f, fun_fact: data.fun_fact }));
      toast.success("AI story generated — tweak then Save.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "AI generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const seedEastTennessee = async () => {
    if (!window.confirm("Seed curated East Tennessee waymarks?\n\nThis only adds pins you don't already have — nothing below will be duplicated. Our seed pool grows over time, so repeat clicks keep filling in new pins."))
      return;
    try {
      setSeeding(true);
      const { data } = await api.post("/admin/waymarks/seed-east-tennessee");
      if (data.inserted === 0) {
        toast.success(`All ${data.total_in_dataset} curated pins already in your list. Nothing to add.`);
      } else {
        toast.success(`Seeded ${data.inserted} new pin${data.inserted === 1 ? "" : "s"} (${data.skipped} already existed)`);
      }
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Seeding failed");
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4 gap-2 flex-wrap">
        <div className="overline">Waymarks · permanent pins</div>
        <div className="flex items-center gap-2">
          <button
            onClick={seedEastTennessee}
            disabled={seeding}
            className="btn-ghost text-xs inline-flex items-center gap-1"
            title="Bulk-insert curated East Tennessee pins (idempotent — skips any you already have)."
            data-testid="admin-waymark-seed-etn"
          >
            <RefreshCw size={12} className={seeding ? "animate-spin" : ""} />
            {seeding ? "Seeding…" : "Seed East TN pins"}
          </button>
          <button
            onClick={() => (open ? cancel() : setOpen(true))}
            className="btn-loot"
            data-testid="admin-create-waymark-btn"
          >
            <Plus size={14} className="inline mr-1" /> {open ? "Cancel" : "New waymark"}
          </button>
        </div>
      </div>

      <WaymarkCategoriesPanel categories={categories} onChange={refresh} />

      {open && (
        <form onSubmit={save} className="loot-card-flat p-6 mb-6 space-y-3" data-testid="admin-waymark-form">
          <input
            className="loot-input" placeholder="Title (e.g. Old Deery Inn historical plaque)"
            required value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
          <div className="grid md:grid-cols-3 gap-3">
            <input
              className="loot-input" placeholder="City" required value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
            />
            <input
              className="loot-input" type="number" step="any" placeholder="Lat" required
              value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })}
              data-testid="admin-waymark-lat"
            />
            <input
              className="loot-input" type="number" step="any" placeholder="Lng" required
              value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })}
              data-testid="admin-waymark-lng"
            />
          </div>
          <div className="flex items-center justify-between gap-2 flex-wrap -mt-1">
            <span className="font-mono text-[11px] text-[#F5F2E8]/50">
              Standing at the spot right now? Skip the typing.
            </span>
            <UseMyLocationButton
              testid="admin-waymark-use-location"
              onLocate={({ lat, lng }) =>
                setForm((f) => ({ ...f, lat: String(lat), lng: String(lng) }))
              }
            />
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <select
              className="loot-input" value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              data-testid="admin-waymark-category"
            >
              {(categories.length ? categories : [
                { key: "history", label: "History" },
                { key: "landmark", label: "Landmark" },
                { key: "mural", label: "Mural" },
                { key: "quirky", label: "Quirky" },
                { key: "nature", label: "Nature" },
                { key: "food", label: "Food" },
              ]).map((c) => (
                <option key={c.key} value={c.key}>{c.label}</option>
              ))}
            </select>
            <select
              className="loot-input" value={form.difficulty}
              onChange={(e) => setForm({ ...form, difficulty: e.target.value })}
            >
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
            </select>
          </div>
          <div>
            <label className="overline block mb-1">Waymark photo (optional)</label>
            <PhotoUploader
              value={form.photo_url}
              onChange={(url) => setForm({ ...form, photo_url: url })}
            />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <label className="overline">Fun fact / story (shown after hunter walks up)</label>
              <button
                type="button"
                onClick={generateFunFact}
                disabled={generating || !editingId}
                className="btn-ghost text-xs inline-flex items-center gap-1"
                title={editingId ? "Use Gemini to write a story from title + city" : "Save first, then generate"}
                data-testid="admin-waymark-generate"
              >
                <Zap size={12} /> {generating ? "Generating…" : "Generate with AI"}
              </button>
            </div>
            <textarea
              className="loot-input mt-1" rows={5} maxLength={2000}
              placeholder="Leave blank and we'll auto-generate this with Gemini the first time a hunter unlocks the pin."
              value={form.fun_fact}
              onChange={(e) => setForm({ ...form, fun_fact: e.target.value })}
              data-testid="admin-waymark-funfact"
            />
          </div>

          {/* Sponsorship / Branded Waymark */}
          <div className="border-t border-[#F5F2E8]/10 pt-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.is_sponsored}
                onChange={(e) => setForm({ ...form, is_sponsored: e.target.checked })}
                data-testid="admin-waymark-sponsored"
              />
              <span className="inline-flex items-center gap-1">
                <Gift size={14} style={{ color: "#D7A62A" }} /> Branded waymark (perk included, $50/mo)
              </span>
            </label>
            {form.is_sponsored && (
              <div className="mt-3 space-y-3 p-4 border" style={{ borderColor: "#D7A62A" }}>
                <select
                  className="loot-input" required value={form.sponsor_id}
                  onChange={(e) => setForm({ ...form, sponsor_id: e.target.value })}
                >
                  <option value="">— Choose sponsor —</option>
                  {sponsors.map((s) => (
                    <option key={s.id} value={s.id}>{s.business_name} · {s.city}</option>
                  ))}
                </select>
                <input
                  className="loot-input"
                  placeholder="Coupon code (optional, e.g. LOOT10)"
                  maxLength={60}
                  value={form.coupon_code}
                  onChange={(e) => setForm({ ...form, coupon_code: e.target.value })}
                />
                <textarea
                  className="loot-input" rows={2} maxLength={280}
                  placeholder="Coupon terms (e.g. 10% off any drink. One per person, dine-in only.)"
                  value={form.coupon_terms}
                  onChange={(e) => setForm({ ...form, coupon_terms: e.target.value })}
                />
                <div>
                  <label className="overline block mb-1">Coupon photo / visual (optional)</label>
                  <PhotoUploader
                    value={form.coupon_photo_url}
                    onChange={(url) => setForm({ ...form, coupon_photo_url: url })}
                  />
                </div>
              </div>
            )}
          </div>

          <button type="submit" className="btn-loot w-full" data-testid="admin-waymark-submit">
            {editingId ? "Save changes" : "Create waymark"}
          </button>
        </form>
      )}

      <div className="space-y-2">
        {rows.length === 0 && (
          <div className="loot-card-flat p-6 text-center text-[#F5F2E8]/60">
            No waymarks yet. Create the first one to pin a permanent spot on the map.
          </div>
        )}
        {rows.map((w) => (
          <div
            key={w.id}
            className="loot-card-flat p-4 flex items-center justify-between"
            style={w.is_sponsored ? { borderColor: "#D7A62A" } : undefined}
          >
            <div>
              <div className="font-display uppercase text-lg flex items-center gap-2">
                {w.is_sponsored && <Gift size={14} style={{ color: "#D7A62A" }} />}
                {w.title}
                {w.is_sponsored && (
                  <span className="font-mono text-[10px] text-[#D7A62A] border border-[#D7A62A] px-1.5 py-0.5">
                    PERK
                  </span>
                )}
              </div>
              <div className="font-mono text-xs text-[#F5F2E8]/60">
                {w.city} · {w.category} · {w.claims_count ?? 0} claim{w.claims_count === 1 ? "" : "s"}
                {w.sponsor?.business_name && <> · {w.sponsor.business_name}</>}
                {!w.fun_fact && <span className="text-[#D7A62A]"> · story not yet generated</span>}
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => startEdit(w)} className="btn-ghost text-xs" data-testid={`waymark-edit-${w.id}`}>
                Edit
              </button>
              <button onClick={() => del(w.id)} className="btn-ghost text-xs" style={{ color: "#E53935" }} data-testid={`waymark-del-${w.id}`}>
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}



function StripeSettingsTab() {
  const [info, setInfo] = useState(null);
  const [newKey, setNewKey] = useState("");
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    const { data } = await api.get("/admin/settings/stripe");
    setInfo(data);
  };
  useEffect(() => { refresh(); }, []);

  const save = async () => {
    if (!newKey.trim()) return;
    setSaving(true);
    try {
      await api.put("/admin/settings/stripe", { api_key: newKey.trim() });
      toast.success("Stripe key saved · sponsor checkout will use it now");
      setNewKey("");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    if (!confirm("Revert to the environment-variable Stripe key? Sponsor checkout will use the fallback key until you save a new one.")) return;
    try {
      await api.delete("/admin/settings/stripe");
      toast.success("Reverted to env-var Stripe key");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const toggleEnabled = async (enabled) => {
    try {
      await api.patch("/admin/settings/stripe/toggle", { enabled });
      toast.success(enabled ? "Sponsor checkout ENABLED" : "Sponsor checkout DISABLED");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  if (!info) return <div className="text-[#F5F2E8]/60">Loading…</div>;

  const modeColor = info.mode === "live" ? "#76B82A" : info.mode === "test" ? "#D7A62A" : "#F5F2E8";

  return (
    <div className="space-y-4 max-w-3xl" data-testid="admin-stripe-settings">
      <div
        className="loot-card-flat p-5 flex items-center justify-between gap-4 flex-wrap"
        style={{
          borderColor: info.enabled ? "#76B82A" : "#E53935",
          background: info.enabled ? "rgba(118,184,42,0.05)" : "rgba(229,57,53,0.06)",
        }}
        data-testid="stripe-toggle-row"
      >
        <div>
          <div className="overline" style={{ color: info.enabled ? "#76B82A" : "#E53935" }}>
            Sponsor checkout
          </div>
          <div className="font-display uppercase text-2xl mt-1">
            {info.enabled ? "Enabled" : "Disabled"}
          </div>
          <p className="font-mono text-xs text-[#F5F2E8]/60 mt-1 max-w-lg">
            {info.enabled
              ? "Live — businesses can pay Stripe checkout to sponsor drops."
              : "Off — the Sponsor Apply page shows an inquiry form instead of a pay button. Existing sponsors, drops, and winners are unaffected."}
          </p>
        </div>
        <label className="flex items-center gap-3 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={!!info.enabled}
            onChange={(e) => toggleEnabled(e.target.checked)}
            data-testid="stripe-enabled-toggle"
            className="sr-only peer"
          />
          <span
            className="relative w-14 h-7 rounded-full transition-colors"
            style={{
              background: info.enabled ? "#76B82A" : "rgba(229,57,53,0.4)",
            }}
          >
            <span
              className="absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-[#F5F2E8] transition-transform"
              style={{ transform: info.enabled ? "translateX(28px)" : "translateX(0)" }}
            />
          </span>
        </label>
      </div>

      <div className="loot-card-flat p-4 text-sm text-[#F5F2E8]/70 flex items-start gap-3">
        <Lock size={16} className="text-[#D7A62A] mt-0.5" />
        <div>
          Your Stripe <strong>Secret Key</strong> powers sponsor package checkout. Find it at{" "}
          <a
            href="https://dashboard.stripe.com/apikeys"
            target="_blank"
            rel="noreferrer"
            className="text-[#76B82A] underline"
          >
            dashboard.stripe.com/apikeys
          </a>
          . Saved keys are stored encrypted at rest and are <strong>never</strong> returned to this screen in full — only a masked preview. Use <code className="font-mono text-[#D7A62A]">sk_test_…</code> for testing, <code className="font-mono text-[#D7A62A]">sk_live_…</code> for real payments.
        </div>
      </div>

      <div className="loot-card-flat p-5 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <CreditCard size={14} style={{ color: modeColor }} />
            <span className="overline" style={{ color: modeColor }}>
              Current Stripe key
            </span>
            <span
              className="tag-pill uppercase"
              style={{ color: modeColor, borderColor: modeColor }}
              data-testid="stripe-mode"
            >
              {info.mode === "unknown" ? "not set" : info.mode}
            </span>
          </div>
          <span className="font-mono text-xs text-[#F5F2E8]/50 uppercase">
            Source: {info.using === "db" ? "Admin-set" : info.using === "env" ? "Environment" : "None"}
          </span>
        </div>
        <div className="font-mono text-lg text-[#F5F2E8]" data-testid="stripe-masked">
          {info.masked || "— no key configured —"}
        </div>
        {info.updated_at && (
          <div className="font-mono text-[10px] text-[#F5F2E8]/40">
            Updated {fmtEstDateTime(info.updated_at)}
          </div>
        )}
        {info.has_db_key && (
          <button
            onClick={clear}
            className="btn-ghost text-xs inline-flex items-center gap-1"
            data-testid="stripe-clear-btn"
          >
            <Trash2 size={12} /> Revert to env key
          </button>
        )}
      </div>

      <div className="loot-card-flat p-5 space-y-3">
        <div className="overline">Set / rotate key</div>
        <input
          className="loot-input font-mono text-sm"
          type="password"
          placeholder="sk_live_… or sk_test_…"
          value={newKey}
          onChange={(e) => setNewKey(e.target.value)}
          autoComplete="off"
          data-testid="stripe-new-key"
        />
        <p className="font-mono text-[10px] text-[#F5F2E8]/40">
          Paste your Stripe Secret Key. It's hidden as you type.
        </p>
        <button
          onClick={save}
          disabled={saving || !newKey.trim()}
          className="btn-loot text-xs inline-flex items-center gap-1"
          data-testid="stripe-save-btn"
        >
          <Save size={12} /> {saving ? "Saving…" : "Save Stripe key"}
        </button>
      </div>

      <div className="loot-card-flat p-4 text-xs font-mono text-[#F5F2E8]/50 leading-relaxed">
        <strong className="text-[#F5F2E8]/80">Webhook setup (optional):</strong><br />
        In Stripe dashboard → Developers → Webhooks, add endpoint{" "}
        <code className="text-[#D7A62A]">{window.location.origin}/api/webhook/stripe</code> listening for <code className="text-[#D7A62A]">checkout.session.completed</code>. This auto-creates sponsor records the moment a business pays.
      </div>

      {/* ---------- Email notification settings ---------- */}
      <div className="pt-4 border-t border-[#F5F2E8]/10">
        <div className="font-display uppercase tracking-tight text-xl mb-4">
          Email <span style={{ color: "#76B82A" }}>notifications</span>
        </div>
        <NotificationSettingsSection />
      </div>

      {/* ---------- Sponsor auto-reply template ---------- */}
      <div className="pt-4 border-t border-[#F5F2E8]/10">
        <div className="font-display uppercase tracking-tight text-xl mb-4">
          Sponsor <span style={{ color: "#76B82A" }}>auto-reply</span>
        </div>
        <AutoReplySection />
      </div>

      {/* ---------- City popup settings ---------- */}
      <div className="pt-4 border-t border-[#F5F2E8]/10">
        <div className="font-display uppercase tracking-tight text-xl mb-4">
          City page <span style={{ color: "#76B82A" }}>email popup</span>
        </div>
        <CityPopupSection />
      </div>

      {/* ---------- Boost add-on ---------- */}
      <div className="pt-4 border-t border-[#F5F2E8]/10">
        <div className="font-display uppercase tracking-tight text-xl mb-4">
          Boost <span style={{ color: "#76B82A" }}>add-on</span>
        </div>
        <BoostSettingsSection />
      </div>

      {/* ---------- Data tools ---------- */}
      <div className="pt-4 border-t border-[#F5F2E8]/10">
        <div className="font-display uppercase tracking-tight text-xl mb-4">
          Data <span style={{ color: "#76B82A" }}>tools</span>
        </div>
        <DataToolsSection />
      </div>
    </div>
  );
}


function DraftsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sponsorLink, setSponsorLink] = useState(null); // {id, kind, title}

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/drafts");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load drafts");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const publish = async (row) => {
    if (!confirm(`Publish "${row.title}" now? This fires the public broadcast notification.`)) return;
    try {
      if (row.kind === "loot_quest") {
        await api.patch(`/admin/loot-quests/${row.id}`, { status: "published" });
      } else {
        await api.patch(`/drops/${row.id}`, { status: "live" });
      }
      toast.success("Published — hunters can see it now");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Publish failed");
    }
  };

  const previewHref = (row) =>
    row.kind === "loot_quest"
      ? `/loot-quest/${row.id}?preview=1`
      : `/drops/${row.id}?preview=1`;

  if (loading) {
    return <div className="text-[#F5F2E8]/55 font-mono text-xs">Loading drafts…</div>;
  }
  const summary = data?.summary || { total: 0, ready: 0, drops: 0, loot_quests: 0, previews_opened: 0 };
  const drafts = data?.drafts || [];

  return (
    <div className="space-y-5" data-testid="drafts-tab">
      <div>
        <div className="overline">What's still cooking</div>
        <h2 className="font-display uppercase text-3xl tracking-tight mt-1">
          Drafts <span style={{ color: "#D7A62A" }}>board</span>
        </h2>
        <p className="text-sm text-[#F5F2E8]/65 mt-2 max-w-2xl">
          Every unpublished hunt across Drops + Loot Quests in one place. Watch sponsor preview engagement, check what's still missing, and publish when each one's ready to roll.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard k="Total drafts" v={summary.total} />
        <StatCard k="Ready" v={summary.ready} color="#76B82A" />
        <StatCard k="Drops" v={summary.drops} />
        <StatCard k="Loot Quests" v={summary.loot_quests} color="#D7A62A" />
        <StatCard k="Preview opens" v={summary.previews_opened} color="#D7A62A" />
      </div>

      {drafts.length === 0 ? (
        <div className="loot-card-flat p-8 text-center">
          <div className="font-display uppercase text-xl">Nothing in the oven</div>
          <p className="text-sm text-[#F5F2E8]/60 mt-2">
            Save a Drop or Loot Quest as draft from those tabs and it'll show up here.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {drafts.map((row) => (
            <DraftRow
              key={`${row.kind}-${row.id}`}
              row={row}
              onPublish={() => publish(row)}
              onSponsorLink={() => setSponsorLink(row)}
              previewHref={previewHref(row)}
            />
          ))}
        </div>
      )}

      {sponsorLink && (
        <SponsorPreviewLinkModal
          dropId={sponsorLink.id}
          drop={{ title: sponsorLink.title }}
          resourceType={sponsorLink.kind}
          resourceTitle={sponsorLink.title}
          resourcePath={sponsorLink.kind === "loot_quest" ? "loot-quest" : "drops"}
          onClose={() => { setSponsorLink(null); refresh(); }}
        />
      )}
    </div>
  );
}

const DRAFT_CHECK_LABELS = {
  title: "Title",
  city: "City",
  claim_code: "Claim code",
  amount: "Prize amount",
  prize: "Prize set",
  clues: "At least 1 clue",
  gps: "GPS coords (when hints on)",
  bounty_instructions: "Bounty instructions",
  sponsor: "Sponsor linked",
};

function DraftRow({ row, onPublish, onSponsorLink, previewHref }) {
  const checkEntries = Object.entries(row.checks);
  const isLQ = row.kind === "loot_quest";
  const fmtDate = (s) => {
    if (!s) return "never";
    try { return new Date(s).toLocaleString(); } catch { return s; }
  };
  return (
    <div
      className="loot-card-flat p-4 md:p-5 grid md:grid-cols-[2fr_1fr_auto] gap-4 items-start"
      style={{ borderColor: row.ready ? "#76B82A" : "#D7A62A", borderStyle: row.ready ? "solid" : "dashed" }}
      data-testid={`draft-row-${row.kind}-${row.id}`}
    >
      {/* Title + meta */}
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className="font-mono text-[10px] tracking-widest px-1.5 py-0.5"
            style={{
              color: isLQ ? "#D7A62A" : "#76B82A",
              border: `1px solid ${isLQ ? "#D7A62A" : "#76B82A"}`,
            }}
          >
            {isLQ ? "LOOT QUEST" : "DROP"}
          </span>
          {row.is_golden && <Crown size={12} style={{ color: "#D7A62A" }} />}
          {row.is_bounty && <Target size={12} style={{ color: "#D7A62A" }} />}
          {row.ready ? (
            <span
              className="font-mono text-[10px] tracking-widest px-1.5 py-0.5"
              style={{ color: "#76B82A", border: "1px solid #76B82A" }}
            >
              READY TO PUBLISH
            </span>
          ) : (
            <span
              className="font-mono text-[10px] tracking-widest px-1.5 py-0.5"
              style={{ color: "#D7A62A", border: "1px dashed #D7A62A" }}
            >
              MISSING ITEMS
            </span>
          )}
        </div>
        <div className="font-display uppercase text-xl mt-2 leading-tight truncate">{row.title || "Untitled"}</div>
        <div className="font-mono text-[11px] text-[#F5F2E8]/55 mt-1">
          {row.city || "no city"}
          {row.amount ? ` · $${row.amount}` : ""}
          {row.prize_custom_label ? ` · ${row.prize_custom_label}` : ""}
        </div>
      </div>

      {/* Checklist + token stats */}
      <div className="space-y-2 text-xs">
        <div className="grid grid-cols-2 gap-x-3 gap-y-1">
          {checkEntries.map(([k, v]) => (
            <div key={k} className="flex items-center gap-1.5">
              <span style={{ color: v ? "#76B82A" : "#E53935" }} className="font-mono leading-none">
                {v ? "✓" : "✕"}
              </span>
              <span className="text-[#F5F2E8]/65 truncate">
                {DRAFT_CHECK_LABELS[k] || k}
              </span>
            </div>
          ))}
        </div>
        <div className="font-mono text-[10px] text-[#F5F2E8]/55 pt-2 border-t border-[#F5F2E8]/10">
          {row.tokens.active}/{row.tokens.total} active sponsor links · {row.tokens.views} views ·
          last open {fmtDate(row.tokens.last_used_at)}
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-2 md:items-end shrink-0">
        <a
          href={previewHref}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-ghost text-xs"
          style={{ color: "#D7A62A", borderColor: "#D7A62A" }}
          data-testid={`draft-preview-${row.kind}-${row.id}`}
        >
          Preview as hunter
        </a>
        <button
          onClick={onSponsorLink}
          className="btn-ghost text-xs"
          style={{ color: "#D7A62A", borderColor: "#D7A62A" }}
          data-testid={`draft-sponsor-link-${row.kind}-${row.id}`}
        >
          Sponsor link
        </button>
        <button
          onClick={onPublish}
          disabled={!row.ready}
          className="btn-loot text-xs"
          style={{ opacity: row.ready ? 1 : 0.4 }}
          data-testid={`draft-publish-${row.kind}-${row.id}`}
        >
          {row.ready ? "Publish now" : "Finish first"}
        </button>
      </div>
    </div>
  );
}


// ----------------------------------------------------------------- ReportsTab
function ReportsTab() {
  const { user } = useAuth();
  const isLootmaster = user?.role === "lootmaster";
  const [days, setDays] = useState(30);
  const [city, setCity] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const cityOptions = isLootmaster
    ? (user?.lootmaster_cities || [])
    : []; // admins type-in via free-text

  const load = async () => {
    setLoading(true);
    try {
      const params = { days };
      if (city) params.city = city;
      const { data: d } = await api.get("/admin/reports/overview", { params });
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days, city]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const params = { days };
      if (city) params.city = city;
      const res = await api.get("/admin/reports/overview.pdf", {
        params, responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const fname = `local-loot-report-${new Date().toISOString().slice(0, 10)}.pdf`;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "PDF generation failed");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="reports-tab">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="overline">Platform telemetry</div>
          <h2 className="font-display uppercase text-3xl mt-1">
            Reports <span style={{ color: "#76B82A" }}>· {days}d</span>
          </h2>
          {isLootmaster && (
            <div className="mt-1 font-mono text-xs text-[#F5F2E8]/60">
              Scoped to: {(user?.lootmaster_cities || []).join(" · ") || "(no cities)"}
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            className="bg-transparent border border-[#F5F2E8]/20 px-3 py-2 font-mono text-sm"
            data-testid="reports-window-select"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={180}>Last 180 days</option>
            <option value={365}>Last 365 days</option>
          </select>
          {isLootmaster && cityOptions.length > 1 ? (
            <select
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className="bg-transparent border border-[#F5F2E8]/20 px-3 py-2 font-mono text-sm"
              data-testid="reports-city-select"
            >
              <option value="">All my cities</option>
              {cityOptions.map((c) => (<option key={c} value={c}>{c}</option>))}
            </select>
          ) : !isLootmaster ? (
            <input
              type="text"
              placeholder="Filter city (optional)"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className="bg-transparent border border-[#F5F2E8]/20 px-3 py-2 font-mono text-sm"
              data-testid="reports-city-input"
            />
          ) : null}
          <button
            onClick={load}
            disabled={loading}
            className="btn-ghost inline-flex items-center gap-2"
            data-testid="reports-refresh-btn"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            {loading ? "Loading…" : "Refresh"}
          </button>
          <button
            onClick={downloadPdf}
            disabled={downloading || !data}
            className="btn-gold inline-flex items-center gap-2"
            data-testid="reports-download-pdf-btn"
          >
            {downloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            {downloading ? "Generating…" : "Download PDF"}
          </button>
        </div>
      </div>

      {!data ? (
        <div className="loot-card-flat p-10 text-center text-[#F5F2E8]/60">
          <BarChart3 className="mx-auto mb-2 text-[#F5F2E8]/30" size={32} />
          {loading ? "Loading report…" : "No data yet."}
        </div>
      ) : (
        <ReportsBody data={data} />
      )}
    </div>
  );
}

function SectionInfo({ children, info, testid, className = "overline mb-2" }) {
  return (
    <div className={`${className} flex items-center gap-1.5`}>
      <span>{children}</span>
      {info && (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label="What is this?"
              className="text-[#F5F2E8]/40 hover:text-[#76B82A] active:text-[#76B82A] transition-colors"
              data-testid={testid}
            >
              <Info size={12} />
            </button>
          </PopoverTrigger>
          <PopoverContent
            side="top"
            className="max-w-[280px] bg-[#0F0F0F] border border-[#76B82A]/30 text-[#F5F2E8] text-xs leading-relaxed normal-case tracking-normal font-mono p-3"
          >
            {info}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}

// ----------------------------------------------------------------- LiveTab
function fmtDuration(s) {
  if (!s || s < 60) return `${s || 0}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function LiveTab() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [auto, setAuto] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/admin/presence/live");
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load presence");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!auto) return;
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, [auto]);

  const isLootmaster = user?.role === "lootmaster";
  const users = data?.users || [];
  const cities = Array.from(new Set(users.map((u) => u.city).filter(Boolean)));

  return (
    <div className="space-y-6" data-testid="live-tab">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="overline">Real-time</div>
          <h2 className="font-display uppercase text-3xl mt-1">
            Live <span style={{ color: "#76B82A" }}>· {users.length} online</span>
          </h2>
          {isLootmaster && (
            <div className="mt-1 font-mono text-xs text-[#F5F2E8]/60">
              Scoped to: {(user?.lootmaster_cities || []).join(" · ") || "(no cities)"}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center gap-2 text-xs font-mono text-[#F5F2E8]/70">
            <input
              type="checkbox"
              checked={auto}
              onChange={(e) => setAuto(e.target.checked)}
              data-testid="live-auto-refresh-toggle"
            />
            Auto-refresh (10s)
          </label>
          <button
            onClick={load}
            disabled={loading}
            className="btn-ghost inline-flex items-center gap-2"
            data-testid="live-refresh-btn"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Online now" value={users.length} testid="live-kpi-online" info={`Distinct hunters + anonymous visitors with a heartbeat in the last ${data?.online_window_s || 90} seconds.`} />
        <KPI label="Logged in" value={users.filter((u) => !u.is_anon).length} color="#D7A62A" testid="live-kpi-logged" info="Authenticated hunters currently on the site." />
        <KPI label="Anonymous" value={users.filter((u) => u.is_anon).length} color="#1e88e5" testid="live-kpi-anon" info="Visitors without an account — top-of-funnel." />
        <KPI label="Cities active" value={cities.length} color="#E53935" testid="live-kpi-cities" info="Distinct cities currently represented in the live list." />
      </div>

      <div className="loot-card-flat overflow-x-auto">
        <table className="w-full text-sm" data-testid="live-users-table">
          <thead className="bg-[#F5F2E8]/5 text-left">
            <tr>
              <ColInfo label="Hunter" info="Username for logged-in hunters; 'Anon-XXXX' for anonymous visitors." testid="th-live-user" />
              <ColInfo label="City" info="Profile city for hunters; blank for anonymous visitors." testid="th-live-city" />
              <ColInfo label="Page" info="The route they're currently viewing." testid="th-live-page" />
              <ColInfo label="Device" info="Detected from User-Agent (iOS / Android / macOS / Windows / etc.)." testid="th-live-device" />
              <ColInfo label="On site" align="right" info="Time since their first heartbeat in this session." testid="th-live-onsite" />
              <ColInfo label="Last seen" align="right" info="Seconds since their most recent heartbeat. Under 30s = active; 30–90s = idle but still considered online." testid="th-live-last" />
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-10 text-center text-[#F5F2E8]/40">
                  {loading ? "Loading…" : "No one online right now."}
                </td>
              </tr>
            )}
            {users.map((u) => {
              const liveDot = u.idle_seconds < 30
                ? { c: "#76B82A", t: "Active" }
                : { c: "#D7A62A", t: "Idle" };
              return (
                <tr key={u.user_id || u.session_id} className="border-t border-[#F5F2E8]/10">
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <span
                        className="inline-block w-2 h-2 rounded-full"
                        style={{ backgroundColor: liveDot.c }}
                        title={liveDot.t}
                      />
                      <span className={u.is_anon ? "text-[#F5F2E8]/60 italic" : ""}>
                        {u.is_anon ? u.username : (
                          <a href={`/u/${u.username}`} className="hover:text-[#76B82A]" target="_blank" rel="noreferrer">
                            @{u.username}
                          </a>
                        )}
                      </span>
                      {u.role && u.role !== "hunter" && u.role !== "anon" && (
                        <span className="font-mono text-[10px] uppercase px-1.5 py-0.5 bg-[#D7A62A]/20 text-[#D7A62A] rounded-sm">
                          {u.role}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-[#F5F2E8]/70">{u.city || <span className="text-[#F5F2E8]/30">—</span>}</td>
                  <td className="px-3 py-2 font-mono text-xs text-[#F5F2E8]/80 truncate max-w-[200px]">{u.page || "/"}</td>
                  <td className="px-3 py-2 font-mono text-xs">{u.device}</td>
                  <td className="px-3 py-2 text-right font-mono text-[#76B82A]">{fmtDuration(u.on_site_seconds)}</td>
                  <td className="px-3 py-2 text-right font-mono text-[#F5F2E8]/60">{u.idle_seconds}s ago</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function ColInfo({ label, info, align = "left", testid }) {
  return (
    <th className={`px-3 py-2 ${align === "right" ? "text-right" : ""}`}>
      <span className="inline-flex items-center gap-1.5">
        <span>{label}</span>
        {info && (
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label={`What is ${label}?`}
                className="text-[#F5F2E8]/40 hover:text-[#76B82A] active:text-[#76B82A] transition-colors"
                data-testid={testid}
              >
                <Info size={12} />
              </button>
            </PopoverTrigger>
            <PopoverContent
              side="top"
              className="max-w-[260px] bg-[#0F0F0F] border border-[#76B82A]/30 text-[#F5F2E8] text-xs leading-relaxed normal-case tracking-normal font-mono p-3"
            >
              {info}
            </PopoverContent>
          </Popover>
        )}
      </span>
    </th>
  );
}

function KPI({ label, value, color = "#76B82A", testid, info }) {
  return (
    <div className="loot-card-flat p-4" data-testid={testid}>
      <div className="overline flex items-center gap-1.5">
        <span>{label}</span>
        {info && (
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label={`What is ${label}?`}
                className="text-[#F5F2E8]/40 hover:text-[#76B82A] active:text-[#76B82A] transition-colors"
                data-testid={`${testid}-info`}
              >
                <Info size={12} />
              </button>
            </PopoverTrigger>
            <PopoverContent
              side="top"
              className="max-w-[260px] bg-[#0F0F0F] border border-[#76B82A]/30 text-[#F5F2E8] text-xs leading-relaxed normal-case tracking-normal font-mono p-3"
            >
              {info}
            </PopoverContent>
          </Popover>
        )}
      </div>
      <div className="font-display text-3xl mt-1" style={{ color }}>{value}</div>
    </div>
  );
}

function ReportsBody({ data }) {
  const hf = data.hunter_funnel || {};
  const dr = data.drops || {};
  const eng = data.engagement || {};
  const sv = data.sponsor_value || {};
  const top_cities = data.geo?.top_cities || [];
  const top_hunters = data.leaderboard?.top_hunters || [];
  const retention = data.retention || [];

  const histMax = Math.max(1, ...(dr.completion_histogram || []).map((h) => h.count));

  return (
    <div className="space-y-8">
      {/* Hunter funnel */}
      <section>
        <SectionInfo
          info="Top-of-funnel hunter metrics. 'Total users' is your all-time install base. 'New signups' are the ones who joined inside the selected window. DAU/WAU/MAU show how many of those are coming back to actually play."
          testid="reports-funnel-info"
        >
          1 · Hunter funnel · DAU / WAU / MAU
        </SectionInfo>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <KPI label="Total users" value={hf.total_users ?? 0} testid="kpi-total-users" info="All hunters who have ever registered an account on The Local Loot." />
          <KPI label="New signups" value={hf.signups_in_window ?? 0} color="#D7A62A" testid="kpi-signups" info="Hunters who registered inside the selected window." />
          <KPI label="DAU" value={hf.dau ?? 0} testid="kpi-dau" info="Daily Active Users — distinct hunters who opened a drop, requested a hint, or claimed a win in the last 24 hours." />
          <KPI label="WAU" value={hf.wau ?? 0} testid="kpi-wau" info="Weekly Active Users — distinct hunters active in the last 7 days." />
          <KPI label="MAU" value={hf.mau ?? 0} testid="kpi-mau" info="Monthly Active Users — distinct hunters active in the last 30 days. The DAU/MAU ratio is a classic 'stickiness' score: 20%+ is excellent." />
        </div>
      </section>

      {/* Drops */}
      <section>
        <SectionInfo
          info="Drop pipeline health + how long it's taking hunters to finish them. Avg time-to-find is your difficulty dial — under 10 min usually means clues are too obvious; over 4h means the drop is brutal (or no one is in the area)."
          testid="reports-drops-info"
        >
          2 · Drops & avg time-to-find
        </SectionInfo>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPI label="Total drops" value={dr.total ?? 0} testid="kpi-drops-total" info="Every drop ever created (live, scheduled, completed, draft)." />
          <KPI label="Live now" value={dr.live ?? 0} color="#E53935" testid="kpi-drops-live" info="Drops currently published with status = live." />
          <KPI label="Completed" value={dr.completed ?? 0} color="#D7A62A" testid="kpi-drops-completed" info="Drops a hunter has successfully claimed (status = found)." />
          <KPI label="Avg time-to-find" value={`${dr.avg_time_to_find_min ?? 0} min`} color="#D7A62A" testid="kpi-avg-ttf" info="Average minutes from drop launch to a verified winner. Lower means clues are too easy or sponsors are paying for fast wins." />
        </div>
        {(dr.completion_histogram || []).length > 0 && (
          <div className="mt-3 loot-card-flat p-4">
            <SectionInfo
              className="overline mb-3"
              info="How long it took hunters to win each completed drop. A heavy 0–15 min bar = drops are way too easy. A heavy 1–3 d bar = drops are launching but no one is finishing — clue or location problem."
              testid="reports-histogram-info"
            >
              Completion-time histogram
            </SectionInfo>
            <div className="space-y-1">
              {dr.completion_histogram.map((h) => (
                <div key={h.label} className="flex items-center gap-3 text-sm">
                  <div className="w-28 font-mono text-[#F5F2E8]/70">{h.label}</div>
                  <div className="flex-1 bg-[#F5F2E8]/10 h-3 rounded-sm overflow-hidden">
                    <div
                      className="h-3 bg-[#76B82A]"
                      style={{ width: `${(h.count / histMax) * 100}%` }}
                    />
                  </div>
                  <div className="w-10 text-right font-mono">{h.count}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Sponsor value (foot traffic + impressions + social shares + waymark visits) */}
      <section>
        <SectionInfo
          info="Per-sponsor ROI snapshot. Use this to renew, upsell, or cut sponsors. High Impressions + low Wins = great brand exposure but no winner photo. High Wins = guaranteed in-store moments. High Waymark visits = passive year-round traffic."
          testid="reports-sponsor-value-info"
        >
          3 · Sponsor value · foot traffic, impressions, redemptions, shares
        </SectionInfo>
        <div className="loot-card-flat overflow-x-auto">
          <table className="w-full text-sm" data-testid="reports-sponsor-table">
            <thead className="bg-[#F5F2E8]/5 text-left">
              <tr>
                <ColInfo label="Sponsor" info="Local business or organization sponsoring drops on The Local Loot." testid="th-sponsor-info" />
                <ColInfo label="City" info="Where the sponsor's drops are based." testid="th-city-info" />
                <ColInfo label="Drops" align="right" info="Total drops the sponsor has live or has run inside the selected window." testid="th-drops-info" />
                <ColInfo label="Impressions" align="right" info="Distinct hunters who opened any of this sponsor's drop pages — your foot-traffic / brand-exposure number." testid="th-impressions-info" />
                <ColInfo label="Wins" align="right" info="Approved winners on this sponsor's drops in the window — guaranteed face-to-face customer moments." testid="th-wins-info" />
                <ColInfo label="Waymark visits" align="right" info="Hunters who unlocked this sponsor's branded waymark in the window — a 365-day passive traffic stream." testid="th-waymark-info" />
                <ColInfo label="Shares" align="right" info="Winner posts that included a selfie — proxy for share-ready, sponsor-tagged social content." testid="th-shares-info" />
              </tr>
            </thead>
            <tbody>
              {(sv.foot_traffic || []).length === 0 && (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-[#F5F2E8]/40">No sponsor activity in this window.</td></tr>
              )}
              {(sv.foot_traffic || []).map((s) => (
                <tr key={s.sponsor_id} className="border-t border-[#F5F2E8]/10">
                  <td className="px-3 py-2">{s.business_name || "—"}</td>
                  <td className="px-3 py-2 text-[#F5F2E8]/70">{s.city || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono">{s.drops}</td>
                  <td className="px-3 py-2 text-right font-mono text-[#76B82A]">{s.impressions}</td>
                  <td className="px-3 py-2 text-right font-mono">{s.wins}</td>
                  <td className="px-3 py-2 text-right font-mono">{s.waymark_visits}</td>
                  <td className="px-3 py-2 text-right font-mono">{s.social_shares}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {(sv.coupon_redemptions || []).length > 0 && (
          <div className="mt-3 loot-card-flat overflow-x-auto">
            <SectionInfo
              className="overline px-3 pt-3"
              info="Hunters who unlocked a sponsored waymark and saw the sponsor's coupon / message. Counts as a measurable 'foot traffic' moment because the unlock requires being physically near the location."
              testid="reports-coupon-info"
            >
              Coupon / sponsored-waymark redemptions
            </SectionInfo>
            <table className="w-full text-sm">
              <thead className="text-left">
                <tr>
                  <ColInfo label="Waymark" info="Sponsored waymark stop available in this city." testid="th-coupon-waymark" />
                  <ColInfo label="City" info="Where this sponsored waymark is located." testid="th-coupon-city" />
                  <ColInfo label="Redemptions" align="right" info="Times a hunter unlocked this waymark and saw the sponsor's coupon / message." testid="th-coupon-redemptions" />
                </tr>
              </thead>
              <tbody>
                {sv.coupon_redemptions.map((c) => (
                  <tr key={c.waymark_id} className="border-t border-[#F5F2E8]/10">
                    <td className="px-3 py-2">{c.title || "—"}</td>
                    <td className="px-3 py-2 text-[#F5F2E8]/70">{c.city || "—"}</td>
                    <td className="px-3 py-2 text-right font-mono text-[#D7A62A]">{c.redemptions}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Geo + leaderboard */}
      <section className="grid md:grid-cols-2 gap-4">
        <div className="loot-card-flat overflow-x-auto">
          <SectionInfo
            className="overline px-3 pt-3"
            info="Cities ranked by approved wins inside the selected window. 'Hunters' counts the distinct people who won there — useful to see whether one city is one super-hunter or a real community."
            testid="reports-top-cities-info"
          >
            4 · Top cities (wins · unique hunters)
          </SectionInfo>
          <table className="w-full text-sm">
            <thead className="text-left">
              <tr>
                <ColInfo label="City" info="Where the win happened." testid="th-city-tc" />
                <ColInfo label="Wins" align="right" info="Approved winner submissions in the selected window." testid="th-wins-tc" />
                <ColInfo label="Hunters" align="right" info="Distinct hunters who won in this city — your real audience size." testid="th-hunters-tc" />
              </tr>
            </thead>
            <tbody>
              {top_cities.length === 0 && (
                <tr><td colSpan={3} className="px-3 py-6 text-center text-[#F5F2E8]/40">No wins recorded.</td></tr>
              )}
              {top_cities.map((c) => (
                <tr key={c.city || "n/a"} className="border-t border-[#F5F2E8]/10">
                  <td className="px-3 py-2">{c.city || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono text-[#D7A62A]">{c.wins}</td>
                  <td className="px-3 py-2 text-right font-mono">{c.unique_hunters}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="loot-card-flat overflow-x-auto">
          <SectionInfo
            className="overline px-3 pt-3"
            info="Top 20 hunters ranked by approved wins inside the selected window. Great list to seed contests, ambassador outreach, or 'Hunter of the Month' features."
            testid="reports-top-hunters-info"
          >
            5 · Top hunters
          </SectionInfo>
          <table className="w-full text-sm">
            <thead className="text-left">
              <tr>
                <ColInfo label="Username" info="Hunter handle (clickable on their public profile elsewhere in the app)." testid="th-username-th" />
                <ColInfo label="City" info="Hunter's home city as set on their profile." testid="th-city-th" />
                <ColInfo label="Wins" align="right" info="Approved wins in the selected window." testid="th-wins-th" />
              </tr>
            </thead>
            <tbody>
              {top_hunters.length === 0 && (
                <tr><td colSpan={3} className="px-3 py-6 text-center text-[#F5F2E8]/40">No hunters yet.</td></tr>
              )}
              {top_hunters.map((h) => (
                <tr key={h.user_id} className="border-t border-[#F5F2E8]/10">
                  <td className="px-3 py-2">@{h.username || "—"}</td>
                  <td className="px-3 py-2 text-[#F5F2E8]/70">{h.city || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono text-[#D7A62A]">{h.wins}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Retention */}
      <section>
        <SectionInfo
          info="Of all hunters who joined in the last 60 days, what % came back D1 / D7 / D14 / D30 after signup. >40% on D7 is excellent for a community-game app; <10% means signups aren't being retained."
          testid="reports-retention-info"
        >
          6 · Hunter retention curve (last 60-day cohort)
        </SectionInfo>
        <div className="grid grid-cols-4 gap-3">
          {retention.map((r) => (
            <div key={r.day} className="loot-card-flat p-4">
              <div className="overline">D{r.day}</div>
              <div className="font-display text-3xl mt-1" style={{ color: "#76B82A" }}>{r.pct}%</div>
              <div className="font-mono text-xs text-[#F5F2E8]/60 mt-1">{r.returning} returning</div>
            </div>
          ))}
        </div>
      </section>

      {/* Engagement: streaks, founder meets, hint, fails */}
      <section className="grid md:grid-cols-2 gap-4">
        <div className="loot-card-flat p-4">
          <SectionInfo
            info="Hunters earn a streak bonus % by submitting wins on consecutive days. This shows how many hunters sit at each bonus tier — heavy weight at +0% means most hunters aren't returning, weight at the top means a hardcore daily-active core."
            testid="reports-streak-info"
          >
            7 · Streak distribution
          </SectionInfo>
          {(eng.streak_distribution || []).length === 0 ? (
            <div className="text-[#F5F2E8]/40 text-sm">No streak data.</div>
          ) : (
            <div className="space-y-1 text-sm">
              {eng.streak_distribution.map((s, idx) => (
                <div key={`s-${idx}-${s.streak}`} className="flex items-center gap-3">
                  <div className="w-20 font-mono">+{s.streak}%</div>
                  <div className="flex-1 bg-[#F5F2E8]/10 h-2 rounded-sm overflow-hidden">
                    <div
                      className="h-2 bg-[#D7A62A]"
                      style={{
                        width: `${Math.min(100, (s.count / Math.max(1, ...(eng.streak_distribution || []).map((x) => x.count))) * 100)}%`,
                      }}
                    />
                  </div>
                  <div className="w-12 text-right font-mono">{s.count}</div>
                </div>
              ))}
            </div>
          )}
          <div className="mt-3 text-xs font-mono text-[#F5F2E8]/60">
            Founder meets: <span className="text-[#76B82A]">{eng.founder_meets ?? 0}</span>
          </div>
        </div>

        <div className="loot-card-flat p-4">
          <SectionInfo
            info="Top 10 drops by warmer/colder hint pings inside the selected window. High pings = clue is too hard or hunters are very engaged. A drop with zero pings probably has a clue that's too easy or hunters aren't on-site yet."
            testid="reports-hint-info"
          >
            8 · Hint engagement (warmer/colder pings)
          </SectionInfo>
          {(eng.hint_engagement || []).length === 0 ? (
            <div className="text-[#F5F2E8]/40 text-sm">No hint pings.</div>
          ) : (
            <ul className="space-y-1 text-sm">
              {eng.hint_engagement.map((h) => (
                <li key={h.drop_id} className="flex justify-between gap-3">
                  <span className="truncate">{h.title || h.drop_id}</span>
                  <span className="font-mono text-[#76B82A]">{h.pings}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <section>
        <SectionInfo
          info="Drops with the most failed claim-code attempts in the window. Could mean a confusing clue (legit hunters trying random codes) OR cheating signals (one user trying many codes fast). Cross-check with Hint engagement to tell them apart."
          testid="reports-code-attempts-info"
        >
          9 · Code attempt analytics (failed claim attempts)
        </SectionInfo>
        <div className="loot-card-flat overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left">
              <tr>
                <ColInfo label="Drop" info="The drop hunters were trying to claim." testid="th-drop-ca" />
                <ColInfo label="City" info="Where the drop is hosted." testid="th-city-ca" />
                <ColInfo label="Failed attempts" align="right" info="Wrong-code submissions in the window. A spike on one drop = bad clue or potential cheater." testid="th-fails-ca" />
              </tr>
            </thead>
            <tbody>
              {(eng.code_attempts || []).length === 0 && (
                <tr><td colSpan={3} className="px-3 py-6 text-center text-[#F5F2E8]/40">No failed-code attempts logged.</td></tr>
              )}
              {(eng.code_attempts || []).map((c) => (
                <tr key={c.drop_id} className="border-t border-[#F5F2E8]/10">
                  <td className="px-3 py-2">{c.title || c.drop_id}</td>
                  <td className="px-3 py-2 text-[#F5F2E8]/70">{c.city || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono text-[#E53935]">{c.fails}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <div className="text-xs font-mono text-[#F5F2E8]/40 text-right">
        Generated {data.generated_at?.slice(0, 19)?.replace("T", " ")} UTC
      </div>
    </div>
  );
}


// ----------------------------------------------------------------- ContactTab
function ContactTab() {
  const [tab, setTab] = useState("messages");
  const [messages, setMessages] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [filter, setFilter] = useState("");
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  const loadMessages = async () => {
    try {
      const params = filter ? { status: filter } : {};
      const { data } = await api.get("/admin/contact/messages", { params });
      setMessages(data.messages || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    }
  };
  const loadSettings = async () => {
    try {
      const { data } = await api.get("/contact/settings");
      setSettings(data);
    } catch {
      setSettings(null);
    }
  };
  useEffect(() => { loadMessages(); }, [filter]);
  useEffect(() => { loadSettings(); }, []);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/contact/messages/${id}`, { status });
      toast.success(`Marked ${status.replace("_", " ")}`);
      await loadMessages();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't update");
    }
  };

  const removeMessage = async (id) => {
    if (!confirm("Archive this message?")) return;
    try {
      await api.delete(`/admin/contact/messages/${id}`);
      await loadMessages();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't delete");
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const { data } = await api.put("/admin/contact/settings", settings);
      setSettings(data);
      toast.success("Contact page updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save");
    } finally {
      setSavingSettings(false);
    }
  };

  const newCount = messages.filter((m) => m.status === "new").length;

  return (
    <div className="space-y-6" data-testid="contact-tab">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="overline">Inbox · leads</div>
          <h2 className="font-display uppercase text-3xl mt-1">
            Contact <span style={{ color: "#76B82A" }}>· {newCount} new</span>
          </h2>
          <div className="font-mono text-xs text-[#F5F2E8]/60 mt-1">
            Public form lives at <a href="/contact" target="_blank" rel="noreferrer" className="text-[#76B82A] hover:underline">/contact</a> — every submission emails you and shows up here.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setTab("messages")}
            className={`px-3 py-1.5 font-display uppercase text-sm transition ${tab === "messages" ? "text-[#76B82A] border-b-2 border-[#76B82A]" : "text-[#F5F2E8]/60"}`}
            data-testid="contact-subtab-messages"
          >
            Messages
          </button>
          <button
            onClick={() => setTab("settings")}
            className={`px-3 py-1.5 font-display uppercase text-sm transition ${tab === "settings" ? "text-[#76B82A] border-b-2 border-[#76B82A]" : "text-[#F5F2E8]/60"}`}
            data-testid="contact-subtab-settings"
          >
            Page copy
          </button>
        </div>
      </div>

      {tab === "messages" && (
        <>
          <div className="flex flex-wrap gap-2">
            {[
              { k: "", l: "All" },
              { k: "new", l: "New" },
              { k: "in_progress", l: "In progress" },
              { k: "handled", l: "Handled" },
              { k: "archived", l: "Archived" },
            ].map((f) => (
              <button
                key={f.k}
                onClick={() => setFilter(f.k)}
                className={`px-3 py-1 text-xs font-mono uppercase tracking-wide ${filter === f.k ? "text-[#76B82A] border border-[#76B82A]/40 bg-[#76B82A]/10" : "text-[#F5F2E8]/60 border border-[#F5F2E8]/15"}`}
                data-testid={`contact-filter-${f.k || "all"}`}
              >
                {f.l}
              </button>
            ))}
          </div>
          {messages.length === 0 ? (
            <div className="loot-card-flat p-10 text-center text-[#F5F2E8]/40">No messages yet.</div>
          ) : (
            <div className="space-y-2">
              {messages.map((m) => {
                const opened = openId === m.id;
                const statusColor = {
                  new: "#76B82A", in_progress: "#D7A62A", handled: "#1e88e5", archived: "#F5F2E8",
                }[m.status] || "#F5F2E8";
                return (
                  <div key={m.id} className="loot-card-flat p-4" data-testid={`contact-row-${m.id}`}>
                    <div className="flex items-start gap-3 cursor-pointer" onClick={() => setOpenId(opened ? null : m.id)}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-display text-base">{m.subject}</span>
                          <span className="font-mono text-[10px] uppercase tracking-wider px-1.5 py-0.5"
                            style={{ background: `${statusColor}1A`, color: statusColor }}>
                            {m.status}
                          </span>
                          {m.subject_unrecognized && (
                            <span
                              className="font-mono text-[10px] uppercase tracking-wider px-1.5 py-0.5"
                              style={{ background: "rgba(229,57,53,0.15)", color: "#E53935" }}
                              title="Subject not in your configured list"
                            >
                              off-list
                            </span>
                          )}
                        </div>
                        <div className="font-mono text-xs text-[#F5F2E8]/60 mt-0.5 truncate">
                          {m.name} · {m.email}
                          {m.phone && <> · {m.phone}</>}
                          {m.company && <> · {m.company}</>}
                        </div>
                        {!opened && (
                          <div className="text-sm text-[#F5F2E8]/70 mt-1 line-clamp-1">{m.message}</div>
                        )}
                      </div>
                      <div className="font-mono text-[10px] text-[#F5F2E8]/50 whitespace-nowrap">
                        {new Date(m.created_at).toLocaleString()}
                      </div>
                    </div>
                    {opened && (
                      <div className="mt-3 border-t border-[#F5F2E8]/10 pt-3 space-y-3">
                        <p className="text-sm whitespace-pre-wrap">{m.message}</p>
                        <div className="flex flex-wrap gap-2">
                          <a
                            href={`mailto:${m.email}?subject=Re%3A%20${encodeURIComponent(m.subject)}`}
                            className="btn-loot text-xs inline-flex items-center gap-2"
                            data-testid={`contact-reply-${m.id}`}
                          >
                            <Send size={12} /> Reply via email
                          </a>
                          {m.status !== "in_progress" && <button onClick={() => setStatus(m.id, "in_progress")} className="btn-ghost text-xs">In progress</button>}
                          {m.status !== "handled" && <button onClick={() => setStatus(m.id, "handled")} className="btn-ghost text-xs">Handled</button>}
                          {m.status !== "archived" && <button onClick={() => setStatus(m.id, "archived")} className="btn-ghost text-xs">Archive</button>}
                          <button onClick={() => removeMessage(m.id)} className="btn-ghost text-xs text-[#E53935] ml-auto">
                            <Trash2 size={12} className="inline mr-1" /> Delete
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {tab === "settings" && settings && (
        <div className="loot-card-flat p-5 space-y-3" data-testid="contact-settings-form">
          <div className="overline">Page copy</div>
          <input
            className="loot-input"
            placeholder="Headline"
            value={settings.headline || ""}
            onChange={(e) => setSettings({ ...settings, headline: e.target.value })}
            data-testid="contact-set-headline"
          />
          <input
            className="loot-input"
            placeholder="Subhead"
            value={settings.subhead || ""}
            onChange={(e) => setSettings({ ...settings, subhead: e.target.value })}
          />
          <textarea
            className="loot-input"
            rows={3}
            placeholder="Intro paragraph (shown next to the form)"
            value={settings.intro || ""}
            onChange={(e) => setSettings({ ...settings, intro: e.target.value })}
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="overline mb-1 block">Public email</label>
              <input className="loot-input" value={settings.public_email || ""} onChange={(e) => setSettings({ ...settings, public_email: e.target.value })} data-testid="contact-set-email" />
              <label className="inline-flex items-center gap-2 mt-1 text-xs">
                <input type="checkbox" checked={!!settings.show_email} onChange={(e) => setSettings({ ...settings, show_email: e.target.checked })} /> Show on page
              </label>
            </div>
            <div>
              <label className="overline mb-1 block">Public phone</label>
              <input className="loot-input" value={settings.public_phone || ""} onChange={(e) => setSettings({ ...settings, public_phone: e.target.value })} data-testid="contact-set-phone" />
              <label className="inline-flex items-center gap-2 mt-1 text-xs">
                <input type="checkbox" checked={!!settings.show_phone} onChange={(e) => setSettings({ ...settings, show_phone: e.target.checked })} /> Show on page
              </label>
            </div>
            <div>
              <label className="overline mb-1 block">Public city</label>
              <input className="loot-input" value={settings.public_city || ""} onChange={(e) => setSettings({ ...settings, public_city: e.target.value })} />
              <label className="inline-flex items-center gap-2 mt-1 text-xs">
                <input type="checkbox" checked={!!settings.show_city} onChange={(e) => setSettings({ ...settings, show_city: e.target.checked })} /> Show on page
              </label>
            </div>
          </div>
          <div>
            <label className="overline mb-1 block">Subject options (one per line)</label>
            <textarea
              className="loot-input"
              rows={5}
              value={(settings.subjects || []).join("\n")}
              onChange={(e) => setSettings({ ...settings, subjects: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })}
              data-testid="contact-set-subjects"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={saveSettings} disabled={savingSettings} className="btn-loot inline-flex items-center gap-2" data-testid="contact-save-settings">
              {savingSettings ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {savingSettings ? "Saving…" : "Save page copy"}
            </button>
          </div>
          <div className="font-mono text-[10px] text-[#F5F2E8]/50">
            Lead emails are sent to your <span className="text-[#76B82A]">notification email</span> set under Settings → Notifications.
          </div>
        </div>
      )}
    </div>
  );
}


// ----------------------------------------------------------------- PrivateHubTab
function Field({ label, hint, children, className = "" }) {
  return (
    <div className={className}>
      <label className="block">
        <span className="font-mono text-[10px] uppercase tracking-wider text-[#76B82A]">{label}</span>
        {hint && (
          <span className="block text-[11px] text-[#F5F2E8]/55 mt-0.5">{hint}</span>
        )}
        <div className="mt-1.5">{children}</div>
      </label>
    </div>
  );
}

function PrivateHubTab() {
  const [subtab, setSubtab] = useState("hunts");
  const [events, setEvents] = useState([]);
  const [creating, setCreating] = useState(false);
  const [openEventId, setOpenEventId] = useState(null); // expanded panel
  const [openCodeId, setOpenCodeId] = useState(null);
  const [openRedemptionsId, setOpenRedemptionsId] = useState(null);
  const [redemptions, setRedemptions] = useState([]);
  const [eventForm, setEventForm] = useState({
    name: "", client_name: "", city: "", max_hunters: 20,
    is_public_visibility: false, branded_push_message: "",
    custom_card_url: "",
  });

  const load = async () => {
    try {
      const { data } = await api.get("/admin/private/events");
      setEvents(data.events || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load private events");
    }
  };
  useEffect(() => { load(); }, []);

  const createEvent = async (e) => {
    e?.preventDefault?.();
    if (!eventForm.name || !eventForm.city) {
      toast.error("Event name and city are required.");
      return;
    }
    setCreating(true);
    try {
      const payload = { ...eventForm };
      if (!payload.max_hunters) delete payload.max_hunters;
      const { data } = await api.post("/admin/private/events", payload);
      toast.success("Event created — now set the access code and add drops");
      setEventForm({
        name: "", client_name: "", city: "", max_hunters: 20,
        is_public_visibility: false, branded_push_message: "", custom_card_url: "",
      });
      await load();
      setOpenEventId(data.event?.id);
      setOpenCodeId(data.event?.id);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't create");
    } finally {
      setCreating(false);
    }
  };

  const setCode = async (eventId, code, max_redemptions) => {
    try {
      await api.post(`/admin/private/events/${eventId}/code`, {
        code, max_redemptions: max_redemptions || null,
      });
      toast.success(`Code set to ${code}`);
      setOpenCodeId(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't set code");
    }
  };

  const launch = async (eventId) => {
    if (!confirm("Send the branded launch notification to every redeemed hunter?")) return;
    try {
      const { data } = await api.post(`/admin/private/events/${eventId}/launch`, {});
      toast.success(`Sent to ${data.sent} hunter${data.sent === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Launch failed");
    }
  };

  const openRedemptions = async (eventId) => {
    setOpenRedemptionsId(eventId);
    try {
      const { data } = await api.get(`/admin/private/events/${eventId}/redemptions`);
      setRedemptions(data.redemptions || []);
    } catch {
      setRedemptions([]);
    }
  };

  const addDrop = async (event) => {
    const title = prompt(`Drop title for "${event.name}"?`, `${event.name} — Drop ${(event.drops || []).length + 1}`);
    if (!title) return;
    const claimCode = prompt("Claim code (the actual winning code hunters submit)?", `${event.name.toUpperCase().replace(/[^A-Z0-9]/g, "-").slice(0, 12)}-${(event.drops || []).length + 1}`);
    if (!claimCode) return;
    try {
      await api.post("/drops", {
        title,
        city: event.city,
        amount: 0,
        claim_code: claimCode,
        sponsor_type: "self",
        is_private: true,
        event_id: event.id,
        is_public_visibility: !!event.is_public_visibility,
        clues: [],
        status: "draft",
      });
      toast.success("Drop added — open it in the Drops tab to add clues");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't add drop");
    }
  };

  const removeDrop = async (drop) => {
    if (!confirm(`Remove "${drop.title}" from this event?`)) return;
    try {
      await api.delete(`/drops/${drop.id}`);
      toast.success("Drop removed");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't remove");
    }
  };

  return (
    <div className="space-y-6" data-testid="private-hub-tab">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="overline">Invite-only · multi-drop events</div>
          <h2 className="font-display uppercase text-3xl mt-1">
            Private hub <span style={{ color: "#D7A62A" }}>· {events.length}</span>
          </h2>
          <p className="text-[#F5F2E8]/60 mt-1 text-sm">
            One event holds one shared access code AND any number of drops. Hunters redeem once and unlock every drop.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setSubtab("hunts")}
            className={`px-3 py-1.5 font-display uppercase text-sm transition ${subtab === "hunts" ? "text-[#76B82A] border-b-2 border-[#76B82A]" : "text-[#F5F2E8]/60"}`}
            data-testid="private-subtab-hunts"
          >
            Events
          </button>
          <button
            onClick={() => setSubtab("sales")}
            className={`px-3 py-1.5 font-display uppercase text-sm transition ${subtab === "sales" ? "text-[#76B82A] border-b-2 border-[#76B82A]" : "text-[#F5F2E8]/60"}`}
            data-testid="private-subtab-sales"
          >
            Sales page
          </button>
        </div>
      </div>

      {subtab === "sales" ? <SalesPageEditor /> : (
        <>
          <form onSubmit={createEvent} className="loot-card-flat p-5 space-y-3">
            <div className="overline">Create new private event</div>
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Event name" hint="Public-facing — shown to redeemed hunters">
                <input className="loot-input" placeholder="e.g. Acme Corporate Day" value={eventForm.name} onChange={(e) => setEventForm({ ...eventForm, name: e.target.value })} data-testid="ph-event-name" />
              </Field>
              <Field label="Client / family name" hint="Optional — who you're hosting for">
                <input className="loot-input" placeholder="e.g. Acme Inc." value={eventForm.client_name} onChange={(e) => setEventForm({ ...eventForm, client_name: e.target.value })} data-testid="ph-client" />
              </Field>
              <Field label="City" hint="Required — used for lootmaster scoping">
                <input className="loot-input" placeholder="e.g. Kingsport" value={eventForm.city} onChange={(e) => setEventForm({ ...eventForm, city: e.target.value })} data-testid="ph-city" />
              </Field>
              <Field label="Max hunters" hint="Cap on how many can redeem the code">
                <input className="loot-input" type="number" min="1" placeholder="20" value={eventForm.max_hunters || ""} onChange={(e) => setEventForm({ ...eventForm, max_hunters: parseInt(e.target.value, 10) || 0 })} data-testid="ph-max" />
              </Field>
              <Field className="md:col-span-2" label="Custom 4×6 card image URL" hint="Optional — branded winner card shown only to redeemed hunters">
                <input className="loot-input" placeholder="https://…" value={eventForm.custom_card_url} onChange={(e) => setEventForm({ ...eventForm, custom_card_url: e.target.value })} data-testid="ph-card-url" />
              </Field>
              <Field className="md:col-span-2" label="Branded launch message" hint="Push notification sent to all hunters when you click 'Launch push'">
                <textarea className="loot-input" placeholder="e.g. Acme is ready to hunt!" value={eventForm.branded_push_message} onChange={(e) => setEventForm({ ...eventForm, branded_push_message: e.target.value })} data-testid="ph-push" />
              </Field>
              <label className="md:col-span-2 inline-flex items-center gap-2 text-sm">
                <input type="checkbox" checked={eventForm.is_public_visibility} onChange={(e) => setEventForm({ ...eventForm, is_public_visibility: e.target.checked })} data-testid="ph-public-vis" />
                Show "Exclusive Hunt" pin on public map (city only — title and clues stay hidden)
              </label>
            </div>
            <button type="submit" disabled={creating} className="btn-loot inline-flex items-center gap-2" data-testid="ph-create-btn">
              {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              {creating ? "Creating…" : "Create event"}
            </button>
            <div className="text-xs font-mono text-[#F5F2E8]/50">
              After creating, set the shared access code and click "Add drop" to attach hunts.
            </div>
          </form>

          <div className="space-y-3">
            {events.length === 0 && (
              <div className="loot-card-flat p-10 text-center text-[#F5F2E8]/40">
                No private events yet.
              </div>
            )}
            {events.map((ev) => (
              <div key={ev.id} className="loot-card-flat p-4" data-testid={`private-event-row-${ev.id}`}>
                <div className="flex flex-col md:flex-row gap-3 md:items-center justify-between">
                  <div>
                    <div className="font-display text-xl">
                      {ev.name}{" "}
                      <span className="font-mono text-xs text-[#F5F2E8]/50">· {ev.city}</span>
                    </div>
                    <div className="font-mono text-xs text-[#F5F2E8]/60 mt-0.5">
                      {ev.client_name && <>{ev.client_name} · </>}
                      status {ev.status} · {ev.hunters_seated}/{ev.max_hunters || "∞"} seated · {(ev.drops || []).length} drop{(ev.drops || []).length === 1 ? "" : "s"}
                      {ev.is_public_visibility && <> · <span className="text-[#D7A62A]">public pin ON</span></>}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => setOpenCodeId(openCodeId === ev.id ? null : ev.id)} className="btn-ghost inline-flex items-center gap-2 text-xs" data-testid={`private-set-code-${ev.id}`}>
                      <KeyRound size={12} /> {ev.access_code ? `Code: ${ev.access_code}` : "Set code"}
                    </button>
                    <button onClick={() => addDrop(ev)} className="btn-loot inline-flex items-center gap-2 text-xs" data-testid={`private-add-drop-${ev.id}`}>
                      <Plus size={12} /> Add drop
                    </button>
                    <button onClick={() => openRedemptions(ev.id)} className="btn-ghost inline-flex items-center gap-2 text-xs" data-testid={`private-redemptions-${ev.id}`}>
                      <UserPlus size={12} /> {ev.code_redemption_count || 0} redeemed
                    </button>
                    <button onClick={() => launch(ev.id)} className="btn-gold inline-flex items-center gap-2 text-xs" data-testid={`private-launch-${ev.id}`}>
                      <Send size={12} /> Launch push
                    </button>
                  </div>
                </div>

                {(ev.drops || []).length > 0 && (
                  <div className="mt-3 border-t border-[#F5F2E8]/10 pt-3 space-y-1.5">
                    <div className="overline">Drops in this event</div>
                    {ev.drops.map((d, i) => (
                      <div key={d.id} className="flex items-center gap-2 text-sm" data-testid={`private-event-drop-${d.id}`}>
                        <span className="font-mono text-[10px] text-[#76B82A] w-8">#{i + 1}</span>
                        <span className="flex-1">{d.title}</span>
                        <span className="font-mono text-[10px] uppercase tracking-wider px-1.5 py-0.5"
                          style={{ background: "rgba(118,184,42,0.12)", color: d.status === "live" ? "#76B82A" : "#D7A62A" }}>
                          {d.status}
                        </span>
                        <button onClick={() => removeDrop(d)} className="text-[#E53935] hover:opacity-80" title="Remove from event"><Trash2 size={12} /></button>
                      </div>
                    ))}
                    <div className="font-mono text-[10px] text-[#F5F2E8]/50 mt-1">
                      Edit clues + flip to live in the regular <strong>Drops</strong> tab.
                    </div>
                  </div>
                )}

                {openCodeId === ev.id && (
                  <CodeEditor
                    event={ev}
                    onSave={setCode}
                    onClose={() => setOpenCodeId(null)}
                  />
                )}

                {openRedemptionsId === ev.id && (
                  <div className="mt-3 border-t border-[#F5F2E8]/10 pt-3">
                    <div className="overline mb-2">Redeemed hunters</div>
                    {redemptions.length === 0 ? (
                      <div className="text-[#F5F2E8]/40 text-sm">No one has redeemed yet.</div>
                    ) : (
                      <table className="w-full text-sm">
                        <thead className="text-left">
                          <tr>
                            <th className="px-2 py-1">Hunter</th>
                            <th className="px-2 py-1">Email</th>
                            <th className="px-2 py-1">City</th>
                            <th className="px-2 py-1">Joined</th>
                          </tr>
                        </thead>
                        <tbody>
                          {redemptions.map((r) => (
                            <tr key={r.user_id} className="border-t border-[#F5F2E8]/10">
                              <td className="px-2 py-1">@{r.username || "—"}</td>
                              <td className="px-2 py-1 text-[#F5F2E8]/60">{r.email}</td>
                              <td className="px-2 py-1 text-[#F5F2E8]/60">{r.city || "—"}</td>
                              <td className="px-2 py-1 font-mono text-xs">{new Date(r.joined_at).toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function CodeEditor({ event, onSave, onClose }) {
  const [code, setCode] = useState(event.access_code || "");
  const [maxRed, setMaxRed] = useState(event.code_max_redemptions || event.max_hunters || "");
  return (
    <div className="mt-3 border-t border-[#F5F2E8]/10 pt-3 space-y-2">
      <div className="overline">Shared event code</div>
      <div className="flex flex-col md:flex-row gap-2">
        <input
          className="loot-input flex-1 font-mono uppercase tracking-widest"
          placeholder="ACME-2026"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          data-testid={`private-code-input-${event.id}`}
        />
        <input
          className="loot-input md:w-40"
          placeholder="Max redemptions"
          type="number"
          value={maxRed}
          onChange={(e) => setMaxRed(e.target.value)}
        />
        <button
          onClick={() => onSave(event.id, code, parseInt(maxRed, 10) || null)}
          className="btn-loot text-sm"
          data-testid={`private-code-save-${event.id}`}
        >
          Save code
        </button>
        <button onClick={onClose} className="btn-ghost text-xs">Cancel</button>
      </div>
      <div className="text-xs font-mono text-[#F5F2E8]/50">
        Hunters share this once and redeem at <span className="text-[#76B82A]">/private</span>. One code unlocks every drop in the event.
      </div>
    </div>
  );
}


function SalesPageEditor() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/private/sales-settings").then(({ data }) => setS(data)).catch(() => setS({}));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/private/sales-settings", s);
      setS(data);
      toast.success("Sales page updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save");
    } finally {
      setBusy(false);
    }
  };

  if (!s) return <div className="loot-card-flat p-10 text-center text-[#F5F2E8]/40">Loading…</div>;

  const update = (k, v) => setS({ ...s, [k]: v });
  const updateStep = (i, k, v) => {
    const arr = [...(s.how_steps || [])];
    arr[i] = { ...arr[i], [k]: v };
    update("how_steps", arr);
  };
  const updateFeature = (i, k, v) => {
    const arr = [...(s.compare_features || [])];
    arr[i] = { ...arr[i], [k]: v };
    update("compare_features", arr);
  };
  const updateTestimonial = (i, k, v) => {
    const arr = [...(s.testimonials || [])];
    arr[i] = { ...arr[i], [k]: v };
    update("testimonials", arr);
  };
  const removeAt = (key, i) => {
    const arr = [...(s[key] || [])];
    arr.splice(i, 1);
    update(key, arr);
  };
  const addTestimonial = () => update("testimonials", [...(s.testimonials || []), { quote: "", who: "" }]);
  const addFeature = () => update("compare_features", [...(s.compare_features || []), { label: "New row", icon: "Sparkles", private_event: true, corporate_hunt: true }]);
  const addStep = () => update("how_steps", [...(s.how_steps || []), { n: String((s.how_steps || []).length + 1).padStart(2, "0"), t: "New step", b: "Step description." }]);

  return (
    <div className="space-y-6" data-testid="sales-page-editor">
      <div className="loot-card-flat p-5 space-y-3">
        <div className="overline">Hero</div>
        <Field label="Overline (small label above headline)">
          <input className="loot-input" value={s.hero_overline || ""} onChange={(e) => update("hero_overline", e.target.value)} data-testid="se-hero-overline" />
        </Field>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Headline word 1 (white)">
            <input className="loot-input" value={s.hero_headline_part1 || ""} onChange={(e) => update("hero_headline_part1", e.target.value)} />
          </Field>
          <Field label="Headline word 2 (gold accent)">
            <input className="loot-input" value={s.hero_headline_part2 || ""} onChange={(e) => update("hero_headline_part2", e.target.value)} />
          </Field>
          <Field label="Headline word 3 (white, line 2)">
            <input className="loot-input" value={s.hero_headline_part3 || ""} onChange={(e) => update("hero_headline_part3", e.target.value)} />
          </Field>
          <Field label="Headline word 4 (green accent)">
            <input className="loot-input" value={s.hero_headline_part4 || ""} onChange={(e) => update("hero_headline_part4", e.target.value)} />
          </Field>
        </div>
        <Field label="Subheadline / intro paragraph">
          <textarea className="loot-input" rows={4} value={s.hero_subhead || ""} onChange={(e) => update("hero_subhead", e.target.value)} data-testid="se-subhead" />
        </Field>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Primary CTA button label">
            <input className="loot-input" value={s.primary_cta_label || ""} onChange={(e) => update("primary_cta_label", e.target.value)} />
          </Field>
          <Field label="Secondary CTA button label">
            <input className="loot-input" value={s.secondary_cta_label || ""} onChange={(e) => update("secondary_cta_label", e.target.value)} />
          </Field>
        </div>
      </div>

      <div className="loot-card-flat p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div className="overline">How it works · {(s.how_steps || []).length} steps</div>
          <button onClick={addStep} className="btn-ghost text-xs inline-flex items-center gap-1"><Plus size={12} /> Add step</button>
        </div>
        <Field label="Section overline">
          <input className="loot-input" value={s.how_overline || ""} onChange={(e) => update("how_overline", e.target.value)} />
        </Field>
        {(s.how_steps || []).map((step, i) => (
          <div key={i} className="border border-[#F5F2E8]/10 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <div className="font-mono text-[10px] uppercase tracking-wider text-[#76B82A]">Step {i + 1}</div>
              <button onClick={() => removeAt("how_steps", i)} className="text-[#E53935] hover:opacity-80"><Trash2 size={14} /></button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <input className="loot-input" placeholder="Number (e.g. 01)" value={step.n || ""} onChange={(e) => updateStep(i, "n", e.target.value)} />
              <input className="loot-input col-span-2" placeholder="Title" value={step.t || ""} onChange={(e) => updateStep(i, "t", e.target.value)} />
            </div>
            <textarea className="loot-input" rows={2} placeholder="Body" value={step.b || ""} onChange={(e) => updateStep(i, "b", e.target.value)} />
          </div>
        ))}
      </div>

      <div className="loot-card-flat p-5 space-y-3">
        <div className="overline">Pricing section</div>
        <Field label="Section overline">
          <input className="loot-input" value={s.pricing_overline || ""} onChange={(e) => update("pricing_overline", e.target.value)} />
        </Field>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Headline part 1 (white)">
            <input className="loot-input" value={s.pricing_headline_part1 || ""} onChange={(e) => update("pricing_headline_part1", e.target.value)} />
          </Field>
          <Field label="Headline part 2 (gold)">
            <input className="loot-input" value={s.pricing_headline_part2 || ""} onChange={(e) => update("pricing_headline_part2", e.target.value)} />
          </Field>
        </div>
        <Field label="Footnote (rendered next to your support email)">
          <textarea className="loot-input" rows={2} value={s.pricing_footnote || ""} onChange={(e) => update("pricing_footnote", e.target.value)} />
        </Field>
        <div className="font-mono text-[10px] text-[#F5F2E8]/50">
          Card prices ($499 / $1500) and perks come from the <strong>Packages</strong> tab.
        </div>
      </div>

      <div className="loot-card-flat p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div className="overline">Feature comparison · {(s.compare_features || []).length} rows</div>
          <button onClick={addFeature} className="btn-ghost text-xs inline-flex items-center gap-1"><Plus size={12} /> Add row</button>
        </div>
        <Field label="Section overline">
          <input className="loot-input" value={s.compare_overline || ""} onChange={(e) => update("compare_overline", e.target.value)} />
        </Field>
        {(s.compare_features || []).map((row, i) => (
          <div key={i} className="border border-[#F5F2E8]/10 p-3 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <input className="loot-input flex-1" placeholder="Feature label" value={row.label || ""} onChange={(e) => updateFeature(i, "label", e.target.value)} />
              <button onClick={() => removeAt("compare_features", i)} className="text-[#E53935] hover:opacity-80 ml-2"><Trash2 size={14} /></button>
            </div>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <select className="loot-input" value={row.icon || "Sparkles"} onChange={(e) => updateFeature(i, "icon", e.target.value)}>
                {["Users", "ImageIcon", "BellRing", "ShieldCheck", "KeyRound", "Sparkles", "Award", "Lock"].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <FeatureCell label="Private Event" value={row.private_event} onChange={(v) => updateFeature(i, "private_event", v)} />
              <FeatureCell label="Corporate Hunt" value={row.corporate_hunt} onChange={(v) => updateFeature(i, "corporate_hunt", v)} />
            </div>
          </div>
        ))}
      </div>

      <div className="loot-card-flat p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div className="overline">Testimonials · {(s.testimonials || []).length}</div>
          <button onClick={addTestimonial} className="btn-ghost text-xs inline-flex items-center gap-1"><Plus size={12} /> Add quote</button>
        </div>
        <Field label="Section overline">
          <input className="loot-input" value={s.testimonials_overline || ""} onChange={(e) => update("testimonials_overline", e.target.value)} />
        </Field>
        {(s.testimonials || []).map((t, i) => (
          <div key={i} className="border border-[#F5F2E8]/10 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <div className="font-mono text-[10px] uppercase tracking-wider text-[#76B82A]">Quote {i + 1}</div>
              <button onClick={() => removeAt("testimonials", i)} className="text-[#E53935] hover:opacity-80"><Trash2 size={14} /></button>
            </div>
            <textarea className="loot-input" rows={2} placeholder="Quote text" value={t.quote || ""} onChange={(e) => updateTestimonial(i, "quote", e.target.value)} />
            <input className="loot-input" placeholder="— Attribution (e.g. A. Johnson, Kingsport)" value={t.who || ""} onChange={(e) => updateTestimonial(i, "who", e.target.value)} />
          </div>
        ))}
      </div>

      <div className="loot-card-flat p-5 space-y-3">
        <div className="overline">Support contact</div>
        <Field label="Support email" hint="Used in the page footer mailto link AND the Stripe-failure fallback toast">
          <input className="loot-input" type="email" value={s.support_email || ""} onChange={(e) => update("support_email", e.target.value)} data-testid="se-support-email" />
        </Field>
        <Field label="Email link label" hint="The visible text of the mailto link">
          <input className="loot-input" value={s.support_email_label || ""} onChange={(e) => update("support_email_label", e.target.value)} />
        </Field>
      </div>

      <div className="sticky bottom-4 flex justify-end">
        <button onClick={save} disabled={busy} className="btn-loot inline-flex items-center gap-2" data-testid="se-save">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {busy ? "Saving…" : "Save sales page"}
        </button>
      </div>
    </div>
  );
}

function FeatureCell({ label, value, onChange }) {
  // tri-state: true / false / string ("20", "100", etc.)
  const isText = typeof value === "string";
  return (
    <div className="space-y-1">
      <div className="font-mono text-[10px] uppercase tracking-wider text-[#F5F2E8]/60">{label}</div>
      <div className="flex gap-1">
        <button type="button" onClick={() => onChange(true)} className={`px-2 py-1 border text-xs ${value === true ? "border-[#76B82A] text-[#76B82A]" : "border-[#F5F2E8]/15 text-[#F5F2E8]/50"}`}>Yes</button>
        <button type="button" onClick={() => onChange(false)} className={`px-2 py-1 border text-xs ${value === false ? "border-[#E53935] text-[#E53935]" : "border-[#F5F2E8]/15 text-[#F5F2E8]/50"}`}>No</button>
        <button type="button" onClick={() => onChange(isText ? value : "20")} className={`px-2 py-1 border text-xs ${isText ? "border-[#D7A62A] text-[#D7A62A]" : "border-[#F5F2E8]/15 text-[#F5F2E8]/50"}`}>Text</button>
      </div>
      {isText && (
        <input className="loot-input mt-1" value={value} onChange={(e) => onChange(e.target.value)} placeholder="e.g. 20" />
      )}
    </div>
  );
}



// ======================================================================
// SplashTab — fully-editable Splash Page Editor (text + images + nav).
// Loads /splash/settings, PUTs partial-patch to /admin/splash/settings,
// POST /admin/splash/reset clears overrides. Every field on /pages/SplashPage.jsx
// is editable from here; lists (nav, how steps, perfect-for, etc.) are
// add/remove/reorder.
// ======================================================================
const ICON_OPTIONS = [
  "Ban", "Lock", "Smile", "Search", "MapPin", "CreditCard", "Footprints", "Coffee",
  "Users", "UtensilsCrossed", "Shirt", "Dumbbell", "Scissors", "Home", "Wrench",
  "Calendar", "Sparkles", "Store", "Music", "Facebook", "Instagram",
];

function ImageField({ label, hint, value, onChange, testid }) {
  return (
    <Field label={label} hint={hint}>
      <PhotoUploader value={value || ""} onChange={onChange} testid={testid} />
      <input
        className="loot-input mt-2 text-xs"
        placeholder="…or paste an image URL"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
      />
    </Field>
  );
}

// =====================================================================
// Badges admin — full CRUD + award + retire + owners
// =====================================================================
const BADGE_RARITIES = ["common", "uncommon", "rare", "epic", "legendary"];
const BADGE_SOURCES = ["purchased", "earned", "awarded", "sponsored"];
const BADGE_TRIGGERS = [
  { k: "", l: "(none)" },
  { k: "stash_streak", l: "Stash Streak (threshold = days)" },
  { k: "first_drop_found", l: "First Drop Found" },
  { k: "first_vault_crack", l: "First Vault Crack" },
  { k: "vault_wins", l: "Vault Wins (threshold = N)" },
  { k: "tank_donor", l: "Tank Donor (any)" },
  { k: "big_donor", l: "Big Donor (threshold = cents)" },
  { k: "featured_holler", l: "Featured Holler" },
  { k: "first_auction_win", l: "First Auction Win" },
  { k: "stash_total_wins", l: "Stash Total Wins (threshold = N)" },
];

function BadgesAdminTab() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({
    sku: "", name: "", lore: "", icon_emoji: "", image_url: "",
    rarity: "common", source: "purchased", cost_coins: 0,
    trigger_event: "", trigger_threshold: 0, sort_order: 100, active: true,
  });
  const [awardFor, setAwardFor] = useState({ id: null, username: "" });
  const [ownersFor, setOwnersFor] = useState({ id: null, items: [] });

  const reload = async () => {
    try {
      const { data } = await api.get("/admin/badges");
      setItems(data?.items || []);
    } catch { setItems([]); }
  };
  useEffect(() => { reload(); }, []);

  const submit = async () => {
    if (!draft.sku.trim() || !draft.name.trim()) return toast.error("SKU + name required");
    setBusy(true);
    try {
      const payload = {
        ...draft,
        sku: draft.sku.toLowerCase().replace(/[^a-z0-9_]/g, "_"),
        cost_coins: parseInt(draft.cost_coins || 0, 10),
        trigger_threshold: parseInt(draft.trigger_threshold || 0, 10),
        sort_order: parseInt(draft.sort_order || 100, 10),
        trigger_event: draft.trigger_event || null,
      };
      if (editingId) {
        const { sku, ...patch } = payload;
        await api.patch(`/admin/badges/${editingId}`, patch);
        toast.success("Badge updated");
      } else {
        await api.post("/admin/badges", payload);
        toast.success("Badge created");
      }
      setEditingId(null);
      setDraft({ sku: "", name: "", lore: "", icon_emoji: "", image_url: "", rarity: "common", source: "purchased", cost_coins: 0, trigger_event: "", trigger_threshold: 0, sort_order: 100, active: true });
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  const startEdit = (b) => {
    setEditingId(b.id);
    setDraft({
      sku: b.sku, name: b.name, lore: b.lore || "", icon_emoji: b.icon_emoji || "", image_url: b.image_url || "",
      rarity: b.rarity, source: b.source, cost_coins: b.cost_coins || 0,
      trigger_event: b.trigger_event || "", trigger_threshold: b.trigger_threshold || 0,
      sort_order: b.sort_order || 100, active: b.active,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const del = async (id) => {
    if (!window.confirm("Hard-delete this badge? All hunters who own it will lose it. (Use 'retire' to keep their copies.)")) return;
    try { await api.delete(`/admin/badges/${id}`); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Delete failed"); }
  };

  const retire = async (b) => {
    try {
      await api.patch(`/admin/badges/${b.id}`, { retired: !b.retired });
      toast.success(b.retired ? "Un-retired" : "Retired (existing owners keep it)");
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    }
  };

  const award = async () => {
    if (!awardFor.username.trim()) return;
    try {
      await api.post(`/admin/badges/${awardFor.id}/award`, { username: awardFor.username.trim() });
      toast.success(`Awarded to @${awardFor.username}`);
      setAwardFor({ id: null, username: "" });
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Award failed");
    }
  };

  const showOwners = async (id) => {
    try {
      const { data } = await api.get(`/admin/badges/${id}/owners`);
      setOwnersFor({ id, items: data?.items || [] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Load failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-badges">
      <div className="loot-card-flat p-5 space-y-3">
        <div>
          <div className="overline">{editingId ? "Edit badge" : "Create badge"}</div>
          <p className="font-mono text-xs text-[#F5F2E8]/55 max-w-2xl mt-1">
            Three sources: <strong>purchased</strong> (in shop) · <strong>earned</strong> (auto-grant on trigger) · <strong>awarded</strong> (admin-only). Pick a rarity for the glow color. Use an emoji for the icon (fast) or upload a custom image.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-2">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">SKU (lowercase, unique)</label>
            <input className="loot-input" placeholder="e.g. early_bird" value={draft.sku} onChange={(e) => setDraft({ ...draft, sku: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })} maxLength={80} disabled={!!editingId} data-testid="badge-create-sku" />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">Display name</label>
            <input className="loot-input" placeholder="e.g. Early Bird" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} maxLength={120} data-testid="badge-create-name" />
          </div>
          <div className="md:col-span-2">
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">Lore (1 line shown under name)</label>
            <input className="loot-input" placeholder="e.g. For the hunters who get up before the sun." value={draft.lore} onChange={(e) => setDraft({ ...draft, lore: e.target.value })} maxLength={400} data-testid="badge-create-lore" />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">Icon emoji</label>
            <input className="loot-input" placeholder="🐤" value={draft.icon_emoji} onChange={(e) => setDraft({ ...draft, icon_emoji: e.target.value })} maxLength={20} data-testid="badge-create-emoji" />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">Rarity</label>
            <select className="loot-input" value={draft.rarity} onChange={(e) => setDraft({ ...draft, rarity: e.target.value })} data-testid="badge-create-rarity">
              {BADGE_RARITIES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">Source</label>
            <select className="loot-input" value={draft.source} onChange={(e) => setDraft({ ...draft, source: e.target.value })} data-testid="badge-create-source">
              {BADGE_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          {draft.source === "purchased" && (
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">Cost (Loot Coins)</label>
              <input className="loot-input" type="number" min={0} value={draft.cost_coins} onChange={(e) => setDraft({ ...draft, cost_coins: parseInt(e.target.value || 0, 10) })} data-testid="badge-create-cost" />
            </div>
          )}
          {draft.source === "earned" && (
            <>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">Trigger event</label>
                <select className="loot-input" value={draft.trigger_event || ""} onChange={(e) => setDraft({ ...draft, trigger_event: e.target.value })} data-testid="badge-create-trigger">
                  {BADGE_TRIGGERS.map((t) => <option key={t.k} value={t.k}>{t.l}</option>)}
                </select>
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">Threshold</label>
                <input className="loot-input" type="number" min={0} value={draft.trigger_threshold} onChange={(e) => setDraft({ ...draft, trigger_threshold: parseInt(e.target.value || 0, 10) })} data-testid="badge-create-threshold" />
              </div>
            </>
          )}
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">Sort order (lower = earlier)</label>
            <input className="loot-input" type="number" min={0} value={draft.sort_order} onChange={(e) => setDraft({ ...draft, sort_order: parseInt(e.target.value || 100, 10) })} data-testid="badge-create-sort" />
          </div>
          <label className="text-xs flex items-center gap-1 mt-6">
            <input type="checkbox" checked={draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} /> Active
          </label>
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">Custom image (optional, overrides emoji)</label>
          <PhotoUploader value={draft.image_url} onChange={(url) => setDraft({ ...draft, image_url: url })} testid="badge-create-image" />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/55">Preview:</div>
          <BadgeTile badge={{ ...draft, sku: draft.sku || "preview" }} size={48} showTooltip={false} />
          <button onClick={submit} disabled={busy} className="btn-loot text-xs ml-auto inline-flex items-center gap-1" data-testid="badge-submit">
            <Plus size={12} /> {busy ? "Saving…" : (editingId ? "Save" : "Create")}
          </button>
          {editingId && (
            <button onClick={() => { setEditingId(null); setDraft({ sku: "", name: "", lore: "", icon_emoji: "", image_url: "", rarity: "common", source: "purchased", cost_coins: 0, trigger_event: "", trigger_threshold: 0, sort_order: 100, active: true }); }} className="btn-ghost text-xs">Cancel edit</button>
          )}
        </div>
      </div>

      <div className="loot-card-flat p-5 space-y-2">
        <div className="overline">All badges ({items.length})</div>
        {items.length === 0 ? <div className="font-mono text-xs text-[#F5F2E8]/45">none</div> : (
          <div className="grid md:grid-cols-2 gap-2">
            {items.map((b) => (
              <div key={b.id} className="border border-[#F5F2E8]/10 p-3 flex items-start gap-3 flex-wrap" data-testid={`admin-badge-${b.sku}`} style={{ opacity: b.retired ? 0.55 : 1 }}>
                <BadgeTile badge={b} size={44} showTooltip={false} />
                <div className="flex-1 min-w-[180px]">
                  <div className="font-display uppercase text-sm">{b.name}</div>
                  <div className="font-mono text-[10px] text-[#F5F2E8]/55">
                    {b.sku} · {b.rarity} · {b.source}
                    {b.source === "purchased" && ` · ${b.cost_coins}c`}
                    {b.trigger_event && ` · ${b.trigger_event}@${b.trigger_threshold}`}
                    {b.retired && " · RETIRED"}
                  </div>
                  <div className="font-mono text-[10px] text-[#F5F2E8]/65 mt-1">{b.owners_count} owner{b.owners_count === 1 ? "" : "s"}</div>
                </div>
                <div className="flex flex-wrap gap-1 ml-auto">
                  <button onClick={() => startEdit(b)} className="btn-ghost text-[10px]" data-testid={`badge-edit-${b.sku}`}>Edit</button>
                  <button onClick={() => setAwardFor({ id: b.id, username: "" })} className="btn-ghost text-[10px]" data-testid={`badge-award-${b.sku}`}>Award</button>
                  <button onClick={() => showOwners(b.id)} className="btn-ghost text-[10px]" data-testid={`badge-owners-${b.sku}`}>Owners</button>
                  <button onClick={() => retire(b)} className="btn-ghost text-[10px]" data-testid={`badge-retire-${b.sku}`}>{b.retired ? "Un-retire" : "Retire"}</button>
                  <button onClick={() => del(b.id)} className="btn-ghost text-[10px]" style={{ color: "#E53935", borderColor: "#E53935" }}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {awardFor.id && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setAwardFor({ id: null, username: "" })}>
          <div className="loot-card-flat max-w-sm w-full p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="overline">Manually award</div>
            <input className="loot-input" placeholder="@username" value={awardFor.username} onChange={(e) => setAwardFor({ ...awardFor, username: e.target.value.replace(/^@/, "") })} maxLength={80} autoFocus data-testid="badge-award-username" />
            <button onClick={award} disabled={!awardFor.username.trim()} className="btn-loot w-full text-xs" data-testid="badge-award-submit">Award</button>
          </div>
        </div>
      )}

      {ownersFor.id && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setOwnersFor({ id: null, items: [] })}>
          <div className="loot-card-flat max-w-md w-full p-5 space-y-3 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="overline">Owners ({ownersFor.items.length})</div>
            {ownersFor.items.length === 0 ? <div className="font-mono text-xs text-[#F5F2E8]/45">no owners</div> : (
              <div className="space-y-1">
                {ownersFor.items.map((o, i) => (
                  <div key={i} className="text-xs flex items-center justify-between border-b border-[#F5F2E8]/5 py-1">
                    <span>@{o.username}</span>
                    <span className="font-mono text-[10px] text-[#F5F2E8]/55">{o.source} · {o.earned_at?.slice(0,10)}</span>
                  </div>
                ))}
              </div>
            )}
            <button onClick={() => setOwnersFor({ id: null, items: [] })} className="btn-ghost text-xs w-full">Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

// =====================================================================
// Pulse admin — Surveys CRUD + responses viewer + FAQ editor
// =====================================================================
const PULSE_Q_TYPES = [
  { k: "single", l: "Single choice (radio)" },
  { k: "multi", l: "Multi choice (checkboxes)" },
  { k: "rating", l: "Rating 1–5" },
  { k: "text", l: "Open text" },
];

function blankQuestion() {
  return { id: "", label: "", type: "single", options: ["", ""], required: true };
}

function blankPulseDraft() {
  return {
    title: "",
    description: "",
    why_asking: "",
    status: "draft",
    show_results: "after_vote",
    expires_at: "",
    questions: [blankQuestion()],
  };
}

function PulseEditGroup({ title, children }) {
  return (
    <div className="border border-[#F5F2E8]/12 p-3 space-y-2">
      <div className="font-mono text-[10px] uppercase tracking-widest text-[#76B82A]">{title}</div>
      {children}
    </div>
  );
}

function PulseEditRow({ k, label, v, u, reset, d, multiline, rows = 2, compact, iconOptions }) {
  const cur = (v?.[k] ?? "");
  const def = d?.[k] ?? "";
  const dirty = cur !== def;
  return (
    <div className={compact ? "" : "mt-1"}>
      <div className="flex items-center justify-between mb-1">
        <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65">{label}</label>
        {dirty && (
          <button onClick={() => reset(k)} className="font-mono text-[10px] text-[#D7A62A] hover:underline" title="Revert to default" data-testid={`pulse-page-reset-${k}`}>
            ↺ revert
          </button>
        )}
      </div>
      {iconOptions ? (
        <select className="loot-input text-sm" value={cur} onChange={(e) => u(k, e.target.value)} data-testid={`pulse-page-field-${k}`}>
          {(iconOptions || []).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      ) : multiline ? (
        <textarea
          className="loot-input text-sm w-full"
          rows={rows}
          value={cur}
          onChange={(e) => u(k, e.target.value)}
          data-testid={`pulse-page-field-${k}`}
        />
      ) : (
        <input
          className="loot-input text-sm w-full"
          value={cur}
          onChange={(e) => u(k, e.target.value)}
          data-testid={`pulse-page-field-${k}`}
        />
      )}
    </div>
  );
}

function PulseAdminTab() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(blankPulseDraft());
  const [responsesFor, setResponsesFor] = useState({ id: null, data: null });
  const [showFaqs, setShowFaqs] = useState(false);
  const [faqs, setFaqs] = useState([]);
  const [showPage, setShowPage] = useState(false);
  const [pageSettings, setPageSettings] = useState(null);
  const [pageDefaults, setPageDefaults] = useState({});
  const [pageIcons, setPageIcons] = useState([]);
  const [pageBusy, setPageBusy] = useState(false);

  const reload = async () => {
    try {
      const { data } = await api.get("/admin/pulse");
      setItems(data?.items || []);
    } catch { setItems([]); }
  };
  const loadFaqs = async () => {
    try {
      const { data } = await api.get("/admin/pulse/faqs");
      setFaqs(data?.items || []);
    } catch { setFaqs([]); }
  };
  const loadPageSettings = async () => {
    try {
      const { data } = await api.get("/admin/pulse/settings");
      setPageSettings(data?.settings || {});
      setPageDefaults(data?.defaults || {});
      setPageIcons(data?.icon_options || []);
    } catch { setPageSettings({}); }
  };
  useEffect(() => { reload(); loadFaqs(); loadPageSettings(); }, []);

  const savePageSettings = async () => {
    if (!pageSettings) return;
    setPageBusy(true);
    try {
      // Only send keys that differ from defaults so the override doc stays small,
      // BUT include all keys the admin may have intentionally cleared.
      const payload = {};
      Object.keys(pageDefaults).forEach((k) => {
        payload[k] = pageSettings[k] ?? "";
      });
      await api.put("/admin/pulse/settings", payload);
      toast.success("Page copy saved");
      loadPageSettings();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setPageBusy(false); }
  };
  const resetPageSettings = async () => {
    if (!window.confirm("Reset every word on the Pulse page back to defaults?")) return;
    try {
      const { data } = await api.post("/admin/pulse/settings/reset");
      setPageSettings(data?.settings || {});
      toast.success("Page reset");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Reset failed"); }
  };
  const updateP = (k, v) => setPageSettings({ ...pageSettings, [k]: v });
  const resetField = (k) => updateP(k, pageDefaults[k] || "");

  const startCreate = () => {
    setEditingId(null);
    setDraft(blankPulseDraft());
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const startEdit = (s) => {
    setEditingId(s.id);
    setDraft({
      title: s.title || "",
      description: s.description || "",
      why_asking: s.why_asking || "",
      status: s.status || "draft",
      show_results: s.show_results || "after_vote",
      expires_at: s.expires_at || "",
      questions: (s.questions || []).map((q) => ({
        id: q.id || "",
        label: q.label || "",
        type: q.type || "single",
        options: Array.isArray(q.options) ? [...q.options] : [],
        required: q.required !== false,
      })),
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async () => {
    if (!draft.title.trim()) return toast.error("Title required.");
    if (!draft.questions.length) return toast.error("At least one question required.");
    // Light client-side validation
    for (const q of draft.questions) {
      if (!q.label.trim()) return toast.error("Every question needs a label.");
      if ((q.type === "single" || q.type === "multi") && (q.options || []).filter((o) => o.trim()).length < 2) {
        return toast.error(`Question "${q.label}" needs 2+ options.`);
      }
    }
    setBusy(true);
    try {
      const payload = {
        ...draft,
        questions: draft.questions.map((q) => ({
          ...q,
          options: (q.options || []).map((o) => o.trim()).filter(Boolean),
        })),
      };
      if (editingId) {
        await api.patch(`/admin/pulse/${editingId}`, payload);
        toast.success("Survey updated.");
      } else {
        await api.post("/admin/pulse", payload);
        toast.success("Survey created.");
      }
      setEditingId(null);
      setDraft(blankPulseDraft());
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this survey AND all responses? Cannot undo.")) return;
    try { await api.delete(`/admin/pulse/${id}`); reload(); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Delete failed"); }
  };

  const setStatus = async (s, status) => {
    try {
      await api.patch(`/admin/pulse/${s.id}`, { status });
      toast.success(`Set to ${status}`);
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const viewResponses = async (id) => {
    try {
      const { data } = await api.get(`/admin/pulse/${id}/responses`);
      setResponsesFor({ id, data });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  // Draft helpers
  const addQ = () => setDraft({ ...draft, questions: [...draft.questions, blankQuestion()] });
  const updateQ = (i, patch) => {
    const q = [...draft.questions];
    q[i] = { ...q[i], ...patch };
    setDraft({ ...draft, questions: q });
  };
  const removeQ = (i) => {
    if (draft.questions.length === 1) return toast.error("Need at least one question.");
    setDraft({ ...draft, questions: draft.questions.filter((_, idx) => idx !== i) });
  };
  const addOpt = (i) => updateQ(i, { options: [...(draft.questions[i].options || []), ""] });
  const setOpt = (qi, oi, val) => {
    const opts = [...(draft.questions[qi].options || [])];
    opts[oi] = val;
    updateQ(qi, { options: opts });
  };
  const removeOpt = (qi, oi) => {
    const opts = [...(draft.questions[qi].options || [])];
    opts.splice(oi, 1);
    updateQ(qi, { options: opts });
  };

  const saveFaqs = async () => {
    try {
      await api.put("/admin/pulse/faqs", { items: faqs.filter((f) => f.q.trim() && f.a.trim()) });
      toast.success("FAQs saved");
      loadFaqs();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
  };
  const resetFaqs = async () => {
    if (!window.confirm("Reset FAQs to defaults?")) return;
    try {
      const { data } = await api.post("/admin/pulse/faqs/reset");
      setFaqs(data?.items || []);
      toast.success("FAQs reset");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Reset failed"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-pulse">
      {/* Top toolbar */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h3 className="font-display uppercase text-xl tracking-tight">Pulse — Community Feedback</h3>
          <p className="font-mono text-xs text-[#F5F2E8]/55 mt-1">
            Surveys + page copy + FAQs. Public page lives at <code>/pulse</code>.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setShowPage(!showPage)} className="btn-ghost text-xs" data-testid="pulse-toggle-page">
            {showPage ? "Hide page editor" : "✏️ Edit page copy"}
          </button>
          <button onClick={() => setShowFaqs(!showFaqs)} className="btn-ghost text-xs" data-testid="pulse-toggle-faqs">
            {showFaqs ? "Hide FAQs editor" : "✏️ Edit FAQs"}
          </button>
        </div>
      </div>

      {/* Page copy editor */}
      {showPage && pageSettings && (
        <div className="loot-card-flat p-5 space-y-3" data-testid="pulse-page-editor">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="overline">Page copy — every word on /pulse</div>
              <p className="font-mono text-[10px] text-[#F5F2E8]/55 mt-1">
                Edit hero, why-cards, section headings, empty-state, CTA. Each row has a ↺ icon to revert just that field.
              </p>
            </div>
            <div className="flex gap-2">
              <button onClick={resetPageSettings} className="btn-ghost text-xs" data-testid="pulse-page-reset">Reset all to defaults</button>
              <button onClick={savePageSettings} disabled={pageBusy} className="btn-loot text-xs" data-testid="pulse-page-save">
                {pageBusy ? "Saving…" : "Save page copy"}
              </button>
            </div>
          </div>

          {/* Group: Hero */}
          <PulseEditGroup title="Hero">
            <PulseEditRow k="hero_eyebrow" label="Eyebrow (small green text above headline)" v={pageSettings} u={updateP} reset={resetField} d={pageDefaults} />
            <div className="grid md:grid-cols-2 gap-2">
              <PulseEditRow k="hero_title_pre" label="Title prefix (white)" v={pageSettings} u={updateP} reset={resetField} d={pageDefaults} />
              <PulseEditRow k="hero_title_accent" label="Title accent (green)" v={pageSettings} u={updateP} reset={resetField} d={pageDefaults} />
            </div>
            <PulseEditRow k="hero_subtitle" label="Hero subtitle (full paragraph)" v={pageSettings} u={updateP} reset={resetField} d={pageDefaults} multiline rows={3} />
            <div className="grid md:grid-cols-3 gap-2">
              <PulseEditRow k="stat_active_label" label="Stat 1 label" v={pageSettings} u={updateP} reset={resetField} d={pageDefaults} compact />
              <PulseEditRow k="stat_closed_label" label="Stat 2 label" v={pageSettings} u={updateP} reset={resetField} d={pageDefaults} compact />
              <PulseEditRow k="stat_voices_label" label="Stat 3 label" v={pageSettings} u={updateP} reset={resetField} d={pageDefaults} compact />
            </div>
          </PulseEditGroup>

          {/* Group: Why cards */}
          {[1, 2, 3].map((n) => (
            <PulseEditGroup key={n} title={`"Why" card ${n}`}>
              <div className="grid md:grid-cols-3 gap-2">
                <PulseEditRow k={`why_card_${n}_title`} label="Title" v={pageSettings} u={updateP} reset={resetField} d={pageDefaults} compact />
                <PulseEditRow k={`why_card_${n}_icon`} label="Icon" v={pageSettings} u={updateP} reset={resetField} d={pageDefaults} compact iconOptions={pageIcons} />
              </div>
              <PulseEditRow k={`why_card_${n}_body`} label="Body" v={pageSettings} u={updateP} reset={resetField} d={pageDefaults} multiline rows={2} />
            </PulseEditGroup>
          ))}

          {/* Group: Sections */}
          <PulseEditGroup title="Section headings & empty-state">
            <div className="grid md:grid-cols-2 gap-2">
              <PulseEditRow k="section_active_title_pre" label="'Drop your' (active section prefix)" v={pageSettings} u={updateP} reset={resetField} d={pageDefaults} compact />
              <PulseEditRow k="section_active_title_accent" label="'vote.' (active section accent)" v={pageSettings} u={updateP} reset={resetField} d={pageDefaults} compact />
            </div>
            <PulseEditRow k="section_active_hint" label="Active section hint text (right side)" v={pageSettings} u={updateP} reset={resetField} d={pageDefaults} compact />
            <PulseEditRow k="empty_state_title" label="Empty state title (when no polls)" v={pageSettings} u={updateP} reset={resetField} d={pageDefaults} compact />
            <PulseEditRow k="empty_state_body" label="Empty state body" v={pageSettings} u={updateP} reset={resetField} d={pageDefaults} multiline rows={2} />
            <PulseEditRow k="section_closed_title" label="'Past polls.' section heading" v={pageSettings} u={updateP} reset={resetField} d={pageDefaults} compact />
          </PulseEditGroup>

          {/* Group: FAQ section labels */}
          <PulseEditGroup title="FAQs section labels (edit the entries via the FAQs editor)">
            <div className="grid md:grid-cols-2 gap-2">
              <PulseEditRow k="faq_eyebrow" label="FAQ eyebrow" v={pageSettings} u={updateP} reset={resetField} d={pageDefaults} compact />
              <PulseEditRow k="faq_title" label="FAQ section heading" v={pageSettings} u={updateP} reset={resetField} d={pageDefaults} compact />
            </div>
          </PulseEditGroup>

          {/* Group: CTA */}
          <PulseEditGroup title="Bottom CTA banner">
            <PulseEditRow k="cta_title" label="CTA headline" v={pageSettings} u={updateP} reset={resetField} d={pageDefaults} compact />
            <PulseEditRow k="cta_body" label="CTA body" v={pageSettings} u={updateP} reset={resetField} d={pageDefaults} multiline rows={2} />
            <div className="grid md:grid-cols-2 gap-2">
              <PulseEditRow k="cta_button_label" label="Button label (clear to hide button)" v={pageSettings} u={updateP} reset={resetField} d={pageDefaults} compact />
              <PulseEditRow k="cta_button_url" label="Button URL (/holler, /vote, https://…)" v={pageSettings} u={updateP} reset={resetField} d={pageDefaults} compact />
            </div>
          </PulseEditGroup>

          <div className="pt-2 flex gap-2">
            <button onClick={savePageSettings} disabled={pageBusy} className="btn-loot text-xs">
              {pageBusy ? "Saving…" : "Save page copy"}
            </button>
            <a href="/pulse" target="_blank" rel="noopener noreferrer" className="btn-ghost text-xs">Preview /pulse ↗</a>
          </div>
        </div>
      )}

      {/* Create / Edit form */}
      <div className="loot-card-flat p-5 space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div>
            <div className="overline">{editingId ? "Edit survey" : "Create survey"}</div>
            <p className="font-mono text-xs text-[#F5F2E8]/55 mt-1 max-w-2xl">
              Ask hunters anything. Single-choice, multi-choice, rating, or open text. Lives at <code>/pulse</code>.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {editingId && (
              <button onClick={startCreate} className="btn-ghost text-xs" data-testid="pulse-new">+ New</button>
            )}
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-2">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">Title</label>
            <input className="loot-input" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} maxLength={200} data-testid="pulse-title" />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">Status</label>
            <select className="loot-input" value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value })} data-testid="pulse-status">
              <option value="draft">draft (only admins see)</option>
              <option value="active">active (live for hunters)</option>
              <option value="closed">closed (read-only)</option>
            </select>
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">Show results</label>
            <select className="loot-input" value={draft.show_results} onChange={(e) => setDraft({ ...draft, show_results: e.target.value })} data-testid="pulse-visibility">
              <option value="after_vote">after vote</option>
              <option value="always">always (live tally)</option>
              <option value="never">never (private)</option>
            </select>
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">Expires (optional, ISO date)</label>
            <input className="loot-input" placeholder="2026-06-01" value={draft.expires_at || ""} onChange={(e) => setDraft({ ...draft, expires_at: e.target.value })} maxLength={32} />
          </div>
          <div className="md:col-span-2">
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">Description (1–2 lines under title)</label>
            <input className="loot-input" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} maxLength={2000} />
          </div>
          <div className="md:col-span-2">
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">Why I'm asking (shown in gold callout)</label>
            <textarea className="loot-input" rows={2} value={draft.why_asking} onChange={(e) => setDraft({ ...draft, why_asking: e.target.value })} maxLength={2000} />
          </div>
        </div>

        {/* Questions */}
        <div className="space-y-2">
          <div className="overline mt-2">Questions ({draft.questions.length})</div>
          {draft.questions.map((q, i) => (
            <div key={i} className="border border-[#F5F2E8]/12 p-3 space-y-2" data-testid={`pulse-q-row-${i}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-[10px] text-[#76B82A]">Q{i + 1}</span>
                <input
                  className="loot-input flex-1 min-w-[200px]"
                  placeholder="Question label"
                  value={q.label}
                  onChange={(e) => updateQ(i, { label: e.target.value })}
                  maxLength={300}
                  data-testid={`pulse-q-label-${i}`}
                />
                <select
                  className="loot-input w-auto"
                  value={q.type}
                  onChange={(e) => updateQ(i, { type: e.target.value, options: (e.target.value === "single" || e.target.value === "multi") ? (q.options.length ? q.options : ["", ""]) : [] })}
                  data-testid={`pulse-q-type-${i}`}
                >
                  {PULSE_Q_TYPES.map((t) => <option key={t.k} value={t.k}>{t.l}</option>)}
                </select>
                <label className="text-[10px] font-mono flex items-center gap-1">
                  <input type="checkbox" checked={q.required} onChange={(e) => updateQ(i, { required: e.target.checked })} /> req
                </label>
                <button onClick={() => removeQ(i)} className="btn-ghost text-[10px]" style={{ color: "#E53935", borderColor: "#E53935" }} data-testid={`pulse-q-del-${i}`}>Remove</button>
              </div>
              {(q.type === "single" || q.type === "multi") && (
                <div className="ml-8 space-y-1">
                  {(q.options || []).map((opt, oi) => (
                    <div key={oi} className="flex items-center gap-1">
                      <input
                        className="loot-input flex-1 text-sm"
                        placeholder={`Option ${oi + 1}`}
                        value={opt}
                        onChange={(e) => setOpt(i, oi, e.target.value)}
                        maxLength={200}
                        data-testid={`pulse-q-opt-${i}-${oi}`}
                      />
                      <button onClick={() => removeOpt(i, oi)} className="btn-ghost text-[10px]" disabled={(q.options || []).length <= 2}>×</button>
                    </div>
                  ))}
                  <button onClick={() => addOpt(i)} className="btn-ghost text-[10px] inline-flex items-center gap-1" data-testid={`pulse-q-add-opt-${i}`}>
                    <Plus size={10} /> Option
                  </button>
                </div>
              )}
            </div>
          ))}
          <button onClick={addQ} className="btn-ghost text-xs inline-flex items-center gap-1" data-testid="pulse-add-q">
            <Plus size={12} /> Add question
          </button>
        </div>

        <div className="flex items-center gap-2 flex-wrap pt-2">
          <button onClick={submit} disabled={busy} className="btn-loot text-xs inline-flex items-center gap-1" data-testid="pulse-save">
            <Plus size={12} /> {busy ? "Saving…" : (editingId ? "Save changes" : "Create survey")}
          </button>
          {editingId && (
            <button onClick={() => { setEditingId(null); setDraft(blankPulseDraft()); }} className="btn-ghost text-xs">Cancel edit</button>
          )}
        </div>
      </div>

      {/* All surveys list */}
      <div className="loot-card-flat p-5 space-y-2">
        <div className="overline">All surveys ({items.length})</div>
        {items.length === 0 ? <div className="font-mono text-xs text-[#F5F2E8]/45">none yet</div> : (
          <div className="space-y-2">
            {items.map((s) => (
              <div key={s.id} className="border border-[#F5F2E8]/10 p-3 flex items-start gap-3 flex-wrap" data-testid={`admin-pulse-${s.id}`}>
                <div className="flex-1 min-w-[200px]">
                  <div className="font-display uppercase text-sm">{s.title}</div>
                  <div className="font-mono text-[10px] text-[#F5F2E8]/55 mt-1">
                    {(s.questions || []).length}q · {s.response_count} responses · {s.status} · show_results={s.show_results}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 ml-auto">
                  <button onClick={() => startEdit(s)} className="btn-ghost text-[10px]" data-testid={`pulse-edit-${s.id}`}>Edit</button>
                  <button onClick={() => viewResponses(s.id)} className="btn-ghost text-[10px]" data-testid={`pulse-responses-${s.id}`}>Responses</button>
                  {s.status !== "active" && <button onClick={() => setStatus(s, "active")} className="btn-ghost text-[10px]" style={{ color: "#76B82A", borderColor: "#76B82A" }}>Activate</button>}
                  {s.status !== "draft" && <button onClick={() => setStatus(s, "draft")} className="btn-ghost text-[10px]">Draft</button>}
                  {s.status !== "closed" && <button onClick={() => setStatus(s, "closed")} className="btn-ghost text-[10px]" style={{ color: "#D7A62A", borderColor: "#D7A62A" }}>Close</button>}
                  <button onClick={() => del(s.id)} className="btn-ghost text-[10px]" style={{ color: "#E53935", borderColor: "#E53935" }}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* FAQs editor */}
      {showFaqs && (
        <div className="loot-card-flat p-5 space-y-2" data-testid="pulse-faqs-editor">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="overline">FAQ entries ({faqs.length})</div>
            <div className="flex gap-1">
              <button onClick={() => setFaqs([...faqs, { q: "", a: "" }])} className="btn-ghost text-xs inline-flex items-center gap-1" data-testid="pulse-faq-add"><Plus size={12} /> Add</button>
              <button onClick={resetFaqs} className="btn-ghost text-xs">Reset to defaults</button>
              <button onClick={saveFaqs} className="btn-loot text-xs" data-testid="pulse-faq-save">Save FAQs</button>
            </div>
          </div>
          {faqs.map((f, i) => (
            <div key={i} className="border border-[#F5F2E8]/10 p-3 space-y-2">
              <input
                className="loot-input text-sm"
                placeholder="Question"
                value={f.q}
                onChange={(e) => { const x = [...faqs]; x[i] = { ...x[i], q: e.target.value }; setFaqs(x); }}
                data-testid={`pulse-faq-q-${i}`}
              />
              <textarea
                rows={2}
                className="loot-input text-sm"
                placeholder="Answer"
                value={f.a}
                onChange={(e) => { const x = [...faqs]; x[i] = { ...x[i], a: e.target.value }; setFaqs(x); }}
                data-testid={`pulse-faq-a-${i}`}
              />
              <button onClick={() => setFaqs(faqs.filter((_, idx) => idx !== i))} className="btn-ghost text-[10px]" style={{ color: "#E53935", borderColor: "#E53935" }}>Remove</button>
            </div>
          ))}
        </div>
      )}

      {/* Responses modal */}
      {responsesFor.id && responsesFor.data && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" onClick={() => setResponsesFor({ id: null, data: null })}>
          <div className="loot-card-flat max-w-3xl w-full p-5 space-y-3 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div>
                <div className="overline">Responses</div>
                <div className="font-display uppercase text-lg">{responsesFor.data.survey?.title}</div>
                <div className="font-mono text-[10px] text-[#F5F2E8]/55">{responsesFor.data.count} total</div>
              </div>
              <button onClick={() => setResponsesFor({ id: null, data: null })} className="btn-ghost text-xs">Close</button>
            </div>
            {(responsesFor.data.items || []).length === 0 ? (
              <div className="font-mono text-xs text-[#F5F2E8]/45">No responses yet.</div>
            ) : (
              <div className="space-y-2">
                {responsesFor.data.items.map((r, i) => (
                  <div key={i} className="border border-[#F5F2E8]/10 p-3 text-xs">
                    <div className="font-mono text-[10px] text-[#F5F2E8]/55 mb-1">
                      @{r.username || "anon"} · {r.created_at?.slice(0, 16)}
                    </div>
                    {(responsesFor.data.survey?.questions || []).map((q) => {
                      const a = r.answers?.[q.id];
                      return (
                        <div key={q.id} className="mb-1">
                          <span className="text-[#F5F2E8]/55">{q.label}: </span>
                          <span className="text-[#F5F2E8]/95">
                            {Array.isArray(a) ? a.join(", ") : (a === undefined || a === "" ? "—" : String(a))}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// =====================================================================
// Vault admin — master toggle, vault CRUD, fragment editor, attempts
// =====================================================================
function VaultAdminTab() {
  const [settings, setSettings] = useState(null);
  const [vaults, setVaults] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState({
    title: "Today's Vault", description: "", scope: "all", city: "",
    prize_label: "", prize_value_cents: 5000, prize_image_url: "", prize_kind: "cash",
    sponsor_name: "", sponsor_logo_url: "", code: "", code_length: 4,
    is_black: false, theme_color: "#D7A62A",
    opens_at: "", closes_at: "",
  });

  const reload = async () => {
    try {
      const [s, v] = await Promise.all([
        api.get("/admin/vault/settings"),
        api.get("/admin/vault/vaults"),
      ]);
      setSettings(s.data || {});
      setVaults(v.data?.items || []);
    } catch {
      setSettings({}); setVaults([]);
    }
  };
  useEffect(() => { reload(); }, []);

  const updateMode = async (mode) => {
    try {
      const { data } = await api.put("/admin/vault/settings", { mode });
      setSettings(data);
      toast.success(`Vault is now ${data.mode.toUpperCase()}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    }
  };

  const create = async () => {
    if (!draft.prize_label.trim() || !draft.opens_at) return toast.error("Prize label + opens_at required");
    setBusy(true);
    try {
      const opensIso = new Date(draft.opens_at).toISOString();
      const closesIso = draft.closes_at ? new Date(draft.closes_at).toISOString() : null;
      const payload = {
        ...draft, opens_at: opensIso, closes_at: closesIso,
        prize_value_cents: parseInt(draft.prize_value_cents || 0, 10),
        code_length: parseInt(draft.code_length || 4, 10),
        code: draft.code.trim().toUpperCase() || undefined,
      };
      const { data } = await api.post("/admin/vault/vaults", payload);
      toast.success(`Vault created. Code: ${data.vault.code || "auto"}`);
      setDraft({ ...draft, prize_label: "", description: "", code: "", opens_at: "", closes_at: "" });
      setOpenId(data.vault.id);
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Create failed");
    } finally { setBusy(false); }
  };

  const patchVault = async (id, body) => {
    try {
      await api.patch(`/admin/vault/vaults/${id}`, body);
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    }
  };

  const delVault = async (id) => {
    if (!window.confirm("Delete this vault? All fragments + attempts go with it.")) return;
    try { await api.delete(`/admin/vault/vaults/${id}`); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Delete failed"); }
  };

  if (!settings) return <div className="text-[#F5F2E8]/60">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="admin-vault">
      {/* Master toggle */}
      <div className="loot-card-flat p-5 space-y-3">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="overline">Master toggle</div>
            <h2 className="font-display uppercase text-2xl mt-1">The Daily Vault</h2>
            <p className="font-mono text-xs text-[#F5F2E8]/55 max-w-xl mt-1">
              Citywide multi-source code-puzzle game. Hunters earn fragments by doing things, then crack the code.
              <br /><strong>off</strong> = totally hidden · <strong>admin</strong> = visible only to admins · <strong>live</strong> = everyone plays.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {["off", "admin", "live"].map((m) => (
              <button
                key={m}
                onClick={() => updateMode(m)}
                data-testid={`vault-mode-${m}`}
                className="text-xs font-display uppercase tracking-widest px-4 py-2 border"
                style={{
                  borderColor: settings.mode === m ? "#76B82A" : "rgba(245,242,232,0.15)",
                  color: settings.mode === m ? "#76B82A" : "#F5F2E8",
                  background: settings.mode === m ? "rgba(118,184,42,0.08)" : "transparent",
                }}
              >
                {m}
              </button>
            ))}
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-2">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">City pts per fragment</label>
            <input className="loot-input" type="number" value={settings.fragment_grant_points || 10} onChange={(e) => setSettings({ ...settings, fragment_grant_points: parseInt(e.target.value || 10, 10) })} onBlur={() => api.put("/admin/vault/settings", { fragment_grant_points: settings.fragment_grant_points })} data-testid="vault-fragment-points" />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">City pts per win</label>
            <input className="loot-input" type="number" value={settings.win_points || 200} onChange={(e) => setSettings({ ...settings, win_points: parseInt(e.target.value || 200, 10) })} onBlur={() => api.put("/admin/vault/settings", { win_points: settings.win_points })} data-testid="vault-win-points" />
          </div>
        </div>
      </div>

      {/* Create vault */}
      <div className="loot-card-flat p-5 space-y-3">
        <div className="overline">Create a vault</div>
        <p className="font-mono text-xs text-[#F5F2E8]/55 max-w-2xl">
          Set the prize, the schedule, and (optionally) a custom code. Add fragments below after creating. Toggle <strong>Black Vault</strong> for a stealthy, high-value drop with horror theming.
        </p>
        <div className="grid md:grid-cols-2 gap-2">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">Title</label>
            <input className="loot-input" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} maxLength={200} data-testid="vault-create-title" />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">Prize label</label>
            <input className="loot-input" value={draft.prize_label} onChange={(e) => setDraft({ ...draft, prize_label: e.target.value })} maxLength={200} data-testid="vault-create-prize" />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">Prize value (cents)</label>
            <input className="loot-input" type="number" value={draft.prize_value_cents} onChange={(e) => setDraft({ ...draft, prize_value_cents: parseInt(e.target.value || 0, 10) })} data-testid="vault-create-value" />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">Scope</label>
            <select className="loot-input" value={draft.scope} onChange={(e) => setDraft({ ...draft, scope: e.target.value })} data-testid="vault-create-scope">
              <option value="all">All cities</option>
              <option value="city">Single city</option>
            </select>
          </div>
          {draft.scope === "city" && (
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">City</label>
              <input className="loot-input" value={draft.city} onChange={(e) => setDraft({ ...draft, city: e.target.value })} maxLength={80} data-testid="vault-create-city" />
            </div>
          )}
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">Opens at</label>
            <input className="loot-input" type="datetime-local" value={draft.opens_at} onChange={(e) => setDraft({ ...draft, opens_at: e.target.value })} data-testid="vault-create-opens" />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">Closes at (optional)</label>
            <input className="loot-input" type="datetime-local" value={draft.closes_at} onChange={(e) => setDraft({ ...draft, closes_at: e.target.value })} data-testid="vault-create-closes" />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">Code (blank = auto)</label>
            <input className="loot-input" value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value.toUpperCase() })} maxLength={40} placeholder="e.g. 4K9X" data-testid="vault-create-code" />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">Code length (auto)</label>
            <input className="loot-input" type="number" min={1} max={40} value={draft.code_length} onChange={(e) => setDraft({ ...draft, code_length: parseInt(e.target.value || 4, 10) })} data-testid="vault-create-code-length" />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">Sponsor name (opt)</label>
            <input className="loot-input" value={draft.sponsor_name} onChange={(e) => setDraft({ ...draft, sponsor_name: e.target.value })} maxLength={120} data-testid="vault-create-sponsor" />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">Theme color</label>
            <input className="loot-input" value={draft.theme_color} onChange={(e) => setDraft({ ...draft, theme_color: e.target.value })} placeholder="#D7A62A" maxLength={20} data-testid="vault-create-theme" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs flex items-center gap-1">
            <input type="checkbox" checked={draft.is_black} onChange={(e) => setDraft({ ...draft, is_black: e.target.checked })} data-testid="vault-create-black" /> 🖤 Black Vault (rare/hidden)
          </label>
        </div>
        <div>
          <label className="overline mb-1 text-[10px] block">Prize image (optional)</label>
          <PhotoUploader value={draft.prize_image_url} onChange={(url) => setDraft({ ...draft, prize_image_url: url })} testid="vault-create-prize-image" />
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">Description (optional)</label>
          <textarea className="loot-input" rows={2} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} maxLength={800} />
        </div>
        <button onClick={create} disabled={busy} className="btn-loot text-xs inline-flex items-center gap-1" data-testid="vault-create">
          <Plus size={12} /> {busy ? "Creating…" : "Create vault"}
        </button>
      </div>

      {/* Existing vaults */}
      <div className="loot-card-flat p-5 space-y-3">
        <div className="overline">Vaults</div>
        {vaults.length === 0 ? (
          <div className="font-mono text-xs text-[#F5F2E8]/45">none yet</div>
        ) : (
          <div className="space-y-3">
            {vaults.map((v) => (
              <VaultRow
                key={v.id}
                v={v}
                expanded={openId === v.id}
                onToggle={() => setOpenId(openId === v.id ? null : v.id)}
                onPatch={(body) => patchVault(v.id, body)}
                onDelete={() => delVault(v.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function VaultRow({ v, expanded, onToggle, onPatch, onDelete }) {
  const [fragments, setFragments] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [draft, setDraft] = useState({
    position: 0, char: "", source_kind: "daily_checkin", hint: "", icon_emoji: "",
    riddle_question: "", riddle_answer: "", min_streak: 0,
    drop_id_ref: "", waymark_id_ref: "", mission_id_ref: "", sponsor_id_ref: "",
    max_grants: 0, available_at: "", expires_at: "",
  });
  const [grantTo, setGrantTo] = useState({});

  const reload = async () => {
    if (!expanded) return;
    try {
      const [f, a] = await Promise.all([
        api.get(`/admin/vault/vaults/${v.id}/fragments`),
        api.get(`/admin/vault/vaults/${v.id}/attempts?limit=50`),
      ]);
      setFragments(f.data?.items || []);
      setAttempts(a.data?.items || []);
      setDraft((d) => ({ ...d, position: f.data?.items?.length || 0 }));
    } catch {
      setFragments([]); setAttempts([]);
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [expanded]);

  const addFragment = async () => {
    if (!draft.char.trim()) return toast.error("Fragment character is required");
    try {
      const payload = { ...draft, char: draft.char.trim().toUpperCase() };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      await api.post(`/admin/vault/vaults/${v.id}/fragments`, payload);
      toast.success("Fragment added");
      setDraft({ ...draft, char: "", hint: "", riddle_question: "", riddle_answer: "" });
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Add failed");
    }
  };

  const delFragment = async (fid) => {
    if (!window.confirm("Delete this fragment?")) return;
    try { await api.delete(`/admin/vault/vaults/${v.id}/fragments/${fid}`); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Delete failed"); }
  };

  const sponsorGrant = async (fid) => {
    const username = (grantTo[fid] || "").trim();
    if (!username) return toast.error("Username required");
    try {
      await api.post(`/admin/vault/vaults/${v.id}/fragments/${fid}/grant`, { username });
      toast.success(`Granted to @${username}`);
      setGrantTo({ ...grantTo, [fid]: "" });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Grant failed");
    }
  };

  return (
    <div className="border border-[#F5F2E8]/10 p-3" data-testid={`admin-vault-row-${v.id}`}>
      <div className="flex items-start gap-3 flex-wrap">
        {v.prize_image_url && <img src={v.prize_image_url} alt="" className="h-12 w-12 object-contain border border-[#F5F2E8]/15" />}
        <div className="flex-1 min-w-[220px]">
          <div className="font-display uppercase text-sm">
            {v.is_black && "🖤 "}{v.title} <span className="text-[#76B82A]">${(v.prize_value_cents/100).toFixed(2)}</span>
          </div>
          <div className="font-mono text-[10px] text-[#F5F2E8]/55">
            {v.scope === "city" && v.city ? `${v.city} · ` : ""}{v.status} · code <code className="text-[#D7A62A] ml-1">{v.code || "(?)"}</code>
            {" · "}{v.fragment_count || 0} frag · opens {v.opens_at?.slice(0,16).replace("T"," ")}
          </div>
          {v.winner_username && <div className="font-mono text-[10px] text-[#76B82A]">Winner: @{v.winner_username}</div>}
        </div>
        <div className="flex gap-1 flex-wrap">
          <button onClick={onToggle} className="btn-ghost text-xs" data-testid={`admin-vault-toggle-${v.id}`}>{expanded ? "Hide" : "Edit"}</button>
          {v.status === "active" && (
            <button onClick={() => onPatch({ status: "finalized" })} className="btn-ghost text-xs" style={{ color: "#76B82A", borderColor: "#76B82A" }}>Finalize</button>
          )}
          {v.status === "scheduled" && (
            <button onClick={() => onPatch({ status: "active" })} className="btn-ghost text-xs">Open now</button>
          )}
          <button onClick={onDelete} className="btn-ghost text-xs" style={{ color: "#E53935", borderColor: "#E53935" }}>Delete</button>
        </div>
      </div>

      {expanded && (
        <div className="mt-3 pt-3 border-t border-[#F5F2E8]/10 space-y-3">
          <div className="overline text-[10px]">Fragments ({fragments.length})</div>
          {fragments.length === 0 ? <div className="font-mono text-[10px] text-[#F5F2E8]/45">none yet — add one below</div> : (
            <div className="space-y-2">
              {fragments.map((f) => (
                <div key={f.id} className="border border-[#F5F2E8]/10 p-2 text-xs space-y-1" data-testid={`admin-fragment-${f.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="frag-source-tile" style={{ width: 30, height: 30, fontSize: 14 }}>{f.icon_emoji || "•"}</span>
                    <span className="font-display text-base text-[#D7A62A]">"{f.char}"</span>
                    <span className="font-mono text-[10px] text-[#F5F2E8]/55">slot {f.position + 1} · {f.source_kind}</span>
                    <span className="font-mono text-[9px] text-[#F5F2E8]/55 ml-auto">granted {f.granted_count}{f.max_grants ? `/${f.max_grants}` : ""}</span>
                    <button onClick={() => delFragment(f.id)} className="btn-ghost text-[10px]" style={{ color: "#E53935", borderColor: "#E53935" }}>✕</button>
                  </div>
                  {f.hint && <div className="font-mono text-[10px] text-[#F5F2E8]/65">hint: {f.hint}</div>}
                  {f.source_kind === "qr_poster" && f.qr_poster_token && (
                    <div className="font-mono text-[10px] text-[#76B82A] break-all">
                      Poster URL: <code>{`${window.location.origin}/vault/qr/${f.qr_poster_token}`}</code>
                    </div>
                  )}
                  {(f.source_kind === "sponsor_visit" || f.source_kind === "custom") && (
                    <div className="flex items-center gap-1 mt-1">
                      <input
                        className="loot-input flex-1"
                        placeholder="@username to grant"
                        value={grantTo[f.id] || ""}
                        onChange={(e) => setGrantTo({ ...grantTo, [f.id]: e.target.value.replace(/^@/, "") })}
                        data-testid={`admin-grant-username-${f.id}`}
                      />
                      <button onClick={() => sponsorGrant(f.id)} className="btn-loot text-[10px]" data-testid={`admin-grant-${f.id}`}>Grant</button>
                    </div>
                  )}
                  {f.source_kind === "riddle" && (
                    <div className="font-mono text-[10px] text-[#F5F2E8]/55">
                      Q: {f.riddle_question} → A: <code className="text-[#D7A62A]">{f.riddle_answer}</code>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="border-l-2 border-[#76B82A]/40 pl-3 space-y-2">
            <div className="overline text-[10px]" style={{ color: "#76B82A" }}>+ add fragment</div>
            <div className="grid md:grid-cols-2 gap-2">
              <input className="loot-input" placeholder="Position (0-based)" type="number" min={0} value={draft.position} onChange={(e) => setDraft({ ...draft, position: parseInt(e.target.value || 0, 10) })} data-testid={`admin-frag-position-${v.id}`} />
              <input className="loot-input" placeholder="Char (e.g. 4)" value={draft.char} onChange={(e) => setDraft({ ...draft, char: e.target.value.slice(0,4).toUpperCase() })} maxLength={4} data-testid={`admin-frag-char-${v.id}`} />
              <select className="loot-input" value={draft.source_kind} onChange={(e) => setDraft({ ...draft, source_kind: e.target.value })} data-testid={`admin-frag-kind-${v.id}`}>
                <option value="daily_checkin">📅 Daily Check-in</option>
                <option value="riddle">🧩 Riddle</option>
                <option value="qr_poster">📍 QR Poster</option>
                <option value="waymark">🗺️ Waymark</option>
                <option value="mission">❤️ Mission</option>
                <option value="drop_view">🎁 Drop view</option>
                <option value="stash_streak">🔥 Stash streak</option>
                <option value="sponsor_visit">🏪 Sponsor visit</option>
                <option value="custom">⚡ Custom</option>
              </select>
              <input className="loot-input" placeholder="Icon emoji (opt)" value={draft.icon_emoji} onChange={(e) => setDraft({ ...draft, icon_emoji: e.target.value })} maxLength={8} />
            </div>
            <input className="loot-input" placeholder="Hint shown to hunter" value={draft.hint} onChange={(e) => setDraft({ ...draft, hint: e.target.value })} maxLength={400} data-testid={`admin-frag-hint-${v.id}`} />
            {draft.source_kind === "riddle" && (
              <>
                <textarea className="loot-input" rows={2} placeholder="Riddle question" value={draft.riddle_question} onChange={(e) => setDraft({ ...draft, riddle_question: e.target.value })} maxLength={600} />
                <input className="loot-input" placeholder="Answer (case-insensitive)" value={draft.riddle_answer} onChange={(e) => setDraft({ ...draft, riddle_answer: e.target.value })} maxLength={200} />
              </>
            )}
            {draft.source_kind === "stash_streak" && (
              <input className="loot-input" type="number" min={0} placeholder="Min streak required" value={draft.min_streak} onChange={(e) => setDraft({ ...draft, min_streak: parseInt(e.target.value || 0, 10) })} />
            )}
            {draft.source_kind === "drop_view" && (
              <input className="loot-input" placeholder="Drop ID to require viewing" value={draft.drop_id_ref} onChange={(e) => setDraft({ ...draft, drop_id_ref: e.target.value })} />
            )}
            {draft.source_kind === "waymark" && (
              <input className="loot-input" placeholder="Waymark ID to require finding" value={draft.waymark_id_ref} onChange={(e) => setDraft({ ...draft, waymark_id_ref: e.target.value })} />
            )}
            {draft.source_kind === "mission" && (
              <input className="loot-input" placeholder="Mission ID to require completing" value={draft.mission_id_ref} onChange={(e) => setDraft({ ...draft, mission_id_ref: e.target.value })} />
            )}
            <input className="loot-input" placeholder="Max grants (0 = unlimited)" type="number" min={0} value={draft.max_grants} onChange={(e) => setDraft({ ...draft, max_grants: parseInt(e.target.value || 0, 10) })} />
            <button onClick={addFragment} className="btn-loot text-xs inline-flex items-center gap-1" data-testid={`admin-frag-add-${v.id}`}>
              <Plus size={12} /> Add fragment
            </button>
          </div>

          {attempts.length > 0 && (
            <div>
              <div className="overline text-[10px]">Attempts ({attempts.length})</div>
              <div className="max-h-48 overflow-y-auto text-xs space-y-1">
                {attempts.map((a) => (
                  <div key={a.id} className="font-mono text-[10px]" data-testid={`admin-attempt-${a.id}`}>
                    <span style={{ color: a.correct ? "#76B82A" : "#E53935" }}>{a.correct ? "✓" : "✗"}</span>{" "}
                    <span className="text-[#F5F2E8]/65">@{a.username}</span>{" → "}
                    <code className="text-[#D7A62A]">{a.code || a.submitted}</code>{" "}
                    <span className="text-[#F5F2E8]/45">{a.attempted_at?.slice(11, 19)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// =====================================================================
// Claims tab — fulfill stash + auction prize claims
// =====================================================================
function ClaimsTab() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("submitted");
  const [active, setActive] = useState(null);

  const reload = async () => {
    try {
      const q = filter ? `?status=${filter}` : "";
      const { data } = await api.get(`/admin/stash/claims${q}`);
      setData(data || { items: [], counts: {} });
    } catch {
      setData({ items: [], counts: {} });
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [filter]);

  if (!data) return <div className="text-[#F5F2E8]/60">Loading…</div>;

  const counts = data.counts || {};
  return (
    <div className="space-y-4" data-testid="admin-claims">
      <div className="loot-card-flat p-5 space-y-3">
        <div>
          <div className="overline">Prize claims queue</div>
          <h2 className="font-display uppercase text-2xl mt-1">Claims</h2>
          <p className="font-mono text-xs text-[#F5F2E8]/55 mt-1 max-w-2xl">
            Hunters who win on the Daily Stash or in a Loot Auction submit their
            fulfillment details here. Mark them <strong>fulfilled</strong> with the
            gift card code / tracking # / pickup window — that text is sent to the
            hunter's My Prizes page automatically.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {["submitted", "unclaimed", "fulfilled", "expired", ""].map((s) => (
            <button
              key={s || "all"}
              onClick={() => setFilter(s)}
              data-testid={`claims-filter-${s || "all"}`}
              className="text-xs font-display uppercase tracking-widest px-3 py-1 border"
              style={{
                borderColor: filter === s ? "#76B82A" : "rgba(245,242,232,0.15)",
                color: filter === s ? "#76B82A" : "#F5F2E8",
                background: filter === s ? "rgba(118,184,42,0.08)" : "transparent",
              }}
            >
              {(s || "all")} ({counts[s] || (s === "" ? Object.values(counts).reduce((a, b) => a + b, 0) : 0)})
            </button>
          ))}
        </div>
        {data.items.length === 0 ? (
          <div className="font-mono text-xs text-[#F5F2E8]/45 italic py-6 text-center">No claims in this bucket.</div>
        ) : (
          <div className="space-y-2">
            {data.items.map((c) => (
              <div key={c.id} className="border border-[#F5F2E8]/10 p-3 flex items-start gap-3 flex-wrap" data-testid={`admin-claim-${c.id}`}>
                {c.prize_image_url && <img src={c.prize_image_url} alt="" className="h-10 w-10 object-contain border border-[#F5F2E8]/15" />}
                <div className="flex-1 min-w-[240px]">
                  <div className="font-display uppercase text-sm">{c.prize_label} <span className="text-[#76B82A]">${(c.value_cents/100).toFixed(2)}</span></div>
                  <div className="font-mono text-[10px] text-[#F5F2E8]/55">@{c.username} · {c.claim_code} · won {c.won_at?.slice(0, 16)}</div>
                  <div className="font-mono text-[10px] mt-1" style={{ color: c.status === "fulfilled" ? "#76B82A" : c.status === "submitted" ? "#D7A62A" : c.status === "expired" ? "#E53935" : "#F5F2E8" }}>
                    {c.status} · method: {c.fulfillment_method || "—"}
                  </div>
                  {c.contact_email && <div className="font-mono text-[10px] text-[#F5F2E8]/65">📧 {c.contact_email}</div>}
                  {c.contact_phone && <div className="font-mono text-[10px] text-[#F5F2E8]/65">📞 {c.contact_phone}</div>}
                  {c.shipping_address && <div className="font-mono text-[10px] text-[#F5F2E8]/65 whitespace-pre-line">📦 {c.shipping_address}</div>}
                  {c.notes && <div className="font-mono text-[10px] text-[#F5F2E8]/55 italic">"{c.notes}"</div>}
                  {c.fulfillment_payload && (
                    <div className="mt-1 text-[10px] font-mono p-1 border border-[#76B82A]/40 bg-[#76B82A]/5">
                      → {c.fulfillment_payload}
                    </div>
                  )}
                </div>
                {c.status !== "fulfilled" && c.status !== "expired" && (
                  <button onClick={() => setActive(c)} className="btn-loot text-xs" data-testid={`admin-claim-fulfill-${c.id}`}>Fulfill</button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      {active && (
        <FulfillModal claim={active} onClose={() => setActive(null)} onDone={() => { setActive(null); reload(); }} />
      )}
    </div>
  );
}

function FulfillModal({ claim, onClose, onDone }) {
  const [payload, setPayload] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!payload.trim()) { toast.error("Enter the gift card code / tracking # / pickup window"); return; }
    setBusy(true);
    try {
      await api.patch(`/admin/stash/claims/${claim.id}`, {
        status: "fulfilled",
        fulfillment_payload: payload.trim(),
        fulfillment_notes: notes.trim() || null,
      });
      toast.success(`Marked fulfilled for @${claim.username}`);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't fulfill");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div className="loot-card-flat max-w-md w-full p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="overline" style={{ color: "#76B82A" }}>fulfill claim</div>
        <div className="font-display uppercase">{claim.prize_label}</div>
        <div className="font-mono text-[10px] text-[#F5F2E8]/55">@{claim.username} · {claim.claim_code}</div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">Payload (visible to hunter)</label>
          <textarea
            className="loot-input"
            rows={3}
            value={payload}
            onChange={(e) => setPayload(e.target.value)}
            placeholder="e.g. Amazon GC code: ABC-123-456  /  USPS tracking 9400111... / Pickup at Joe's Pizza Sat 10-2"
            maxLength={600}
            data-testid="admin-fulfill-payload"
          />
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">Internal notes (private)</label>
          <input
            className="loot-input"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. Got from Costco run 5/12"
            maxLength={1000}
            data-testid="admin-fulfill-notes"
          />
        </div>
        <button onClick={submit} disabled={busy} className="btn-loot w-full text-xs" data-testid="admin-fulfill-submit">
          {busy ? "Saving…" : "Mark fulfilled"}
        </button>
      </div>
    </div>
  );
}

// =====================================================================
// Bazaar admin — shop catalog + coupon redemption + transactions
// =====================================================================
function BazaarAdminTab() {
  const [shop, setShop] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [redeemCode, setRedeemCode] = useState("");
  const [redeemNote, setRedeemNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [txns, setTxns] = useState(null);

  const reload = async () => {
    try {
      const [a, b, c] = await Promise.all([
        api.get("/admin/coins/shop"),
        api.get("/admin/coins/coupons?limit=100"),
        api.get("/admin/coins/transactions?limit=50"),
      ]);
      setShop(a.data?.items || []);
      setCoupons(b.data?.items || []);
      setTxns(c.data || null);
    } catch {
      setShop([]); setCoupons([]); setTxns(null);
    }
  };
  useEffect(() => { reload(); }, []);

  const patchItem = async (sku, body) => {
    try {
      await api.patch(`/admin/coins/shop/${sku}`, body);
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    }
  };

  const redeem = async () => {
    if (!redeemCode.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/coins/coupons/redeem", { code: redeemCode.trim(), note: redeemNote.trim() || null });
      toast.success(`Redeemed @${data.username}'s ${(data.value_cents/100).toFixed(2)} coupon!`);
      setRedeemCode(""); setRedeemNote(""); reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Redeem failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="admin-bazaar">
      <div className="loot-card-flat p-5 space-y-3">
        <div className="overline">Shop catalog</div>
        <p className="font-mono text-xs text-[#F5F2E8]/55 max-w-2xl">
          Each item is a coin sink. Toggle <strong>active</strong> to remove it from the public Bazaar without deleting. Adjust the price to tune the economy.
        </p>
        {shop.map((it) => (
          <div key={it.sku} className="border border-[#F5F2E8]/10 p-3 flex items-center gap-3 flex-wrap" data-testid={`admin-shop-${it.sku}`}>
            <div className="flex-1 min-w-[220px]">
              <div className="font-display uppercase text-sm">{it.name}</div>
              <div className="font-mono text-[10px] text-[#F5F2E8]/55">{it.sku} · {it.kind}{it.min_streak > 0 && ` · streak ${it.min_streak}+`}</div>
            </div>
            <input
              type="number"
              min={0}
              max={1_000_000}
              defaultValue={it.cost_coins}
              onBlur={(e) => {
                const v = parseInt(e.target.value || 0, 10);
                if (v !== it.cost_coins) patchItem(it.sku, { cost_coins: v });
              }}
              className="loot-input"
              style={{ width: 100 }}
              data-testid={`admin-shop-cost-${it.sku}`}
            />
            <span className="font-mono text-[10px] text-[#F5F2E8]/55">coins</span>
            <label className="text-xs flex items-center gap-1">
              <input type="checkbox" checked={it.active} onChange={(e) => patchItem(it.sku, { active: e.target.checked })} /> Active
            </label>
          </div>
        ))}
      </div>

      <div className="loot-card-flat p-5 space-y-3">
        <div className="overline">Redeem a coupon</div>
        <p className="font-mono text-xs text-[#F5F2E8]/55 max-w-2xl">
          When a sponsor (or you) wants to redeem a hunter's $1-off voucher, type the code here. Marks it <code>redeemed</code> permanently.
        </p>
        <div className="flex gap-2 items-center flex-wrap">
          <input className="loot-input flex-1 min-w-[200px]" placeholder="LOOT-XXXXXXXX" value={redeemCode} onChange={(e) => setRedeemCode(e.target.value.toUpperCase())} maxLength={40} data-testid="admin-coupon-code" />
          <input className="loot-input flex-1 min-w-[200px]" placeholder="Note (optional)" value={redeemNote} onChange={(e) => setRedeemNote(e.target.value)} maxLength={240} data-testid="admin-coupon-note" />
          <button onClick={redeem} disabled={busy || !redeemCode.trim()} className="btn-loot text-xs" data-testid="admin-coupon-redeem">
            {busy ? "Redeeming…" : "Redeem"}
          </button>
        </div>
        <div className="overline mt-3">Recent vouchers</div>
        <div className="grid md:grid-cols-2 gap-2 max-h-72 overflow-y-auto">
          {coupons.length === 0 ? <div className="font-mono text-[10px] text-[#F5F2E8]/45">none</div> : coupons.slice(0, 30).map((c) => (
            <div key={c.code} className="border border-[#F5F2E8]/10 p-2 text-xs flex items-center gap-2 flex-wrap">
              <span className="font-mono">{c.code}</span>
              <span className="text-[#D7A62A]">${(c.value_cents/100).toFixed(2)}</span>
              <span className="text-[#F5F2E8]/55">@{c.username}</span>
              <span className="ml-auto font-mono text-[10px]" style={{ color: c.status === "redeemed" ? "#76B82A" : c.status === "expired" ? "#E53935" : "#D7A62A" }}>{c.status}</span>
            </div>
          ))}
        </div>
      </div>

      {txns && (
        <div className="loot-card-flat p-5 space-y-2">
          <div className="overline">Coin economy stats</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {Object.entries(txns.by_kind || {}).map(([k, v]) => (
              <div key={k} className="border border-[#F5F2E8]/10 p-2">
                <div className="font-mono text-[9px] uppercase tracking-widest text-[#F5F2E8]/55">{k}</div>
                <div className="font-display text-sm">{v.count} txns · {v.volume}c volume</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// =====================================================================
// Auctions admin — create + list + finalize
// =====================================================================
function AuctionsAdminTab() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState({
    prize_label: "", prize_value_cents: 2500, prize_image_url: "",
    description: "", ends_at: "", min_bid: 50, bid_increment: 25,
  });

  const reload = async () => {
    try {
      const { data } = await api.get("/admin/auctions");
      setItems(data?.items || []);
    } catch {
      setItems([]);
    }
  };
  useEffect(() => { reload(); }, []);

  const create = async () => {
    if (!draft.prize_label.trim() || !draft.ends_at) return toast.error("Label + end time required");
    setBusy(true);
    try {
      // Convert local datetime-local input to ISO Z.
      const endsAtIso = new Date(draft.ends_at).toISOString();
      await api.post("/admin/auctions", { ...draft, ends_at: endsAtIso, prize_value_cents: parseInt(draft.prize_value_cents || 0, 10) });
      toast.success("Auction created — live now");
      setDraft({ prize_label: "", prize_value_cents: 2500, prize_image_url: "", description: "", ends_at: "", min_bid: 50, bid_increment: 25 });
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't create");
    } finally { setBusy(false); }
  };

  const finalize = async (id) => {
    if (!window.confirm("Finalize this auction? Picks the winner, refunds losers 50%, sends 50% to the Tank.")) return;
    try {
      const { data } = await api.post(`/admin/auctions/${id}/finalize`);
      if (data.winner) {
        toast.success(`Winner: @${data.winner.username} for ${data.winner.amount}c. Tank +$${(data.tank_proceeds_cents/100).toFixed(2)}`);
      } else {
        toast.info("No bids — closed without a winner.");
      }
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Finalize failed");
    }
  };

  const cancelAuction = async (id) => {
    if (!window.confirm("Cancel this auction? Bidders won't be refunded automatically — handle that manually.")) return;
    try {
      await api.patch(`/admin/auctions/${id}`, { status: "cancelled" });
      toast.success("Cancelled");
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Cancel failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this auction? Only allowed if no bids.")) return;
    try {
      await api.delete(`/admin/auctions/${id}`);
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-auctions">
      <div className="loot-card-flat p-5 space-y-3">
        <div className="overline">Create auction</div>
        <p className="font-mono text-xs text-[#F5F2E8]/55 max-w-2xl">
          Schedule a prize for hunters to bid coins on. Once you click "Finalize" (or after the end time + a manual finalize), the highest bidder wins, losers get half back, the rest hits the Tank.
        </p>
        <div className="grid md:grid-cols-2 gap-2">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">Prize label</label>
            <input className="loot-input" value={draft.prize_label} onChange={(e) => setDraft({ ...draft, prize_label: e.target.value })} maxLength={200} data-testid="admin-auction-label" />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">Retail value (cents)</label>
            <input className="loot-input" type="number" value={draft.prize_value_cents} onChange={(e) => setDraft({ ...draft, prize_value_cents: parseInt(e.target.value || 0, 10) })} data-testid="admin-auction-value" />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">Ends at</label>
            <input className="loot-input" type="datetime-local" value={draft.ends_at} onChange={(e) => setDraft({ ...draft, ends_at: e.target.value })} data-testid="admin-auction-ends-at" />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">Min bid · Increment</label>
            <div className="flex gap-2">
              <input className="loot-input" type="number" value={draft.min_bid} onChange={(e) => setDraft({ ...draft, min_bid: parseInt(e.target.value || 50, 10) })} placeholder="min" data-testid="admin-auction-min-bid" />
              <input className="loot-input" type="number" value={draft.bid_increment} onChange={(e) => setDraft({ ...draft, bid_increment: parseInt(e.target.value || 25, 10) })} placeholder="step" data-testid="admin-auction-increment" />
            </div>
          </div>
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">Description (optional)</label>
          <textarea className="loot-input" rows={2} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} maxLength={800} data-testid="admin-auction-description" />
        </div>
        <div>
          <div className="overline mb-1 text-[10px]">Image (optional)</div>
          <PhotoUploader value={draft.prize_image_url} onChange={(url) => setDraft({ ...draft, prize_image_url: url })} testid="admin-auction-image" />
        </div>
        <button onClick={create} disabled={busy} className="btn-loot text-xs inline-flex items-center gap-1" data-testid="admin-auction-create">
          <Plus size={12} /> {busy ? "Creating…" : "Create auction"}
        </button>
      </div>

      <div className="loot-card-flat p-5 space-y-3">
        <div className="overline">Auctions</div>
        {items.length === 0 ? <div className="font-mono text-xs text-[#F5F2E8]/45">none yet</div> : items.map((a) => (
          <div key={a.id} className="border border-[#F5F2E8]/10 p-3 flex items-start gap-3 flex-wrap" data-testid={`admin-auction-${a.id}`}>
            {a.prize_image_url && <img src={a.prize_image_url} alt="" className="h-12 w-12 object-contain border border-[#F5F2E8]/15" />}
            <div className="flex-1 min-w-[220px]">
              <div className="font-display uppercase text-sm">{a.prize_label} <span className="text-[#76B82A]">${(a.prize_value_cents/100).toFixed(2)}</span></div>
              <div className="font-mono text-[10px] text-[#F5F2E8]/55">
                {a.status} · top {a.top_bid}c by @{a.top_bidder || "—"} · {a.bidder_count} bidders · ends {a.ends_at?.slice(0,16).replace("T"," ")}
              </div>
              {a.winner_username && (
                <div className="font-mono text-[10px] text-[#76B82A] mt-1">Winner: @{a.winner_username} ({a.winning_amount}c)</div>
              )}
            </div>
            {a.status === "active" && (
              <>
                <button onClick={() => finalize(a.id)} className="btn-loot text-xs" data-testid={`admin-auction-finalize-${a.id}`}>Finalize</button>
                <button onClick={() => cancelAuction(a.id)} className="btn-ghost text-xs">Cancel</button>
              </>
            )}
            {a.status !== "active" && a.bid_count === 0 && (
              <button onClick={() => del(a.id)} className="btn-ghost text-xs" style={{ color: "#E53935", borderColor: "#E53935" }}>Delete</button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


function StashTab() {
  const [s, setS] = useState(null);
  const [prizes, setPrizes] = useState([]);
  const [history, setHistory] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sayings, setSayings] = useState([]);
  const [newSaying, setNewSaying] = useState("");
  const [genCount, setGenCount] = useState(8);
  const [generatingSayings, setGeneratingSayings] = useState(false);
  const [newPrize, setNewPrize] = useState({
    label: "", kind: "prize", value_cents: 1000, inventory_total: 5,
    base_weight: 10, image_url: "", active: true, sponsor_name: "",
  });

  const reload = async () => {
    try {
      const [a, b, c, d] = await Promise.all([
        api.get("/admin/stash/settings"),
        api.get("/admin/stash/prizes"),
        api.get("/admin/stash/history?limit=50"),
        api.get("/admin/stash/sayings"),
      ]);
      setS(a.data || {});
      setPrizes(b.data?.items || []);
      setHistory(c.data || null);
      setSayings(d.data?.items || []);
    } catch {
      setS({}); setPrizes([]); setHistory(null); setSayings([]);
    }
  };
  useEffect(() => { reload(); }, []);

  const update = (k, v) => setS((p) => ({ ...(p || {}), [k]: v }));
  const updateWeight = (k, v) => setS((p) => ({
    ...(p || {}),
    weights: { ...(p?.weights || {}), [k]: Math.max(0, parseInt(v || 0, 10)) },
  }));

  const updateSponsor = (idx, key, value) => setS((p) => {
    const list = [...((p?.sponsors) || [])];
    list[idx] = { ...(list[idx] || { name: "", logo_url: "", blurb: "" }), [key]: value };
    return { ...(p || {}), sponsors: list };
  });
  const addSponsorRow = () => setS((p) => ({
    ...(p || {}),
    sponsors: [...((p?.sponsors) || []), { name: "", logo_url: "", blurb: "" }],
  }));
  const removeSponsorRow = (idx) => setS((p) => {
    const list = [...((p?.sponsors) || [])];
    list.splice(idx, 1);
    return { ...(p || {}), sponsors: list };
  });

  const save = async () => {
    if (!s) return;
    setBusy(true);
    try {
      // Strip empty rows from sponsors before saving.
      const sponsors = (s.sponsors || [])
        .map((sp) => ({
          name: (sp.name || "").trim(),
          logo_url: (sp.logo_url || "").trim(),
          blurb: (sp.blurb || "").trim(),
        }))
        .filter((sp) => sp.name);
      const payload = {
        mode: s.mode,
        whitelist_user_ids: s.whitelist_user_ids || [],
        daily_cap_cents: parseInt(s.daily_cap_cents || 0, 10),
        cooldown_hours: parseInt(s.cooldown_hours || 24, 10),
        streak_target_days: parseInt(s.streak_target_days || 7, 10),
        streak_guaranteed_prize: !!s.streak_guaranteed_prize,
        weights: s.weights || {},
        coins_min: parseInt(s.coins_min || 5, 10),
        coins_max: parseInt(s.coins_max || 25, 10),
        coins_to_extra_crack: parseInt(s.coins_to_extra_crack || 50, 10),
        sponsors,
      };
      const { data } = await api.put("/admin/stash/settings", payload);
      setS(data);
      toast.success("Stash settings saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  const addPrize = async () => {
    if (!newPrize.label.trim()) { toast.error("Label required"); return; }
    try {
      await api.post("/admin/stash/prizes", newPrize);
      toast.success("Prize added");
      setNewPrize({
        label: "", kind: "prize", value_cents: 1000, inventory_total: 5,
        base_weight: 10, image_url: "", active: true, sponsor_name: "",
      });
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't add prize");
    }
  };

  const patchPrize = async (id, body) => {
    try {
      await api.patch(`/admin/stash/prizes/${id}`, body);
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    }
  };

  const deletePrize = async (id) => {
    if (!window.confirm("Delete this prize?")) return;
    try {
      await api.delete(`/admin/stash/prizes/${id}`);
      toast.success("Deleted"); reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  const grantCoins = async (username, amount, reason) => {
    if (!username.trim() || !amount) {
      toast.error("Username + amount required.");
      return;
    }
    try {
      const { data } = await api.post("/admin/stash/grant-coins", {
        username: username.trim(),
        amount: parseInt(amount, 10),
        reason: reason || null,
      });
      toast.success(`@${data.username}: ${data.previous_balance} → ${data.new_balance} coins (Δ ${data.delta > 0 ? "+" : ""}${data.delta})`);
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't grant");
    }
  };

  const addSaying = async () => {
    const t = newSaying.trim();
    if (t.length < 2) { toast.error("Saying is too short."); return; }
    try {
      const { data } = await api.post("/admin/stash/sayings", { text: t });
      setSayings(data.items || []);
      setNewSaying("");
      toast.success("Saying added");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't add saying");
    }
  };

  const deleteSaying = async (text) => {
    try {
      const { data } = await api.post("/admin/stash/sayings/delete", { text });
      setSayings(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't delete");
    }
  };

  const resetSayings = async () => {
    if (!window.confirm("Reset to the built-in default sayings? Your custom + AI-generated ones will be removed.")) return;
    try {
      const { data } = await api.post("/admin/stash/sayings/reset");
      setSayings(data.items || []);
      toast.success("Sayings reset to defaults");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't reset");
    }
  };

  const generateSayings = async () => {
    setGeneratingSayings(true);
    try {
      const n = Math.max(1, Math.min(25, parseInt(genCount || 8, 10)));
      const { data } = await api.post(`/admin/stash/sayings/generate?count=${n}`);
      setSayings(data.items || []);
      const added = (data.added || []).length;
      if (added > 0) toast.success(`Added ${added} new saying${added === 1 ? "" : "s"}`);
      else toast.info(data.note || "No new sayings this time — try again.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't generate");
    } finally { setGeneratingSayings(false); }
  };

  if (!s) return <div className="text-[#F5F2E8]/60">Loading…</div>;

  const modeColor = {
    off: "#E53935", admin: "#D7A62A", whitelist: "#D7A62A", live: "#76B82A",
  }[s.mode] || "#F5F2E8";

  return (
    <div className="space-y-4" data-testid="admin-stash">
      {/* Settings card */}
      <div className="loot-card-flat p-5 space-y-4">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="overline">Daily-tap engagement game</div>
            <h2 className="font-display uppercase text-2xl mt-1">
              Daily Stash
              <span className="ml-2 inline-block px-2 py-0.5 text-[10px] tracking-widest uppercase border" style={{ color: modeColor, borderColor: modeColor }}>
                {s.mode || "off"}
              </span>
            </h2>
            <p className="font-mono text-xs text-[#F5F2E8]/60 mt-1 max-w-2xl">
              Hunters tap once per 24h to crack a chest. Server picks a weighted outcome — empty / coins / bonus / prize / jackpot. Test in <code>admin</code> mode before flipping to <code>live</code>.
            </p>
          </div>
          <div className="text-right">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/55">Today's payout</div>
            <div className="font-display text-2xl" style={{ color: "#76B82A" }}>
              ${((s.today_payout_cents || 0) / 100).toFixed(2)}
              <span className="text-xs text-[#F5F2E8]/55"> / ${((s.daily_cap_cents || 0) / 100).toFixed(2)}</span>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <div className="overline mb-1">Mode — who can play</div>
            <select
              className="loot-input"
              value={s.mode || "off"}
              onChange={(e) => update("mode", e.target.value)}
              data-testid="stash-mode"
            >
              <option value="off">Off — game completely hidden, even nav links</option>
              <option value="admin">Admin only — only you can see + play (testing)</option>
              <option value="whitelist">Whitelist — admins + listed user_ids only</option>
              <option value="live">Live — visible + playable for every signed-in hunter</option>
            </select>
            <p className="font-mono text-[10px] text-[#F5F2E8]/45 mt-1">
              The "Stash" nav link is hidden from regular users until mode = <strong>live</strong>. Admins always see it for testing.
            </p>
          </div>
          <div>
            <div className="overline mb-1">Daily payout cap (in cents · $1 = 100)</div>
            <input
              className="loot-input"
              type="number"
              value={s.daily_cap_cents || 0}
              onChange={(e) => update("daily_cap_cents", e.target.value)}
              data-testid="stash-cap"
            />
            <p className="font-mono text-[10px] text-[#F5F2E8]/45 mt-1">
              Once today's real-prize payouts hit this cap, prize + jackpot weights drop to 0 server-side. Currently <strong>${((s.daily_cap_cents || 0) / 100).toFixed(2)}/day</strong>. Set 0 to disable the safety net.
            </p>
          </div>
          <div>
            <div className="overline mb-1">Streak target (days for guaranteed prize)</div>
            <input
              className="loot-input"
              type="number"
              value={s.streak_target_days || 7}
              onChange={(e) => update("streak_target_days", e.target.value)}
              min={2} max={30}
            />
            <p className="font-mono text-[10px] text-[#F5F2E8]/45 mt-1">
              Hunters playing N days in a row get a guaranteed real prize on day {s.streak_target_days || 7}. The retention magic.
            </p>
          </div>
          <label className="flex items-center gap-2 mt-6 text-sm">
            <input
              type="checkbox"
              checked={!!s.streak_guaranteed_prize}
              onChange={(e) => update("streak_guaranteed_prize", e.target.checked)}
            />
            Guarantee a real prize on the streak day (recommended)
          </label>
        </div>

        {/* Weights */}
        <div>
          <div className="overline mb-1">Outcome weights — how often each result comes up</div>
          <p className="font-mono text-[10px] text-[#F5F2E8]/55 mb-2">
            Relative numbers — e.g. 60/25/8/6/1 means "empty" draws 60% of the time on average. Inventory and daily-cap modify these server-side.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {[
              ["empty",   "🪨 Dust",       "Nothin' but dust — most common."],
              ["coins",   "🪙 Loot Coins", "In-app currency, used for extra cracks."],
              ["bonus",   "✨ Bonus",      "Instant re-crack, no cooldown applied."],
              ["prize",   "🎁 Prize",      "Real gift card / swag from inventory."],
              ["jackpot", "🏆 Jackpot",    "Top-tier prize tier (separate inventory pool)."],
            ].map(([k, label, hint]) => (
              <div key={k}>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1">{label}</div>
                <input
                  className="loot-input text-center"
                  type="number"
                  min={0}
                  max={10000}
                  value={(s.weights || {})[k] ?? 0}
                  onChange={(e) => updateWeight(k, e.target.value)}
                  data-testid={`stash-w-${k}`}
                />
                <p className="font-mono text-[9px] text-[#F5F2E8]/45 mt-1 leading-snug">{hint}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Coins config */}
        <div>
          <div className="overline mb-1">Loot Coins — economy settings</div>
          <p className="font-mono text-[10px] text-[#F5F2E8]/55 mb-2">
            Coins are awarded on the "coins" outcome and used to buy extra cracks the same day.
          </p>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1">Min coins per win</div>
              <input className="loot-input" type="number" value={s.coins_min || 5} onChange={(e) => update("coins_min", e.target.value)} />
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1">Max coins per win</div>
              <input className="loot-input" type="number" value={s.coins_max || 25} onChange={(e) => update("coins_max", e.target.value)} />
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1">Cost of an extra crack</div>
              <input className="loot-input" type="number" value={s.coins_to_extra_crack || 50} onChange={(e) => update("coins_to_extra_crack", e.target.value)} />
            </div>
          </div>
        </div>

        {/* Sponsors (multi) */}
        <div className="pt-3 border-t border-[#F5F2E8]/10" data-testid="stash-sponsors-section">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <div>
              <div className="overline" style={{ color: "#76B82A" }}>Stash sponsors</div>
              <p className="font-mono text-[10px] text-[#F5F2E8]/55 mt-1 max-w-xl">
                Add one or more local businesses as sponsors of the Stash. The hunter sees one featured sponsor per day (rotated automatically), and the full list shows on the Stash page so everyone gets recognition.
              </p>
            </div>
            <button
              type="button"
              onClick={addSponsorRow}
              className="btn-ghost text-xs inline-flex items-center gap-1"
              data-testid="stash-add-sponsor"
            >
              <Plus size={12} /> Add sponsor
            </button>
          </div>

          {(!s.sponsors || s.sponsors.length === 0) ? (
            <div className="font-mono text-xs text-[#F5F2E8]/45 italic border border-dashed border-[#F5F2E8]/15 p-4 text-center">
              No sponsors yet. Click "Add sponsor" to attach one.
            </div>
          ) : (
            <div className="space-y-3">
              {(s.sponsors || []).map((sp, i) => (
                <div
                  key={i}
                  className="border border-[#F5F2E8]/10 p-3 space-y-2"
                  data-testid={`stash-sponsor-row-${i}`}
                >
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/55">
                      Sponsor #{i + 1}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeSponsorRow(i)}
                      className="btn-ghost text-[10px]"
                      style={{ color: "#E53935", borderColor: "#E53935" }}
                      data-testid={`stash-sponsor-remove-${i}`}
                    >
                      Remove
                    </button>
                  </div>
                  <div className="grid md:grid-cols-2 gap-2">
                    <div>
                      <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">
                        Business name
                      </label>
                      <input
                        className="loot-input"
                        placeholder="e.g. Joe's Pizza"
                        value={sp.name || ""}
                        onChange={(e) => updateSponsor(i, "name", e.target.value)}
                        maxLength={120}
                        data-testid={`stash-sponsor-name-${i}`}
                      />
                    </div>
                    <div>
                      <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">
                        Blurb (optional, shown under name)
                      </label>
                      <input
                        className="loot-input"
                        placeholder="e.g. Slingin' slices since '92"
                        value={sp.blurb || ""}
                        onChange={(e) => updateSponsor(i, "blurb", e.target.value)}
                        maxLength={400}
                        data-testid={`stash-sponsor-blurb-${i}`}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">
                      Logo (optional)
                    </label>
                    <PhotoUploader
                      value={sp.logo_url || ""}
                      onChange={(url) => updateSponsor(i, "logo_url", url)}
                      testid={`stash-sponsor-logo-${i}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="pt-3 border-t border-[#F5F2E8]/10">
          <button
            onClick={save}
            disabled={busy}
            className="btn-loot text-xs inline-flex items-center gap-1"
            data-testid="stash-save"
          >
            <Save size={12} /> {busy ? "Saving…" : "Save settings"}
          </button>
        </div>
      </div>

      {/* Prize inventory */}
      <div className="loot-card-flat p-5 space-y-3">
        <div className="overline">Prize inventory</div>
        <p className="font-mono text-[10px] text-[#F5F2E8]/55 max-w-2xl">
          Each row below is one real-world prize a hunter can win on a "🎁 Prize" or "🏆 Jackpot" outcome. Two numbers control how it shows up: <strong>Base weight</strong> = how often it gets drawn vs. other prizes, and <strong>Quantity available</strong> = total times it can ever be won (decrements on each win).
        </p>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">
              Prize label
            </label>
            <input className="loot-input" placeholder="e.g. $10 Joe's Pizza gift card" value={newPrize.label} onChange={(e) => setNewPrize({ ...newPrize, label: e.target.value })} maxLength={160} data-testid="stash-prize-label" />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">
              Tier
            </label>
            <select className="loot-input" value={newPrize.kind} onChange={(e) => setNewPrize({ ...newPrize, kind: e.target.value })} data-testid="stash-prize-kind">
              <option value="prize">Regular prize</option>
              <option value="jackpot">Jackpot tier</option>
            </select>
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">
              Value (cents · $1 = 100)
            </label>
            <input className="loot-input" type="number" placeholder="1000 = $10" value={newPrize.value_cents} onChange={(e) => setNewPrize({ ...newPrize, value_cents: parseInt(e.target.value || 0, 10) })} data-testid="stash-prize-value" />
            <p className="font-mono text-[9px] text-[#F5F2E8]/45 mt-1">Counts toward the daily payout cap.</p>
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">
              Quantity available
            </label>
            <input className="loot-input" type="number" placeholder="e.g. 10" value={newPrize.inventory_total} onChange={(e) => setNewPrize({ ...newPrize, inventory_total: parseInt(e.target.value || 0, 10) })} data-testid="stash-prize-qty" />
            <p className="font-mono text-[9px] text-[#F5F2E8]/45 mt-1">Total times this prize can ever be won. Drops to 0 = retired.</p>
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">
              Base weight (1–100)
            </label>
            <input className="loot-input" type="number" min={1} max={10000} placeholder="e.g. 10" value={newPrize.base_weight} onChange={(e) => setNewPrize({ ...newPrize, base_weight: parseInt(e.target.value || 1, 10) })} data-testid="stash-prize-weight" />
            <p className="font-mono text-[9px] text-[#F5F2E8]/45 mt-1">Higher = drawn more often when the "prize" outcome hits. 10 is normal, 50 is featured.</p>
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65 mb-1 block">
              Sponsor name (optional)
            </label>
            <input className="loot-input" placeholder="e.g. Joe's Pizza" value={newPrize.sponsor_name} onChange={(e) => setNewPrize({ ...newPrize, sponsor_name: e.target.value })} maxLength={120} data-testid="stash-prize-sponsor" />
          </div>
        </div>
        <div>
          <div className="overline mb-1 text-[10px]">Prize image (optional)</div>
          <PhotoUploader value={newPrize.image_url} onChange={(url) => setNewPrize({ ...newPrize, image_url: url })} testid="stash-prize-image" />
        </div>
        <button onClick={addPrize} className="btn-loot text-xs inline-flex items-center gap-1" data-testid="stash-add-prize">
          <Plus size={12} /> Add prize
        </button>
        {prizes.length > 0 && (
          <div className="mt-3 space-y-2">
            {prizes.map((p) => (
              <div key={p.id} className="border border-[#F5F2E8]/10 p-3 flex items-center gap-3 flex-wrap" data-testid={`stash-prize-${p.id}`}>
                {p.image_url && <img src={p.image_url} alt="" className="h-10 w-10 object-contain border border-[#F5F2E8]/15" />}
                <div className="flex-1 min-w-[200px]">
                  <div className="font-display uppercase">{p.label}</div>
                  <div className="font-mono text-[10px] text-[#F5F2E8]/55">
                    {p.kind} · ${(p.value_cents / 100).toFixed(2)} · weight {p.base_weight} · {p.inventory_remaining}/{p.inventory_total} left
                    {p.sponsor_name && ` · ${p.sponsor_name}`}
                  </div>
                </div>
                <label className="text-xs flex items-center gap-1">
                  <input type="checkbox" checked={p.active} onChange={(e) => patchPrize(p.id, { active: e.target.checked })} /> Active
                </label>
                <button
                  onClick={() => {
                    const v = window.prompt("New inventory remaining:", String(p.inventory_remaining));
                    if (v != null) patchPrize(p.id, { inventory_remaining: Math.max(0, parseInt(v, 10) || 0) });
                  }}
                  className="btn-ghost text-xs"
                >
                  Restock
                </button>
                <button onClick={() => deletePrize(p.id)} className="btn-ghost text-xs" style={{ color: "#E53935", borderColor: "#E53935" }}>
                  Delete
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Empty-crack sayings */}
      <div className="loot-card-flat p-5 space-y-3" data-testid="stash-sayings-card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="overline">Empty crack sayings</div>
            <p className="font-mono text-[11px] text-[#F5F2E8]/55 max-w-2xl mt-1">
              The funny one-liners shown when a hunter cracks open the crate and gets nothing. Edit, add, delete, or click the button to have the AI write fresh ones in the Local Loot voice.
            </p>
          </div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/55">
            {sayings.length} {sayings.length === 1 ? "saying" : "sayings"}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="number"
            min={1}
            max={25}
            value={genCount}
            onChange={(e) => setGenCount(Math.max(1, Math.min(25, parseInt(e.target.value || 8, 10))))}
            className="loot-input"
            style={{ width: 80 }}
            data-testid="stash-sayings-gen-count"
          />
          <button
            onClick={generateSayings}
            disabled={generatingSayings}
            className="btn-loot text-xs inline-flex items-center gap-1"
            data-testid="stash-sayings-generate"
          >
            <Sparkles size={12} /> {generatingSayings ? "Generating…" : "Generate more sayings"}
          </button>
          <button
            onClick={resetSayings}
            className="btn-ghost text-xs"
            data-testid="stash-sayings-reset"
          >
            Reset to defaults
          </button>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <input
            className="loot-input flex-1 min-w-[240px]"
            placeholder='Add your own (e.g. "Just a raccoon. He waved.")'
            value={newSaying}
            onChange={(e) => setNewSaying(e.target.value)}
            maxLength={200}
            onKeyDown={(e) => { if (e.key === "Enter") addSaying(); }}
            data-testid="stash-sayings-input"
          />
          <button
            onClick={addSaying}
            className="btn-loot text-xs inline-flex items-center gap-1"
            data-testid="stash-sayings-add"
          >
            <Plus size={12} /> Add
          </button>
        </div>

        {sayings.length === 0 ? (
          <div className="font-mono text-xs text-[#F5F2E8]/45 italic">No sayings yet — hit "Reset to defaults" or "Generate more sayings" to seed the bank.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-2 max-h-80 overflow-y-auto pr-1">
            {sayings.map((t, i) => (
              <div
                key={`${i}-${t}`}
                className="flex items-start gap-2 border border-[#F5F2E8]/10 p-2 text-sm"
                data-testid={`stash-saying-${i}`}
              >
                <div className="flex-1">{t}</div>
                <button
                  onClick={() => deleteSaying(t)}
                  className="btn-ghost text-[10px] shrink-0"
                  style={{ color: "#E53935", borderColor: "#E53935" }}
                  data-testid={`stash-saying-delete-${i}`}
                  title="Delete"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Grant Loot Coins */}
      <div className="loot-card-flat p-5 space-y-3" data-testid="stash-grant-card">
        <div className="overline">Grant Loot Coins</div>
        <p className="font-mono text-[11px] text-[#F5F2E8]/55">
          Manually add (or deduct) Loot Coins from a hunter's balance. Useful for rewarding off-platform engagement, fixing bugs, or seeding test accounts. Negative numbers deduct.
        </p>
        <GrantCoinsForm onSubmit={grantCoins} />
      </div>

      {/* History */}
      {history && (
        <div className="loot-card-flat p-5 space-y-2">
          <div className="overline">Recent plays</div>
          <div className="grid grid-cols-3 gap-3">
            <StatCard k="Plays today" v={history.stats?.today_plays || 0} />
            <StatCard k="Real wins today" v={history.stats?.today_real_wins || 0} color="#76B82A" />
            <StatCard k="Today's payout" v={`$${((history.stats?.today_payout_cents || 0) / 100).toFixed(2)}`} color="#D7A62A" />
          </div>
          <div className="overflow-x-auto mt-2">
            <table className="w-full text-xs">
              <thead className="text-left text-[#F5F2E8]/55 font-mono uppercase tracking-widest text-[10px]">
                <tr>
                  <th className="py-1">When</th>
                  <th>User</th>
                  <th>Outcome</th>
                  <th>Prize</th>
                  <th>Coins</th>
                  <th>Streak</th>
                </tr>
              </thead>
              <tbody>
                {history.items.map((p) => (
                  <tr key={p.id} className="border-t border-[#F5F2E8]/5">
                    <td className="py-1.5 font-mono text-[10px] text-[#F5F2E8]/55">
                      {p.played_at ? new Date(p.played_at).toLocaleString() : ""}
                    </td>
                    <td>@{p.username || p.user_id?.slice(-6)}</td>
                    <td>
                      <span style={{
                        color: p.outcome_kind === "jackpot" ? "#D7A62A"
                          : p.outcome_kind === "prize" ? "#76B82A"
                          : p.outcome_kind === "coins" ? "#D7A62A"
                          : p.outcome_kind === "bonus" ? "#76B82A"
                          : "#F5F2E8",
                      }}>
                        {p.outcome_kind}
                      </span>
                      {p.forced_streak_win && <span className="ml-1 text-[#E53935]">🔥</span>}
                      {p.via_coins && <span className="ml-1 text-[#D7A62A]">🪙</span>}
                    </td>
                    <td>{p.prize_label || "—"}</td>
                    <td>{p.coins_awarded || ""}</td>
                    <td>{p.streak_at_play || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function GrantCoinsForm({ onSubmit }) {
  const [username, setUsername] = useState("");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  return (
    <div className="grid md:grid-cols-4 gap-2">
      <input
        className="loot-input"
        placeholder="Username (e.g. jesica)"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        maxLength={80}
        data-testid="stash-grant-username"
      />
      <input
        className="loot-input"
        type="number"
        placeholder="Amount (+/-)"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        data-testid="stash-grant-amount"
      />
      <input
        className="loot-input md:col-span-1"
        placeholder="Reason (optional)"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        maxLength={240}
      />
      <button
        onClick={() => {
          onSubmit(username, amount, reason);
          if (amount) { setAmount(""); setReason(""); }
        }}
        className="btn-loot text-xs inline-flex items-center justify-center gap-1"
        data-testid="stash-grant-submit"
      >
        <Coins size={12} /> Apply
      </button>
    </div>
  );
}

function HollerTab() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState(null);
  const [filter, setFilter] = useState("pending");
  const [editingId, setEditingId] = useState(null);
  const [edit, setEdit] = useState({});
  const [addOpen, setAddOpen] = useState(false);
  const [newH, setNewH] = useState({
    name: "", city: "", title: "", body: "", rating: 5,
    did_win: false, featured: false, photo_url: "", status: "approved",
  });

  const reload = async () => {
    try {
      const url = filter === "all" ? "/admin/holler" : `/admin/holler?status=${filter}`;
      const [list, s] = await Promise.all([
        api.get(url),
        api.get("/admin/holler/settings").catch(() => ({ data: { enabled: true } })),
      ]);
      setItems(list.data?.items || []);
      setStats(list.data?.stats || null);
      setSettings(s.data || { enabled: true });
    } catch {
      setItems([]); setStats(null);
    }
  };
  useEffect(() => { reload(); }, [filter]);

  const toggleEnabled = async () => {
    try {
      const { data } = await api.put("/admin/holler/settings", { enabled: !settings?.enabled });
      setSettings(data);
      toast.success(data.enabled ? "Holler is LIVE" : "Holler paused");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't update");
    }
  };

  const patch = async (id, body) => {
    try {
      await api.patch(`/admin/holler/${id}`, body);
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this holler? This can't be undone.")) return;
    try {
      await api.delete(`/admin/holler/${id}`);
      toast.success("Deleted");
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  const submitManual = async () => {
    if (!newH.name.trim() || newH.body.trim().length < 10) {
      toast.error("Name + at least 10 chars of story.");
      return;
    }
    try {
      await api.post("/admin/holler", {
        name: newH.name.trim(),
        city: newH.city.trim() || null,
        title: newH.title.trim() || null,
        body: newH.body.trim(),
        rating: newH.rating || null,
        photo_url: newH.photo_url || null,
        did_win: !!newH.did_win,
        featured: !!newH.featured,
        status: newH.status,
      });
      toast.success("Holler added");
      setNewH({
        name: "", city: "", title: "", body: "", rating: 5,
        did_win: false, featured: false, photo_url: "", status: "approved",
      });
      setAddOpen(false);
      setFilter(newH.status === "approved" ? "approved" : "pending");
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't add");
    }
  };

  const startEdit = (h) => {
    setEditingId(h.id);
    setEdit({
      name: h.name || "",
      city: h.city || "",
      title: h.title || "",
      body: h.body || "",
      rating: h.rating || 5,
      did_win: !!h.did_win,
    });
  };
  const saveEdit = async () => {
    await patch(editingId, edit);
    setEditingId(null);
    toast.success("Saved");
  };

  return (
    <div className="space-y-4" data-testid="admin-holler">
      <div className="loot-card-flat p-5 space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="overline">Community testimonials</div>
            <h2 className="font-display uppercase text-2xl mt-1">
              The Holler
              <span
                className={`ml-2 inline-block px-2 py-0.5 text-[10px] tracking-widest uppercase ${settings?.enabled ? "bg-[#76B82A]/20 text-[#76B82A]" : "bg-[#E53935]/20 text-[#E53935]"}`}
              >
                {settings?.enabled ? "Live" : "Paused"}
              </span>
            </h2>
            <p className="font-mono text-xs text-[#F5F2E8]/60 mt-1 max-w-2xl">
              Stories hunters submit at <code>/holler</code>. Pending entries don't appear publicly until you approve them.
              Featured ones pin to the top of the wall + appear on the splash strip. When paused, the public page and splash strip are hidden entirely.
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <button
              onClick={toggleEnabled}
              className={`px-4 py-2 font-display uppercase text-sm border-2 ${settings?.enabled ? "border-[#E53935] text-[#E53935]" : "border-[#76B82A] text-[#76B82A]"}`}
              data-testid="holler-toggle"
            >
              {settings?.enabled ? "Pause" : "Go live"}
            </button>
            <button
              onClick={() => setAddOpen((v) => !v)}
              className="btn-loot text-xs inline-flex items-center gap-1"
              data-testid="holler-add-manual-btn"
            >
              <Plus size={12} /> {addOpen ? "Close form" : "Add manually"}
            </button>
          </div>
        </div>

        {addOpen && (
          <div className="pt-3 border-t border-[#F5F2E8]/10 space-y-2" data-testid="holler-manual-form">
            <div className="overline" style={{ color: "#76B82A" }}>Manual add</div>
            <p className="font-mono text-[11px] text-[#F5F2E8]/55">
              Use this for feedback that came via email, DM, or word-of-mouth. Defaults to approved so it appears immediately.
            </p>
            <div className="grid md:grid-cols-2 gap-2">
              <input className="loot-input" placeholder="Name or @handle *" value={newH.name} onChange={(e) => setNewH({ ...newH, name: e.target.value })} maxLength={80} data-testid="holler-manual-name" />
              <input className="loot-input" placeholder="City (optional)" value={newH.city} onChange={(e) => setNewH({ ...newH, city: e.target.value })} maxLength={80} />
            </div>
            <input className="loot-input" placeholder="Headline (optional)" value={newH.title} onChange={(e) => setNewH({ ...newH, title: e.target.value })} maxLength={120} />
            <textarea className="loot-input" rows={3} placeholder="Story (10-1500 chars) *" value={newH.body} onChange={(e) => setNewH({ ...newH, body: e.target.value })} maxLength={1500} data-testid="holler-manual-body" />
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-1">
                <span className="font-mono text-xs text-[#F5F2E8]/55">Stars:</span>
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" onClick={() => setNewH({ ...newH, rating: n })} className="p-0.5">
                    <span style={{ color: n <= newH.rating ? "#D7A62A" : "rgba(245,242,232,0.4)", fontSize: 18 }}>★</span>
                  </button>
                ))}
              </div>
              <label className="flex items-center gap-1 text-xs">
                <input type="checkbox" checked={newH.did_win} onChange={(e) => setNewH({ ...newH, did_win: e.target.checked })} /> Lootmaker 🏆
              </label>
              <label className="flex items-center gap-1 text-xs">
                <input type="checkbox" checked={newH.featured} onChange={(e) => setNewH({ ...newH, featured: e.target.checked })} /> Feature ★
              </label>
              <select className="loot-input text-xs" value={newH.status} onChange={(e) => setNewH({ ...newH, status: e.target.value })}>
                <option value="approved">Publish (approved)</option>
                <option value="pending">Save as pending</option>
              </select>
            </div>
            <div>
              <div className="overline mb-1 text-[10px]">Photo (optional)</div>
              <PhotoUploader value={newH.photo_url} onChange={(url) => setNewH({ ...newH, photo_url: url })} testid="holler-manual-photo" />
            </div>
            <button onClick={submitManual} className="btn-loot text-xs inline-flex items-center gap-1" data-testid="holler-manual-submit">
              <Plus size={12} /> Add holler
            </button>
          </div>
        )}

        {stats && (
          <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-[#F5F2E8]/10">
            <FilterPillSimple active={filter === "pending"} onClick={() => setFilter("pending")} color="#D7A62A">
              Pending {stats.pending}
            </FilterPillSimple>
            <FilterPillSimple active={filter === "approved"} onClick={() => setFilter("approved")} color="#76B82A">
              Approved {stats.approved}
            </FilterPillSimple>
            <FilterPillSimple active={filter === "rejected"} onClick={() => setFilter("rejected")} color="#E53935">
              Rejected {stats.rejected}
            </FilterPillSimple>
            <FilterPillSimple active={filter === "all"} onClick={() => setFilter("all")} color="#F5F2E8">
              All
            </FilterPillSimple>
          </div>
        )}
      </div>

      {items.length === 0 ? (
        <div className="loot-card-flat p-10 text-center text-[#F5F2E8]/55 text-sm">
          No {filter === "all" ? "" : filter} hollers.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((h) => {
            const isEditing = editingId === h.id;
            return (
              <div
                key={h.id}
                className={`loot-card-flat p-4 ${h.featured ? "ring-2 ring-[#D7A62A]" : ""}`}
                data-testid={`holler-row-${h.id}`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-display uppercase text-lg">{h.name}</span>
                      {h.city && <span className="font-mono text-[10px] text-[#F5F2E8]/55">· {h.city}</span>}
                      <StatusPill status={h.status} />
                      {h.did_win && (
                        <span className="font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border" style={{ color: "#76B82A", borderColor: "#76B82A" }}>
                          🏆 Lootmaker
                        </span>
                      )}
                      {h.featured && (
                        <span className="font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5" style={{ background: "#D7A62A", color: "#0F0F0F" }}>
                          ★ Featured
                        </span>
                      )}
                      {h.rating && (
                        <span className="font-mono text-[10px] text-[#D7A62A]">
                          {"★".repeat(h.rating)}{"☆".repeat(5 - h.rating)}
                        </span>
                      )}
                    </div>
                    {h.title && <div className="font-display uppercase text-sm mt-1">{h.title}</div>}
                  </div>
                  <div className="font-mono text-[10px] text-[#F5F2E8]/45 whitespace-nowrap">
                    {h.created_at ? new Date(h.created_at).toLocaleDateString() : ""}
                  </div>
                </div>

                {h.photo_url && !isEditing && (
                  <img src={h.photo_url} alt="" className="mt-2 max-h-40 object-contain border border-[#F5F2E8]/10" />
                )}

                {isEditing ? (
                  <div className="mt-3 grid md:grid-cols-2 gap-2">
                    <input className="loot-input" placeholder="Name" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} />
                    <input className="loot-input" placeholder="City" value={edit.city} onChange={(e) => setEdit({ ...edit, city: e.target.value })} />
                    <input className="loot-input md:col-span-2" placeholder="Title" value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} />
                    <textarea
                      className="loot-input md:col-span-2"
                      rows={4}
                      value={edit.body}
                      onChange={(e) => setEdit({ ...edit, body: e.target.value })}
                      maxLength={1500}
                    />
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-[#F5F2E8]/55">Stars:</span>
                      {[1, 2, 3, 4, 5].map((n) => (
                        <button key={n} type="button" onClick={() => setEdit({ ...edit, rating: n })}>
                          <span style={{ color: n <= edit.rating ? "#D7A62A" : "rgba(245,242,232,0.4)" }}>★</span>
                        </button>
                      ))}
                    </div>
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={!!edit.did_win} onChange={(e) => setEdit({ ...edit, did_win: e.target.checked })} />
                      <span>Marked as lootmaker</span>
                    </label>
                  </div>
                ) : (
                  <p className="text-sm text-[#F5F2E8]/85 mt-2 leading-relaxed whitespace-pre-line">
                    “{h.body}”
                  </p>
                )}

                <div className="mt-3 pt-3 border-t border-[#F5F2E8]/10 flex items-center gap-2 flex-wrap">
                  {isEditing ? (
                    <>
                      <button onClick={saveEdit} className="btn-loot text-xs" data-testid={`holler-save-${h.id}`}>
                        Save
                      </button>
                      <button onClick={() => setEditingId(null)} className="btn-ghost text-xs">Cancel</button>
                    </>
                  ) : (
                    <>
                      {h.status !== "approved" && (
                        <button
                          onClick={() => patch(h.id, { status: "approved" })}
                          className="btn-loot text-xs"
                          data-testid={`holler-approve-${h.id}`}
                        >
                          ✓ Approve
                        </button>
                      )}
                      {h.status !== "rejected" && (
                        <button
                          onClick={() => patch(h.id, { status: "rejected" })}
                          className="btn-ghost text-xs"
                          style={{ color: "#E53935", borderColor: "#E53935" }}
                          data-testid={`holler-reject-${h.id}`}
                        >
                          ✗ Reject
                        </button>
                      )}
                      <button
                        onClick={() => patch(h.id, { featured: !h.featured })}
                        className="btn-ghost text-xs"
                        style={{ color: "#D7A62A", borderColor: "#D7A62A" }}
                        data-testid={`holler-feature-${h.id}`}
                      >
                        {h.featured ? "Unfeature" : "★ Feature"}
                      </button>
                      <button onClick={() => startEdit(h)} className="btn-ghost text-xs">
                        Edit
                      </button>
                      <button
                        onClick={() => remove(h.id)}
                        className="btn-ghost text-xs ml-auto"
                        style={{ color: "#E53935", borderColor: "#E53935" }}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function FilterPillSimple({ active, onClick, color, children }) {
  return (
    <button
      onClick={onClick}
      className="px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest border"
      style={{
        color: active ? "#0F0F0F" : color,
        background: active ? color : "transparent",
        borderColor: color,
      }}
    >
      {children}
    </button>
  );
}

function StatusPill({ status }) {
  const map = {
    pending:  { c: "#D7A62A", l: "Pending" },
    approved: { c: "#76B82A", l: "Approved" },
    rejected: { c: "#E53935", l: "Rejected" },
  };
  const m = map[status] || map.pending;
  return (
    <span
      className="font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border"
      style={{ color: m.c, borderColor: m.c }}
    >
      {m.l}
    </span>
  );
}

function PushTab() {
  const [stats, setStats] = useState(null);
  const [form, setForm] = useState({ title: "", body: "", url: "/", city: "" });
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    try {
      const { data } = await api.get("/admin/push/stats");
      setStats(data);
    } catch {
      setStats(null);
    }
  };
  useEffect(() => { reload(); }, []);

  const sendTest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/push/test", {
        title: "Test push 🔔",
        body: "If you can read this, push notifications work.",
      });
      toast.success(`Test sent (${data.sent}/${data.total}).`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Test failed");
    } finally { setBusy(false); }
  };

  const sendBroadcast = async () => {
    if (!form.title.trim() || !form.body.trim()) {
      toast.error("Title and body are required.");
      return;
    }
    if (!window.confirm(
      `Send this push to ${form.city ? `everyone subscribed to ${form.city}` : "everyone subscribed"}? This can't be undone.`
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/push/broadcast", {
        title: form.title.trim(),
        body: form.body.trim(),
        url: form.url || "/",
        city: form.city.trim() || null,
        tag: "admin-broadcast",
      });
      toast.success(`Sent to ${data.sent} devices (${data.failed} failed).`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Broadcast failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="admin-push">
      <div className="loot-card-flat p-5 space-y-3">
        <div>
          <div className="overline">Web push notifications</div>
          <h2 className="font-display uppercase text-2xl mt-1">
            Subscribers
          </h2>
          <p className="font-mono text-xs text-[#F5F2E8]/60 mt-1 max-w-2xl">
            Every hunter who taps "Enable notifications" on{" "}
            <code>/notify</code> or their profile shows up here. Drops automatically ping subscribers in
            the drop's city when they go live — this tab lets you send manual pushes too.
          </p>
        </div>
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard k="Total subs" v={stats.total_subscriptions} color="#76B82A" />
            <StatCard k="Signed-in" v={stats.linked_to_user} />
            <StatCard k="Anonymous" v={stats.anonymous} color="#D7A62A" />
            <StatCard k="Cities" v={(stats.by_city || []).length} />
          </div>
        )}
        {stats && stats.by_city && stats.by_city.length > 0 && (
          <div className="flex flex-wrap gap-2 pt-2 border-t border-[#F5F2E8]/10">
            {stats.by_city.map((c) => (
              <span key={c.city} className="tag-pill text-xs">
                {c.city} · <span className="text-[#76B82A] ml-1">{c.count}</span>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="loot-card-flat p-5 space-y-3">
        <div className="overline">Send a manual push</div>
        <p className="font-mono text-[11px] text-[#F5F2E8]/55">
          Use this for one-off announcements — Golden Drop teasers, community events, weather changes.
          Drops that go live broadcast automatically.
        </p>
        <div className="grid md:grid-cols-2 gap-2">
          <input
            className="loot-input"
            placeholder="Title (120 char max)"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            maxLength={120}
            data-testid="push-title"
          />
          <input
            className="loot-input"
            placeholder="City (blank = everyone)"
            value={form.city}
            onChange={(e) => setForm({ ...form, city: e.target.value })}
            data-testid="push-city"
          />
        </div>
        <textarea
          className="loot-input"
          rows={2}
          placeholder="Message body (300 char max)"
          value={form.body}
          onChange={(e) => setForm({ ...form, body: e.target.value })}
          maxLength={300}
          data-testid="push-body"
        />
        <input
          className="loot-input"
          placeholder="Link destination (e.g. /drops or /golden)"
          value={form.url}
          onChange={(e) => setForm({ ...form, url: e.target.value })}
          data-testid="push-url"
        />
        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-[#F5F2E8]/10">
          <button
            onClick={sendBroadcast}
            disabled={busy}
            className="btn-loot text-xs inline-flex items-center gap-1"
            data-testid="push-broadcast-btn"
          >
            <Send size={12} /> {busy ? "Sending…" : "Send broadcast"}
          </button>
          <button
            onClick={sendTest}
            disabled={busy}
            className="btn-ghost text-xs inline-flex items-center gap-1"
            data-testid="push-test-btn"
            title="Sends a test push to your own device (if you've enabled push)"
          >
            <BellIcon size={12} /> Test on my device
          </button>
          <button
            onClick={reload}
            className="btn-ghost text-xs inline-flex items-center gap-1"
          >
            <RefreshCw size={12} /> Refresh stats
          </button>
        </div>
      </div>
    </div>
  );
}

function EventPromoTab() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    try {
      const { data } = await api.get("/admin/event-promo");
      setS(data || {});
    } catch {
      setS({});
    }
  };
  useEffect(() => { reload(); }, []);

  const update = (k, v) => setS((prev) => ({ ...(prev || {}), [k]: v }));

  const save = async () => {
    if (!s) return;
    setBusy(true);
    try {
      const payload = {
        enabled: !!s.enabled,
        flyer_url: s.flyer_url || "",
        headline: s.headline || "",
        subheadline: s.subheadline || "",
        cta_label: s.cta_label || "",
        cta_url: s.cta_url || "",
        event_date: s.event_date || "",
      };
      const { data } = await api.put("/admin/event-promo", payload);
      setS(data);
      toast.success("Event Promo saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Clear all Event Promo settings?")) return;
    try {
      const { data } = await api.post("/admin/event-promo/reset");
      setS(data);
      toast.success("Event Promo reset");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Reset failed");
    }
  };

  if (!s) return <div className="text-[#F5F2E8]/60">Loading…</div>;

  const expired = !!s.expired;
  const live = !!s.enabled && !!s.flyer_url && !expired;

  return (
    <div className="space-y-4" data-testid="admin-event-promo">
      <div className="loot-card-flat p-5 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="overline">Splash page popup</div>
            <h2 className="font-display uppercase text-2xl mt-1">Event Promo</h2>
            <p className="font-mono text-xs text-[#F5F2E8]/60 mt-1 max-w-xl">
              Toggle a promo popup on the splash page. Upload your flyer image, optionally add a headline + CTA. Auto-hides after the event date so you don't have to remember to flip it off.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {live && (
              <span className="tag-pill text-[10px]" style={{ color: "#76B82A", borderColor: "#76B82A" }}>
                LIVE
              </span>
            )}
            {!live && s.enabled && expired && (
              <span className="tag-pill text-[10px]" style={{ color: "#E53935", borderColor: "#E53935" }}>
                EXPIRED — past event_date
              </span>
            )}
            {!s.enabled && (
              <span className="tag-pill text-[10px]" style={{ color: "#F5F2E8", borderColor: "#F5F2E8" }}>
                OFF
              </span>
            )}
            {!s.flyer_url && s.enabled && (
              <span className="tag-pill text-[10px]" style={{ color: "#D7A62A", borderColor: "#D7A62A" }}>
                NEEDS FLYER
              </span>
            )}
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={!!s.enabled}
            onChange={(e) => update("enabled", e.target.checked)}
            data-testid="ep-enabled"
          />
          <span>Show the popup on the splash page</span>
        </label>

        <div>
          <div className="overline mb-1">Flyer image</div>
          <p className="font-mono text-[11px] text-[#F5F2E8]/50 mb-2">
            Square flyers (1080×1080) work best — they show full-bleed without cropping on mobile.
          </p>
          <PhotoUploader
            value={s.flyer_url || ""}
            onChange={(url) => update("flyer_url", url)}
            testid="ep-flyer"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <div className="overline mb-1">Headline (optional)</div>
            <input
              className="loot-input"
              placeholder="3 Cities. One Epic Day."
              value={s.headline || ""}
              onChange={(e) => update("headline", e.target.value)}
              maxLength={160}
              data-testid="ep-headline"
            />
          </div>
          <div>
            <div className="overline mb-1">Event date (auto-hide)</div>
            <input
              className="loot-input"
              type="date"
              value={s.event_date || ""}
              onChange={(e) => update("event_date", e.target.value)}
              data-testid="ep-date"
            />
            <p className="font-mono text-[10px] text-[#F5F2E8]/45 mt-1">
              Popup auto-hides after this date passes. Leave blank for "show until I turn it off."
            </p>
          </div>
        </div>

        <div>
          <div className="overline mb-1">Subheadline (optional)</div>
          <textarea
            className="loot-input"
            rows={2}
            placeholder="Friday, May 1st · Bristol · Kingsport · Johnson City"
            value={s.subheadline || ""}
            onChange={(e) => update("subheadline", e.target.value)}
            maxLength={400}
            data-testid="ep-subheadline"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <div className="overline mb-1">CTA button label (optional)</div>
            <input
              className="loot-input"
              placeholder="Get clue alerts"
              value={s.cta_label || ""}
              onChange={(e) => update("cta_label", e.target.value)}
              maxLength={60}
              data-testid="ep-cta-label"
            />
          </div>
          <div>
            <div className="overline mb-1">CTA URL</div>
            <input
              className="loot-input"
              placeholder="/drops or https://example.com"
              value={s.cta_url || ""}
              onChange={(e) => update("cta_url", e.target.value)}
              maxLength={2000}
              data-testid="ep-cta-url"
            />
            <p className="font-mono text-[10px] text-[#F5F2E8]/45 mt-1">
              Internal: <code>/drops</code>, <code>/vote</code>, <code>/hunts/Kingsport</code>. External: full https URL.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-[#F5F2E8]/10">
          <button
            onClick={save}
            disabled={busy}
            className="btn-loot text-xs inline-flex items-center gap-1"
            data-testid="ep-save"
          >
            <Save size={12} /> {busy ? "Saving…" : "Save"}
          </button>
          <button
            onClick={reset}
            className="btn-ghost text-xs"
            style={{ color: "#E53935", borderColor: "#E53935" }}
            data-testid="ep-reset"
          >
            <Trash2 size={12} className="inline" /> Reset all
          </button>
          <span className="ml-auto font-mono text-[10px] text-[#F5F2E8]/45">
            Splash visitors will see the popup until they close it (per visit).
          </span>
        </div>
      </div>

      {s.flyer_url && (
        <div className="loot-card-flat p-4 space-y-2">
          <div className="overline">Live preview</div>
          <p className="font-mono text-[11px] text-[#F5F2E8]/55">
            This is what splash visitors see when the popup is live.
          </p>
          <div className="border-2 border-[#76B82A] bg-black p-3 max-w-[420px] mx-auto">
            <img
              src={s.flyer_url}
              alt="flyer preview"
              className="block w-full h-auto"
              style={{ maxHeight: 480, objectFit: "contain", background: "#000" }}
            />
            {(s.headline || s.subheadline) && (
              <div className="text-center pt-3">
                {s.headline && (
                  <div className="font-display uppercase text-lg leading-tight">{s.headline}</div>
                )}
                {s.subheadline && (
                  <div className="font-mono text-[11px] text-[#F5F2E8]/70 mt-1">{s.subheadline}</div>
                )}
              </div>
            )}
            {s.cta_label && s.cta_url && (
              <div className="mt-3 text-center">
                <span className="inline-block px-4 py-2 bg-[#76B82A] text-black font-display uppercase text-sm tracking-wider">
                  {s.cta_label}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SplashTab() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    try { const { data } = await api.get("/splash/settings"); setS(data || {}); }
    catch { setS({}); }
  };
  useEffect(() => { reload(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/splash/settings", s);
      setS(data);
      toast.success("Splash page updated — view it at /");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save");
    } finally { setBusy(false); }
  };

  const reset = async () => {
    if (!confirm("Reset the splash page to factory defaults? You'll lose every text & image override.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/splash/reset");
      setS(data);
      toast.success("Splash reset to defaults");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't reset");
    } finally { setBusy(false); }
  };

  if (!s) return <div className="loot-card-flat p-10 text-center text-[#F5F2E8]/40">Loading…</div>;

  const u = (k, v) => setS({ ...s, [k]: v });
  const upd = (key, i, k, v) => {
    const arr = [...(s[key] || [])];
    arr[i] = { ...arr[i], [k]: v };
    u(key, arr);
  };
  const add = (key, item) => u(key, [...(s[key] || []), item]);
  const removeAt = (key, i) => {
    const arr = [...(s[key] || [])];
    arr.splice(i, 1);
    u(key, arr);
  };

  const Section = ({ title, children, action }) => (
    <div className="loot-card-flat p-5 space-y-3">
      <div className="flex items-center justify-between">
        <div className="overline">{title}</div>
        {action}
      </div>
      {children}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="splash-page-editor">
      {/* Top bar */}
      <div className="loot-card-flat p-5 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="overline">Splash page</div>
          <h2 className="font-display uppercase text-3xl mt-1">Edit the front door</h2>
          <p className="text-[#F5F2E8]/60 mt-1 text-sm">
            Every text string, image, and link below renders live at <code className="font-mono text-[#76B82A]">/</code>. Save → refresh the splash to see it.
          </p>
        </div>
        <div className="flex gap-2">
          <a href="/" target="_blank" rel="noopener noreferrer" className="btn-ghost text-xs inline-flex items-center gap-1">Open splash ↗</a>
          <button onClick={reset} disabled={busy} className="btn-ghost text-xs" style={{ color: "#E53935", borderColor: "#E53935" }} data-testid="splash-reset">Reset to defaults</button>
        </div>
      </div>

      {/* Header / brand */}
      <Section title="Header / brand">
        <ImageField label="Logo image (PNG/SVG with transparent bg)" value={s.logo_image_url} onChange={(v) => u("logo_image_url", v)} testid="se-logo" />
        <Field label="Logo alt text"><input className="loot-input" value={s.logo_alt || ""} onChange={(e) => u("logo_alt", e.target.value)} /></Field>
        <div className="border-t border-[#F5F2E8]/10 pt-3">
          <div className="flex items-center justify-between">
            <div className="overline">Nav links · {(s.nav_links || []).length}</div>
            <button onClick={() => add("nav_links", { label: "New", href: "/" })} className="btn-ghost text-xs inline-flex items-center gap-1"><Plus size={12} /> Add link</button>
          </div>
          {(s.nav_links || []).map((l, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 mt-2">
              <input className="loot-input col-span-5" placeholder="Label" value={l.label || ""} onChange={(e) => upd("nav_links", i, "label", e.target.value)} />
              <input className="loot-input col-span-6" placeholder="/href" value={l.href || ""} onChange={(e) => upd("nav_links", i, "href", e.target.value)} />
              <button onClick={() => removeAt("nav_links", i)} className="text-[#E53935] hover:opacity-80 col-span-1 flex items-center justify-center"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </Section>

      {/* Hero */}
      <Section title="Hero">
        <ImageField label="Hero background photo (sunset town street)" hint="Renders behind the dark overlay. 1920×1080+ looks best." value={s.hero_bg_image_url} onChange={(v) => u("hero_bg_image_url", v)} testid="se-hero-bg" />
        <ImageField label="Hero card image (rendered inside the floating card mockup)" hint="Use the official logo or a winner-card design." value={s.hero_card_image_url} onChange={(v) => u("hero_card_image_url", v)} testid="se-hero-card" />
        <div className="grid md:grid-cols-3 gap-3">
          <Field label="Headline line 1 (white)"><input className="loot-input" value={s.hero_headline_line1 || ""} onChange={(e) => u("hero_headline_line1", e.target.value)} data-testid="se-h1" /></Field>
          <Field label="Headline line 2 (white)"><input className="loot-input" value={s.hero_headline_line2 || ""} onChange={(e) => u("hero_headline_line2", e.target.value)} /></Field>
          <Field label="Headline line 3 (green)"><input className="loot-input" value={s.hero_headline_line3 || ""} onChange={(e) => u("hero_headline_line3", e.target.value)} /></Field>
        </div>
        <Field label="Hero kicker (one bold line)"><input className="loot-input" value={s.hero_kicker || ""} onChange={(e) => u("hero_kicker", e.target.value)} /></Field>
        <Field label="Hero body paragraph"><textarea className="loot-input" rows={3} value={s.hero_body || ""} onChange={(e) => u("hero_body", e.target.value)} /></Field>
        <Field label="Tagline (green script under badges)"><input className="loot-input" value={s.hero_tagline || ""} onChange={(e) => u("hero_tagline", e.target.value)} /></Field>

        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Primary CTA label"><input className="loot-input" value={s.hero_primary_cta_label || ""} onChange={(e) => u("hero_primary_cta_label", e.target.value)} /></Field>
          <Field label="Primary CTA href"><input className="loot-input" value={s.hero_primary_cta_href || ""} onChange={(e) => u("hero_primary_cta_href", e.target.value)} /></Field>
          <Field label="Secondary CTA label"><input className="loot-input" value={s.hero_secondary_cta_label || ""} onChange={(e) => u("hero_secondary_cta_label", e.target.value)} /></Field>
          <Field label="Secondary CTA href"><input className="loot-input" value={s.hero_secondary_cta_href || ""} onChange={(e) => u("hero_secondary_cta_href", e.target.value)} /></Field>
        </div>

        <div className="border-t border-[#F5F2E8]/10 pt-3">
          <div className="flex items-center justify-between">
            <div className="overline">Trust badges · {(s.hero_trust_badges || []).length}</div>
            <button onClick={() => add("hero_trust_badges", { label: "New", icon: "Sparkles" })} className="btn-ghost text-xs inline-flex items-center gap-1"><Plus size={12} /> Add badge</button>
          </div>
          {(s.hero_trust_badges || []).map((b, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 mt-2 items-center">
              <select className="loot-input col-span-3" value={b.icon || "Sparkles"} onChange={(e) => upd("hero_trust_badges", i, "icon", e.target.value)}>
                {ICON_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <input className="loot-input col-span-8" placeholder="Label" value={b.label || ""} onChange={(e) => upd("hero_trust_badges", i, "label", e.target.value)} />
              <button onClick={() => removeAt("hero_trust_badges", i)} className="text-[#E53935] hover:opacity-80 col-span-1 flex items-center justify-center"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>

        <div className="border-t border-[#F5F2E8]/10 pt-3">
          <div className="flex items-center justify-between">
            <div className="overline">Right-side chalk lines · {(s.hero_chalk_lines || []).length}</div>
            <button onClick={() => add("hero_chalk_lines", "New line.")} className="btn-ghost text-xs inline-flex items-center gap-1"><Plus size={12} /> Add line</button>
          </div>
          {(s.hero_chalk_lines || []).map((line, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 mt-2 items-center">
              <input className="loot-input col-span-11" value={line || ""} onChange={(e) => {
                const arr = [...(s.hero_chalk_lines || [])]; arr[i] = e.target.value; u("hero_chalk_lines", arr);
              }} />
              <button onClick={() => removeAt("hero_chalk_lines", i)} className="text-[#E53935] hover:opacity-80 col-span-1 flex items-center justify-center"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </Section>

      {/* How it works */}
      <Section title={`How it works · ${(s.how_steps || []).length} steps`} action={
        <button onClick={() => add("how_steps", { n: String((s.how_steps || []).length + 1), title: "New step", body: "", icon: "Sparkles", bullets: [], tip: "" })} className="btn-ghost text-xs inline-flex items-center gap-1"><Plus size={12} /> Add step</button>
      }>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Kicker (small italic line above heading)"><input className="loot-input" value={s.how_overline_kicker || ""} onChange={(e) => u("how_overline_kicker", e.target.value)} /></Field>
          <Field label="Section heading"><input className="loot-input" value={s.how_overline || ""} onChange={(e) => u("how_overline", e.target.value)} /></Field>
        </div>
        <Field label="Intro paragraph (under the heading)"><textarea className="loot-input" rows={2} value={s.how_intro || ""} onChange={(e) => u("how_intro", e.target.value)} /></Field>
        {(s.how_steps || []).map((step, i) => {
          const bullets = Array.isArray(step.bullets) ? step.bullets : [];
          const setStepBullets = (next) => upd("how_steps", i, "bullets", next);
          return (
            <div key={i} className="border border-[#F5F2E8]/10 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div className="font-mono text-[10px] uppercase tracking-wider text-[#76B82A]">Step {i + 1}</div>
                <button onClick={() => removeAt("how_steps", i)} className="text-[#E53935] hover:opacity-80"><Trash2 size={14} /></button>
              </div>
              <div className="grid grid-cols-12 gap-2">
                <input className="loot-input col-span-2" placeholder="N (1)" value={step.n || ""} onChange={(e) => upd("how_steps", i, "n", e.target.value)} />
                <select className="loot-input col-span-3" value={step.icon || "Sparkles"} onChange={(e) => upd("how_steps", i, "icon", e.target.value)}>
                  {ICON_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
                <input className="loot-input col-span-7" placeholder="Title" value={step.title || ""} onChange={(e) => upd("how_steps", i, "title", e.target.value)} />
              </div>
              <textarea className="loot-input" rows={2} placeholder="Body paragraph" value={step.body || ""} onChange={(e) => upd("how_steps", i, "body", e.target.value)} />
              <div className="border-t border-[#F5F2E8]/10 pt-2">
                <div className="flex items-center justify-between mb-1">
                  <div className="font-mono text-[10px] uppercase tracking-wider text-[#F5F2E8]/55">Bullets · {bullets.length}</div>
                  <button onClick={() => setStepBullets([...bullets, ""])} className="btn-ghost text-xs inline-flex items-center gap-1"><Plus size={12} /> Add bullet</button>
                </div>
                {bullets.map((b, bi) => (
                  <div key={bi} className="grid grid-cols-12 gap-2 mt-1.5">
                    <input className="loot-input col-span-11" placeholder="Bullet text" value={b || ""} onChange={(e) => {
                      const next = [...bullets]; next[bi] = e.target.value; setStepBullets(next);
                    }} />
                    <button onClick={() => {
                      const next = [...bullets]; next.splice(bi, 1); setStepBullets(next);
                    }} className="text-[#E53935] hover:opacity-80 col-span-1 flex items-center justify-center"><Trash2 size={14} /></button>
                  </div>
                ))}
              </div>
              <Field label="💡 Tip (script line under bullets, optional)">
                <input className="loot-input" value={step.tip || ""} onChange={(e) => upd("how_steps", i, "tip", e.target.value)} />
              </Field>
            </div>
          );
        })}
        <Field label="Banner pill (under steps)"><input className="loot-input" value={s.how_banner || ""} onChange={(e) => u("how_banner", e.target.value)} /></Field>
        <div className="grid md:grid-cols-2 gap-3 border-t border-[#F5F2E8]/10 pt-3">
          <Field label="More-info button label"><input className="loot-input" value={s.how_more_label || ""} onChange={(e) => u("how_more_label", e.target.value)} /></Field>
          <Field label="More-info button href"><input className="loot-input" value={s.how_more_href || ""} onChange={(e) => u("how_more_href", e.target.value)} /></Field>
        </div>
      </Section>

      {/* Claim CTA */}
      <Section title="Claim my loot CTA">
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Overline"><input className="loot-input" value={s.claim_overline || ""} onChange={(e) => u("claim_overline", e.target.value)} /></Field>
          <Field label="Headline (last word colored green)"><input className="loot-input" value={s.claim_headline || ""} onChange={(e) => u("claim_headline", e.target.value)} /></Field>
        </div>
        <Field label="Subhead"><textarea className="loot-input" rows={2} value={s.claim_subhead || ""} onChange={(e) => u("claim_subhead", e.target.value)} /></Field>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="CTA label"><input className="loot-input" value={s.claim_cta_label || ""} onChange={(e) => u("claim_cta_label", e.target.value)} /></Field>
          <Field label="CTA href"><input className="loot-input" value={s.claim_cta_href || ""} onChange={(e) => u("claim_cta_href", e.target.value)} /></Field>
        </div>
      </Section>

      {/* Why */}
      <Section title="Why The Local Loot exists (founder)">
        <ImageField label="Founder photo (Mickey's back-shot looks best)" value={s.why_image_url} onChange={(v) => u("why_image_url", v)} testid="se-why-img" />
        <Field label="Section heading"><input className="loot-input" value={s.why_overline || ""} onChange={(e) => u("why_overline", e.target.value)} /></Field>
        <Field label="Intro line (green script)"><input className="loot-input" value={s.why_intro || ""} onChange={(e) => u("why_intro", e.target.value)} /></Field>
        <Field label="Body paragraph"><textarea className="loot-input" rows={4} value={s.why_body || ""} onChange={(e) => u("why_body", e.target.value)} /></Field>
        <div className="border-t border-[#F5F2E8]/10 pt-3">
          <div className="flex items-center justify-between">
            <div className="overline">Gold icons · {(s.why_icons || []).length}</div>
            <button onClick={() => add("why_icons", { label: "New", icon: "Sparkles" })} className="btn-ghost text-xs inline-flex items-center gap-1"><Plus size={12} /> Add icon</button>
          </div>
          {(s.why_icons || []).map((it, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 mt-2 items-center">
              <select className="loot-input col-span-3" value={it.icon || "Sparkles"} onChange={(e) => upd("why_icons", i, "icon", e.target.value)}>
                {ICON_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <input className="loot-input col-span-8" placeholder="Label" value={it.label || ""} onChange={(e) => upd("why_icons", i, "label", e.target.value)} />
              <button onClick={() => removeAt("why_icons", i)} className="text-[#E53935] hover:opacity-80 col-span-1 flex items-center justify-center"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
        <div className="grid md:grid-cols-2 gap-3 border-t border-[#F5F2E8]/10 pt-3">
          <Field label="Side-quote heading"><input className="loot-input" value={s.why_quote_title || ""} onChange={(e) => u("why_quote_title", e.target.value)} /></Field>
          <Field label="Side-quote body"><textarea className="loot-input" rows={3} value={s.why_quote_body || ""} onChange={(e) => u("why_quote_body", e.target.value)} /></Field>
        </div>
      </Section>

      {/* Live ticker */}
      <Section title={`Live ticker · ${(s.ticker_items || []).length} cells`} action={
        <button onClick={() => add("ticker_items", { key: "online", label: "label" })} className="btn-ghost text-xs inline-flex items-center gap-1"><Plus size={12} /> Add cell</button>
      }>
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={s.ticker_show !== false} onChange={(e) => u("ticker_show", e.target.checked)} />
          Show live ticker
        </label>
        <Field label="Overline"><input className="loot-input" value={s.ticker_overline || ""} onChange={(e) => u("ticker_overline", e.target.value)} /></Field>
        <div className="font-mono text-[11px] text-[#F5F2E8]/55">
          Available keys: <code className="text-[#76B82A]">online · hunters_total · hunters_this_week · drops_live · drops_total · winners_total</code>
        </div>
        {(s.ticker_items || []).map((it, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 mt-2 items-center">
            <select className="loot-input col-span-3" value={it.key || "online"} onChange={(e) => upd("ticker_items", i, "key", e.target.value)}>
              {["online","hunters_total","hunters_this_week","drops_live","drops_total","winners_total"].map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
            <input className="loot-input col-span-8" placeholder="Label" value={it.label || ""} onChange={(e) => upd("ticker_items", i, "label", e.target.value)} />
            <button onClick={() => removeAt("ticker_items", i)} className="text-[#E53935] hover:opacity-80 col-span-1 flex items-center justify-center"><Trash2 size={14} /></button>
          </div>
        ))}
        <div className="grid md:grid-cols-2 gap-3 border-t border-[#F5F2E8]/10 pt-3">
          <Field label="CTA label"><input className="loot-input" value={s.ticker_cta_label || ""} onChange={(e) => u("ticker_cta_label", e.target.value)} /></Field>
          <Field label="CTA href"><input className="loot-input" value={s.ticker_cta_href || ""} onChange={(e) => u("ticker_cta_href", e.target.value)} /></Field>
        </div>
        <div className="border-t border-[#F5F2E8]/10 pt-3 space-y-2">
          <div className="overline">Countdown chip</div>
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={s.ticker_countdown_show !== false} onChange={(e) => u("ticker_countdown_show", e.target.checked)} />
            Show "Next drop in HH:MM:SS" countdown when a scheduled drop is approaching
          </label>
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Countdown overline"><input className="loot-input" value={s.ticker_countdown_overline || ""} onChange={(e) => u("ticker_countdown_overline", e.target.value)} /></Field>
            <Field label="Idle label (when no drop is scheduled)"><input className="loot-input" value={s.ticker_countdown_idle_label || ""} onChange={(e) => u("ticker_countdown_idle_label", e.target.value)} /></Field>
          </div>
        </div>
      </Section>

      {/* Recent winners */}
      <Section title={`Recent winners fallback · ${(s.winners_fallback || []).length} cards`} action={
        <button onClick={() => add("winners_fallback", { name: "New", town: "", prize: "", sponsor: "", photo_url: "" })} className="btn-ghost text-xs inline-flex items-center gap-1"><Plus size={12} /> Add winner</button>
      }>
        <div className="font-mono text-[11px] text-[#F5F2E8]/55">
          The carousel auto-fills with real approved winners from <code className="text-[#76B82A]">/api/winners</code>; if fewer than 6 exist these placeholder cards fill the rest. They go away as soon as you have enough real winners.
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Heading"><input className="loot-input" value={s.winners_overline || ""} onChange={(e) => u("winners_overline", e.target.value)} /></Field>
          <Field label="View-all label"><input className="loot-input" value={s.winners_view_all_label || ""} onChange={(e) => u("winners_view_all_label", e.target.value)} /></Field>
          <Field label="View-all href"><input className="loot-input" value={s.winners_view_all_href || ""} onChange={(e) => u("winners_view_all_href", e.target.value)} /></Field>
        </div>
        {(s.winners_fallback || []).map((w, i) => (
          <div key={i} className="border border-[#F5F2E8]/10 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <div className="font-mono text-[10px] uppercase tracking-wider text-[#76B82A]">Winner {i + 1}</div>
              <button onClick={() => removeAt("winners_fallback", i)} className="text-[#E53935] hover:opacity-80"><Trash2 size={14} /></button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input className="loot-input" placeholder="Name" value={w.name || ""} onChange={(e) => upd("winners_fallback", i, "name", e.target.value)} />
              <input className="loot-input" placeholder="Town" value={w.town || ""} onChange={(e) => upd("winners_fallback", i, "town", e.target.value)} />
              <input className="loot-input" placeholder="Prize" value={w.prize || ""} onChange={(e) => upd("winners_fallback", i, "prize", e.target.value)} />
              <input className="loot-input" placeholder="Sponsor" value={w.sponsor || ""} onChange={(e) => upd("winners_fallback", i, "sponsor", e.target.value)} />
            </div>
            <PhotoUploader value={w.photo_url || ""} onChange={(v) => upd("winners_fallback", i, "photo_url", v)} testid={`se-w-${i}-photo`} />
          </div>
        ))}
      </Section>

      {/* Sponsor + Pitch */}
      <Section title="Sponsor a drop">
        <Field label="Heading"><input className="loot-input" value={s.sponsor_overline || ""} onChange={(e) => u("sponsor_overline", e.target.value)} /></Field>
        <Field label="Question line"><input className="loot-input" value={s.sponsor_question || ""} onChange={(e) => u("sponsor_question", e.target.value)} /></Field>
        <Field label="Body"><textarea className="loot-input" rows={3} value={s.sponsor_body || ""} onChange={(e) => u("sponsor_body", e.target.value)} /></Field>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="CTA label"><input className="loot-input" value={s.sponsor_cta_label || ""} onChange={(e) => u("sponsor_cta_label", e.target.value)} /></Field>
          <Field label="CTA href"><input className="loot-input" value={s.sponsor_cta_href || ""} onChange={(e) => u("sponsor_cta_href", e.target.value)} /></Field>
        </div>
        <Field label="Notepad title"><input className="loot-input" value={s.sponsor_perfect_for_title || ""} onChange={(e) => u("sponsor_perfect_for_title", e.target.value)} /></Field>
        <div className="border-t border-[#F5F2E8]/10 pt-3">
          <div className="flex items-center justify-between">
            <div className="overline">Perfect for · {(s.sponsor_perfect_for || []).length}</div>
            <button onClick={() => add("sponsor_perfect_for", { label: "New", icon: "Sparkles" })} className="btn-ghost text-xs inline-flex items-center gap-1"><Plus size={12} /> Add</button>
          </div>
          {(s.sponsor_perfect_for || []).map((p, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 mt-2 items-center">
              <select className="loot-input col-span-3" value={p.icon || "Sparkles"} onChange={(e) => upd("sponsor_perfect_for", i, "icon", e.target.value)}>
                {ICON_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <input className="loot-input col-span-8" placeholder="Label" value={p.label || ""} onChange={(e) => upd("sponsor_perfect_for", i, "label", e.target.value)} />
              <button onClick={() => removeAt("sponsor_perfect_for", i)} className="text-[#E53935] hover:opacity-80 col-span-1 flex items-center justify-center"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Pitch your city">
        <Field label="Heading"><input className="loot-input" value={s.pitch_overline || ""} onChange={(e) => u("pitch_overline", e.target.value)} /></Field>
        <Field label="Question line"><input className="loot-input" value={s.pitch_question || ""} onChange={(e) => u("pitch_question", e.target.value)} /></Field>
        <Field label="Body"><textarea className="loot-input" rows={3} value={s.pitch_body || ""} onChange={(e) => u("pitch_body", e.target.value)} /></Field>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="CTA label"><input className="loot-input" value={s.pitch_cta_label || ""} onChange={(e) => u("pitch_cta_label", e.target.value)} /></Field>
          <Field label="CTA href"><input className="loot-input" value={s.pitch_cta_href || ""} onChange={(e) => u("pitch_cta_href", e.target.value)} /></Field>
        </div>
      </Section>

      {/* Private hunt */}
      <Section title="Host a private hunt">
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={s.private_show !== false} onChange={(e) => u("private_show", e.target.checked)} />
          Show "Host a private hunt" section on splash
        </label>
        <ImageField label="Section photo" value={s.private_image_url} onChange={(v) => u("private_image_url", v)} testid="se-private-img" />
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Overline"><input className="loot-input" value={s.private_overline || ""} onChange={(e) => u("private_overline", e.target.value)} /></Field>
          <Field label="Headline"><input className="loot-input" value={s.private_headline || ""} onChange={(e) => u("private_headline", e.target.value)} /></Field>
        </div>
        <Field label="Kicker (script)"><input className="loot-input" value={s.private_kicker || ""} onChange={(e) => u("private_kicker", e.target.value)} /></Field>
        <Field label="Body paragraph"><textarea className="loot-input" rows={3} value={s.private_body || ""} onChange={(e) => u("private_body", e.target.value)} /></Field>
        <div className="grid md:grid-cols-2 gap-3 border-t border-[#F5F2E8]/10 pt-3">
          <div className="space-y-2">
            <div className="overline">Personal events card</div>
            <Field label="Title"><input className="loot-input" value={s.private_personal_title || ""} onChange={(e) => u("private_personal_title", e.target.value)} /></Field>
            <Field label="Body"><textarea className="loot-input" rows={3} value={s.private_personal_body || ""} onChange={(e) => u("private_personal_body", e.target.value)} /></Field>
            <div className="grid grid-cols-2 gap-2">
              <input className="loot-input" placeholder="CTA label" value={s.private_personal_cta_label || ""} onChange={(e) => u("private_personal_cta_label", e.target.value)} />
              <input className="loot-input" placeholder="/href" value={s.private_personal_cta_href || ""} onChange={(e) => u("private_personal_cta_href", e.target.value)} />
            </div>
          </div>
          <div className="space-y-2">
            <div className="overline">Corporate card</div>
            <Field label="Title"><input className="loot-input" value={s.private_corporate_title || ""} onChange={(e) => u("private_corporate_title", e.target.value)} /></Field>
            <Field label="Body"><textarea className="loot-input" rows={3} value={s.private_corporate_body || ""} onChange={(e) => u("private_corporate_body", e.target.value)} /></Field>
            <div className="grid grid-cols-2 gap-2">
              <input className="loot-input" placeholder="CTA label" value={s.private_corporate_cta_label || ""} onChange={(e) => u("private_corporate_cta_label", e.target.value)} />
              <input className="loot-input" placeholder="/href" value={s.private_corporate_cta_href || ""} onChange={(e) => u("private_corporate_cta_href", e.target.value)} />
            </div>
          </div>
        </div>
      </Section>

      {/* FAQ */}
      <Section title={`FAQ · ${(s.faq_items || []).length} questions`} action={
        <button onClick={() => add("faq_items", { q: "New question", a: "" })} className="btn-ghost text-xs inline-flex items-center gap-1"><Plus size={12} /> Add Q/A</button>
      }>
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={s.faq_show !== false} onChange={(e) => u("faq_show", e.target.checked)} />
          Show FAQ section
        </label>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Overline"><input className="loot-input" value={s.faq_overline || ""} onChange={(e) => u("faq_overline", e.target.value)} /></Field>
          <Field label="Headline"><input className="loot-input" value={s.faq_headline || ""} onChange={(e) => u("faq_headline", e.target.value)} /></Field>
        </div>
        {(s.faq_items || []).map((it, i) => (
          <div key={i} className="border border-[#F5F2E8]/10 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <div className="font-mono text-[10px] uppercase tracking-wider text-[#76B82A]">Q&A {i + 1}</div>
              <button onClick={() => removeAt("faq_items", i)} className="text-[#E53935] hover:opacity-80"><Trash2 size={14} /></button>
            </div>
            <input className="loot-input" placeholder="Question" value={it.q || ""} onChange={(e) => upd("faq_items", i, "q", e.target.value)} />
            <textarea className="loot-input" rows={3} placeholder="Answer" value={it.a || ""} onChange={(e) => upd("faq_items", i, "a", e.target.value)} />
          </div>
        ))}
      </Section>

      {/* Quote band */}
      <Section title="Daily quote band">
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={s.quote_band_show !== false} onChange={(e) => u("quote_band_show", e.target.checked)} />
          Show quote band above the footer
        </label>
        <div className="font-mono text-[11px] text-[#F5F2E8]/55">
          Pulls today's quote from <code className="text-[#76B82A]">/api/daily-quote</code> when available; the fallback below shows otherwise.
        </div>
        <Field label="Overline"><input className="loot-input" value={s.quote_band_overline || ""} onChange={(e) => u("quote_band_overline", e.target.value)} /></Field>
        <Field label="Fallback quote"><textarea className="loot-input" rows={2} value={s.quote_band_fallback || ""} onChange={(e) => u("quote_band_fallback", e.target.value)} /></Field>
        <Field label="Fallback attribution"><input className="loot-input" value={s.quote_band_attribution || ""} onChange={(e) => u("quote_band_attribution", e.target.value)} /></Field>
      </Section>

      {/* Footer */}
      <Section title="Footer">
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Overline"><input className="loot-input" value={s.footer_overline || ""} onChange={(e) => u("footer_overline", e.target.value)} /></Field>
          <Field label="Brand blurb"><textarea className="loot-input" rows={2} value={s.footer_brand_blurb || ""} onChange={(e) => u("footer_brand_blurb", e.target.value)} /></Field>
          <Field label="Mission line (script)"><textarea className="loot-input" rows={2} value={s.footer_mission || ""} onChange={(e) => u("footer_mission", e.target.value)} /></Field>
          <Field label="Copyright"><input className="loot-input" value={s.footer_copyright || ""} onChange={(e) => u("footer_copyright", e.target.value)} /></Field>
        </div>
        <div className="border-t border-[#F5F2E8]/10 pt-3">
          <div className="flex items-center justify-between">
            <div className="overline">Socials · {(s.footer_socials || []).length}</div>
            <button onClick={() => add("footer_socials", { label: "New", href: "https://", icon: "Sparkles" })} className="btn-ghost text-xs inline-flex items-center gap-1"><Plus size={12} /> Add</button>
          </div>
          {(s.footer_socials || []).map((sc, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 mt-2 items-center">
              <select className="loot-input col-span-2" value={sc.icon || "Sparkles"} onChange={(e) => upd("footer_socials", i, "icon", e.target.value)}>
                {ICON_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <input className="loot-input col-span-4" placeholder="Label" value={sc.label || ""} onChange={(e) => upd("footer_socials", i, "label", e.target.value)} />
              <input className="loot-input col-span-5" placeholder="https://…" value={sc.href || ""} onChange={(e) => upd("footer_socials", i, "href", e.target.value)} />
              <button onClick={() => removeAt("footer_socials", i)} className="text-[#E53935] hover:opacity-80 col-span-1 flex items-center justify-center"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
        <div className="border-t border-[#F5F2E8]/10 pt-3">
          <div className="flex items-center justify-between">
            <div className="overline">Legal links · {(s.footer_legal_links || []).length}</div>
            <button onClick={() => add("footer_legal_links", { label: "New", href: "/" })} className="btn-ghost text-xs inline-flex items-center gap-1"><Plus size={12} /> Add</button>
          </div>
          {(s.footer_legal_links || []).map((l, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 mt-2 items-center">
              <input className="loot-input col-span-5" placeholder="Label" value={l.label || ""} onChange={(e) => upd("footer_legal_links", i, "label", e.target.value)} />
              <input className="loot-input col-span-6" placeholder="/href" value={l.href || ""} onChange={(e) => upd("footer_legal_links", i, "href", e.target.value)} />
              <button onClick={() => removeAt("footer_legal_links", i)} className="text-[#E53935] hover:opacity-80 col-span-1 flex items-center justify-center"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </Section>

      <div className="sticky bottom-4 flex justify-end z-10">
        <button onClick={save} disabled={busy} className="btn-loot inline-flex items-center gap-2" data-testid="splash-save">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {busy ? "Saving…" : "Save splash page"}
        </button>
      </div>
    </div>
  );
}


// ======================================================================
// LootMasterTab — toggle, copy editor, and applications inbox.
// ======================================================================
function LootMasterTab() {
  const [s, setS] = useState(null);
  const [apps, setApps] = useState([]);
  const [busy, setBusy] = useState(false);
  const [activeApp, setActiveApp] = useState(null);

  const reload = async () => {
    try {
      const [{ data: settings }, { data: list }] = await Promise.all([
        api.get("/lootmaster/settings"),
        api.get("/admin/lootmaster/applications"),
      ]);
      setS(settings || {});
      setApps(list?.applications || []);
    } catch {
      setS({});
      setApps([]);
    }
  };
  useEffect(() => { reload(); }, []);

  const u = (k, v) => setS({ ...s, [k]: v });
  const updArr = (key, i, k, v) => {
    const arr = [...(s[key] || [])];
    arr[i] = { ...arr[i], [k]: v };
    u(key, arr);
  };
  const addItem = (key, item) => u(key, [...(s[key] || []), item]);
  const removeAt = (key, i) => {
    const arr = [...(s[key] || [])];
    arr.splice(i, 1);
    u(key, arr);
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/lootmaster/settings", s);
      setS(data);
      toast.success("Loot Master page saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save");
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    if (!confirm("Reset Loot Master settings to defaults? You'll lose every override (recruitment_open will reset to ON).")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/lootmaster/reset");
      setS(data);
      toast.success("Reset to defaults");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't reset");
    } finally {
      setBusy(false);
    }
  };

  const patchApp = async (id, body) => {
    try {
      const { data } = await api.patch(`/admin/lootmaster/applications/${id}`, body);
      setApps((arr) => arr.map((a) => (a.id === id ? data : a)));
      if (activeApp?.id === id) setActiveApp(data);
      toast.success("Application updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't update");
    }
  };
  const deleteApp = async (id) => {
    if (!confirm("Delete this application? This can't be undone.")) return;
    try {
      await api.delete(`/admin/lootmaster/applications/${id}`);
      setApps((arr) => arr.filter((a) => a.id !== id));
      if (activeApp?.id === id) setActiveApp(null);
      toast.success("Application deleted");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't delete");
    }
  };

  if (!s) return <div className="loot-card-flat p-10 text-center text-[#F5F2E8]/40">Loading…</div>;

  const newCount = apps.filter((a) => a.status === "new").length;

  return (
    <div className="space-y-6" data-testid="lootmaster-admin">
      {/* Master switch */}
      <div className="loot-card-flat p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="overline">Recruitment status</div>
            <h2 className="font-display uppercase text-2xl mt-1">Loot Master Recruitment</h2>
            <p className="text-[#F5F2E8]/60 mt-1 text-sm max-w-xl">
              When this is <strong className="text-[#76B82A]">ON</strong>, the splash CTA renders and <code className="font-mono text-[#76B82A]">/lootmaster</code> is live. When <strong className="text-[#E53935]">OFF</strong>, the CTA disappears and visitors to <code className="font-mono">/lootmaster</code> are redirected back to <code className="font-mono">/</code> with the closed message below.
            </p>
          </div>
          <label className="inline-flex items-center gap-3 shrink-0 cursor-pointer">
            <span className={`text-xs uppercase tracking-widest font-semibold ${s.recruitment_open !== false ? "text-[#76B82A]" : "text-[#E53935]"}`}>
              {s.recruitment_open !== false ? "Open" : "Closed"}
            </span>
            <input
              type="checkbox"
              checked={s.recruitment_open !== false}
              onChange={(e) => u("recruitment_open", e.target.checked)}
              className="w-12 h-6 appearance-none bg-[#2a2d2a] checked:bg-[#76B82A] rounded-full relative transition-colors cursor-pointer"
              style={{
                boxShadow: "inset 0 0 0 2px #14110d",
              }}
              data-testid="lm-recruitment-toggle"
            />
          </label>
        </div>
        <div className="mt-3">
          <Field label="Closed message (shown as toast when /lootmaster is hit while OFF)">
            <input className="loot-input" value={s.closed_message || ""} onChange={(e) => u("closed_message", e.target.value)} data-testid="lm-closed-message" />
          </Field>
        </div>
        <div className="mt-3 flex gap-2">
          <a href="/lootmaster" target="_blank" rel="noopener noreferrer" className="btn-ghost text-xs inline-flex items-center gap-1">Open page ↗</a>
          <button onClick={reset} disabled={busy} className="btn-ghost text-xs" style={{ color: "#E53935", borderColor: "#E53935" }}>Reset to defaults</button>
        </div>
      </div>

      {/* Applications inbox */}
      <div className="loot-card-flat p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="overline">Applications inbox</div>
            <div className="font-display uppercase text-xl mt-1">
              {apps.length} total {newCount > 0 && <span className="text-[#76B82A]">· {newCount} new</span>}
            </div>
          </div>
          <button onClick={reload} className="btn-ghost text-xs">Refresh</button>
        </div>
        {apps.length === 0 ? (
          <div className="text-center text-[#F5F2E8]/40 py-10 text-sm">
            No applications yet. They'll show up here as soon as someone applies.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-[#F5F2E8]/55 border-b border-[#F5F2E8]/10">
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Name</th>
                  <th className="py-2 pr-3">Location</th>
                  <th className="py-2 pr-3">Background</th>
                  <th className="py-2 pr-3">When</th>
                  <th className="py-2 pr-3"></th>
                </tr>
              </thead>
              <tbody>
                {apps.map((a) => (
                  <tr key={a.id} className="border-b border-[#F5F2E8]/5 hover:bg-[#F5F2E8]/[0.02]" data-testid={`lm-app-row-${a.id}`}>
                    <td className="py-2 pr-3">
                      <select
                        value={a.status || "new"}
                        onChange={(e) => patchApp(a.id, { status: e.target.value })}
                        className="loot-input py-1 text-xs"
                        data-testid={`lm-app-status-${a.id}`}
                      >
                        {["new", "reviewing", "contacted", "accepted", "declined"].map((st) => (
                          <option key={st} value={st}>{st}</option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2 pr-3 font-semibold">{a.name}</td>
                    <td className="py-2 pr-3 text-[#F5F2E8]/75">{a.location}</td>
                    <td className="py-2 pr-3 text-[#F5F2E8]/65 max-w-[220px] truncate">{a.background}</td>
                    <td className="py-2 pr-3 text-[#F5F2E8]/55 text-xs">{(a.created_at || "").slice(0, 10)}</td>
                    <td className="py-2 pr-3 flex gap-2">
                      <button onClick={() => setActiveApp(a)} className="btn-ghost text-xs">View</button>
                      <button onClick={() => deleteApp(a.id)} className="text-[#E53935] hover:opacity-80"><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeApp && (
          <div
            className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
            onClick={() => setActiveApp(null)}
            data-testid="lm-app-modal"
          >
            <div
              className="loot-card-flat p-5 max-w-2xl w-full max-h-[90vh] overflow-y-auto space-y-3"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="overline">{activeApp.status || "new"}</div>
                  <div className="font-display uppercase text-2xl mt-1">{activeApp.name}</div>
                  <div className="text-[#F5F2E8]/65 text-sm">{activeApp.location}</div>
                  {activeApp.email && <div className="text-[#76B82A] text-sm font-mono mt-1">{activeApp.email}</div>}
                </div>
                <button onClick={() => setActiveApp(null)} className="text-[#F5F2E8]/55 hover:text-[#F5F2E8]"><X size={20} /></button>
              </div>
              <div>
                <div className="overline">Background</div>
                <div className="text-sm text-[#F5F2E8]/85 mt-1">{activeApp.background}</div>
              </div>
              <div>
                <div className="overline">Why</div>
                <div className="text-sm text-[#F5F2E8]/85 mt-1 whitespace-pre-wrap">{activeApp.why}</div>
              </div>
              {activeApp.social && (
                <div>
                  <div className="overline">Social</div>
                  <div className="text-sm text-[#76B82A] font-mono mt-1">{activeApp.social}</div>
                </div>
              )}
              <Field label="Notes (admin only)">
                <textarea
                  className="loot-input"
                  rows={3}
                  defaultValue={activeApp.notes || ""}
                  onBlur={(e) => {
                    if (e.target.value !== (activeApp.notes || "")) {
                      patchApp(activeApp.id, { notes: e.target.value });
                    }
                  }}
                />
              </Field>
              <div className="flex flex-wrap gap-2 pt-2 border-t border-[#F5F2E8]/10">
                {["reviewing", "contacted", "accepted", "declined"].map((st) => (
                  <button
                    key={st}
                    onClick={() => patchApp(activeApp.id, { status: st })}
                    className="btn-ghost text-xs uppercase"
                  >
                    Mark as {st}
                  </button>
                ))}
                <button
                  onClick={() => deleteApp(activeApp.id)}
                  className="ml-auto text-[#E53935] hover:opacity-80 text-xs uppercase"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Page content editors */}
      <div className="loot-card-flat p-5 space-y-3">
        <div className="overline">Homepage CTA block</div>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Overline"><input className="loot-input" value={s.cta_overline || ""} onChange={(e) => u("cta_overline", e.target.value)} /></Field>
          <Field label="Headline (last word colored green)"><input className="loot-input" value={s.cta_headline || ""} onChange={(e) => u("cta_headline", e.target.value)} /></Field>
        </div>
        <Field label="Sub-headline"><textarea className="loot-input" rows={3} value={s.cta_subhead || ""} onChange={(e) => u("cta_subhead", e.target.value)} /></Field>
        <Field label="Button label"><input className="loot-input" value={s.cta_button_label || ""} onChange={(e) => u("cta_button_label", e.target.value)} /></Field>
      </div>

      <div className="loot-card-flat p-5 space-y-3">
        <div className="overline">/lootmaster page hero</div>
        <ImageField label="Hero photo" value={s.page_hero_image_url} onChange={(v) => u("page_hero_image_url", v)} testid="lm-hero-img" />
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Overline"><input className="loot-input" value={s.page_overline || ""} onChange={(e) => u("page_overline", e.target.value)} /></Field>
          <Field label="Headline (split on first '.': first part white, rest green)"><input className="loot-input" value={s.page_headline || ""} onChange={(e) => u("page_headline", e.target.value)} /></Field>
        </div>
      </div>

      <div className="loot-card-flat p-5 space-y-3">
        <div className="overline">Mission</div>
        <Field label="Section overline"><input className="loot-input" value={s.mission_overline || ""} onChange={(e) => u("mission_overline", e.target.value)} /></Field>
        <Field label="Mission statement (rendered as a stencil quote)"><textarea className="loot-input" rows={4} value={s.mission_body || ""} onChange={(e) => u("mission_body", e.target.value)} /></Field>
      </div>

      {/* What you'll do */}
      <div className="loot-card-flat p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div className="overline">What you'll do · {(s.do_items || []).length} cards</div>
          <button onClick={() => addItem("do_items", { icon: "Sparkles", title: "New", body: "" })} className="btn-ghost text-xs inline-flex items-center gap-1"><Plus size={12} /> Add</button>
        </div>
        <Field label="Section heading"><input className="loot-input" value={s.do_overline || ""} onChange={(e) => u("do_overline", e.target.value)} /></Field>
        {(s.do_items || []).map((it, i) => (
          <div key={i} className="border border-[#F5F2E8]/10 p-3 space-y-2">
            <div className="grid grid-cols-12 gap-2">
              <select className="loot-input col-span-3" value={it.icon || "Sparkles"} onChange={(e) => updArr("do_items", i, "icon", e.target.value)}>
                {["MapPin","Handshake","Users","Radio","ShieldCheck","Sparkles","Briefcase"].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <input className="loot-input col-span-8" placeholder="Title" value={it.title || ""} onChange={(e) => updArr("do_items", i, "title", e.target.value)} />
              <button onClick={() => removeAt("do_items", i)} className="text-[#E53935] hover:opacity-80 col-span-1 flex items-center justify-center"><Trash2 size={14} /></button>
            </div>
            <textarea className="loot-input" rows={2} placeholder="Body" value={it.body || ""} onChange={(e) => updArr("do_items", i, "body", e.target.value)} />
          </div>
        ))}
      </div>

      {/* Why join */}
      <div className="loot-card-flat p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div className="overline">Why join · {(s.why_items || []).length} cards</div>
          <button onClick={() => addItem("why_items", { icon: "Sparkles", title: "New", body: "" })} className="btn-ghost text-xs inline-flex items-center gap-1"><Plus size={12} /> Add</button>
        </div>
        <Field label="Section heading"><input className="loot-input" value={s.why_overline || ""} onChange={(e) => u("why_overline", e.target.value)} /></Field>
        {(s.why_items || []).map((it, i) => (
          <div key={i} className="border border-[#F5F2E8]/10 p-3 space-y-2">
            <div className="grid grid-cols-12 gap-2">
              <select className="loot-input col-span-3" value={it.icon || "Sparkles"} onChange={(e) => updArr("why_items", i, "icon", e.target.value)}>
                {["ShieldCheck","Sparkles","Briefcase","MapPin","Handshake","Users","Radio"].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <input className="loot-input col-span-8" placeholder="Title" value={it.title || ""} onChange={(e) => updArr("why_items", i, "title", e.target.value)} />
              <button onClick={() => removeAt("why_items", i)} className="text-[#E53935] hover:opacity-80 col-span-1 flex items-center justify-center"><Trash2 size={14} /></button>
            </div>
            <textarea className="loot-input" rows={2} placeholder="Body" value={it.body || ""} onChange={(e) => updArr("why_items", i, "body", e.target.value)} />
          </div>
        ))}
      </div>

      {/* FAQ */}
      <div className="loot-card-flat p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div className="overline">FAQ · {(s.faq_items || []).length}</div>
          <button onClick={() => addItem("faq_items", { q: "New question", a: "" })} className="btn-ghost text-xs inline-flex items-center gap-1"><Plus size={12} /> Add</button>
        </div>
        <Field label="Section heading"><input className="loot-input" value={s.faq_overline || ""} onChange={(e) => u("faq_overline", e.target.value)} /></Field>
        {(s.faq_items || []).map((it, i) => (
          <div key={i} className="border border-[#F5F2E8]/10 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <div className="font-mono text-[10px] uppercase tracking-wider text-[#76B82A]">Q&A {i + 1}</div>
              <button onClick={() => removeAt("faq_items", i)} className="text-[#E53935] hover:opacity-80"><Trash2 size={14} /></button>
            </div>
            <input className="loot-input" placeholder="Question" value={it.q || ""} onChange={(e) => updArr("faq_items", i, "q", e.target.value)} />
            <textarea className="loot-input" rows={3} placeholder="Answer" value={it.a || ""} onChange={(e) => updArr("faq_items", i, "a", e.target.value)} />
          </div>
        ))}
      </div>

      {/* Form */}
      <div className="loot-card-flat p-5 space-y-3">
        <div className="overline">Application form copy</div>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Overline"><input className="loot-input" value={s.form_overline || ""} onChange={(e) => u("form_overline", e.target.value)} /></Field>
          <Field label="Headline"><input className="loot-input" value={s.form_headline || ""} onChange={(e) => u("form_headline", e.target.value)} /></Field>
        </div>
        <Field label="Subhead"><input className="loot-input" value={s.form_subhead || ""} onChange={(e) => u("form_subhead", e.target.value)} /></Field>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Submit button label"><input className="loot-input" value={s.form_submit_label || ""} onChange={(e) => u("form_submit_label", e.target.value)} /></Field>
          <Field label="Success message"><input className="loot-input" value={s.form_success_message || ""} onChange={(e) => u("form_success_message", e.target.value)} /></Field>
        </div>
      </div>

      <div className="sticky bottom-4 flex justify-end z-10">
        <button onClick={save} disabled={busy} className="btn-loot inline-flex items-center gap-2" data-testid="lm-save">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {busy ? "Saving…" : "Save Loot Master page"}
        </button>
      </div>
    </div>
  );
}


// ======================================================================
// TankTab — Community LootFund admin: toggle, settings, donation entry,
// loot history log, "notify me" sign-up list.
// ======================================================================
function TankTab() {
  const [s, setS] = useState(null);
  const [donations, setDonations] = useState([]);
  const [history, setHistory] = useState([]);
  const [notify, setNotify] = useState([]);
  const [busy, setBusy] = useState(false);

  const [donor, setDonor] = useState({
    user_handle: "", display_name: "", donor_real_name: "",
    amount_dollars: "", source: "venmo", note: "", city: "", pinned: false,
  });
  const [hist, setHist] = useState({
    title: "", drop_id: "", amount_dollars: "", sponsor_business: "",
    city: "", happened_at: "", note: "", is_mystery: false,
  });
  const [linkable, setLinkable] = useState([]);
  const [linkPick, setLinkPick] = useState("");  // "{kind}:{id}" or ""

  const reload = async () => {
    try {
      const [a, b, c, d, e] = await Promise.all([
        api.get("/admin/tank/settings"),
        api.get("/admin/tank/donations"),
        api.get("/admin/tank/loot-history"),
        api.get("/admin/tank/notify-list").catch(() => ({ data: { emails: [] } })),
        api.get("/admin/tank/linkable-drops").catch(() => ({ data: { items: [] } })),
      ]);
      setS(a.data || {});
      setDonations(b.data?.donations || []);
      setHistory(c.data?.history || []);
      setNotify(d.data?.emails || []);
      setLinkable(e.data?.items || []);
    } catch (e) {
      setS({}); setDonations([]); setHistory([]); setNotify([]); setLinkable([]);
    }
  };
  useEffect(() => { reload(); }, []);

  const u = (k, v) => setS({ ...s, [k]: v });
  const saveSettings = async () => {
    setBusy(true);
    try {
      await api.put("/admin/tank/settings", s);
      toast.success("Saved");
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save");
    } finally { setBusy(false); }
  };
  const resetSettings = async () => {
    if (!confirm("Reset tank settings to defaults?")) return;
    try { await api.post("/admin/tank/settings/reset"); toast.success("Reset"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't reset"); }
  };

  const addDonation = async () => {
    const cents = Math.round(parseFloat(donor.amount_dollars || "0") * 100);
    if (!cents || cents <= 0) { toast.error("Enter an amount > 0"); return; }
    try {
      await api.post("/admin/tank/donations", {
        user_handle: donor.user_handle.trim() || null,
        display_name: donor.display_name.trim() || null,
        donor_real_name: donor.donor_real_name.trim() || null,
        amount_cents: cents,
        source: donor.source,
        note: donor.note.trim() || null,
        city: donor.city.trim() || null,
        pinned: !!donor.pinned,
      });
      toast.success("Donation added");
      setDonor({ user_handle: "", display_name: "", donor_real_name: "", amount_dollars: "", source: "venmo", note: "", city: "", pinned: false });
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't add"); }
  };
  const patchDonation = async (id, patch) => {
    try { await api.patch(`/admin/tank/donations/${id}`, patch); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't update"); }
  };
  const deleteDonation = async (id) => {
    if (!confirm("Delete this donation?")) return;
    try { await api.delete(`/admin/tank/donations/${id}`); reload(); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't delete"); }
  };

  const addHistory = async () => {
    const cents = Math.round(parseFloat(hist.amount_dollars || "0") * 100);
    if (!cents || cents <= 0 || !hist.title.trim()) { toast.error("Title + amount required"); return; }
    try {
      await api.post("/admin/tank/loot-history", {
        title: hist.title.trim(),
        drop_id: hist.drop_id.trim() || null,
        amount_cents: cents,
        sponsor_business: hist.sponsor_business.trim() || null,
        city: hist.city.trim() || null,
        happened_at: hist.happened_at || null,
        note: hist.note.trim() || null,
        is_mystery: !!hist.is_mystery,
      });
      toast.success("Logged");
      setHist({ title: "", drop_id: "", amount_dollars: "", sponsor_business: "", city: "", happened_at: "", note: "", is_mystery: false });
      setLinkPick("");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't log"); }
  };

  const applyLinkPick = (value) => {
    setLinkPick(value);
    if (!value) return;
    const [kind, id] = value.split(":");
    const row = linkable.find((x) => x.kind === kind && x.id === id);
    if (!row) return;
    const amt = row.amount_dollars != null ? String(row.amount_dollars) : "";
    // Only write drop_id for real Drops — Loot Quests are not in the drops
    // collection so linking their id there would be misleading.
    const dropIdValue = kind === "drop" ? id : "";
    setHist((prev) => ({
      ...prev,
      title: row.title || prev.title,
      amount_dollars: amt || prev.amount_dollars,
      city: row.city || prev.city,
      drop_id: dropIdValue,
      happened_at: (row.when || "").slice(0, 10) || prev.happened_at,
    }));
    toast.success(`Linked from ${kind === "quest" ? "Loot Quest" : "Drop"}: ${row.title}`);
  };
  const deleteHistory = async (id) => {
    if (!confirm("Delete this history entry?")) return;
    try { await api.delete(`/admin/tank/loot-history/${id}`); reload(); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't delete"); }
  };

  if (!s) return <div className="text-[#F5F2E8]/55">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="tank-tab">
      {/* Master toggle + page settings */}
      <div className="loot-card-flat p-5" data-testid="tank-master-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="overline">Master switch</div>
            <div className="font-display uppercase text-2xl mt-1">
              Community Tank
              <span className={`ml-2 inline-block px-2 py-0.5 text-[10px] tracking-widest uppercase ${s.enabled ? "bg-[#76B82A]/20 text-[#76B82A]" : "bg-[#F5F2E8]/10 text-[#F5F2E8]/55"}`}>
                {s.enabled ? "Live" : "Paused"}
              </span>
            </div>
          </div>
          <button onClick={() => u("enabled", !s.enabled)} className={`px-4 py-2 font-display uppercase text-sm border-2 ${s.enabled ? "border-[#E53935] text-[#E53935]" : "border-[#76B82A] text-[#76B82A]"}`} data-testid="tank-toggle">
            {s.enabled ? "Pause" : "Go live"}
          </button>
        </div>
        <p className="text-[#F5F2E8]/55 text-xs mb-4">
          When paused, public visitors see a "Coming soon" page with email signup. Splash and home sections hide automatically.
        </p>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Goal target ($)"><input type="number" min="0" className="loot-input" value={(s.target_cents || 0) / 100} onChange={(e) => u("target_cents", Math.round(parseFloat(e.target.value || "0") * 100))} /></Field>
          <Field label="Igniter threshold ($)"><input type="number" className="loot-input" value={(s.tier_igniter_cents || 10000) / 100} onChange={(e) => u("tier_igniter_cents", Math.round(parseFloat(e.target.value || "0") * 100))} /></Field>
          <Field label="Page overline"><input className="loot-input" value={s.page_overline || ""} onChange={(e) => u("page_overline", e.target.value)} /></Field>
          <Field label="Page headline"><input className="loot-input" value={s.page_headline || ""} onChange={(e) => u("page_headline", e.target.value)} /></Field>
          <Field label="Page subhead" className="md:col-span-2"><textarea rows={2} className="loot-input" value={s.page_subhead || ""} onChange={(e) => u("page_subhead", e.target.value)} /></Field>
          <Field label="Pledge" className="md:col-span-2"><textarea rows={2} className="loot-input" value={s.page_pledge || ""} onChange={(e) => u("page_pledge", e.target.value)} /></Field>
          <Field label="Splash overline"><input className="loot-input" value={s.splash_overline || ""} onChange={(e) => u("splash_overline", e.target.value)} /></Field>
          <Field label="Splash headline"><input className="loot-input" value={s.splash_headline || ""} onChange={(e) => u("splash_headline", e.target.value)} /></Field>
          <Field label="Splash body" className="md:col-span-2"><textarea rows={2} className="loot-input" value={s.splash_body || ""} onChange={(e) => u("splash_body", e.target.value)} /></Field>
          <Field label="Splash CTA label"><input className="loot-input" value={s.splash_cta_label || ""} onChange={(e) => u("splash_cta_label", e.target.value)} /></Field>
          <Field label="Venmo handle"><input className="loot-input" value={s.venmo_handle || ""} onChange={(e) => u("venmo_handle", e.target.value)} /></Field>
          <Field label="Cash App handle"><input className="loot-input" value={s.cashapp_handle || ""} onChange={(e) => u("cashapp_handle", e.target.value)} /></Field>
          <Field label="Cash hand-off instructions" className="md:col-span-2"><textarea rows={2} className="loot-input" value={s.cash_instructions || ""} onChange={(e) => u("cash_instructions", e.target.value)} /></Field>
          <Field label="Offline follow-up" className="md:col-span-2"><textarea rows={2} className="loot-input" value={s.offline_followup || ""} onChange={(e) => u("offline_followup", e.target.value)} /></Field>
          <Field label="Disclaimer (fine print)" className="md:col-span-2"><textarea rows={3} className="loot-input" value={s.disclaimer || ""} onChange={(e) => u("disclaimer", e.target.value)} /></Field>
          <Field label="Coming-soon headline"><input className="loot-input" value={s.comingsoon_headline || ""} onChange={(e) => u("comingsoon_headline", e.target.value)} /></Field>
          <Field label="Coming-soon body"><input className="loot-input" value={s.comingsoon_body || ""} onChange={(e) => u("comingsoon_body", e.target.value)} /></Field>
        </div>
        <div className="flex gap-2 mt-4">
          <button onClick={saveSettings} disabled={busy} className="btn-primary text-xs" data-testid="tank-save-settings">{busy ? "Saving…" : "Save settings"}</button>
          <a href="/community-tank" target="_blank" rel="noopener noreferrer" className="btn-ghost text-xs">Open /community-tank ↗</a>
          <button onClick={resetSettings} className="btn-ghost text-xs" style={{ color: "#E53935", borderColor: "#E53935" }}>Reset to defaults</button>
        </div>
      </div>

      {/* Add donation */}
      <div className="loot-card-flat p-5" data-testid="tank-add-donation-card">
        <div className="overline">Log a new donation</div>
        <div className="font-display uppercase text-xl mt-1">Add to the tank</div>
        <p className="text-[#F5F2E8]/55 text-xs mt-1">All donations are offline — log them here as they come in via Venmo, Cash App, or cash.</p>
        <div className="grid md:grid-cols-2 gap-3 mt-4">
          <Field label="Donor @username (or email) — optional"><input className="loot-input" placeholder="@Jesica or jesica@email.com" value={donor.user_handle} onChange={(e) => setDonor({ ...donor, user_handle: e.target.value })} data-testid="tank-donor-handle" /></Field>
          <Field label="Display name on the wall"><input className="loot-input" placeholder="@JESICA (auto if linked)" value={donor.display_name} onChange={(e) => setDonor({ ...donor, display_name: e.target.value })} data-testid="tank-donor-display" /></Field>
          <Field label="Real name (private)"><input className="loot-input" value={donor.donor_real_name} onChange={(e) => setDonor({ ...donor, donor_real_name: e.target.value })} /></Field>
          <Field label="Amount ($)"><input type="number" step="0.01" min="0" className="loot-input" value={donor.amount_dollars} onChange={(e) => setDonor({ ...donor, amount_dollars: e.target.value })} data-testid="tank-donor-amount" /></Field>
          <Field label="Source"><select className="loot-input" value={donor.source} onChange={(e) => setDonor({ ...donor, source: e.target.value })} data-testid="tank-donor-source">{["venmo","cashapp","cash","other"].map((x) => <option key={x} value={x}>{x}</option>)}</select></Field>
          <Field label="City"><input className="loot-input" value={donor.city} onChange={(e) => setDonor({ ...donor, city: e.target.value })} /></Field>
          <Field label="Custom shout-out (optional)" className="md:col-span-2"><textarea rows={2} className="loot-input" placeholder="e.g. Jesica just fueled the tank with $100! She officially lit the fuse for the next community takeover." value={donor.note} onChange={(e) => setDonor({ ...donor, note: e.target.value })} /></Field>
          <Field label="Pin to top of wall"><label className="flex items-center gap-2 mt-1"><input type="checkbox" checked={donor.pinned} onChange={(e) => setDonor({ ...donor, pinned: e.target.checked })} /><span className="text-xs">Pinned (overrides amount sort)</span></label></Field>
        </div>
        <div className="mt-3"><button onClick={addDonation} className="btn-primary text-xs" data-testid="tank-add-donation-submit">Add to tank</button></div>
      </div>

      {/* Donation list */}
      <div className="loot-card-flat p-5" data-testid="tank-donations-list">
        <div className="overline">Donation log</div>
        <div className="font-display uppercase text-xl mt-1">{donations.length} donations</div>
        {donations.length === 0 ? (
          <div className="text-center text-[#F5F2E8]/40 py-6 text-sm">No donations yet.</div>
        ) : (
          <div className="space-y-2 mt-3">
            {donations.map((d) => (
              <div key={d.id} className="border border-[#F5F2E8]/10 p-3 flex flex-wrap items-start gap-3" data-testid={`tank-donation-${d.id}`}>
                <div className={`px-2 py-0.5 text-[10px] uppercase tracking-widest font-mono shrink-0 ${d.tier === "igniter" ? "bg-[#76B82A]/20 text-[#76B82A]" : d.tier === "booster" ? "bg-[#d7a62a]/20 text-[#d7a62a]" : "bg-[#F5F2E8]/10 text-[#F5F2E8]/55"}`}>
                  {d.tier}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold flex items-center gap-2 flex-wrap">
                    {d.display_name}
                    {d.pinned && <span className="text-[10px] uppercase tracking-widest text-[#76B82A]">📌 pinned</span>}
                  </div>
                  <div className="text-[12px] text-[#F5F2E8]/65 mt-0.5">
                    {d.amount_label} · {d.source}{d.donor_real_name ? ` · ${d.donor_real_name}` : ""}{d.city ? ` · ${d.city}` : ""}
                  </div>
                  {d.note && <p className="text-[12px] text-[#F5F2E8]/75 mt-1 italic">"{d.note}"</p>}
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <button onClick={() => patchDonation(d.id, { pinned: !d.pinned })} className="btn-ghost text-xs">{d.pinned ? "Unpin" : "Pin"}</button>
                  <button onClick={() => deleteDonation(d.id)} className="btn-ghost text-xs" style={{ color: "#E53935", borderColor: "#E53935" }} data-testid={`tank-donation-delete-${d.id}`}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Loot history */}
      <div className="loot-card-flat p-5" data-testid="tank-history-card">
        <div className="overline">Loot history</div>
        <div className="font-display uppercase text-xl mt-1">Receipts</div>
        <p className="text-[#F5F2E8]/55 text-xs mt-1">Log every People's Drop the tank funded — title, amount, sponsor business, city.</p>

        {/* Quick-link picker — auto-fills the receipt form from an existing
            drop or Loot Quest so the admin doesn't retype everything. */}
        <div className="mt-4 p-3 border border-[#76B82A]/30 bg-[#76B82A]/5">
          <div className="overline mb-1" style={{ color: "#76B82A" }}>Quick link</div>
          <label className="text-[11px] text-[#F5F2E8]/60 font-mono block mb-2">
            Pick an existing Drop or Loot Quest to auto-fill title, amount, city, and date.
          </label>
          <select
            className="loot-input"
            value={linkPick}
            onChange={(e) => applyLinkPick(e.target.value)}
            data-testid="tank-link-picker"
          >
            <option value="">— Select a Drop or Loot Quest —</option>
            {linkable.length > 0 && (
              <optgroup label="Drops">
                {linkable.filter((x) => x.kind === "drop").map((r) => (
                  <option key={`drop:${r.id}`} value={`drop:${r.id}`}>
                    {(r.is_golden ? "⭐ " : "") + r.label} {r.status ? `· ${String(r.status).toUpperCase()}` : ""}
                  </option>
                ))}
              </optgroup>
            )}
            {linkable.some((x) => x.kind === "quest") && (
              <optgroup label="Loot Quests">
                {linkable.filter((x) => x.kind === "quest").map((r) => (
                  <option key={`quest:${r.id}`} value={`quest:${r.id}`}>
                    🔑 {r.label} {r.status ? `· ${String(r.status).toUpperCase()}` : ""}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
          <div className="mt-2 font-mono text-[10px] text-[#F5F2E8]/45">
            You can still edit any field below before saving. Past, live, and scheduled items all appear here.
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-3 mt-4">
          <Field label="Drop title *"><input className="loot-input" value={hist.title} onChange={(e) => setHist({ ...hist, title: e.target.value })} data-testid="tank-hist-title" /></Field>
          <Field label="Amount ($)"><input type="number" step="0.01" className="loot-input" value={hist.amount_dollars} onChange={(e) => setHist({ ...hist, amount_dollars: e.target.value })} data-testid="tank-hist-amount" /></Field>
          <Field label="Sponsor business / shop"><input className="loot-input" value={hist.sponsor_business} onChange={(e) => setHist({ ...hist, sponsor_business: e.target.value })} /></Field>
          <Field label="City"><input className="loot-input" value={hist.city} onChange={(e) => setHist({ ...hist, city: e.target.value })} /></Field>
          <Field label="When (ISO date, optional)"><input className="loot-input" value={hist.happened_at} onChange={(e) => setHist({ ...hist, happened_at: e.target.value })} placeholder="2026-02-15" /></Field>
          <Field label="Drop ID (optional)"><input className="loot-input" value={hist.drop_id} onChange={(e) => setHist({ ...hist, drop_id: e.target.value })} /></Field>
          <Field label="Note" className="md:col-span-2"><textarea rows={2} className="loot-input" value={hist.note} onChange={(e) => setHist({ ...hist, note: e.target.value })} /></Field>
          <label className="md:col-span-2 flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={!!hist.is_mystery}
              onChange={(e) => setHist({ ...hist, is_mystery: e.target.checked })}
              data-testid="tank-hist-mystery"
            />
            <span className="flex items-center gap-1">
              <span style={{ color: "#D7A62A" }}>🎁</span>
              Mystery receipt — hide the amount on the public tank (shows "Mystery Loot")
            </span>
          </label>
        </div>
        <div className="mt-3"><button onClick={addHistory} className="btn-primary text-xs" data-testid="tank-add-history-submit">Log it</button></div>
        {history.length > 0 && (
          <div className="mt-4 space-y-2">
            {history.map((h) => (
              <div key={h.id} className="border border-[#F5F2E8]/10 p-3 flex items-start gap-3" data-testid={`tank-history-${h.id}`}>
                <div className="flex-1">
                  <div className="font-semibold flex items-center gap-2">
                    {h.title}
                    {h.is_mystery && (
                      <span
                        className="text-[9px] font-mono tracking-widest uppercase px-1.5 py-0.5 border"
                        style={{ color: "#D7A62A", borderColor: "#D7A62A" }}
                        title="Hidden on public tank"
                      >
                        🎁 Mystery
                      </span>
                    )}
                  </div>
                  <div className="text-[12px] text-[#F5F2E8]/65 mt-0.5">
                    {h.amount_label}
                    {h.is_mystery && <span className="text-[#D7A62A]/80"> · shown as "Mystery Loot"</span>}
                    {h.sponsor_business ? ` · ${h.sponsor_business}` : ""}
                    {h.city ? ` · ${h.city}` : ""}
                  </div>
                </div>
                <button onClick={() => deleteHistory(h.id)} className="btn-ghost text-xs" style={{ color: "#E53935", borderColor: "#E53935" }}>Delete</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Notify-me list (only shown if not enabled) */}
      {!s.enabled && (
        <div className="loot-card-flat p-5">
          <div className="overline">Coming-soon waitlist</div>
          <div className="font-display uppercase text-xl mt-1">{notify.length} emails</div>
          {notify.length === 0 ? (
            <div className="text-center text-[#F5F2E8]/40 py-4 text-sm">No signups yet.</div>
          ) : (
            <ul className="mt-3 space-y-1 font-mono text-xs">
              {notify.map((n) => <li key={n.id}>{n.email}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}


// ======================================================================
// MissionsTab — Community Missions admin: settings + missions CRUD + proofs.
// ======================================================================
function MissionsTab() {
  const [s, setS] = useState(null);
  const [missions, setMissions] = useState([]);
  const [proofs, setProofs] = useState([]);
  const [proofFilter, setProofFilter] = useState("pending");
  const [busy, setBusy] = useState(false);
  const [editor, setEditor] = useState(null);  // mission being created/edited
  const [activeProof, setActiveProof] = useState(null);
  const [sponsors, setSponsors] = useState([]);
  const [sponsorApps, setSponsorApps] = useState([]);

  const reload = async () => {
    try {
      const [a, b, c, d, e] = await Promise.all([
        api.get("/missions/settings"),
        api.get("/admin/missions"),
        api.get(`/admin/missions/proofs${proofFilter ? "?status=" + proofFilter : ""}`),
        api.get("/admin/sponsors").catch(() => ({ data: { sponsors: [] } })),
        api.get("/admin/missions/sponsor-applications").catch(() => ({ data: { applications: [] } })),
      ]);
      setS(a.data || {});
      setMissions(b.data?.missions || []);
      setProofs(c.data?.proofs || []);
      setSponsors(d.data?.sponsors || d.data || []);
      setSponsorApps(e.data?.applications || []);
    } catch (e) {
      setS({});
      setMissions([]);
      setProofs([]);
      setSponsorApps([]);
    }
  };
  useEffect(() => { reload(); }, [proofFilter]);

  const u = (k, v) => setS({ ...s, [k]: v });

  const saveSettings = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/missions/settings", s);
      setS(data);
      toast.success("Settings saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save"); }
    finally { setBusy(false); }
  };

  const resetSettings = async () => {
    if (!confirm("Reset all Missions settings (including the master toggle) to defaults?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/missions/reset");
      setS(data);
      toast.success("Reset to defaults");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't reset"); }
    finally { setBusy(false); }
  };

  const saveMission = async (m) => {
    setBusy(true);
    try {
      if (m.id) {
        const { id, completion_count, sponsor, created_at, ...patch } = m;
        await api.patch(`/admin/missions/${id}`, patch);
      } else {
        await api.post("/admin/missions", m);
      }
      toast.success("Mission saved");
      setEditor(null);
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save"); }
    finally { setBusy(false); }
  };

  const deleteMission = async (id) => {
    if (!confirm("Delete this mission? Approved proofs are preserved.")) return;
    try {
      await api.delete(`/admin/missions/${id}`);
      toast.success("Mission deleted");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't delete"); }
  };

  const patchProof = async (id, body) => {
    try {
      const { data } = await api.patch(`/admin/missions/proofs/${id}`, body);
      setProofs((arr) => arr.map((p) => (p.id === id ? data : p)));
      if (activeProof?.id === id) setActiveProof(data);
      toast.success("Proof updated");
      // Reload missions so completion_count reflects.
      api.get("/admin/missions").then(({ data }) => setMissions(data?.missions || []));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't update"); }
  };

  const deleteProof = async (id) => {
    if (!confirm("Delete this proof?")) return;
    try {
      await api.delete(`/admin/missions/proofs/${id}`);
      setProofs((arr) => arr.filter((p) => p.id !== id));
      if (activeProof?.id === id) setActiveProof(null);
      toast.success("Deleted");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't delete"); }
  };

  const patchSponsorApp = async (id, body) => {
    try {
      const { data } = await api.patch(`/admin/missions/sponsor-applications/${id}`, body);
      setSponsorApps((arr) => arr.map((a) => (a.id === id ? data : a)));
      toast.success("Updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't update"); }
  };

  const deleteSponsorApp = async (id) => {
    if (!confirm("Delete this sponsor pitch?")) return;
    try {
      await api.delete(`/admin/missions/sponsor-applications/${id}`);
      setSponsorApps((arr) => arr.filter((a) => a.id !== id));
      toast.success("Deleted");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't delete"); }
  };

  if (!s) return <div className="loot-card-flat p-10 text-center text-[#F5F2E8]/40">Loading…</div>;

  const pendingCount = proofs.filter((p) => p.status === "pending").length;

  return (
    <div className="space-y-6" data-testid="missions-admin">
      {/* Master toggle */}
      <div className="loot-card-flat p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="overline">Section status</div>
            <h2 className="font-display uppercase text-2xl mt-1">Community Missions</h2>
            <p className="text-[#F5F2E8]/60 mt-1 text-sm max-w-xl">
              When <strong className="text-[#76B82A]">ON</strong>, the splash CTA, the <code className="font-mono text-[#76B82A]">/missions</code> list, and the Good Deeds wall all render. When <strong className="text-[#E53935]">OFF</strong>, the splash CTA hides, the public mission list returns empty, and proof submissions are blocked.
            </p>
          </div>
          <label className="inline-flex items-center gap-3 shrink-0 cursor-pointer">
            <span className={`text-xs uppercase tracking-widest font-semibold ${s.missions_enabled !== false ? "text-[#76B82A]" : "text-[#E53935]"}`}>
              {s.missions_enabled !== false ? "On" : "Off"}
            </span>
            <input
              type="checkbox"
              checked={s.missions_enabled !== false}
              onChange={(e) => u("missions_enabled", e.target.checked)}
              className="w-12 h-6 appearance-none bg-[#2a2d2a] checked:bg-[#76B82A] rounded-full cursor-pointer"
              style={{ boxShadow: "inset 0 0 0 2px #14110d" }}
              data-testid="missions-toggle"
            />
          </label>
        </div>
        <Field label="Closed message"><input className="loot-input" value={s.closed_message || ""} onChange={(e) => u("closed_message", e.target.value)} /></Field>
        <div className="flex gap-2 mt-3">
          <a href="/missions" target="_blank" rel="noopener noreferrer" className="btn-ghost text-xs">Open /missions ↗</a>
          <a href="/good-deeds" target="_blank" rel="noopener noreferrer" className="btn-ghost text-xs">Open /good-deeds ↗</a>
          <a href="/sponsor-a-mission" target="_blank" rel="noopener noreferrer" className="btn-ghost text-xs">Open /sponsor-a-mission ↗</a>
          <button onClick={resetSettings} disabled={busy} className="btn-ghost text-xs" style={{ color: "#E53935", borderColor: "#E53935" }}>Reset to defaults</button>
        </div>
      </div>

      {/* Sponsor pitches inbox */}
      <div className="loot-card-flat p-5" data-testid="sponsor-pitches-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="overline">Sponsor pitches</div>
            <div className="font-display uppercase text-xl mt-1">
              {sponsorApps.length} total
              {sponsorApps.filter((a) => a.status === "new").length > 0 && (
                <span className="ml-2 inline-flex items-center px-1.5 h-5 rounded-full bg-[#E53935] text-white font-mono text-[10px]">
                  {sponsorApps.filter((a) => a.status === "new").length} new
                </span>
              )}
            </div>
            <p className="text-[#F5F2E8]/55 text-xs mt-1">
              Submissions from <code className="font-mono text-[#76B82A]">/sponsor-a-mission</code>. Reach out within 2 business days.
            </p>
          </div>
        </div>
        {sponsorApps.length === 0 ? (
          <div className="text-center text-[#F5F2E8]/40 py-6 text-sm">No pitches yet.</div>
        ) : (
          <div className="space-y-2">
            {sponsorApps.map((a) => (
              <div key={a.id} className="border border-[#F5F2E8]/10 p-3" data-testid={`sponsor-pitch-${a.id}`}>
                <div className="flex flex-wrap items-start gap-3">
                  <div className={`px-2 py-0.5 text-[10px] uppercase tracking-widest font-mono shrink-0 ${a.status === "new" ? "bg-[#E53935]/20 text-[#E53935]" : a.status === "won" ? "bg-[#76B82A]/20 text-[#76B82A]" : "bg-[#F5F2E8]/10 text-[#F5F2E8]/55"}`}>
                    {a.status}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold">{a.business_name}</div>
                    <div className="text-[12px] text-[#F5F2E8]/65 mt-0.5">{a.contact_name} · {a.email}{a.phone ? ` · ${a.phone}` : ""}</div>
                    {a.budget && <div className="font-mono text-[11px] text-[#d7a62a] mt-0.5">Budget: {a.budget}</div>}
                    <p className="text-[13px] text-[#F5F2E8]/75 mt-2 whitespace-pre-wrap">{a.cause}</p>
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    <select
                      className="loot-input text-xs"
                      value={a.status || "new"}
                      onChange={(e) => patchSponsorApp(a.id, { status: e.target.value })}
                      data-testid={`sponsor-pitch-status-${a.id}`}
                    >
                      {["new", "reviewing", "contacted", "won", "lost"].map((st) => <option key={st} value={st}>{st}</option>)}
                    </select>
                    <button
                      onClick={() => deleteSponsorApp(a.id)}
                      className="btn-ghost text-xs"
                      style={{ color: "#E53935", borderColor: "#E53935" }}
                      data-testid={`sponsor-pitch-delete-${a.id}`}
                    >Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Missions list */}
      <div className="loot-card-flat p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="overline">Missions</div>
            <div className="font-display uppercase text-xl mt-1">{missions.length} total</div>
          </div>
          <button
            onClick={() => setEditor({
              title: "", kicker: "", body: "", image_url: "",
              type: "weekly", status: "active", max_completions: 10,
              starts_at: "", ends_at: "", city: "", location_label: "",
              sponsor_id: "", good_deed_label: "Good Deed",
            })}
            className="btn-loot text-xs inline-flex items-center gap-1"
            data-testid="missions-add"
          ><Plus size={12} /> New mission</button>
        </div>
        {missions.length === 0 ? (
          <div className="text-center text-[#F5F2E8]/40 py-8 text-sm">No missions yet — create your first one.</div>
        ) : (
          <div className="space-y-2">
            {missions.map((m) => (
              <div key={m.id} className="border border-[#F5F2E8]/10 p-3 flex items-center gap-3" data-testid={`mission-row-${m.id}`}>
                <div className={`px-2 py-0.5 text-[10px] uppercase tracking-widest font-mono ${m.status === "active" ? "bg-[#76B82A]/20 text-[#76B82A]" : "bg-[#F5F2E8]/10 text-[#F5F2E8]/55"}`}>
                  {m.status}
                </div>
                <div className="px-2 py-0.5 text-[10px] uppercase tracking-widest font-mono bg-[#d7a62a]/15 text-[#d7a62a]">
                  {m.type}
                </div>
                <div className="flex-1">
                  <div className="font-semibold">{m.title}</div>
                  {m.sponsor && <div className="text-[11px] text-[#F5F2E8]/55">backed by {m.sponsor.business_name}</div>}
                </div>
                <div className="text-xs text-[#F5F2E8]/55 hidden md:block">
                  {m.completion_count}{m.max_completions ? ` / ${m.max_completions}` : ""} done
                </div>
                <button onClick={() => setEditor(m)} className="btn-ghost text-xs">Edit</button>
                <button onClick={() => deleteMission(m.id)} className="text-[#E53935] hover:opacity-80"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {editor && (
        <MissionEditor
          mission={editor}
          sponsors={sponsors}
          onCancel={() => setEditor(null)}
          onSave={saveMission}
          busy={busy}
        />
      )}

      {/* Proofs inbox */}
      <div className="loot-card-flat p-5">
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <div>
            <div className="overline">Proofs inbox</div>
            <div className="font-display uppercase text-xl mt-1">
              {proofs.length} {proofFilter || "total"}{pendingCount > 0 && proofFilter !== "pending" && <span className="text-[#76B82A]"> · {pendingCount} pending</span>}
            </div>
          </div>
          <div className="flex gap-1">
            {["pending", "approved", "rejected", ""].map((st) => (
              <button
                key={st || "all"}
                onClick={() => setProofFilter(st)}
                className={`btn-ghost text-xs ${proofFilter === st ? "border-[#76B82A] text-[#76B82A]" : ""}`}
              >
                {st || "all"}
              </button>
            ))}
          </div>
        </div>
        {proofs.length === 0 ? (
          <div className="text-center text-[#F5F2E8]/40 py-6 text-sm">Nothing here.</div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {proofs.map((p) => (
              <div key={p.id} className="border border-[#F5F2E8]/10 p-3 flex gap-3" data-testid={`proof-row-${p.id}`}>
                {p.photo_url && (
                  <a href={p.photo_url} target="_blank" rel="noopener noreferrer" className="shrink-0">
                    <img src={p.photo_url.startsWith("http") ? p.photo_url : `${process.env.REACT_APP_BACKEND_URL}${p.photo_url}`} alt="" className="w-20 h-20 object-cover border border-[#F5F2E8]/10" />
                  </a>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className={`px-1.5 py-0.5 text-[9px] uppercase tracking-widest font-mono ${p.status === "approved" ? "bg-[#76B82A]/20 text-[#76B82A]" : p.status === "rejected" ? "bg-[#E53935]/20 text-[#E53935]" : "bg-[#d7a62a]/15 text-[#d7a62a]"}`}>
                      {p.status}
                    </div>
                    <div className="font-semibold truncate">{p.name || p.username || "—"}</div>
                  </div>
                  <div className="text-[11px] text-[#F5F2E8]/55 truncate">{p.mission_title}</div>
                  <p className="text-[12px] text-[#F5F2E8]/75 mt-1 line-clamp-2">{p.caption}</p>
                  <div className="flex gap-1 mt-2 flex-wrap">
                    {p.status !== "approved" && <button onClick={() => patchProof(p.id, { status: "approved" })} className="btn-ghost text-[10px] uppercase" style={{ color: "#76B82A", borderColor: "#76B82A" }}>Approve</button>}
                    {p.status !== "rejected" && <button onClick={() => patchProof(p.id, { status: "rejected" })} className="btn-ghost text-[10px] uppercase" style={{ color: "#E53935", borderColor: "#E53935" }}>Reject</button>}
                    <button onClick={() => setActiveProof(p)} className="btn-ghost text-[10px] uppercase">View</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {activeProof && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setActiveProof(null)}>
          <div className="loot-card-flat p-5 max-w-2xl w-full max-h-[90vh] overflow-y-auto space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <div className="overline">{activeProof.status}</div>
                <div className="font-display uppercase text-2xl mt-1">{activeProof.name || activeProof.username}</div>
                <div className="text-[#F5F2E8]/65 text-sm">{activeProof.mission_title}</div>
              </div>
              <button onClick={() => setActiveProof(null)} className="text-[#F5F2E8]/55 hover:text-[#F5F2E8]"><X size={20} /></button>
            </div>
            {activeProof.photo_url && (
              <img src={activeProof.photo_url.startsWith("http") ? activeProof.photo_url : `${process.env.REACT_APP_BACKEND_URL}${activeProof.photo_url}`} alt="" className="w-full max-h-[400px] object-contain bg-black border border-[#F5F2E8]/10" />
            )}
            <div>
              <div className="overline">Caption</div>
              <p className="text-sm mt-1 whitespace-pre-wrap">{activeProof.caption}</p>
            </div>
            {activeProof.lat && activeProof.lng && (
              <div className="splash-mono text-[11px] text-[#76B82A]">
                📍 {activeProof.lat.toFixed(5)}, {activeProof.lng.toFixed(5)}
                {activeProof.captured_at && <> · {new Date(activeProof.captured_at).toLocaleString()}</>}
              </div>
            )}
            <Field label="Admin notes">
              <textarea
                className="loot-input"
                rows={3}
                defaultValue={activeProof.admin_notes || ""}
                onBlur={(e) => {
                  if (e.target.value !== (activeProof.admin_notes || "")) {
                    patchProof(activeProof.id, { admin_notes: e.target.value });
                  }
                }}
              />
            </Field>
            <div className="flex gap-2 flex-wrap pt-2 border-t border-[#F5F2E8]/10">
              <button onClick={() => patchProof(activeProof.id, { status: "approved" })} className="btn-loot text-xs">Approve & award badge</button>
              <button onClick={() => patchProof(activeProof.id, { status: "rejected" })} className="btn-ghost text-xs" style={{ color: "#E53935", borderColor: "#E53935" }}>Reject</button>
              <button onClick={() => deleteProof(activeProof.id)} className="ml-auto text-[#E53935] hover:opacity-80 text-xs uppercase">Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Settings copy */}
      <div className="loot-card-flat p-5 space-y-3">
        <div className="overline">Splash CTA copy</div>
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={s.cta_show !== false} onChange={(e) => u("cta_show", e.target.checked)} />
          Show CTA section on the splash page
        </label>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Overline"><input className="loot-input" value={s.cta_overline || ""} onChange={(e) => u("cta_overline", e.target.value)} /></Field>
          <Field label="Headline (last word colored green)"><input className="loot-input" value={s.cta_headline || ""} onChange={(e) => u("cta_headline", e.target.value)} /></Field>
        </div>
        <Field label="Subhead"><textarea className="loot-input" rows={3} value={s.cta_subhead || ""} onChange={(e) => u("cta_subhead", e.target.value)} /></Field>
        <Field label="Button label"><input className="loot-input" value={s.cta_button_label || ""} onChange={(e) => u("cta_button_label", e.target.value)} /></Field>
      </div>

      <div className="loot-card-flat p-5 space-y-3">
        <div className="overline">/missions page hero</div>
        <ImageField label="Hero photo" value={s.page_hero_image_url} onChange={(v) => u("page_hero_image_url", v)} testid="missions-hero-img" />
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Overline"><input className="loot-input" value={s.page_overline || ""} onChange={(e) => u("page_overline", e.target.value)} /></Field>
          <Field label="Headline"><input className="loot-input" value={s.page_headline || ""} onChange={(e) => u("page_headline", e.target.value)} /></Field>
        </div>
        <Field label="Intro paragraph"><textarea className="loot-input" rows={3} value={s.page_intro || ""} onChange={(e) => u("page_intro", e.target.value)} /></Field>
      </div>

      <div className="loot-card-flat p-5 space-y-3">
        <div className="overline">Badges & legend rules</div>
        <div className="grid md:grid-cols-3 gap-3">
          <Field label="Good Deed badge label"><input className="loot-input" value={s.good_deed_label || ""} onChange={(e) => u("good_deed_label", e.target.value)} /></Field>
          <Field label="Loot Legend label"><input className="loot-input" value={s.legend_label || ""} onChange={(e) => u("legend_label", e.target.value)} /></Field>
          <Field label="Good Deeds needed for Loot Legend"><input className="loot-input" type="number" min="1" max="999" value={s.legend_threshold || 5} onChange={(e) => u("legend_threshold", parseInt(e.target.value, 10) || 5)} /></Field>
        </div>
        <div className="grid md:grid-cols-3 gap-3 border-t border-[#F5F2E8]/10 pt-3">
          <Field label="Caption min chars"><input className="loot-input" type="number" min="1" max="400" value={s.submit_caption_min || 10} onChange={(e) => u("submit_caption_min", parseInt(e.target.value, 10) || 10)} /></Field>
          <Field label="Caption max chars"><input className="loot-input" type="number" min="10" max="2000" value={s.submit_caption_max || 600} onChange={(e) => u("submit_caption_max", parseInt(e.target.value, 10) || 600)} /></Field>
          <label className="flex items-end gap-2 text-sm pb-2">
            <input type="checkbox" checked={!!s.require_location} onChange={(e) => u("require_location", e.target.checked)} />
            Require location capture on every submission
          </label>
        </div>
      </div>

      <div className="sticky bottom-4 flex justify-end z-10">
        <button onClick={saveSettings} disabled={busy} className="btn-loot inline-flex items-center gap-2" data-testid="missions-save">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {busy ? "Saving…" : "Save settings"}
        </button>
      </div>
    </div>
  );
}

// Mission editor modal — used for both create and edit.
function MissionEditor({ mission, sponsors, onCancel, onSave, busy }) {
  const [m, setM] = useState({ ...mission });
  const u = (k, v) => setM({ ...m, [k]: v });
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="loot-card-flat p-5 max-w-2xl w-full max-h-[90vh] overflow-y-auto space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <div className="overline">{m.id ? "Edit mission" : "New mission"}</div>
            <div className="font-display uppercase text-xl mt-1">{m.title || "Untitled"}</div>
          </div>
          <button onClick={onCancel} className="text-[#F5F2E8]/55 hover:text-[#F5F2E8]"><X size={20} /></button>
        </div>
        <Field label="Title *"><input className="loot-input" value={m.title || ""} onChange={(e) => u("title", e.target.value)} /></Field>
        <Field label="Kicker (script line under title)"><input className="loot-input" value={m.kicker || ""} onChange={(e) => u("kicker", e.target.value)} /></Field>
        <Field label="Body *"><textarea className="loot-input" rows={4} value={m.body || ""} onChange={(e) => u("body", e.target.value)} /></Field>
        <ImageField label="Hero image" value={m.image_url} onChange={(v) => u("image_url", v)} testid="mission-image" />
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Type">
            <select className="loot-input" value={m.type || "weekly"} onChange={(e) => u("type", e.target.value)}>
              <option value="weekly">weekly (rotates)</option>
              <option value="evergreen">evergreen (always-on)</option>
            </select>
          </Field>
          <Field label="Status">
            <select className="loot-input" value={m.status || "active"} onChange={(e) => u("status", e.target.value)}>
              <option value="draft">draft</option>
              <option value="active">active</option>
              <option value="completed">completed</option>
              <option value="archived">archived</option>
            </select>
          </Field>
          <Field label="Max completions (0 = unlimited)"><input className="loot-input" type="number" min="0" max="9999" value={m.max_completions || 0} onChange={(e) => u("max_completions", parseInt(e.target.value, 10) || 0)} /></Field>
          <Field label="City"><input className="loot-input" value={m.city || ""} onChange={(e) => u("city", e.target.value)} /></Field>
          <Field label="Starts at (ISO)"><input className="loot-input" placeholder="2026-04-29T18:00:00Z" value={m.starts_at || ""} onChange={(e) => u("starts_at", e.target.value)} /></Field>
          <Field label="Ends at (ISO)"><input className="loot-input" placeholder="2026-05-06T18:00:00Z" value={m.ends_at || ""} onChange={(e) => u("ends_at", e.target.value)} /></Field>
        </div>
        <Field label="Location label (e.g. 'Jonesborough Food Pantry, 312 Main St')"><input className="loot-input" value={m.location_label || ""} onChange={(e) => u("location_label", e.target.value)} /></Field>
        <Field label="Sponsor (optional — backs the mission with their name)">
          <select className="loot-input" value={m.sponsor_id || ""} onChange={(e) => u("sponsor_id", e.target.value)}>
            <option value="">— No sponsor —</option>
            {(sponsors || []).map((sp) => (
              <option key={sp.id || sp._id} value={sp.id || sp._id}>{sp.business_name || sp.name}</option>
            ))}
          </select>
        </Field>
        <Field label="Good Deed badge label override (optional)"><input className="loot-input" value={m.good_deed_label || ""} onChange={(e) => u("good_deed_label", e.target.value)} /></Field>
        <div className="flex gap-2 pt-2 border-t border-[#F5F2E8]/10">
          <button onClick={() => onSave(m)} disabled={busy} className="btn-loot inline-flex items-center gap-2">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {m.id ? "Save changes" : "Create mission"}
          </button>
          <button onClick={onCancel} className="btn-ghost text-xs">Cancel</button>
        </div>
      </div>
    </div>
  );
}


export default function AdminPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("drafts");
  const [reseeding, setReseeding] = useState(false);

  const loadStats = async () => {
    if (user?.role !== "admin") {
      // Lootmasters don't have access to /admin/stats — skip the call entirely
      // to avoid 403 console noise.
      setStats(null);
      return;
    }
    try {
      const { data } = await api.get("/admin/stats");
      setStats(data);
    } catch {
      setStats(null);
    }
  };

  useEffect(() => { loadStats(); }, [tab, user?.role]);
  const reseed = async () => {
    if (!confirm("Reseed just the Kingsport demo drop? This only affects the seeded demo — all your real drops stay intact.")) return;
    setReseeding(true);
    try {
      const { data } = await api.post("/admin/reseed-demo");
      toast.success(`Demo drop reseeded (removed ${data.removed})`);
      loadStats();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setReseeding(false);
    }
  };

  const isLootmaster = user?.role === "lootmaster";
  const tabGroups = [
    {
      title: "Today's Hunt",
      tabs: [
        { k: "drafts", l: "Drafts" },
        { k: "live", l: "Live" },
        { k: "drops", l: "Drops" },
        { k: "waymarks", l: "Waymarks" },
        { k: "loot_quest", l: "Loot Quest" },
        { k: "telemetry", l: "Hint Telemetry", adminOnly: true },
      ],
    },
    {
      title: "Community",
      tabs: [
        { k: "missions", l: "Missions", adminOnly: true },
        { k: "tank", l: "Tank ⛽", adminOnly: true },
        { k: "stash", l: "Stash 🪙", adminOnly: true },
        { k: "vault", l: "Vault 🗝️", adminOnly: true },
        { k: "badges", l: "Badges 🏅", adminOnly: true },
        { k: "claims", l: "Claims 🎁", adminOnly: true },
        { k: "bazaar", l: "Bazaar 🛍", adminOnly: true },
        { k: "auctions", l: "Auctions 🏆", adminOnly: true },
        { k: "holler", l: "Holler", adminOnly: true },
        { k: "pulse", l: "Pulse 📊", adminOnly: true },
        { k: "push", l: "Push", adminOnly: true },
        { k: "lootmaster", l: "LM Recruit", adminOnly: true },
        { k: "lootmasters", l: "Lootmasters", adminOnly: true },
      ],
    },
    {
      title: "Public site",
      tabs: [
        { k: "splash", l: "Splash", adminOnly: true },
        { k: "event_promo", l: "Event Promo", adminOnly: true },
        { k: "quote", l: "Daily Quote", adminOnly: true },
        { k: "teaser", l: "Teaser", adminOnly: true },
        { k: "founder", l: "Founder", adminOnly: true },
        { k: "met", l: "Met-the-Founder 👑", adminOnly: true },
        { k: "spoils", l: "The Spoils 💰", adminOnly: true },
      ],
    },
    {
      title: "Sponsors & money",
      tabs: [
        { k: "sponsors", l: "Sponsors", adminOnly: true },
        { k: "packages", l: "Packages", adminOnly: true },
        { k: "leads", l: "Leads", adminOnly: true },
        { k: "stripe", l: "Stripe / Settings", adminOnly: true },
      ],
    },
    {
      title: "Private events",
      tabs: [
        { k: "private", l: "Private" },
      ],
    },
    {
      title: "People & winners",
      tabs: [
        { k: "winners", l: "Winners" },
        { k: "hunters", l: "Hunters" },
      ],
    },
    {
      title: "Inbox",
      tabs: [
        { k: "contact", l: "Contact", adminOnly: true },
        { k: "reports", l: "Reports" },
        { k: "support", l: "Support", adminOnly: true },
        { k: "notif", l: "Notify", adminOnly: true },
      ],
    },
  ];
  const visibleGroups = tabGroups
    .map((g) => ({ ...g, tabs: g.tabs.filter((t) => !t.adminOnly || !isLootmaster) }))
    .filter((g) => g.tabs.length > 0);

  return (
    <div className="py-8 space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="overline">{isLootmaster ? "Lootmaster console" : "Command center"}</div>
          <h1 className="font-display uppercase text-5xl tracking-tight mt-2">
            {isLootmaster ? (
              <>Lootmaster <span style={{ color: "#D7A62A" }}>console</span></>
            ) : (
              <>Admin <span style={{ color: "#E53935" }}>dashboard</span></>
            )}
          </h1>
          {isLootmaster && (
            <div className="mt-2 font-mono text-xs text-[#F5F2E8]/60">
              Cities you run: {(user?.lootmaster_cities || []).join(" · ") || "(none assigned yet)"}
            </div>
          )}
        </div>
        <button
          onClick={reseed}
          disabled={reseeding}
          className="btn-gold inline-flex items-center gap-2 self-start hidden"
          data-testid="admin-reseed-btn"
        >
          <RefreshCw size={14} className={reseeding ? "animate-spin" : ""} />
          {reseeding ? "Reseeding..." : "Reseed demo drop"}
        </button>
      </div>
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard k="Live" v={stats.drops_live} color="#E53935" />
          <StatCard k="Drops total" v={stats.drops_total} />
          <StatCard k="Golden" v={stats.drops_golden || 0} color="#D7A62A" />
          <StatCard k="Winners" v={stats.winners_total} color="#D7A62A" />
          <StatCard k="Pending winners" v={stats.winners_pending} color="#D7A62A" />
          <StatCard k="Sponsors" v={stats.sponsors} />
          <StatCard k="New leads" v={stats.sponsor_apps_new} color="#D7A62A" />
          <StatCard k="Hunters" v={stats.users} />
        </div>
      )}
      <nav className="space-y-3 pb-2 border-b border-[#F5F2E8]/10" aria-label="Admin sections" data-testid="admin-tab-nav">
        {visibleGroups.map((g) => (
          <div key={g.title} className="flex flex-wrap items-center gap-x-3 gap-y-2" data-testid={`admin-tab-group-${g.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <div className="overline shrink-0 min-w-[110px] text-[#F5F2E8]/45">{g.title}</div>
            <div className="flex flex-wrap gap-1.5">
              {g.tabs.map((t) => (
                <button
                  key={t.k}
                  onClick={() => setTab(t.k)}
                  className={`px-3 py-1.5 font-display uppercase tracking-wide text-xs border-2 transition ${tab === t.k ? "border-[#76B82A] text-[#76B82A] bg-[#76B82A]/10" : "border-transparent text-[#F5F2E8]/60 hover:text-[#F5F2E8]/85"}`}
                  data-testid={`admin-tab-${t.k}`}
                >
                  {t.l}
                </button>
              ))}
            </div>
          </div>
        ))}
      </nav>
      <div>
        {tab === "drafts" && <DraftsTab />}
        {tab === "live" && <LiveTab />}
        {tab === "splash" && <SplashTab />}
        {tab === "event_promo" && <EventPromoTab />}
        {tab === "lootmaster" && <LootMasterTab />}
        {tab === "missions" && <MissionsTab />}
        {tab === "tank" && <TankTab />}
        {tab === "stash" && <StashTab />}
        {tab === "vault" && <VaultAdminTab />}
        {tab === "badges" && <BadgesAdminTab />}
        {tab === "claims" && <ClaimsTab />}
        {tab === "bazaar" && <BazaarAdminTab />}
        {tab === "auctions" && <AuctionsAdminTab />}
        {tab === "holler" && <HollerTab />}
        {tab === "pulse" && <PulseAdminTab />}
        {tab === "push" && <PushTab />}
        {tab === "drops" && <DropsTab />}
        {tab === "waymarks" && <WaymarksTab />}
        {tab === "loot_quest" && <LootQuestTab />}
        {tab === "private" && <PrivateHubTab />}
        {tab === "contact" && <ContactTab />}
        {tab === "founder" && <FounderTab />}
        {tab === "met" && <MetFounderTab />}
        {tab === "spoils" && <SpoilsAdminTab />}
        {tab === "winners" && <WinnersTab />}
        {tab === "sponsors" && <SponsorsTab />}
        {tab === "packages" && <PackagesTab />}
        {tab === "leads" && <LeadsTab />}
        {tab === "hunters" && <HuntersTab />}
        {tab === "lootmasters" && <LootmastersTab />}
        {tab === "notif" && <NotifTab />}
        {tab === "quote" && <DailyQuoteTab />}
        {tab === "teaser" && <TeaserTab />}
        {tab === "support" && <SupportTab />}
        {tab === "telemetry" && <HintTelemetryTab />}
        {tab === "reports" && <ReportsTab />}
        {tab === "stripe" && <StripeSettingsTab />}
      </div>
    </div>
  );
}
